import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react';
import { toast, ToastContainer } from 'react-toastify';
import { API_BASE_URL } from '../config';
import { formatUTCDate } from '../utils/dateUtils';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  IconButton,
  Box,
  Rating,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputAdornment,
  Select,
  FormControl,
  FormHelperText,
  InputLabel,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import { Close as CloseIcon, Event as EventIcon, Star as StarIcon, StarBorder as StarBorderIcon, Info as InfoIcon, Cancel as CancelIcon, PersonAdd as PersonAddIcon, Person as PersonIcon, Delete as DeleteIcon, Edit as EditIcon, ExpandMore as ExpandMoreIcon, AccountBalance as AccountBalanceIcon, Add as AddIcon, Save as SaveIcon, LinkedIn as LinkedInIcon, Email as EmailIcon, Work as WorkIcon, Lightbulb as LightbulbIcon, FactCheck as FactCheckIcon, VerifiedUser as VerifiedUserIcon, Business as BusinessIcon, AssignmentInd as AssignmentIndIcon, BarChart as ChartBarIcon, Warning as ExclamationTriangleIcon, CalendarToday as CalendarIcon, Search as SearchIcon, Check as CheckIcon, CloudUpload as CloudUploadIcon, AttachFile as AttachFileIcon, Link as LinkIcon, Description as DescriptionIcon, FilterList as FilterListIcon, ChatBubble as ChatBubbleLeftRightIcon, Article as DocumentTextIcon, History as HistoryIcon, AccessTime as AccessTimeIcon, LocationOn as LocationOnIcon, Notes as NotesIcon, Flag as FlagIcon, InsertDriveFile as FileIcon, PictureAsPdf as PdfIcon, Image as ImageIcon, TableChart as ExcelIcon, Slideshow as PptIcon, Article as WordIcon } from '@mui/icons-material';
import { Group as GroupIcon, TrendingUp as TrendingUpIcon } from '@mui/icons-material';
import { expertiseCategories, industryDetailOptions, industryRoles } from '../config/mentorExpertise';
import { ACHIEVEMENT_CATEGORIES, PROFILE_SECTIONS } from '../config/profileProgressConfig';

// Time slot options for availability
const timeSlotOptions = [
  { id: "wd_morning", label: "Weekdays (Morning)" },
  { id: "wd_afternoon", label: "Weekdays (Afternoon)" },
  { id: "wd_evening", label: "Weekdays (Evening)" },
  { id: "we_morning", label: "Weekends (Morning)" },
  { id: "we_afternoon", label: "Weekends (Afternoon)" },
  { id: "we_evening", label: "Weekends (Evening)" },
  { id: "flexible", label: "Flexible / On request" },
];

// Maximum length for session titles in scheduling dialogs
const MAX_SESSION_TITLE_LENGTH = 100;

// Helper function to truncate titles for display
const truncateTitle = (title, maxLength = 50) => {
  if (!title) return '';
  if (title.length <= maxLength) return title;
  return title.substring(0, maxLength).trim() + '...';
};

const DialogContext = createContext(null);

let externalDialogHandler = null;

const setExternalDialogHandler = (handler) => {
  externalDialogHandler = handler;
};

const noopPromise = (fallback) => Promise.resolve(fallback);

export const dialogService = {
  confirm: (options) =>
    externalDialogHandler
      ? externalDialogHandler({ type: 'confirm', ...options })
      : noopPromise(false),
  alert: (options) =>
    externalDialogHandler
      ? externalDialogHandler({ type: 'alert', ...options })
      : noopPromise(true),
  prompt: (options) =>
    externalDialogHandler
      ? externalDialogHandler({ type: 'prompt', ...options })
      : noopPromise(null),
  rating: (options) =>
    externalDialogHandler
      ? externalDialogHandler({ type: 'rating', ...options })
      : noopPromise(null),
  multiselect: (options) =>
    externalDialogHandler
      ? externalDialogHandler({ type: 'multiselect', ...options })
      : noopPromise([]),
  scores: (options) =>
    externalDialogHandler
      ? externalDialogHandler({ type: 'scores', ...options })
      : noopPromise(null),
  juryassign: (options) =>
    externalDialogHandler
      ? externalDialogHandler({ type: 'juryassign', ...options })
      : noopPromise([]),
  addjury: (options) =>
    externalDialogHandler
      ? externalDialogHandler({ type: 'addjury', ...options })
      : noopPromise(null),
  addfunding: (options) =>
    externalDialogHandler
      ? externalDialogHandler({ type: 'addfunding', ...options })
      : noopPromise(null),
  funding: (options) =>
    externalDialogHandler
      ? externalDialogHandler({ type: 'funding', ...options })
      : noopPromise(null),
  fundusage: (options) =>
    externalDialogHandler
      ? externalDialogHandler({ type: 'fundusage', ...options })
      : noopPromise(null),
  fundinghistory: (options) =>
    externalDialogHandler
      ? externalDialogHandler({ type: 'fundinghistory', ...options })
      : noopPromise(null),
  paymentmethod: (options) =>
    externalDialogHandler
      ? externalDialogHandler({ type: 'paymentmethod', ...options })
      : noopPromise(null),
  quickschedule: (options) =>
    externalDialogHandler
      ? externalDialogHandler({ type: 'quickschedule', ...options })
      : noopPromise(null),
  addexpense: (options) =>
    externalDialogHandler
      ? externalDialogHandler({ type: 'addexpense', ...options })
      : noopPromise(null),
  assignmentaction: (options) =>
    externalDialogHandler
      ? externalDialogHandler({ type: 'assignmentaction', ...options })
      : noopPromise(null),
  startupapplicationlist: (options) =>
    externalDialogHandler
      ? externalDialogHandler({ type: 'startupapplicationlist', ...options })
      : noopPromise(null),
  addfundingdocuments: (options) =>
    externalDialogHandler
      ? externalDialogHandler({ type: 'addfundingdocuments', ...options })
      : noopPromise(null),
  assignmentdetails: (options) =>
    externalDialogHandler
      ? externalDialogHandler({ type: 'assignmentdetails', ...options })
      : noopPromise(null),
  declineassignment: (options) =>
    externalDialogHandler
      ? externalDialogHandler({ type: 'declineassignment', ...options })
      : noopPromise(null),
  editexpertise: (options) =>
    externalDialogHandler
      ? externalDialogHandler({ type: 'editexpertise', ...options })
      : noopPromise([]),
  editindustries: (options) =>
    externalDialogHandler
      ? externalDialogHandler({ type: 'editindustries', ...options })
      : noopPromise({ industries: [], industryDetails: {} }),
  manageachievements: (options) =>
    externalDialogHandler
      ? externalDialogHandler({ type: 'manageachievements', ...options })
      : noopPromise([]),
  managespeaking: (options) =>
    externalDialogHandler
      ? externalDialogHandler({ type: 'managespeaking', ...options })
      : noopPromise([]),
  managepublications: (options) =>
    externalDialogHandler
      ? externalDialogHandler({ type: 'managepublications', ...options })
      : noopPromise([]),
  manageavailability: (options) =>
    externalDialogHandler
      ? externalDialogHandler({ type: 'manageavailability', ...options })
      : noopPromise(null),
  bulkverification: (options) =>
    externalDialogHandler
      ? externalDialogHandler({ type: 'bulkverification', ...options })
      : noopPromise(null),
  verification: (options) =>
    externalDialogHandler
      ? externalDialogHandler({ type: 'verification', ...options })
      : noopPromise(null),
  documenthistory: (options) =>
    externalDialogHandler
      ? externalDialogHandler({ type: 'documenthistory', ...options })
      : noopPromise(null),
  bulkreview: (options) =>
    externalDialogHandler
      ? externalDialogHandler({ type: 'bulkreview', ...options })
      : noopPromise(null),
  profilereview: (options) =>
    externalDialogHandler
      ? externalDialogHandler({ type: 'profilereview', ...options })
      : noopPromise(null),
  reviewhistory: (options) =>
    externalDialogHandler
      ? externalDialogHandler({ type: 'reviewhistory', ...options })
      : noopPromise(null),
  contextreview: (options) =>
    externalDialogHandler
      ? externalDialogHandler({ type: 'contextreview', ...options })
      : noopPromise(null),
  startupdetails: (options) =>
    externalDialogHandler
      ? externalDialogHandler({ type: 'startupdetails', ...options })
      : noopPromise(null),
};

const enhanceConfig = (config) => {
  const baseTitles = {
    alert: 'Notice',
    confirm: 'Confirm Action',
    prompt: 'Input Required',
    rating: 'Rate Event',
    select: 'Select Option',
    multiselect: 'Select Options',
    scores: 'Jury Scores',
    juryassign: 'Assign Jury Members',
    addfunding: 'Add Fund',
    funding: 'Funding',
    fundusage: 'Add Fund Usage',
    fundinghistory: 'Funding History',
    paymentmethod: 'Add Reimbursement Details',
    addfundingdocuments: 'Add Documents',
    quickschedule: 'Quick Schedule',
    assignmentaction: 'Assignment Actions',
    startupapplicationlist: 'Select Startup & Application',
    mentordetails: 'Mentor Information',
    mentorverify: 'Verify Mentor',
    mentorrejectionnotes: 'Rejection Notes',
    confirmassignment: 'Confirm Assignment',
    assignmentdetails: 'Assignment Details',
    editexpertise: 'Edit Areas of Expertise',
    editindustries: 'Edit Industry Experience',
    manageachievements: 'Manage Achievements',
    managespeaking: 'Manage Speaking Engagements',
    managepublications: 'Manage Publications',
    manageavailability: 'Edit Availability',
    declineassignment: 'Decline Assignment',
    bulkverification: 'Bulk Verification',
    verification: 'Verify Document',
    documenthistory: 'Document History',
    bulkreview: 'Bulk Review',
    profilereview: 'Review Profile',
    reviewhistory: 'Review History',
    contextreview: 'Review Assessment',
    startupdetails: 'Startup Details',
  };

  const baseConfirmLabels = {
    alert: 'OK',
    confirm: 'Confirm',
    prompt: 'Submit',
    rating: 'Submit Rating',
    select: 'Confirm',
    multiselect: 'Confirm',
    scores: 'Close',
    juryassign: 'Assign Jury Members',
    addfunding: 'Add Fund',
    funding: 'Close',
    fundusage: 'Add Expense',
    fundinghistory: 'Close',
    paymentmethod: 'Submit',
    quickschedule: 'Confirm Booking',
    addexpense: 'Save Expense',
    assignmentaction: 'Confirm',
    startupapplicationlist: 'Select',
    mentordetails: 'Close',
    mentorrejectionnotes: 'Close',
    confirmassignment: 'Confirm Assignment',
    assignmentdetails: 'Close',
    manageachievements: 'Save',
    managespeaking: 'Save',
    managepublications: 'Save',
    manageavailability: 'Save',
    declineassignment: 'Decline Assignment',
    bulkverification: 'Apply to All',
    verification: 'Verify Document',
    bulkreview: 'Review Startups',
    profilereview: 'Submit Review',
    reviewhistory: 'Close',
    contextreview: 'Submit Review',
    startupdetails: 'Close',
  };

  const resolvedConfirmText =
    config.confirmText || baseConfirmLabels[config.type] || 'Confirm';
  // Auto-flag destructive intent so the confirm submit picks up the
  // .an-btn--danger / submitButtonDanger styling without each caller
  // having to wire `destructive: true` explicitly.
  const looksDestructive = /^(delete|remove|discard|cancel\b|archive|reject|decline|revoke)/i.test(
    resolvedConfirmText.trim()
  );
  return {
    ...config,
    title: config.title || baseTitles[config.type] || 'Confirm',
    subtitle: config.subtitle || '',
    confirmText: resolvedConfirmText,
    destructive: typeof config.destructive === 'boolean' ? config.destructive : looksDestructive,
    cancelText: config.cancelText || 'Cancel',
    message: config.message || '',
    placeholder: config.placeholder || '',
    inputLabel: config.inputLabel || 'Response',
    defaultValue: config.defaultValue || '',
    options: config.options || [],
    selectLabel: config.selectLabel || 'Select an option',
    infoChips: config.infoChips || [],
    showCancel:
      typeof config.showCancel === 'boolean' ? config.showCancel : true,
    ratingValue: config.ratingValue || 0,
    scoresData: config.scoresData || null,
    loadingScores: config.loadingScores || false,
  };
};

// Currency mapping for expense form
const EXPENSE_CURRENCIES = {
  'INR': { name: 'Indian Rupee', symbol: '₹' },
  'USD': { name: 'US Dollar', symbol: '$' },
  'EUR': { name: 'Euro', symbol: '€' },
  'GBP': { name: 'British Pound', symbol: '£' },
  'JPY': { name: 'Japanese Yen', symbol: '¥' },
  'CNY': { name: 'Chinese Yuan', symbol: '¥' },
  'AUD': { name: 'Australian Dollar', symbol: 'A$' },
  'CAD': { name: 'Canadian Dollar', symbol: 'C$' },
  'SGD': { name: 'Singapore Dollar', symbol: 'S$' },
  'AED': { name: 'UAE Dirham', symbol: 'د.إ' },
};

export const DialogProvider = ({ children }) => {
  const [dialogConfig, setDialogConfig] = useState(null);
  const [resolver, setResolver] = useState(null);
  const previousActiveElementRef = useRef(null);
  const [inputValue, setInputValue] = useState('');
  const [ratingValue, setRatingValue] = useState(0);
  const [selectedValues, setSelectedValues] = useState([]);
  const [deadlineDate, setDeadlineDate] = useState('');
  const [deadlineDateError, setDeadlineDateError] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [juryForm, setJuryForm] = useState({
    firstName: '',
    lastName: '',
    email: ''
  });
  const [juryFormErrors, setJuryFormErrors] = useState({});
  const [submittingJury, setSubmittingJury] = useState(false);
  const [assignmentForm, setAssignmentForm] = useState({
    goals: '',
    reason: ''
  });
  const [declineForm, setDeclineForm] = useState({
    reason: ''
  });
  const [assignmentFormErrors, setAssignmentFormErrors] = useState({});
  const [declineFormErrors, setDeclineFormErrors] = useState({});
  const [fundingForm, setFundingForm] = useState({
    startupId: '',
    applicationId: '',
    amount: '',
    currencyId: '', // Will default to INR
    paymentType: 'full', // 'full' or 'installment' - default to 'full'
    installmentType: 'custom', // only 'custom' is used for installments now
    numberOfInstallments: '', // Only for fix installments
    paymentFrequency: 'monthly', // 'monthly' or 'yearly' (only for fix installments)
    customInstallments: [], // Array of {amount: '', date: ''} for custom installments
    documents: [], // Array of File objects for document uploads
    notes: '' // Notes field for funding records
  });
  // Hardcoded currencies list (can be fetched from API later)
  // INR is first (default), rest are sorted alphabetically by code
  const currenciesList = useMemo(() => {
    const allCurrencies = [
      { id: 1, code: 'INR', name: 'Indian Rupee', symbol: '₹' },
      { id: 2, code: 'USD', name: 'US Dollar', symbol: '$' },
      { id: 3, code: 'EUR', name: 'Euro', symbol: '€' },
      { id: 4, code: 'GBP', name: 'British Pound', symbol: '£' },
      { id: 5, code: 'JPY', name: 'Japanese Yen', symbol: '¥' },
      { id: 6, code: 'CNY', name: 'Chinese Yuan', symbol: '¥' },
      { id: 7, code: 'AUD', name: 'Australian Dollar', symbol: 'A$' },
      { id: 8, code: 'CAD', name: 'Canadian Dollar', symbol: 'C$' },
      { id: 9, code: 'SGD', name: 'Singapore Dollar', symbol: 'S$' },
      { id: 10, code: 'AED', name: 'UAE Dirham', symbol: 'د.إ' }
    ];

    // Separate INR and other currencies
    const inrCurrency = allCurrencies.find(c => c.code === 'INR');
    const otherCurrencies = allCurrencies.filter(c => c.code !== 'INR');

    // Sort other currencies alphabetically by code
    otherCurrencies.sort((a, b) => a.code.localeCompare(b.code));

    // Return INR first, then sorted others
    return inrCurrency ? [inrCurrency, ...otherCurrencies] : otherCurrencies;
  }, []);
  const defaultCurrencyId = 1; // INR
  const [fundingFormErrors, setFundingFormErrors] = useState({});
  const [submittingFunding, setSubmittingFunding] = useState(false);
  const [showFundingConfirmation, setShowFundingConfirmation] = useState(false);
  // State for startup-application list dialog
  const [startupApplicationListData, setStartupApplicationListData] = useState([]);
  const [loadingStartupApplications, setLoadingStartupApplications] = useState({});
  const [startupsList, setStartupsList] = useState([]);
  const [applicationsList, setApplicationsList] = useState([]);
  const [loadingApplications, setLoadingApplications] = useState(false);
  // Catalog of published fund offerings — loaded when the Add Fund dialog opens
  // so the user can pick which fund the new funding row draws from.
  const [fundOfferingsList, setFundOfferingsList] = useState([]);
  const [loadingFundOfferings, setLoadingFundOfferings] = useState(false);
  // State for selectstartupapplication dialog
  const [startupApplicationForm, setStartupApplicationForm] = useState({
    startupId: '',
    applicationId: ''
  });
  const [startupApplicationErrors, setStartupApplicationErrors] = useState({});
  const [fundUsageForm, setFundUsageForm] = useState({
    title: '',
    amount: '',
    description: '',
    documents: [], // Changed from single file to array of files
  });
  const [fundUsageFormErrors, setFundUsageFormErrors] = useState({});
  const [submittingFundUsage, setSubmittingFundUsage] = useState(false);

  // State for add funding documents dialog
  const [fundingDocumentsForm, setFundingDocumentsForm] = useState({
    fundingId: '',
    title: '',
    documents: []
  });
  const [fundingDocumentsFormErrors, setFundingDocumentsFormErrors] = useState({});
  const [submittingFundingDocuments, setSubmittingFundingDocuments] = useState(false);

  // State for payment method dialog
  const [paymentMethodForm, setPaymentMethodForm] = useState({
    paymentType: 'full', // 'full' or 'installment'
    customInstallments: [{ amount: '', date: '', status: 'unpaid', notes: '' }], // For installment - each milestone has its own notes
    notes: '' // Admin notes for full payment reimbursement details
  });
  const [paymentMethodFormErrors, setPaymentMethodFormErrors] = useState({});
  const [submittingPaymentMethod, setSubmittingPaymentMethod] = useState(false);
  const [showPaymentMethodConfirmation, setShowPaymentMethodConfirmation] = useState(false);
  const [pendingPaymentMethodData, setPendingPaymentMethodData] = useState(null);
  const [mentorVerifyForm, setMentorVerifyForm] = useState({
    linkedin_verified: false,
    experience_verified: false,
    domain_expertise_assessed: false,
    notes: ''
  });
  const [assignmentDetailsState, setAssignmentDetailsState] = useState({
    completionRating: 5,
    completionComment: '',
    pendingAction: null,
    submitting: false
  });
  const [expertiseSearch, setExpertiseSearch] = useState('');
  const [selectedExpertiseAreas, setSelectedExpertiseAreas] = useState([]);
  const [expertiseError, setExpertiseError] = useState('');
  const [selectedIndustries, setSelectedIndustries] = useState([]);
  const [industryDetails, setIndustryDetails] = useState({});
  const [industryError, setIndustryError] = useState('');
  const [industryYearsErrors, setIndustryYearsErrors] = useState({});
  const [achievements, setAchievements] = useState([]);
  const [achievementsErrors, setAchievementsErrors] = useState({});
  const [achievementDragOver, setAchievementDragOver] = useState({});
  const [speakingEngagements, setSpeakingEngagements] = useState([]);
  const [speakingEngagementsErrors, setSpeakingEngagementsErrors] = useState({});
  const [publications, setPublications] = useState([]);
  const [publicationsErrors, setPublicationsErrors] = useState({});
  const [availability, setAvailability] = useState({
    frequency: '',
    commitment: '',
    timeSlots: []
  });
  const [availabilityErrors, setAvailabilityErrors] = useState({});
  const [bulkVerificationForm, setBulkVerificationForm] = useState({
    action: 'update_status', // 'update_status' or 'export_selected'
    status: '',
    comments: '',
    exportFormat: 'csv' // 'csv', 'excel', or 'pdf'
  });
  const [bulkVerificationErrors, setBulkVerificationErrors] = useState({});
  const [verificationForm, setVerificationForm] = useState({
    status: '',
    comments: ''
  });
  const [verificationErrors, setVerificationErrors] = useState({});
  const [bulkReviewForm, setBulkReviewForm] = useState({
    status: '',
    comments: ''
  });
  const [bulkReviewErrors, setBulkReviewErrors] = useState({});
  const [submittingBulkReview, setSubmittingBulkReview] = useState(false);
  const [profileReviewForm, setProfileReviewForm] = useState({
    status: '',
    comments: '',
    feedback: {},
    revision_requirements: {}
  });
  const [profileReviewErrors, setProfileReviewErrors] = useState({});
  const [submittingProfileReview, setSubmittingProfileReview] = useState(false);
  const [profileReviewData, setProfileReviewData] = useState(null);
  const [profileReviewLoading, setProfileReviewLoading] = useState(false);
  const [profileReviewExpandedSections, setProfileReviewExpandedSections] = useState({});
  const [reviewHistoryData, setReviewHistoryData] = useState([]);
  const [reviewHistoryLoading, setReviewHistoryLoading] = useState(false);

  // Context Review state (for context-preserving review mode)
  const [contextReviewForm, setContextReviewForm] = useState({
    status: '', // Empty string to show placeholder, not 'UNDER_REVIEW' which is invalid
    sectionComments: {},
    overallComment: ''
  });
  // Keep a ref of the latest status to avoid any stale-state issues during validation
  const contextReviewStatusRef = useRef('');
  const [contextReviewSelectedSections, setContextReviewSelectedSections] = useState([]);
  const [contextReviewSections, setContextReviewSections] = useState([]);
  const [contextReviewErrors, setContextReviewErrors] = useState({});
  const [submittingContextReview, setSubmittingContextReview] = useState(false);
  const [submitAttemptedContextReview, setSubmitAttemptedContextReview] = useState(false);

  // Auto-clear expertise error when selections meet minimum requirement
  useEffect(() => {
    if (dialogConfig?.type === 'editexpertise' && selectedExpertiseAreas.length >= 5 && expertiseError) {
      setExpertiseError('');
    }
  }, [selectedExpertiseAreas, dialogConfig?.type, expertiseError]);

  // Auto-clear industry error when selections are made
  useEffect(() => {
    if (dialogConfig?.type === 'editindustries' && selectedIndustries.length > 0 && industryError) {
      setIndustryError('');
    }
  }, [selectedIndustries, dialogConfig?.type, industryError]);

  // Auto-clear bulk verification status error when a valid status is selected
  useEffect(() => {
    if (dialogConfig?.type === 'bulkverification' && bulkVerificationForm.status && bulkVerificationForm.status.trim() && bulkVerificationErrors.status) {
      setBulkVerificationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.status;
        return newErrors;
      });
    }
  }, [bulkVerificationForm.status, dialogConfig?.type, bulkVerificationErrors.status]);

  // Auto-clear verification status error when a valid status is selected
  useEffect(() => {
    if (dialogConfig?.type === 'verification' && verificationForm.status && verificationForm.status.trim() && verificationErrors.status) {
      setVerificationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.status;
        return newErrors;
      });
    }
  }, [verificationForm.status, dialogConfig?.type, verificationErrors.status]);

  // Auto-clear bulk review status error when a valid status is selected
  useEffect(() => {
    if (dialogConfig?.type === 'bulkreview' && bulkReviewForm.status && bulkReviewForm.status.trim() && bulkReviewErrors.status) {
      setBulkReviewErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.status;
        return newErrors;
      });
    }
  }, [bulkReviewForm.status, dialogConfig?.type, bulkReviewErrors.status]);

  // Load profile review data
  const loadProfileReviewData = useCallback(async (startupId, currentStatus) => {
    try {
      setProfileReviewLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication token not found. Please log in again.');
        setProfileReviewLoading(false);
        return;
      }

      const response = await fetch(`${API_BASE_URL}/startups/${startupId}/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setProfileReviewData(data);

        // Initialize section feedback for each official section only
        const initialFeedback = {};
        const initialRevisionRequirements = {};
        if (data.profile) {
          const officialSections = Object.values(PROFILE_SECTIONS);
          const filteredProfile = {};

          Object.keys(data.profile).forEach(sectionName => {
            if (officialSections.includes(sectionName)) {
              filteredProfile[sectionName] = data.profile[sectionName];
            }
          });

          // Auto-expand the first section when modal opens
          const sectionNames = Object.keys(filteredProfile);
          if (sectionNames.length > 0) {
            setProfileReviewExpandedSections({ [sectionNames[0]]: true });
          }

          Object.keys(filteredProfile).forEach(sectionName => {
            initialFeedback[sectionName] = '';
            initialRevisionRequirements[sectionName] = '';
          });
        }

        setProfileReviewForm(prev => ({
          ...prev,
          feedback: initialFeedback,
          revision_requirements: initialRevisionRequirements
        }));

        // Load review history for read-only mode
        const normalizedStatus = currentStatus ? currentStatus.toUpperCase().replace(/\s+/g, '_') : '';
        const isReadOnly = normalizedStatus === 'APPROVED' || normalizedStatus === 'REJECTED';

        if (isReadOnly) {
          let history = [];
          if (data.progressTracking && data.progressTracking.reviewHistory) {
            history = data.progressTracking.reviewHistory;
          } else if (data.progressTracking && data.progressTracking.reviewStatus && data.progressTracking.reviewStatus.history) {
            history = data.progressTracking.reviewStatus.history;
          } else if (data.reviewHistory) {
            history = data.reviewHistory;
          } else if (data.reviews) {
            history = data.reviews;
          }

          // If profile is approved/rejected, load the latest review comments
          if (history && history.length > 0) {
            const latestReview = history[0]; // Most recent review
            if (latestReview.feedback) {
              setProfileReviewForm(prev => ({
                ...prev,
                comments: latestReview.feedback.general_comments || latestReview.feedback.comments || '',
                feedback: latestReview.feedback.section_feedback || prev.feedback,
                revision_requirements: latestReview.feedback.revision_requirements || prev.revision_requirements
              }));
            }
          }
        }
      } else {
        toast.error('Failed to load profile data');
      }
    } catch (error) {
      toast.error('Failed to load profile data:', error);
    } finally {
      setProfileReviewLoading(false);
    }
  }, []);

  // Load review history data
  const loadReviewHistory = useCallback(async (startupId) => {
    try {
      setReviewHistoryLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        setReviewHistoryLoading(false);
        return;
      }

      const response = await fetch(`${API_BASE_URL}/startups/${startupId}/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();

        if (data.progressTracking && data.progressTracking.reviewStatus && data.progressTracking.reviewStatus.history) {
          setReviewHistoryData(data.progressTracking.reviewStatus.history);
        } else if (data.progressTracking && data.progressTracking.reviewHistory) {
          setReviewHistoryData(data.progressTracking.reviewHistory);
        } else if (data.reviewHistory) {
          setReviewHistoryData(data.reviewHistory);
        } else if (data.reviews) {
          setReviewHistoryData(data.reviews);
        } else {
          setReviewHistoryData([]);
        }
      } else {
        setReviewHistoryData([]);
      }
    } catch (error) {
      toast.error('Error loading review history:', error);
      setReviewHistoryData([]);
    } finally {
      setReviewHistoryLoading(false);
    }
  }, []);

  // Auto-focus on title input when manageachievements dialog opens with a single empty achievement
  useEffect(() => {
    if (dialogConfig?.type === 'manageachievements' && achievements.length === 1 &&
      achievements[0]?.title === '' && achievements[0]?.description === '' &&
      achievements[0]?.date === '' && achievements[0]?.category === '') {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        const titleInput = document.querySelector('[data-achievements-list] input[placeholder*="Helped"]');
        if (titleInput) {
          titleInput.focus();
        }
      }, 100);
    }
  }, [dialogConfig?.type, achievements]);

  // Auto-focus on title input when managespeaking dialog opens with a single empty engagement
  useEffect(() => {
    if (dialogConfig?.type === 'managespeaking' && speakingEngagements.length === 1 &&
      speakingEngagements[0]?.title === '' && speakingEngagements[0]?.event === '' &&
      speakingEngagements[0]?.date === '' && speakingEngagements[0]?.location === '' &&
      speakingEngagements[0]?.description === '') {
      setTimeout(() => {
        const titleInput = document.querySelector('[data-speaking-list] input[placeholder*="e.g."]');
        if (titleInput) {
          titleInput.focus();
        }
      }, 100);
    }
  }, [dialogConfig?.type, speakingEngagements]);

  // Auto-focus on title input when managepublications dialog opens with a single empty publication
  useEffect(() => {
    if (dialogConfig?.type === 'managepublications' && publications.length === 1 &&
      publications[0]?.title === '' && publications[0]?.publisher === '' &&
      publications[0]?.date === '' && publications[0]?.url === '' &&
      publications[0]?.description === '') {
      setTimeout(() => {
        const titleInput = document.querySelector('[data-publications-list] input[placeholder*="e.g."]');
        if (titleInput) {
          titleInput.focus();
        }
      }, 100);
    }
  }, [dialogConfig?.type, publications]);

  // Fetch applications for a selected startup
  const fetchApplicationsForStartup = useCallback(async (startupId) => {
    if (!startupId) {
      setApplicationsList([]);
      return;
    }

    setLoadingApplications(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setApplicationsList([]);
        setLoadingApplications(false);
        return;
      }

      const response = await fetch(`${API_BASE_URL}/cfa/applications/center?limit=100&offset=0`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        const applications = data.data || data.results || data.applications || (Array.isArray(data) ? data : []);
        // Filter applications by startup_id and status (only accepted)
        const filteredApplications = applications.filter(app =>
          app.startup_id === parseInt(startupId) &&
          app.status?.toLowerCase() === 'accepted'
        );
        setApplicationsList(filteredApplications);
      } else {
        setApplicationsList([]);
      }
    } catch (error) {
      console.error('Error fetching applications:', error);
      setApplicationsList([]);
    } finally {
      setLoadingApplications(false);
    }
  }, []);

  // Fetch applications for a startup in the list dialog
  const fetchApplicationsForStartupInList = useCallback(async (startupId) => {
    if (!startupId) return;

    setLoadingStartupApplications(prev => ({ ...prev, [startupId]: true }));
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoadingStartupApplications(prev => ({ ...prev, [startupId]: false }));
        return;
      }

      const response = await fetch(`${API_BASE_URL}/cfa/applications/center?limit=100&offset=0`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        const applications = data.data || data.results || data.applications || (Array.isArray(data) ? data : []);
        // Filter applications by startup_id and status (only eligible for fund)
        const filteredApplications = applications.filter(app =>
          app.startup_id === parseInt(startupId) &&
          app.status?.toLowerCase() === 'eligible for fund'
        );

        setStartupApplicationListData(prev =>
          prev.map(startup =>
            startup.id === parseInt(startupId)
              ? { ...startup, applications: filteredApplications, loading: false }
              : startup
          )
        );
      }
    } catch (error) {
      console.error('Error fetching applications for startup:', error);
      setStartupApplicationListData(prev =>
        prev.map(startup =>
          startup.id === parseInt(startupId)
            ? { ...startup, applications: [], loading: false }
            : startup
        )
      );
    } finally {
      setLoadingStartupApplications(prev => ({ ...prev, [startupId]: false }));
    }
  }, []);

  const [quickScheduleForm, setQuickScheduleForm] = useState({
    title: '',
    targetStartup: '',
    sessionType: 'in-person',
    startDate: '',
    endDate: '',
    startTime: '',
    endTime: '',
    timeSlot: '',
    addBuffer: true, // Always true - buffer is mandatory
    bufferMinutes: 15, // Fixed 15-minute buffer
    requestedResources: [],
    location: '',
    link: '',
    description: '',
    notes: '',
    attachmentFileName: '',
    attachmentFile: null, // File object for notes PDF
  });
  const [quickScheduleErrors, setQuickScheduleErrors] = useState({});
  const [expenseForm, setExpenseForm] = useState({
    expense_type: 'travel',
    amount: '',
    currency: 'INR',
    description: '',
  });
  const [assignmentActionForm, setAssignmentActionForm] = useState({
    selectedAction: '',
    reassignMentorId: '',
    escalationReason: '',
  });
  const [assignmentActionErrors, setAssignmentActionErrors] = useState({});
  const [availableMentors, setAvailableMentors] = useState([]);
  const [loadingMentors, setLoadingMentors] = useState(false);

  const openDialog = useCallback((config) => {
    const preparedConfig = enhanceConfig(config);
    // Store the currently focused element before opening dialog
    previousActiveElementRef.current = document.activeElement;
    return new Promise((resolve) => {
      setDialogConfig(preparedConfig);
      const initialValue = preparedConfig.type === 'select'
        ? preparedConfig.defaultValue || (preparedConfig.options[0]?.value ?? '')
        : preparedConfig.defaultValue;
      setInputValue(initialValue);
      setRatingValue(preparedConfig.ratingValue || 0);
      const initialSelected = Array.isArray(preparedConfig.defaultValue)
        ? preparedConfig.defaultValue
        : (preparedConfig.defaultValue ? [preparedConfig.defaultValue] : []);
      setSelectedValues(initialSelected);
      // Initialize deadline date for jury assignment dialogs
      if (preparedConfig.type === 'juryassign') {
        setDeadlineDate(preparedConfig.defaultDeadlineDate || '');
      } else {
        setDeadlineDate('');
        setDeadlineDateError('');
      }
      // Initialize startup/application form for selectstartupapplication dialogs
      if (preparedConfig.type === 'selectstartupapplication') {
        setStartupApplicationForm({
          startupId: preparedConfig.defaultStartupId || '',
          applicationId: preparedConfig.defaultApplicationId || ''
        });
        setStartupApplicationErrors({});

        // If startups list is provided, set it
        if (preparedConfig.startupsList) {
          setStartupsList(preparedConfig.startupsList);
        } else {
          setStartupsList([]);
        }

        // Reset applications list
        setApplicationsList([]);

        // If defaultStartupId is provided, fetch applications
        if (preparedConfig.defaultStartupId) {
          fetchApplicationsForStartup(preparedConfig.defaultStartupId);
        }
      }
      // Initialize funding form for addfunding dialogs
      else if (preparedConfig.type === 'addfunding') {
        // Check if editing existing installments
        const isEditingInstallments = preparedConfig.defaultFundingId && preparedConfig.existingInstallments;

        // If editing, load existing installments and set payment type to installment
        let initialCustomInstallments = [];
        if (isEditingInstallments && preparedConfig.existingInstallments) {
          initialCustomInstallments = preparedConfig.existingInstallments.map(inst => ({
            amount: inst.amount || '',
            date: inst.installment_date ? inst.installment_date.split('T')[0] : '', // Convert ISO date to YYYY-MM-DD
            status: inst.status || 'unpaid',
            id: inst.id, // Keep installment ID for reference
            installment_number: inst.installment_number,
            notes: inst.notes || '' // Load existing notes for each milestone
          }));
        }

        setFundingForm({
          startupId: preparedConfig.defaultStartupId || '',
          applicationId: preparedConfig.defaultApplicationId || '',
          amount: preparedConfig.defaultAmount || '',
          currencyId: preparedConfig.defaultCurrencyId || defaultCurrencyId,
          fundOfferingId: preparedConfig.defaultFundOfferingId || '',
          paymentType: isEditingInstallments ? 'installment' : 'full', // Set to installment if editing
          installmentType: 'custom', // always use custom installments when payment type is installment
          numberOfInstallments: '',
          paymentFrequency: 'monthly',
          customInstallments: initialCustomInstallments.length > 0 ? initialCustomInstallments : [],
          documents: [],
          notes: preparedConfig.defaultNotes || '' // Load existing notes if editing
        });
        setFundingFormErrors({});
        setSubmittingFunding(false); // Reset submitting state

        // If startups list is provided, set it
        if (preparedConfig.startupsList) {
          setStartupsList(preparedConfig.startupsList);
        } else {
          setStartupsList([]);
        }

        // Reset applications list
        setApplicationsList([]);

        // If defaultStartupId is provided, fetch applications
        if (preparedConfig.defaultStartupId) {
          fetchApplicationsForStartup(preparedConfig.defaultStartupId);
        }

        // Load fund offerings so the user can pick the source fund.
        // Skip when editing an existing funding record since the link is set on create.
        if (!isEditingInstallments) {
          // If the caller already knows which funds are linked to the program,
          // use those directly instead of fetching all published funds.
          if (Array.isArray(preparedConfig.preloadedFundOfferings) && preparedConfig.preloadedFundOfferings.length > 0) {
            setFundOfferingsList(preparedConfig.preloadedFundOfferings);
            // Auto-select if there's exactly one fund in the program catalog.
            if (preparedConfig.preloadedFundOfferings.length === 1) {
              const only = preparedConfig.preloadedFundOfferings[0];
              setFundingForm((prev) => ({
                ...prev,
                fundOfferingId: String(only.id),
                amount: prev.amount || (only.max_amount ? String(only.max_amount) : prev.amount),
              }));
            }
          } else {
            (async () => {
              try {
                setLoadingFundOfferings(true);
                const token = localStorage.getItem('token');
                const res = await fetch(`${API_BASE_URL}/funds?status=published`, {
                  headers: { Authorization: `Bearer ${token}` },
                });
                if (res.ok) {
                  const body = await res.json();
                  const items = Array.isArray(body?.data) ? body.data : (body?.data?.items || []);
                  setFundOfferingsList(items);
                } else {
                  setFundOfferingsList([]);
                }
              } catch (_) {
                setFundOfferingsList([]);
              } finally {
                setLoadingFundOfferings(false);
              }
            })();
          }
        }
      }
      // Initialize startup-application list dialog
      else if (preparedConfig.type === 'startupapplicationlist') {
        // Set startups list if provided
        if (preparedConfig.startupsList) {
          setStartupApplicationListData(preparedConfig.startupsList.map(startup => ({
            ...startup,
            applications: [],
            loading: false
          })));
        } else {
          setStartupApplicationListData([]);
        }
        setLoadingStartupApplications({});
        // Initialize funding form for dropdowns
        setFundingForm({
          startupId: preparedConfig.defaultStartupId || '',
          applicationId: preparedConfig.defaultApplicationId || '',
          amount: '',
          currencyId: defaultCurrencyId,
          paymentType: 'full',
          installmentType: 'fix',
          numberOfInstallments: '',
          paymentFrequency: 'monthly',
          customInstallments: [],
          documents: []
        });
        setFundingFormErrors({});
      } else {
        setFundingForm({
          startupId: '',
          applicationId: '',
          amount: '',
          currencyId: defaultCurrencyId,
          paymentType: 'full', // Default to 'full'
          installmentType: 'fix',
          numberOfInstallments: '',
          paymentFrequency: 'monthly',
          customInstallments: [],
          documents: []
        });
        setFundingFormErrors({});
        setStartupsList([]);
        setApplicationsList([]);
        setSubmittingFunding(false); // Reset submitting state
      }
      // Initialize payment method form for paymentmethod dialogs
      if (preparedConfig.type === 'paymentmethod') {
        setPaymentMethodForm({
          paymentType: 'full', // Default to Full Payment as requested
          customInstallments: [{ amount: preparedConfig.defaultAmount || '', date: '', status: 'unpaid', notes: '' }],
          notes: '' // Initialize empty notes field for full payment
        });
        setPaymentMethodFormErrors({});
        setSubmittingPaymentMethod(false);
      }
      // Initialize fund usage form for fundusage dialogs
      else if (preparedConfig.type === 'fundusage') {
        setFundUsageForm({
          title: '',
          amount: '',
          description: '',
          documents: []
        });
        setFundUsageFormErrors({});
      } else {
        setFundUsageForm({ title: '', amount: '', description: '', documents: [] });
        setFundUsageFormErrors({});
      }
      // Initialize funding documents form
      if (preparedConfig.type === 'addfundingdocuments') {
        setFundingDocumentsForm({
          fundingId: preparedConfig.defaultFundingId || '',
          title: '',
          documents: []
        });
        setFundingDocumentsFormErrors({});
        setSubmittingFundingDocuments(false); // Reset submitting state when dialog opens
      }
      // Initialize quick schedule form
      if (preparedConfig.type === 'quickschedule') {
        setQuickScheduleErrors({});
        setQuickScheduleForm({
          title: preparedConfig.defaultTitle || '',
          targetStartup: preparedConfig.defaultTargetStartup || '',
          sessionType: 'in-person', // Locked to in-person for physical-only
          startDate: preparedConfig.defaultStartDate || '',
          endDate: preparedConfig.defaultEndDate || '',
          startTime: preparedConfig.defaultStartTime || '09:00',
          endTime: preparedConfig.defaultEndTime || '',
          timeSlot: '',
          addBuffer: true, // Always true - buffer is mandatory
          bufferMinutes: 15, // Fixed 15-minute buffer
          requestedResources: preparedConfig.defaultRequestedResources || [],
          location: preparedConfig.defaultLocation || '',
          link: preparedConfig.defaultLink || '',
          description: preparedConfig.defaultDescription || '',
          notes: preparedConfig.defaultNotes || '',
          attachmentFileName: preparedConfig.defaultAttachmentFileName || '',
          attachmentFile: null,
          mentorAvailability: preparedConfig.mentorAvailability || null,
        });
      }
      // Initialize expense form
      if (preparedConfig.type === 'addexpense') {
        setExpenseForm({
          expense_type: preparedConfig.defaultExpenseType || 'travel',
          amount: preparedConfig.defaultAmount || '',
          currency: preparedConfig.defaultCurrency || 'INR',
          description: preparedConfig.defaultDescription || '',
        });
      }
      // Initialize assignment action form
      if (preparedConfig.type === 'assignmentaction') {
        setAssignmentActionForm({
          selectedAction: '',
          reassignMentorId: '',
          escalationReason: '',
        });
        setAssignmentActionErrors({});
        setAvailableMentors([]);
        setLoadingMentors(false);

        // Fetch mentors if onFetchMentors callback is provided
        if (preparedConfig.onFetchMentors) {
          setLoadingMentors(true);
          preparedConfig.onFetchMentors()
            .then((mentors) => {
              setAvailableMentors(mentors || []);
            })
            .catch((error) => {
              console.error('Error fetching mentors:', error);
              toast.error('Failed to load mentors');
            })
            .finally(() => {
              setLoadingMentors(false);
            });
        }
      }
      // Initialize mentor verify form
      if (preparedConfig.type === 'mentorverify') {
        const initial = preparedConfig.defaultValue || {};
        setMentorVerifyForm({
          linkedin_verified: initial.linkedin_verified || false,
          experience_verified: initial.experience_verified || false,
          domain_expertise_assessed: initial.domain_expertise_assessed || false,
          notes: initial.notes || ''
        });
      }
      // Initialize assignment form
      if (preparedConfig.type === 'confirmassignment') {
        setAssignmentForm({
          goals: preparedConfig.defaultGoals || '',
          reason: preparedConfig.defaultReason || ''
        });
        setAssignmentFormErrors({});
      }
      // Initialize assignment details state
      if (preparedConfig.type === 'assignmentdetails') {
        setAssignmentDetailsState({
          completionRating: 5,
          completionComment: '',
          pendingAction: preparedConfig.defaultAction || null,
          submitting: false
        });
      }
      // Initialize decline assignment form
      if (preparedConfig.type === 'declineassignment') {
        setDeclineForm({
          reason: preparedConfig.defaultReason || ''
        });
        setDeclineFormErrors({});
      }
      // Initialize expertise areas for editexpertise dialogs
      if (preparedConfig.type === 'editexpertise') {
        const initialExpertise = Array.isArray(preparedConfig.defaultValue)
          ? preparedConfig.defaultValue
          : (preparedConfig.defaultValue ? [preparedConfig.defaultValue] : []);
        setSelectedExpertiseAreas(initialExpertise);
        setExpertiseSearch('');
        // Clear any previous error - if there are initial selections, no error should show
        setExpertiseError('');
      }
      // Initialize industries for editindustries dialogs
      if (preparedConfig.type === 'editindustries') {
        const initialIndustries = Array.isArray(preparedConfig.defaultIndustries)
          ? preparedConfig.defaultIndustries
          : (preparedConfig.defaultIndustries ? [preparedConfig.defaultIndustries] : []);
        const initialDetails = preparedConfig.defaultIndustryDetails || {};
        setSelectedIndustries(initialIndustries);
        setIndustryDetails(initialDetails);
        setIndustryError('');
        setIndustryYearsErrors({});
      }
      // Initialize achievements for manageachievements dialogs
      if (preparedConfig.type === 'manageachievements') {
        const initialAchievements = Array.isArray(preparedConfig.defaultAchievements)
          ? preparedConfig.defaultAchievements
          : [];
        // If no achievements exist, automatically add one empty achievement form
        const achievementsToSet = initialAchievements.length === 0
          ? [{ title: '', description: '', date: '', category: '', files: [] }]
          : initialAchievements.map(ach => ({
            ...ach,
            files: ach.files || []
          }));
        setAchievements(achievementsToSet);
        setAchievementsErrors({});
        setAchievementDragOver({});
      }
      // Initialize speaking engagements for managespeaking dialogs
      if (preparedConfig.type === 'managespeaking') {
        const initialSpeaking = Array.isArray(preparedConfig.defaultSpeakingEngagements)
          ? preparedConfig.defaultSpeakingEngagements
          : [];
        // If no speaking engagements exist, automatically add one empty form
        const speakingToSet = initialSpeaking.length === 0
          ? [{ title: '', event: '', date: '', location: '', description: '' }]
          : initialSpeaking;
        setSpeakingEngagements(speakingToSet);
        setSpeakingEngagementsErrors({});
      }
      // Initialize publications for managepublications dialogs
      if (preparedConfig.type === 'managepublications') {
        const initialPublications = Array.isArray(preparedConfig.defaultPublications)
          ? preparedConfig.defaultPublications
          : [];
        // If no publications exist, automatically add one empty form
        const publicationsToSet = initialPublications.length === 0
          ? [{ title: '', publisher: '', date: '', url: '', description: '' }]
          : initialPublications;
        setPublications(publicationsToSet);
        setPublicationsErrors({});
      }
      // Initialize availability for manageavailability dialogs
      if (preparedConfig.type === 'manageavailability') {
        const defaultAvailability = preparedConfig.defaultAvailability || {};
        setAvailability({
          frequency: defaultAvailability.frequency || '',
          commitment: defaultAvailability.commitment || '',
          timeSlots: Array.isArray(defaultAvailability.timeSlots) ? defaultAvailability.timeSlots : []
        });
        setAvailabilityErrors({});
      }
      // Initialize bulk verification form
      if (preparedConfig.type === 'bulkverification') {
        setBulkVerificationForm({
          action: 'update_status',
          status: preparedConfig.defaultStatus || '',
          comments: preparedConfig.defaultComments || '',
          exportFormat: 'csv'
        });
        setBulkVerificationErrors({});
      }
      // Initialize verification form
      if (preparedConfig.type === 'verification') {
        const getDefaultStatus = () => {
          if (preparedConfig.statuses && Object.keys(preparedConfig.statuses).length > 0) {
            return Object.keys(preparedConfig.statuses)[0];
          }
          return 'pending';
        };
        setVerificationForm({
          status: preparedConfig.defaultStatus || getDefaultStatus(),
          comments: preparedConfig.defaultComments || ''
        });
        setVerificationErrors({});
      }
      // Initialize bulk review form
      if (preparedConfig.type === 'bulkreview') {
        setBulkReviewForm({
          status: preparedConfig.defaultStatus || '',
          comments: preparedConfig.defaultComments || ''
        });
        setBulkReviewErrors({});
        setSubmittingBulkReview(false);
      }
      // Initialize profile review form
      if (preparedConfig.type === 'profilereview') {
        const startup = preparedConfig.startup;
        const normalizeStatus = (status) => {
          if (!status) return status;
          const upperStatus = status.toUpperCase().replace(/\s+/g, '_');
          if (upperStatus === 'NEEDS_REVISION' || upperStatus === 'NEEDS REVISION') return 'NEEDS_REVISION';
          if (upperStatus === 'UNDER_REVIEW' || upperStatus === 'UNDER REVIEW') return 'UNDER_REVIEW';
          if (upperStatus === 'APPROVED') return 'APPROVED';
          if (upperStatus === 'REJECTED') return 'REJECTED';
          if (upperStatus === 'RESUBMITTED' || upperStatus === 'RE-SUBMITTED') return 'RESUBMITTED';
          return status;
        };
        const normalizedStatus = normalizeStatus(startup?.status);
        const currentReviewStatus = normalizedStatus ? normalizedStatus.toLowerCase() : '';

        setProfileReviewForm({
          status: currentReviewStatus,
          comments: '',
          feedback: {},
          revision_requirements: {}
        });
        setProfileReviewErrors({});
        setSubmittingProfileReview(false);
        setProfileReviewData(null);
        setProfileReviewLoading(true);
        setProfileReviewExpandedSections({});

        // Load profile data
        if (startup?.id) {
          loadProfileReviewData(startup.id, currentReviewStatus);
        }
      }
      // Initialize review history
      if (preparedConfig.type === 'reviewhistory') {
        const startup = preparedConfig.startup;
        setReviewHistoryData([]);
        setReviewHistoryLoading(true);

        // Load review history data
        if (startup?.id) {
          loadReviewHistory(startup.id);
        }
      }

      // Initialize context review
      if (preparedConfig.type === 'contextreview') {
        const { status, selectedSections, sections, sectionComments, overallComment } = preparedConfig;
        // Valid statuses for the Select dropdown
        const validStatuses = ['APPROVED', 'REJECTED', 'NEEDS_REVISION'];
        // If status is UNDER_REVIEW or not a valid option, set to empty string to show placeholder
        const statusStr = status ? String(status).trim() : '';
        const initialStatus = (statusStr && validStatuses.includes(statusStr.toUpperCase()))
          ? statusStr.toUpperCase()
          : '';

        // Keep ref in sync
        contextReviewStatusRef.current = initialStatus;

        setContextReviewForm({
          status: initialStatus,
          sectionComments: sectionComments || {},
          overallComment: overallComment || ''
        });
        setContextReviewSelectedSections(selectedSections || []);
        setContextReviewSections(sections || []);
        setContextReviewErrors({});
        setSubmittingContextReview(false);
        setSubmitAttemptedContextReview(false);
      }

      setResolver(() => resolve);
    });
  }, [fetchApplicationsForStartup, loadProfileReviewData, loadReviewHistory]);

  const closeDialog = useCallback(
    (result) => {
      if (resolver) {
        resolver(result);
      }
      setResolver(null);
      setDialogConfig(null);
      setInputValue('');
      setRatingValue(0);
      setSelectedValues([]);
      setDeadlineDate('');
      setDeadlineDateError('');
      setExpertiseSearch('');
      setSelectedExpertiseAreas([]);
      setExpertiseError('');
      setSelectedIndustries([]);
      setIndustryDetails({});
      setIndustryError('');
      setIndustryYearsErrors({});
      setAchievements([]);
      setAchievementsErrors({});
      setSpeakingEngagements([]);
      setSpeakingEngagementsErrors({});
      setPublications([]);
      setPublicationsErrors({});
      setAvailability({ frequency: '', commitment: '', timeSlots: [] });
      setAvailabilityErrors({});
      setBulkVerificationForm({ status: '', comments: '' });
      setBulkVerificationErrors({});
      setVerificationForm({ status: '', comments: '' });
      setVerificationErrors({});
      setJuryForm({ firstName: '', lastName: '', email: '' });
      setJuryFormErrors({});
      setSubmittingJury(false);
      setFundingForm({ startupId: '', applicationId: '', amount: '' });
      setFundingFormErrors({});
      setSubmittingFunding(false);
      setStartupsList([]);
      setApplicationsList([]);
      setStartupApplicationListData([]);
      setLoadingStartupApplications({});
      setFundUsageForm({ title: '', amount: '', description: '', file: null });
      setFundUsageFormErrors({});
      setSubmittingFundUsage(false);
      setQuickScheduleForm({
        targetStartup: '',
        sessionType: '',
        startTime: '',
        endTime: '',
        addBuffer: true, // Always true - buffer is mandatory
        bufferMinutes: 15, // Fixed 15-minute buffer
        requestedResources: [],
        location: '',
        link: '',
        description: '',
      });
      setExpenseForm({
        expense_type: 'travel',
        amount: '',
        currency: 'INR',
        description: '',
      });
      setAssignmentActionForm({ selectedAction: '', reassignMentorId: '', escalationReason: '' });
      setAssignmentActionErrors({});
      setAvailableMentors([]);
      setMentorVerifyForm({ linkedin_verified: false, experience_verified: false, domain_expertise_assessed: false, notes: '' });
      setAssignmentDetailsState({ completionRating: 5, completionComment: '', pendingAction: null, submitting: false });
      setDeclineForm({ reason: '' });
      setDeclineFormErrors({});

      // Fix accessibility issue: immediately remove aria-hidden from root element
      // This prevents the accessibility warning when Material-UI restores focus
      requestAnimationFrame(() => {
        const rootElement = document.getElementById('root');
        if (rootElement && rootElement.getAttribute('aria-hidden') === 'true') {
          rootElement.removeAttribute('aria-hidden');
        }

        // Also blur any focused element that might be causing issues
        // Use a small delay to ensure this runs after Material-UI's focus restoration
        setTimeout(() => {
          const activeElement = document.activeElement;
          if (activeElement && activeElement instanceof HTMLElement) {
            const rootEl = document.getElementById('root');
            // If root still has aria-hidden or the focused element is inside an aria-hidden parent, blur it
            if (rootEl && (rootEl.getAttribute('aria-hidden') === 'true' ||
              activeElement.closest('[aria-hidden="true"]'))) {
              activeElement.blur();
              if (rootEl.getAttribute('aria-hidden') === 'true') {
                rootEl.removeAttribute('aria-hidden');
              }
            }
          }
        }, 0);
      });
    },
    [resolver],
  );

  useEffect(() => {
    setExternalDialogHandler(openDialog);
    return () => setExternalDialogHandler(null);
  }, [openDialog]);

  // Monitor and fix aria-hidden on root element to prevent accessibility issues
  useEffect(() => {
    const rootElement = document.getElementById('root');
    if (!rootElement) return;

    // Only monitor when dialog is closed or closing
    if (!dialogConfig) {
      // Clean up any stuck aria-hidden when dialog is closed
      const cleanup = () => {
        if (rootElement.getAttribute('aria-hidden') === 'true') {
          const activeElement = document.activeElement;
          // If there's a focused element, blur it first
          if (activeElement && activeElement instanceof HTMLElement && rootElement.contains(activeElement)) {
            activeElement.blur();
          }
          // Remove aria-hidden from root
          rootElement.removeAttribute('aria-hidden');
        }
      };

      // Clean up immediately and after a short delay
      requestAnimationFrame(() => {
        cleanup();
        setTimeout(cleanup, 100);
      });
    }
  }, [dialogConfig]);

  const handleCancel = useCallback(() => {
    if (!dialogConfig) return;

    if (dialogConfig.type === 'alert') {
      closeDialog(true);
    } else if (dialogConfig.type === 'contextreview') {
      // For contextreview, return current form state so parent can sync comments
      // This allows comments typed in modal to be reflected in inline comment boxes
      closeDialog({
        cancelled: true,
        status: contextReviewForm.status,
        sectionComments: contextReviewForm.sectionComments,
        overallComment: contextReviewForm.overallComment,
        selectedSections: contextReviewSelectedSections,
        feedback: contextReviewForm.sectionComments // Use sectionComments as feedback for consistency
      });
    } else if (dialogConfig.type === 'prompt' || dialogConfig.type === 'rating' || dialogConfig.type === 'select' || dialogConfig.type === 'multiselect' || dialogConfig.type === 'scores' || dialogConfig.type === 'juryassign' || dialogConfig.type === 'addjury' || dialogConfig.type === 'addfunding' || dialogConfig.type === 'funding' || dialogConfig.type === 'fundusage' || dialogConfig.type === 'paymentmethod' || dialogConfig.type === 'fundinghistory' || dialogConfig.type === 'quickschedule' || dialogConfig.type === 'addexpense' || dialogConfig.type === 'assignmentaction' || dialogConfig.type === 'selectstartupapplication' || dialogConfig.type === 'startupapplicationlist' || dialogConfig.type === 'addfundingdocuments' || dialogConfig.type === 'declineassignment' || dialogConfig.type === 'bulkverification' || dialogConfig.type === 'verification' || dialogConfig.type === 'documenthistory' || dialogConfig.type === 'bulkreview' || dialogConfig.type === 'profilereview' || dialogConfig.type === 'reviewhistory') {
      closeDialog(null);
    } else {
      closeDialog(false);
    }
  }, [closeDialog, dialogConfig, contextReviewForm, contextReviewSelectedSections]);

  const handleConfirm = useCallback(async () => {
    if (!dialogConfig) return;

    if (dialogConfig.type === 'confirmassignment') {
      const errors = {};
      if (!assignmentForm.goals || !assignmentForm.goals.trim()) {
        errors.goals = 'Assignment goals are required';
      }
      if (Object.keys(errors).length > 0) {
        setAssignmentFormErrors(errors);
        return;
      }
      closeDialog(assignmentForm);
    } else if (dialogConfig.type === 'declineassignment') {
      const errors = {};
      if (!declineForm.reason || !declineForm.reason.trim()) {
        errors.reason = 'A reason for declining is required';
      }
      if (Object.keys(errors).length > 0) {
        setDeclineFormErrors(errors);
        return;
      }
      closeDialog(declineForm.reason);
    } else if (dialogConfig.type === 'prompt') {
      closeDialog(inputValue);
    } else if (dialogConfig.type === 'select') {
      closeDialog(inputValue);
    } else if (dialogConfig.type === 'rating') {
      if (ratingValue === 0) {
        return; // Don't close if rating is not selected
      }
      closeDialog({ rating: ratingValue, comment: '' });
    } else if (dialogConfig.type === 'select') {
      closeDialog(inputValue || null);
    } else if (dialogConfig.type === 'multiselect') {
      closeDialog(selectedValues);
    } else if (dialogConfig.type === 'editexpertise') {
      // Validate that at least 5 expertise areas are selected
      if (!selectedExpertiseAreas || selectedExpertiseAreas.length === 0) {
        toast.error('Please select at least 5 expertise areas');
        return;
      }
      if (selectedExpertiseAreas.length < 5) {
        toast.error(`Please select at least 5 expertise areas (currently ${selectedExpertiseAreas.length})`);
        return;
      }
      // Clear any error and close dialog with selected expertise
      setExpertiseError('');
      closeDialog(selectedExpertiseAreas);
    } else if (dialogConfig.type === 'editindustries') {
      // Validate that at least one industry is selected
      if (!selectedIndustries || selectedIndustries.length === 0) {
        setIndustryError('Please select at least one industry');
        return;
      }

      // Validate years for all selected industries
      const yearsErrors = {};
      let hasYearsError = false;

      selectedIndustries.forEach((industryId) => {
        const details = industryDetails[industryId] || {};
        if (details.years !== undefined && details.years !== '') {
          const years = typeof details.years === 'number' ? details.years : parseInt(details.years, 10);
          if (isNaN(years) || years < 1 || years > 60) {
            yearsErrors[industryId] = years < 1 ? 'Years must be at least 1' : 'Years cannot exceed 60';
            hasYearsError = true;
          }
        }
      });

      if (hasYearsError) {
        setIndustryYearsErrors(yearsErrors);
        toast.error('Please enter valid years of experience (1-60) for all selected industries');
        return;
      }

      // Clear any error and close dialog with selected industries and details
      setIndustryError('');
      setIndustryYearsErrors({});
      closeDialog({
        industries: selectedIndustries,
        industryDetails: industryDetails
      });
    } else if (dialogConfig.type === 'manageachievements') {
      // Validate achievements - achievements state is already in the dependency array, so we have the latest value
      const errors = {};

      if (!achievements || !Array.isArray(achievements)) {
        setAchievementsErrors({ 'general': 'Invalid achievements data' });
        return;
      }

      achievements.forEach((achievement, idx) => {
        if (!achievement || typeof achievement !== 'object') {
          errors[`achievements.${idx}.title`] = 'Invalid achievement data';
          return;
        }

        // Check if title exists and is not empty after trimming
        const titleValue = achievement.title;
        // Handle null, undefined, or non-string values
        const trimmedTitle = (titleValue != null && typeof titleValue === 'string') ? titleValue.trim() : '';

        // Only set error if title is truly empty (after trimming)
        if (!trimmedTitle || trimmedTitle.length === 0) {
          errors[`achievements.${idx}.title`] = 'Title is required';
        }

        // Validate date if provided
        if (achievement.date) {
          const dateValue = new Date(achievement.date);
          if (isNaN(dateValue.getTime())) {
            errors[`achievements.${idx}.date`] = 'Please enter a valid date';
          }
        }
      });

      if (Object.keys(errors).length > 0) {
        setAchievementsErrors(errors);
        return;
      }

      // Clear any errors and close dialog with achievements (filter out empty achievements)
      setAchievementsErrors({});
      // Filter out completely empty achievements before closing
      const validAchievements = achievements.filter(achievement => {
        if (!achievement || typeof achievement !== 'object') return false;
        const titleValue = achievement.title;
        const trimmedTitle = (titleValue != null && typeof titleValue === 'string') ? titleValue.trim() : '';
        return trimmedTitle && trimmedTitle.length > 0;
      });

      // Keep files in achievement objects - they will be uploaded when profile is saved
      const achievementsWithFiles = validAchievements.map(achievement => ({
        ...achievement,
        files: achievement.files || []
      }));

      closeDialog(achievementsWithFiles);
    } else if (dialogConfig.type === 'managespeaking') {
      // Validate speaking engagements
      const errors = {};

      if (!speakingEngagements || !Array.isArray(speakingEngagements)) {
        setSpeakingEngagementsErrors({ 'general': 'Invalid speaking engagements data' });
        return;
      }

      speakingEngagements.forEach((engagement, idx) => {
        if (!engagement || typeof engagement !== 'object') {
          errors[`speakingEngagements.${idx}.title`] = 'Invalid engagement data';
          return;
        }

        // Check if title exists and is not empty after trimming
        const titleValue = engagement.title;
        const trimmedTitle = (titleValue != null && typeof titleValue === 'string') ? titleValue.trim() : '';

        if (!trimmedTitle || trimmedTitle.length === 0) {
          errors[`speakingEngagements.${idx}.title`] = 'Title is required';
        }

        // Validate date if provided
        if (engagement.date) {
          const dateValue = new Date(engagement.date);
          if (isNaN(dateValue.getTime())) {
            errors[`speakingEngagements.${idx}.date`] = 'Please enter a valid date';
          }
        }
      });

      if (Object.keys(errors).length > 0) {
        setSpeakingEngagementsErrors(errors);
        return;
      }

      // Clear any errors and close dialog with speaking engagements
      setSpeakingEngagementsErrors({});
      const validEngagements = speakingEngagements.filter(engagement => {
        if (!engagement || typeof engagement !== 'object') return false;
        const titleValue = engagement.title;
        const trimmedTitle = (titleValue != null && typeof titleValue === 'string') ? titleValue.trim() : '';
        return trimmedTitle && trimmedTitle.length > 0;
      });
      closeDialog(validEngagements);
    } else if (dialogConfig.type === 'managepublications') {
      // Validate publications
      const errors = {};

      if (!publications || !Array.isArray(publications)) {
        setPublicationsErrors({ 'general': 'Invalid publications data' });
        return;
      }

      publications.forEach((publication, idx) => {
        if (!publication || typeof publication !== 'object') {
          errors[`publications.${idx}.title`] = 'Invalid publication data';
          return;
        }

        // Check if title exists and is not empty after trimming
        const titleValue = publication.title;
        const trimmedTitle = (titleValue != null && typeof titleValue === 'string') ? titleValue.trim() : '';

        if (!trimmedTitle || trimmedTitle.length === 0) {
          errors[`publications.${idx}.title`] = 'Title is required';
        }

        // Validate URL if provided
        if (publication.url) {
          try {
            // Throws TypeError if the URL is invalid
            new URL(publication.url);
          } catch (e) {
            errors[`publications.${idx}.url`] = 'Please enter a valid URL';
          }
        }

        // Validate date if provided
        if (publication.date) {
          const dateValue = new Date(publication.date);
          if (isNaN(dateValue.getTime())) {
            errors[`publications.${idx}.date`] = 'Please enter a valid date';
          }
        }
      });

      if (Object.keys(errors).length > 0) {
        setPublicationsErrors(errors);
        return;
      }

      // Clear any errors and close dialog with publications
      setPublicationsErrors({});
      const validPublications = publications.filter(publication => {
        if (!publication || typeof publication !== 'object') return false;
        const titleValue = publication.title;
        const trimmedTitle = (titleValue != null && typeof titleValue === 'string') ? titleValue.trim() : '';
        return trimmedTitle && trimmedTitle.length > 0;
      });
      closeDialog(validPublications);
    } else if (dialogConfig.type === 'manageavailability') {
      // Validate availability
      const errors = {};

      // Frequency and commitment are optional, but timeSlots could be required depending on business logic
      // For now, all fields are optional

      if (Object.keys(errors).length > 0) {
        setAvailabilityErrors(errors);
        return;
      }

      // Clear any errors and close dialog with availability data
      setAvailabilityErrors({});
      closeDialog(availability);
    } else if (dialogConfig.type === 'bulkverification') {
      // Validate bulk verification form
      const errors = {};

      // Status is only required when action is 'update_status'
      if (bulkVerificationForm.action === 'update_status') {
        if (!bulkVerificationForm.status || !bulkVerificationForm.status.trim()) {
          errors.status = 'Verification Status is required';
        }
      }

      if (bulkVerificationForm.comments && bulkVerificationForm.comments.length > 500) {
        errors.comments = 'Comments cannot exceed 500 characters';
      }

      if (Object.keys(errors).length > 0) {
        setBulkVerificationErrors(errors);
        return;
      }

      // Clear any errors and close dialog with bulk verification data
      setBulkVerificationErrors({});
      closeDialog(bulkVerificationForm);
    } else if (dialogConfig.type === 'verification') {
      // Validate verification form
      const errors = {};

      if (!verificationForm.status || !verificationForm.status.trim()) {
        errors.status = 'Verification Status is required';
      }

      if (verificationForm.comments && verificationForm.comments.length > 500) {
        errors.comments = 'Comments cannot exceed 500 characters';
      }

      if (Object.keys(errors).length > 0) {
        setVerificationErrors(errors);
        return;
      }

      // Clear any errors and close dialog with verification data
      setVerificationErrors({});
      // Create a fresh copy to ensure we're passing the current state values
      closeDialog({
        status: verificationForm.status || '',
        comments: verificationForm.comments || ''
      });
    } else if (dialogConfig.type === 'bulkreview') {
      // Validate bulk review form
      const errors = {};

      if (!bulkReviewForm.status || !bulkReviewForm.status.trim()) {
        errors.status = 'Review Decision is required';
      }

      if (Object.keys(errors).length > 0) {
        setBulkReviewErrors(errors);
        return;
      }

      // Clear any errors and close dialog with bulk review data
      setBulkReviewErrors({});
      closeDialog({
        status: bulkReviewForm.status,
        comments: bulkReviewForm.comments || ''
      });
    } else if (dialogConfig.type === 'profilereview') {
      // Validate profile review form
      const errors = {};

      if (!profileReviewForm.status || !profileReviewForm.status.trim()) {
        errors.status = 'Review Decision is required';
      }

      if (Object.keys(errors).length > 0) {
        setProfileReviewErrors(errors);
        return;
      }

      // Submit review
      setSubmittingProfileReview(true);
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          toast.error('Authentication token not found. Please log in again.');
          setSubmittingProfileReview(false);
          return;
        }

        const startupId = dialogConfig.startup?.id;
        if (!startupId) {
          toast.error('Startup ID is missing');
          setSubmittingProfileReview(false);
          return;
        }

        const response = await fetch(`${API_BASE_URL}/startups/${startupId}/review`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            status: profileReviewForm.status,
            comments: profileReviewForm.comments,
            feedback: profileReviewForm.feedback,
            revision_requirements: profileReviewForm.revision_requirements
          })
        });

        if (response.ok) {
          toast.success('Review submitted successfully');
          setProfileReviewErrors({});
          // Call onConfirm callback if provided
          if (dialogConfig.onConfirm) {
            try {
              await dialogConfig.onConfirm();
            } catch (error) {
              console.error('Error in onConfirm callback:', error);
            }
          }
          closeDialog({
            status: profileReviewForm.status,
            comments: profileReviewForm.comments,
            feedback: profileReviewForm.feedback,
            revision_requirements: profileReviewForm.revision_requirements
          });
        } else {
          const error = await response.json();
          toast.error(error.error || 'Failed to submit review');
          setSubmittingProfileReview(false);
        }
      } catch (error) {
        toast.error('Failed to submit review:', error);
        setSubmittingProfileReview(false);
      }
    } else if (dialogConfig.type === 'contextreview') {
      // Validate context review form
      setSubmitAttemptedContextReview(true);
      const errors = {};
      // Always read the latest status from ref (fallback to state)
      const rawStatus = contextReviewStatusRef.current ?? contextReviewForm.status;
      const { sectionComments, overallComment } = contextReviewForm;

      // Valid statuses that backend accepts
      const validStatuses = ['APPROVED', 'REJECTED', 'NEEDS_REVISION'];

      // Normalize status: trim whitespace and convert to uppercase for comparison
      // Check if status exists and is a non-empty string
      const statusStr = rawStatus ? String(rawStatus).trim() : '';
      const normalizedStatus = statusStr.toUpperCase();

      // Validate status - must be a non-empty string and must be one of the valid statuses
      if (!statusStr || statusStr.length === 0) {
        errors.status = 'Review Decision is required';
      } else if (!validStatuses.includes(normalizedStatus)) {
        errors.status = `Invalid status. Must be one of: ${validStatuses.join(', ')}`;
      }

      // Use normalized status for comparisons (only if status is valid)
      // Only proceed with status-specific validations if status validation passed
      const isStatusValid = normalizedStatus && validStatuses.includes(normalizedStatus);
      const currentStatus = isStatusValid ? normalizedStatus : '';

      // Only validate status-specific requirements if status itself is valid
      if (isStatusValid) {
        // NOTE: For REJECTED we no longer hard‑require an overall comment here.
        // This avoids blocking submission when the text field state is slightly behind.
        // Backend can still enforce any comment requirements if needed.
        if (currentStatus === 'NEEDS_REVISION') {
          if (contextReviewSelectedSections.length === 0) {
            errors.sections = 'Please select at least one section for revision';
          } else {
            // Check that all selected sections have comments
            const missingComments = contextReviewSelectedSections.filter(
              sectionId => !sectionComments[sectionId] || !sectionComments[sectionId].trim()
            );
            if (missingComments.length > 0) {
              errors.sectionComments = 'Please provide comments for all selected sections';
            }
          }
        }
      }

      if (Object.keys(errors).length > 0) {
        setContextReviewErrors(errors);
        return;
      }

      // Submit review
      setSubmittingContextReview(true);
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          toast.error('Authentication token not found');
          setSubmittingContextReview(false);
          return;
        }

        const startupId = dialogConfig.startupId;
        if (!startupId) {
          toast.error('Startup ID is missing');
          setSubmittingContextReview(false);
          return;
        }

        // Build feedback object with section-wise comments
        const feedback = {};
        contextReviewSelectedSections.forEach(sectionId => {
          if (sectionComments[sectionId]) {
            feedback[sectionId] = sectionComments[sectionId];
          }
        });

        // Final validation: ensure status is valid before sending
        const validStatuses = ['APPROVED', 'REJECTED', 'NEEDS_REVISION'];
        // Normalize status: trim and uppercase for comparison, using latest status from ref
        const rawFinalStatus = contextReviewStatusRef.current ?? contextReviewForm.status;
        const normalizedStatus = rawFinalStatus ? String(rawFinalStatus).trim().toUpperCase() : '';

        if (!normalizedStatus || !validStatuses.includes(normalizedStatus)) {
          toast.error('Please select a valid review decision');
          setSubmittingContextReview(false);
          setContextReviewErrors({ status: 'Please select a valid review decision' });
          return;
        }

        const response = await fetch(`${API_BASE_URL}/startups/${startupId}/review`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            status: normalizedStatus,
            comments: overallComment,
            feedback: feedback,
            revision_requirements: normalizedStatus === 'NEEDS_REVISION' ? {
              sections: contextReviewSelectedSections,
              comments: feedback
            } : {}
          })
        });

        const result = await response.json();

        if (response.ok && result.success) {
          setContextReviewErrors({});
          setSubmitAttemptedContextReview(false);
          // Call onConfirm callback if provided
          if (dialogConfig.onConfirm) {
            try {
              await dialogConfig.onConfirm();
            } catch (error) {
              console.error('Error in onConfirm callback:', error);
            }
          }
          closeDialog({
            status: normalizedStatus,
            comments: overallComment,
            feedback: feedback,
            revision_requirements: normalizedStatus === 'NEEDS_REVISION' ? {
              sections: contextReviewSelectedSections,
              comments: feedback
            } : {}
          });
        } else {
          toast.error(result.error || 'Failed to submit review');
          setSubmittingContextReview(false);
        }
      } catch (error) {
        console.error('Error submitting review:', error);
        toast.error('Failed to submit review');
        setSubmittingContextReview(false);
      }
    } else if (dialogConfig.type === 'juryassign') {
      // Validate deadline date is required
      if (!deadlineDate || !deadlineDate.trim()) {
        setDeadlineDateError('Evaluation Deadline is required');
        return;
      }

      // Validate date is not in the past
      const selectedDate = new Date(deadlineDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      selectedDate.setHours(0, 0, 0, 0);

      if (selectedDate < today) {
        setDeadlineDateError('Deadline date cannot be in the past');
        return;
      }

      // Clear any previous errors
      setDeadlineDateError('');

      // Return both selected jury IDs and deadline date
      closeDialog({
        selectedIds: selectedValues,
        deadlineDate: deadlineDate,
      });
    } else if (dialogConfig.type === 'scores') {
      closeDialog(true);
    } else if (dialogConfig.type === 'addjury') {
      // Validate form
      const errors = {};
      if (!juryForm.firstName.trim()) {
        errors.firstName = 'First name is required';
      }
      if (!juryForm.lastName.trim()) {
        errors.lastName = 'Last name is required';
      }
      if (!juryForm.email.trim()) {
        errors.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(juryForm.email)) {
        errors.email = 'Invalid email address';
      }

      if (Object.keys(errors).length > 0) {
        setJuryFormErrors(errors);
        return;
      }

      setSubmittingJury(true);
      setJuryFormErrors({});

      try {
        const token = localStorage.getItem('token');
        if (!token) {
          toast.error('Session expired. Please log in again.');
          closeDialog(null);
          return;
        }

        const response = await fetch(`${API_BASE_URL}/jury/members`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            firstName: juryForm.firstName.trim(),
            lastName: juryForm.lastName.trim(),
            email: juryForm.email.trim(),
          }),
        });

        const result = await response.json();

        if (response.status === 409) {
          toast.error('Email already exists');
          setJuryFormErrors({ email: 'This email is already registered' });
          setSubmittingJury(false);
          return;
        } else if (!response.ok) {
          toast.error(result.message || 'Failed to create jury member');
          setSubmittingJury(false);
          return;
        }

        toast.success('Jury member created successfully');

        // Call onConfirm callback if provided
        if (dialogConfig.onConfirm) {
          dialogConfig.onConfirm(result.data);
        }

        closeDialog(result.data);
      } catch (error) {
        console.error('Error creating jury member:', error);
        toast.error('Network error. Please try again.');
        setSubmittingJury(false);
      }
    } else if (dialogConfig.type === 'addfunding') {
      // Validate funding form
      const errors = {};

      // Only validate startup/application if startups list is provided (FundingManagement case)
      if (startupsList.length > 0) {
        if (!fundingForm.startupId || !fundingForm.startupId.toString().trim()) {
          errors.startupId = 'Startup is required';
        }

        // Application is required when startup is selected (only for FundingManagement)
        if (fundingForm.startupId && (!fundingForm.applicationId || !fundingForm.applicationId.toString().trim())) {
          errors.applicationId = 'Application is required';
        }
      }
      // If startupsList is empty (ApplicationDetails/StartupProfileView case), 
      // startup_id and application_id will come from defaultStartupId/defaultApplicationId

      // If navigateToApplication is true, skip amount validation (will be done on ApplicationDetails page)
      if (!dialogConfig.navigateToApplication) {
        // Validate payment type is selected
        if (!fundingForm.paymentType || !fundingForm.paymentType.toString().trim()) {
          errors.paymentType = 'Payment method is required';
        }

        // Fund selection — required for new funding (skipped when editing installments
        // of an existing record, where the offering link is already set).
        if (!(dialogConfig?.defaultFundingId && dialogConfig?.existingInstallments)) {
          if (!fundingForm.fundOfferingId || !fundingForm.fundOfferingId.toString().trim()) {
            errors.fundOfferingId = 'Fund is required';
          }
        }

        if (!fundingForm.amount || !fundingForm.amount.toString().trim()) {
          errors.amount = 'Amount is required';
        } else {
          const amountValue = parseFloat(fundingForm.amount);
          if (isNaN(amountValue) || amountValue <= 0) {
            errors.amount = 'Amount must be a positive number';
          } else if (fundingForm.fundOfferingId) {
            // Reject amounts above the catalog's tracked available balance.
            // If the offering doesn't track available (NULL), skip the check.
            const offering = fundOfferingsList.find((o) => String(o.id) === String(fundingForm.fundOfferingId));
            const available = Number(offering?.amount_available);
            // Only enforce the cap when the fund tracks a positive available
            // balance; 0 / untracked means no balance restriction.
            if (Number.isFinite(available) && available > 0 && amountValue > available) {
              errors.amount = `Amount exceeds the fund's available balance.`;
            }
          }
        }
      }

      // Validate installment fields if payment type is installment (skip if navigating)
      if (!dialogConfig.navigateToApplication && fundingForm.paymentType === 'installment') {
        // Only custom installments are supported now
        if (fundingForm.installmentType === 'custom') {
          // Validate custom installments
          if (!fundingForm.amount || !fundingForm.amount.toString().trim()) {
            errors.amount = 'Total amount is required';
          } else {
            const totalAmount = parseFloat(fundingForm.amount);
            if (isNaN(totalAmount) || totalAmount <= 0) {
              errors.amount = 'Total amount must be a positive number';
            }
          }

          if (!fundingForm.customInstallments || fundingForm.customInstallments.length === 0) {
            errors.customInstallments = 'At least one milestone is required';
          } else {
            let totalInstallmentAmount = 0;
            fundingForm.customInstallments.forEach((installment, index) => {
              if (!installment.amount || !installment.amount.toString().trim()) {
                errors[`installment_${index}_amount`] = 'Amount is required';
              } else {
                const amountValue = parseFloat(installment.amount);
                if (isNaN(amountValue) || amountValue <= 0) {
                  errors[`installment_${index}_amount`] = 'Amount must be a positive number';
                } else {
                  totalInstallmentAmount += amountValue;
                }
              }
              if (!installment.date || !installment.date.toString().trim()) {
                errors[`installment_${index}_date`] = 'Date is required';
              }
            });
            // Validate that total installment amount matches total amount (remaining must be 0)
            if (fundingForm.amount && totalInstallmentAmount > 0) {
              const totalAmount = parseFloat(fundingForm.amount);
              if (!isNaN(totalAmount)) {
                const remaining = totalAmount - totalInstallmentAmount;
                if (Math.abs(remaining) > 0.01) {
                  errors.customInstallments = `Milestone total (${totalInstallmentAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}) must equal total amount (${totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}). Remaining: ${remaining.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                }
              }
            }
          }
        }
      }

      // Validate documents - mandatory (only when not editing installments)
      if (!(dialogConfig?.defaultFundingId && dialogConfig?.existingInstallments)) {
        if (!fundingForm.documents || fundingForm.documents.length === 0) {
          errors.documents = 'At least one document is required';
        }
      }

      if (Object.keys(errors).length > 0) {
        setFundingFormErrors(errors);
        return;
      }

      // If navigateToApplication is true, skip confirmation and navigate directly
      if (dialogConfig.navigateToApplication) {
        const applicationId = fundingForm.applicationId && fundingForm.applicationId.toString().trim()
          ? parseInt(fundingForm.applicationId)
          : (dialogConfig.defaultApplicationId ? parseInt(dialogConfig.defaultApplicationId) : null);

        if (!applicationId) {
          setFundingFormErrors({ applicationId: 'Application is required' });
          return;
        }

        // Determine startup_id
        let startupId = null;
        if (fundingForm.startupId && fundingForm.startupId.toString().trim()) {
          startupId = parseInt(fundingForm.startupId);
        } else if (dialogConfig.defaultStartupId) {
          startupId = parseInt(dialogConfig.defaultStartupId);
        }

        const result = {
          navigateToApplication: true,
          applicationId: applicationId,
          startupId: startupId
        };

        // Call onConfirm callback if provided (for navigation)
        if (dialogConfig.onConfirm) {
          try {
            await dialogConfig.onConfirm(result);
          } catch (error) {
            console.error('Error in onConfirm callback:', error);
          }
        }

        closeDialog(result);
        return;
      }

      // Show confirmation dialog before proceeding
      setShowFundingConfirmation(true);
      return; // Exit here, API call will happen after confirmation
    } else if (dialogConfig.type === 'paymentmethod') {
      // Validate payment method form
      const errors = {};

      // Check if installments exist and are valid - if they do, it's installment type
      const hasValidInstallments = paymentMethodForm.customInstallments &&
        paymentMethodForm.customInstallments.length > 0 &&
        paymentMethodForm.customInstallments.some(inst => inst.amount && parseFloat(inst.amount) > 0 && inst.date);

      // Determine actual payment type - if installments exist, it's installment; otherwise check paymentType
      const actualPaymentType = hasValidInstallments ? 'installment' : (paymentMethodForm.paymentType || null);

      // Only show payment method error if no payment type is selected AND no valid installments exist
      if (!actualPaymentType && !hasValidInstallments) {
        errors.paymentType = 'Payment method is required';
      }

      // Validate installments (milestones) if payment type is installment or if installments exist
      if (actualPaymentType === 'installment' || paymentMethodForm.paymentType === 'installment' || hasValidInstallments) {
        if (!paymentMethodForm.customInstallments || paymentMethodForm.customInstallments.length === 0) {
          errors.customInstallments = 'At least one milestone is required';
        } else {
          // Validate each milestone
          let hasInstallmentError = false;
          paymentMethodForm.customInstallments.forEach((inst, idx) => {
            if (!inst.amount || parseFloat(inst.amount) <= 0) {
              errors[`installment_${idx}_amount`] = 'Amount is required and must be greater than 0';
              hasInstallmentError = true;
            }
            if (!inst.date) {
              errors[`installment_${idx}_date`] = 'Date is required';
              hasInstallmentError = true;
            }
            // Validate notes - mandatory for each milestone
            if (!inst.notes || !inst.notes.trim()) {
              errors[`installment_${idx}_notes`] = 'Notes are required for each milestone';
              hasInstallmentError = true;
            }
          });
          if (hasInstallmentError) {
            errors.customInstallments = 'Please fill all milestone details';
          }
        }
      }

      // Validate notes field - mandatory only for full payment
      // For installment type, notes are per milestone (optional)
      if (actualPaymentType === 'full') {
        if (!paymentMethodForm.notes || !paymentMethodForm.notes.trim()) {
          errors.notes = 'Notes are required';
        }
      }

      if (Object.keys(errors).length > 0) {
        setPaymentMethodFormErrors(errors);
        // Surface why the submit didn't proceed so it never silently does
        // nothing (e.g. milestone/full-payment notes are required but the submit
        // button doesn't disable for that).
        const firstError = Object.values(errors).find((m) => typeof m === 'string' && m.trim());
        if (firstError) toast.error(firstError);
        return;
      }

      setSubmittingPaymentMethod(true);
      setPaymentMethodFormErrors({});

      try {
        const token = localStorage.getItem('token');
        if (!token) {
          toast.error('Session expired. Please log in again.');
          closeDialog(null);
          return;
        }

        const updateData = {};

        // Determine payment type - prioritize user's explicit selection
        // If user selected 'installment', it's installment
        // If user selected 'full', it's full payment
        // Otherwise, check if valid installments exist

        const hasValidInstallments = paymentMethodForm.customInstallments &&
          paymentMethodForm.customInstallments.length > 0 &&
          paymentMethodForm.customInstallments.some(inst => {
            const amountStr = inst.amount?.toString().trim();
            const dateStr = inst.date?.toString().trim();
            return amountStr && amountStr !== '' && !isNaN(parseFloat(amountStr)) && parseFloat(amountStr) > 0 && dateStr && dateStr !== '';
          });

        const finalPaymentType = paymentMethodForm.paymentType === 'installment' ? 'installment' :
          paymentMethodForm.paymentType === 'full' ? 'full' :
            hasValidInstallments ? 'installment' : null;

        if (finalPaymentType === 'full') {
          // If Full Payment: Show confirmation dialog first
          updateData.status = 'paid';
          updateData.payment_type = 'full';
          // Add notes if provided
          if (paymentMethodForm.notes && paymentMethodForm.notes.trim()) {
            updateData.notes = paymentMethodForm.notes.trim();
          }

          // Store the data and show confirmation dialog
          setPendingPaymentMethodData({
            updateData,
            fundingId: dialogConfig.defaultFundingId
          });
          setShowPaymentMethodConfirmation(true);
          setSubmittingPaymentMethod(false);
          return; // Exit here, API call will happen after confirmation
        } else if (finalPaymentType === 'installment') {
          // If Installment: Update payment_type to 'installment', keep status as 'unpaid', and create installments
          updateData.payment_type = 'installment';
          updateData.status = 'unpaid'; // Ensure status remains unpaid for installments
          updateData.installment_type = 'custom';

          // Add notes if provided
          if (paymentMethodForm.notes && paymentMethodForm.notes.trim()) {
            updateData.notes = paymentMethodForm.notes.trim();
          }

          // Add custom installments - filter out empty ones and ensure all valid installments are included
          updateData.custom_installments = paymentMethodForm.customInstallments
            .filter(inst => {
              const amountStr = inst.amount?.toString().trim();
              const dateStr = inst.date?.toString().trim();
              const hasAmount = amountStr && amountStr !== '' && !isNaN(parseFloat(amountStr)) && parseFloat(amountStr) > 0;
              const hasDate = dateStr && dateStr !== '';
              return hasAmount && hasDate;
            })
            .map(inst => ({
              amount: parseFloat(inst.amount),
              date: inst.date,
              notes: inst.notes ? inst.notes.trim() : '' // Include notes for each milestone
            }));
        } else {
          // Fallback - should not happen, but handle gracefully
          toast.error('Please select a valid payment method');
          setSubmittingPaymentMethod(false);
          return;
        }

        const response = await fetch(`${API_BASE_URL}/funding/${dialogConfig.defaultFundingId}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(updateData)
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            toast.success(paymentMethodForm.paymentType === 'full'
              ? 'Payment method updated to paid successfully'
              : 'Payment method updated to installment successfully');
            const result = {
              fundingId: dialogConfig.defaultFundingId
            };

            // Call onConfirm callback if provided (for refreshing data)
            if (dialogConfig.onConfirm) {
              try {
                await dialogConfig.onConfirm(result);
              } catch (error) {
                console.error('Error in onConfirm callback:', error);
              }
            }

            closeDialog(result);
            setSubmittingPaymentMethod(false);
            return;
          } else {
            toast.error(data.message || 'Failed to update payment method');
            setSubmittingPaymentMethod(false);
            return;
          }
        } else {
          const errorData = await response.json().catch(() => ({}));
          toast.error(errorData.message || 'Failed to update payment method');
          setSubmittingPaymentMethod(false);
          return;
        }
      } catch (error) {
        console.error('Error updating payment method:', error);
        toast.error('Network error. Please try again.');
        setSubmittingPaymentMethod(false);
      }
    } else if (dialogConfig.type === 'fundusage') {
      // Validate fund usage form
      const errors = {};
      if (!fundUsageForm.title || !fundUsageForm.title.trim()) {
        errors.title = 'Title is required';
      }
      if (!fundUsageForm.amount || !fundUsageForm.amount.toString().trim()) {
        errors.amount = 'Amount is required';
      } else {
        const amountValue = parseFloat(fundUsageForm.amount);
        if (isNaN(amountValue) || amountValue <= 0) {
          errors.amount = 'Amount must be a positive number';
        }
      }
      if (!fundUsageForm.description || !fundUsageForm.description.trim()) {
        errors.description = 'Description is required';
      }
      // At least 1 document is mandatory
      if (!fundUsageForm.documents || fundUsageForm.documents.length === 0) {
        errors.documents = 'At least one document is required';
      }

      if (Object.keys(errors).length > 0) {
        setFundUsageFormErrors(errors);
        return;
      }

      setSubmittingFundUsage(true);
      setFundUsageFormErrors({});

      try {
        const token = localStorage.getItem('token');
        if (!token) {
          toast.error('Session expired. Please log in again.');
          closeDialog(null);
          return;
        }

        // Fetch funding data and existing expenses to validate amount
        const fundingResponse = await fetch(`${API_BASE_URL}/funding/${dialogConfig.fundingId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!fundingResponse.ok) {
          toast.error('Failed to fetch funding details');
          setSubmittingFundUsage(false);
          return;
        }

        const fundingData = await fundingResponse.json();
        if (!fundingData.success || !fundingData.data) {
          toast.error('Failed to fetch funding details');
          setSubmittingFundUsage(false);
          return;
        }

        const funding = fundingData.data;

        const expenseAmount = parseFloat(fundUsageForm.amount);

        // Calculate paid amount
        let paidAmount = 0;
        const statusLower = (funding.status || '').toLowerCase();
        const isInstallmentType = funding.payment_type === 'installment';

        if (isInstallmentType) {
          // Fetch installments for installment type funding
          const installmentsResponse = await fetch(`${API_BASE_URL}/funding/${dialogConfig.fundingId}/installments`, {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });

          if (installmentsResponse.ok) {
            const installmentsData = await installmentsResponse.json();

            if (installmentsData.success && installmentsData.data) {
              // For installment type: count acknowledged installments (same as received amount calculation)
              // Use same filter logic as ApplicationDetails.jsx: inst.acknowledged (truthy check)
              const allInstallments = installmentsData.data || [];
              const acknowledgedInstallments = allInstallments.filter(inst => inst.acknowledged);
              paidAmount = acknowledgedInstallments.reduce((sum, inst) => sum + parseFloat(inst.amount || 0), 0);
            } else {
              console.error('Installments API response error:', installmentsData);
              paidAmount = 0; // Explicitly set to 0 if no data
            }
          } else {
            const errorText = await installmentsResponse.text();
            console.error('Installments API request failed:', {
              status: installmentsResponse.status,
              statusText: installmentsResponse.statusText,
              response: errorText
            });
            paidAmount = 0; // Explicitly set to 0 if API fails
          }
        } else {
          // For full payment: count if status is paid/approved/disbursed OR if acknowledged (same as received amount calculation)
          if (statusLower === 'paid' || statusLower === 'approved' || statusLower === 'disbursed' || funding.acknowledged === true) {
            paidAmount = parseFloat(funding.amount || 0);
          }
        }

        // Check if this is milestone-wise expense submission
        const isMilestoneExpense = dialogConfig.installmentId && dialogConfig.installmentAmount;

        // Fetch existing fund usage entries
        const usageResponse = await fetch(`${API_BASE_URL}/funding/usage/${dialogConfig.fundingId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        let existingExpenses = 0;
        let availableAmount = paidAmount;

        if (usageResponse.ok) {
          const usageData = await usageResponse.json();
          if (usageData.success && usageData.data) {
            if (isMilestoneExpense) {
              // For milestone-wise: only count expenses for this specific milestone
              existingExpenses = usageData.data
                .filter(entry => entry.installment_id === dialogConfig.installmentId)
                .reduce((sum, entry) => sum + parseFloat(entry.amount || 0), 0);
              // Available amount is the milestone amount
              availableAmount = parseFloat(dialogConfig.installmentAmount || 0);
            } else {
              // For general expenses: count all expenses
              existingExpenses = usageData.data.reduce((sum, entry) => {
                return sum + parseFloat(entry.amount || 0);
              }, 0);
            }
          }
        }

        // Validate: check if there's any paid amount available
        if (availableAmount <= 0) {
          console.error('Validation failed: availableAmount <= 0', { availableAmount });
          errors.amount = isMilestoneExpense
            ? 'This milestone has no available balance for expense'
            : 'You Have not Enough Balance for expense';
          setFundUsageFormErrors(errors);
          setSubmittingFundUsage(false);
          return;
        }

        // Validate: new expense + existing expenses should not exceed available amount
        const totalExpenses = existingExpenses + expenseAmount;
        if (totalExpenses > availableAmount) {
          console.error('Validation failed: totalExpenses > availableAmount', {
            totalExpenses,
            availableAmount,
            existingExpenses,
            expenseAmount
          });
          errors.amount = isMilestoneExpense
            ? `Expense amount cannot exceed milestone amount (${dialogConfig.currencySymbol || '₹'}${availableAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})`
            : 'You Have not Enough Balance for expense';
          setFundUsageFormErrors(errors);
          setSubmittingFundUsage(false);
          return;
        }

        const formData = new FormData();
        formData.append('funding_id', dialogConfig.fundingId);
        formData.append('title', fundUsageForm.title.trim());
        formData.append('amount', fundUsageForm.amount);
        formData.append('description', fundUsageForm.description.trim());
        // Add installment_id if this is milestone-wise expense
        if (isMilestoneExpense && dialogConfig.installmentId) {
          formData.append('installment_id', dialogConfig.installmentId);
        }
        // Append all documents (at least 1 is required)
        if (fundUsageForm.documents && fundUsageForm.documents.length > 0) {
          fundUsageForm.documents.forEach((file, index) => {
            formData.append('files', file); // Backend should accept 'files' as array
          });
        }

        const response = await fetch(`${API_BASE_URL}/funding/usage`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          body: formData,
        });

        const result = await response.json();

        if (!response.ok) {
          toast.error(result.message || 'Failed to add fund usage');
          setSubmittingFundUsage(false);
          return;
        }

        toast.success('Fund usage added successfully');

        // Call onConfirm callback if provided
        if (dialogConfig.onConfirm) {
          dialogConfig.onConfirm(result.data);
        }

        closeDialog(result.data);
      } catch (error) {
        console.error('Error adding fund usage:', error);
        toast.error('Network error. Please try again.');
        setSubmittingFundUsage(false);
      }
    } else if (dialogConfig.type === 'addfundingdocuments') {
      // Validate funding documents form
      const errors = {};
      if (!fundingDocumentsForm.fundingId || !fundingDocumentsForm.fundingId.toString().trim()) {
        errors.fundingId = 'Funding record is required';
      }
      // At least 1 document is mandatory
      if (!fundingDocumentsForm.documents || fundingDocumentsForm.documents.length === 0) {
        errors.documents = 'At least one document is required';
      }

      if (Object.keys(errors).length > 0) {
        setFundingDocumentsFormErrors(errors);
        return;
      }

      setSubmittingFundingDocuments(true);
      setFundingDocumentsFormErrors({});

      try {
        const token = localStorage.getItem('token');
        if (!token) {
          toast.error('Session expired. Please log in again.');
          closeDialog(null);
          return;
        }

        // Compute documents to send – use state (handleConfirm now has fresh state via dependencies)
        const documentsToSend = fundingDocumentsForm.documents || [];

        // Auto-generate a simple title for backend requirement (use first file name or a generic label)
        const autoTitle =
          (documentsToSend[0] && documentsToSend[0].name) ||
          `Supporting Document (${new Date().toLocaleDateString()})`;

        const formData = new FormData();
        // Append required title field expected by backend (hidden from UI)
        formData.append('title', autoTitle);
        // Append all documents
        documentsToSend.forEach((file) => {
          formData.append('documents', file);
        });

        const url = `${API_BASE_URL}/funding/${fundingDocumentsForm.fundingId}/documents`;
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          body: formData,
        });

        const result = await response.json();

        if (!response.ok) {
          console.error('Failed to add documents:', result);
          toast.error(result.message || 'Failed to add documents');
          setSubmittingFundingDocuments(false);
          return;
        }

        toast.success('Documents added successfully');

        // Call onConfirm callback if provided
        if (dialogConfig.onConfirm) {
          dialogConfig.onConfirm(result.data);
        }

        closeDialog(result.data);
      } catch (error) {
        console.error('Error adding documents:', error);
        toast.error('Network error. Please try again.');
        setSubmittingFundingDocuments(false);
      }
    } else if (dialogConfig.type === 'quickschedule') {
      // Validate required fields
      const errors = {};

      const trimmedTitle = (quickScheduleForm.title || '').trim();
      if (!trimmedTitle) {
        errors.title = 'Please enter a title';
      } else if (trimmedTitle.length > MAX_SESSION_TITLE_LENGTH) {
        errors.title = `Title cannot exceed ${MAX_SESSION_TITLE_LENGTH} characters`;
      }
      // Only require startup selection if startup selection is enabled and options exist
      if (
        (dialogConfig.startupOptions && dialogConfig.startupOptions.length > 0) &&
        dialogConfig.enableStartupSelection !== false &&
        !quickScheduleForm.targetStartup
      ) {
        errors.targetStartup = 'Please select a startup';
      }
      if (!quickScheduleForm.startTime) {
        errors.startTime = 'Please select a start time';
      }
      if (!quickScheduleForm.endTime) {
        errors.endTime = 'Please select an end time';
      }
      // Validate end time is after start time
      if (quickScheduleForm.startTime && quickScheduleForm.endTime && quickScheduleForm.endTime <= quickScheduleForm.startTime) {
        errors.endTime = 'End time must be after start time';
      }

      // Validate time within selected slot
      if (quickScheduleForm.timeSlot) {
        const slotRanges = {
          morning: { min: '08:00', max: '12:00', label: 'Morning (8 to 12)' },
          afternoon: { min: '12:00', max: '17:00', label: 'Afternoon (12 to 5)' },
          evening: { min: '17:00', max: '20:00', label: 'Evening (5 to 8)' }
        };
        const range = slotRanges[quickScheduleForm.timeSlot];
        if (range) {
          if (quickScheduleForm.startTime && (quickScheduleForm.startTime < range.min || quickScheduleForm.startTime > range.max)) {
            errors.startTime = `Time must be within ${range.label}`;
          }
          if (quickScheduleForm.endTime && (quickScheduleForm.endTime < range.min || quickScheduleForm.endTime > range.max)) {
            errors.endTime = `Time must be within ${range.label}`;
          }
        }
      }
      // Validate description is required
      if (!quickScheduleForm.description || !quickScheduleForm.description.trim()) {
        errors.description = 'Please enter a session description';
      }

      if (Object.keys(errors).length > 0) {
        setQuickScheduleErrors(errors);
        return;
      }

      // Clear errors if validation passes
      setQuickScheduleErrors({});

      // If link is provided, validate it's a proper HTTP/HTTPS URL
      if (quickScheduleForm.link && quickScheduleForm.link.trim()) {
        try {
          let urlToValidate = quickScheduleForm.link.trim();
          // Ensure URL has protocol
          if (!/^https?:\/\//i.test(urlToValidate)) {
            urlToValidate = 'https://' + urlToValidate;
          }
          const url = new URL(urlToValidate);
          if (!['http:', 'https:'].includes(url.protocol)) {
            toast.error('Link must be a valid HTTP or HTTPS URL');
            return;
          }
          // Validate URL format - must have a valid hostname
          if (!url.hostname || url.hostname.length === 0) {
            toast.error('Please enter a valid URL with a domain name');
            return;
          }
          // Update form with properly formatted URL
          if (urlToValidate !== quickScheduleForm.link.trim()) {
            setQuickScheduleForm({ ...quickScheduleForm, link: urlToValidate });
          }
        } catch (e) {
          toast.error('Please enter a valid URL for the link (e.g., meet.google.com/abc-xyz-def)');
          return;
        }
      }

      // Return quick schedule form data. If a PDF file was selected, read it as base64
      // and save in notes so it is stored in mentoring_sessions.notes.
      let notesValue = quickScheduleForm.attachmentFileName
        ? `Attachment: ${quickScheduleForm.attachmentFileName}`
        : '';
      if (quickScheduleForm.attachmentFile) {
        try {
          notesValue = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = () => reject(new Error('Failed to read file'));
            reader.readAsDataURL(quickScheduleForm.attachmentFile);
          });
        } catch (e) {
          toast.error('Failed to read notes file. Please try again.');
          return;
        }
      }

      closeDialog({
        ...quickScheduleForm,
        notes: notesValue,
      });
    } else if (dialogConfig.type === 'addexpense') {
      // Validate required fields
      if (!expenseForm.amount || !expenseForm.description) {
        toast.error('Please fill in all required fields');
        return;
      }
      // Validate amount is a valid number
      const amountNum = parseFloat(expenseForm.amount);
      if (isNaN(amountNum) || amountNum <= 0) {
        toast.error('Please enter a valid amount');
        return;
      }
      // Return expense form data
      closeDialog({
        ...expenseForm,
        amount: amountNum.toString(),
      });
    } else if (dialogConfig.type === 'assignmentaction') {
      // Validate assignment action form
      const errors = {};

      if (!assignmentActionForm.selectedAction) {
        errors.selectedAction = 'Please select an action';
      }

      if (assignmentActionForm.selectedAction === 'reassign') {
        if (!assignmentActionForm.reassignMentorId) {
          errors.reassignMentorId = 'Please select a mentor to reassign';
        }
      } else if (assignmentActionForm.selectedAction === 'escalate') {
        if (!assignmentActionForm.escalationReason || !assignmentActionForm.escalationReason.trim()) {
          errors.escalationReason = 'Please provide an escalation reason';
        }
      }

      if (Object.keys(errors).length > 0) {
        setAssignmentActionErrors(errors);
        return;
      }

      // Return assignment action form data
      closeDialog({
        action: assignmentActionForm.selectedAction,
        reassignMentorId: assignmentActionForm.selectedAction === 'reassign' ? parseInt(assignmentActionForm.reassignMentorId, 10) : null,
        escalationReason: assignmentActionForm.selectedAction === 'escalate' ? assignmentActionForm.escalationReason.trim() : null,
      });
    } else if (dialogConfig.type === 'selectstartupapplication') {
      // Validate startup and application selection
      const errors = {};

      if (!startupApplicationForm.startupId || !startupApplicationForm.startupId.toString().trim()) {
        errors.startupId = 'Startup is required';
      }

      if (!startupApplicationForm.applicationId || !startupApplicationForm.applicationId.toString().trim()) {
        errors.applicationId = 'Application is required';
      }

      if (Object.keys(errors).length > 0) {
        setStartupApplicationErrors(errors);
        return;
      }

      // Return selected startup and application IDs for navigation
      const result = {
        startupId: parseInt(startupApplicationForm.startupId),
        applicationId: parseInt(startupApplicationForm.applicationId)
      };

      // Call onConfirm callback if provided (for navigation)
      if (dialogConfig.onConfirm) {
        try {
          await dialogConfig.onConfirm(result);
        } catch (error) {
          console.error('Error in onConfirm callback:', error);
        }
      }

      closeDialog(result);
    } else {
      closeDialog(true);
    }
  }, [
    closeDialog,
    dialogConfig,
    inputValue,
    ratingValue,
    selectedValues,
    selectedExpertiseAreas,
    selectedIndustries,
    industryDetails,
    juryForm,
    deadlineDate,
    fundingForm,
    fundUsageForm,
    quickScheduleForm,
    expenseForm,
    assignmentActionForm,
    fundingDocumentsForm,
    declineForm,
    assignmentForm,
    paymentMethodForm,
    achievements,
    speakingEngagements,
    publications,
    availability,
    bulkVerificationForm,
    verificationForm,
    bulkReviewForm,
    profileReviewForm,
    contextReviewForm,
    contextReviewSelectedSections,
    fundOfferingsList,
    startupApplicationForm.applicationId,
    startupApplicationForm.startupId,
    startupsList.length,
  ]);

  const contextValue = useMemo(
    () => ({
      confirm: (options) => openDialog({ type: 'confirm', ...options }),
      alert: (options) => openDialog({ type: 'alert', ...options }),
      prompt: (options) => openDialog({ type: 'prompt', ...options }),
      rating: (options) => openDialog({ type: 'rating', ...options }),
      select: (options) => openDialog({ type: 'select', ...options }),
      multiselect: (options) => openDialog({ type: 'multiselect', ...options }),
      scores: (options) => openDialog({ type: 'scores', ...options }),
      juryassign: (options) => openDialog({ type: 'juryassign', ...options }),
      addjury: (options) => openDialog({ type: 'addjury', ...options }),
      addfunding: (options) => openDialog({ type: 'addfunding', ...options }),
      funding: (options) => openDialog({ type: 'funding', ...options }),
      quickschedule: (options) => openDialog({ type: 'quickschedule', ...options }),
      sessiondetails: (options) => openDialog({ type: 'sessiondetails', ...options }),
      addexpense: (options) => openDialog({ type: 'addexpense', ...options }),
      assignmentaction: (options) => openDialog({ type: 'assignmentaction', ...options }),
      selectstartupapplication: (options) => openDialog({ type: 'selectstartupapplication', ...options }),
      mentordetails: (options) => openDialog({ type: 'mentordetails', ...options }),
      mentorverify: (options) => openDialog({ type: 'mentorverify', ...options }),
      mentorrejectionnotes: (options) => openDialog({ type: 'mentorrejectionnotes', ...options }),
      confirmassignment: (options) => openDialog({ type: 'confirmassignment', ...options }),
      assignmentdetails: (options) => openDialog({ type: 'assignmentdetails', ...options }),
      startupdetails: (options) => openDialog({ type: 'startupdetails', ...options }),
      editexpertise: (options) => openDialog({ type: 'editexpertise', ...options }),
      editindustries: (options) => openDialog({ type: 'editindustries', ...options }),
      manageachievements: (options) => openDialog({ type: 'manageachievements', ...options }),
      managespeaking: (options) => openDialog({ type: 'managespeaking', ...options }),
      managepublications: (options) => openDialog({ type: 'managepublications', ...options }),
      manageavailability: (options) => openDialog({ type: 'manageavailability', ...options }),
      bulkverification: (options) => openDialog({ type: 'bulkverification', ...options }),
      verification: (options) => openDialog({ type: 'verification', ...options }),
      documenthistory: (options) => openDialog({ type: 'documenthistory', ...options }),
      bulkreview: (options) => openDialog({ type: 'bulkreview', ...options }),
      profilereview: (options) => openDialog({ type: 'profilereview', ...options }),
      reviewhistory: (options) => openDialog({ type: 'reviewhistory', ...options }),
      contextreview: (options) => openDialog({ type: 'contextreview', ...options }),
    }),
    [openDialog],
  );

  return (
    <DialogContext.Provider value={contextValue}>
      <ToastContainer position="top-right" autoClose={3000} />
      {children}
      <Dialog
        open={Boolean(dialogConfig) && dialogConfig?.type !== 'bulkverification' && dialogConfig?.type !== 'verification' && dialogConfig?.type !== 'documenthistory' && dialogConfig?.type !== 'bulkreview' && dialogConfig?.type !== 'profilereview' && dialogConfig?.type !== 'reviewhistory'}
        onClose={dialogConfig?.type === 'alert' ? handleConfirm : handleCancel}
        disableRestoreFocus={true}
        TransitionProps={{
          onExited: () => {
            // Fix accessibility issue: ensure no element has focus when dialog fully closes
            // This prevents aria-hidden accessibility warnings
            requestAnimationFrame(() => {
              const rootElement = document.getElementById('root');

              // Remove aria-hidden from root if Material-UI forgot to remove it
              if (rootElement && rootElement.getAttribute('aria-hidden') === 'true') {
                rootElement.removeAttribute('aria-hidden');
              }

              // Don't restore focus to the previous element if it's a button
              // This prevents the aria-hidden accessibility warning
              const previousElement = previousActiveElementRef.current;
              if (previousElement && previousElement instanceof HTMLButtonElement) {
                // Don't restore focus to buttons - let the user navigate naturally
                previousElement.blur();
              } else if (previousElement && previousElement instanceof HTMLElement) {
                // Only restore focus to non-button elements
                try {
                  previousElement.focus();
                } catch (e) {
                  // Element might not be focusable, ignore
                }
              }
            });
          },
        }}
        fullWidth
        maxWidth={dialogConfig?.type === 'scores' || dialogConfig?.type === 'juryassign' || dialogConfig?.type === 'addjury' || dialogConfig?.type === 'addfunding' || dialogConfig?.type === 'funding' || dialogConfig?.type === 'quickschedule' || dialogConfig?.type === 'addexpense' || dialogConfig?.type === 'assignmentaction' || dialogConfig?.type === 'selectstartupapplication' || dialogConfig?.type === 'mentordetails' || dialogConfig?.type === 'mentorverify' || dialogConfig?.type === 'mentorrejectionnotes' || dialogConfig?.type === 'assignmentdetails' || dialogConfig?.type === 'editexpertise' || dialogConfig?.type === 'editindustries' || dialogConfig?.type === 'startupdetails' ? 'md' : 'sm'}
        PaperProps={{
          sx: {
            borderRadius: dialogConfig?.type === 'quickschedule' ? { xs: 0, sm: 3 } : { xs: 0, sm: '24px' },
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            overflowX: 'hidden',
            overflowY: dialogConfig?.type === 'quickschedule' ? 'hidden' : 'hidden',
            maxWidth: dialogConfig?.type === 'scores' ? '800px' : dialogConfig?.type === 'juryassign' ? '700px' : dialogConfig?.type === 'addjury' ? '600px' : dialogConfig?.type === 'addfunding' ? '600px' : dialogConfig?.type === 'funding' ? '600px' : dialogConfig?.type === 'quickschedule' ? '510px' : dialogConfig?.type === 'addexpense' ? '500px' : dialogConfig?.type === 'assignmentaction' ? '600px' : dialogConfig?.type === 'selectstartupapplication' ? '500px' : dialogConfig?.type === 'mentordetails' ? '700px' : dialogConfig?.type === 'mentorverify' ? '600px' : dialogConfig?.type === 'mentorrejectionnotes' ? '600px' : dialogConfig?.type === 'assignmentdetails' ? '900px' : dialogConfig?.type === 'editexpertise' ? '700px' : dialogConfig?.type === 'editindustries' ? '800px' : dialogConfig?.type === 'startupdetails' ? '700px' : '500px',
            width: dialogConfig?.type === 'quickschedule' ? { xs: '100%', sm: '510px' } : '100%',
            height: (dialogConfig?.type === 'quickschedule' || dialogConfig?.type === 'manageachievements' || dialogConfig?.type === 'managespeaking' || dialogConfig?.type === 'managepublications' || dialogConfig?.type === 'manageavailability') ? 'auto' : { xs: '100vh', sm: 'auto' },
            maxHeight: dialogConfig?.type === 'quickschedule' ? '90vh' : { xs: '100vh', sm: '92vh' },
            margin: (dialogConfig?.type === 'quickschedule' || dialogConfig?.type === 'sessiondetails') ? { xs: 0, sm: 0 } : { xs: 0, sm: 'auto' },
            position: (dialogConfig?.type === 'quickschedule' || dialogConfig?.type === 'sessiondetails') ? 'fixed' : 'relative',
            top: (dialogConfig?.type === 'quickschedule' || dialogConfig?.type === 'sessiondetails')
              ? (dialogConfig?.anchorTop || '50%')
              : undefined,
            left: (dialogConfig?.type === 'quickschedule' || dialogConfig?.type === 'sessiondetails')
              ? (dialogConfig?.anchorLeft || '50%')
              : undefined,
            transform: (dialogConfig?.type === 'quickschedule' || dialogConfig?.type === 'sessiondetails')
              ? (dialogConfig?.anchorTransform || 'translate(-50%, -50%)')
              : undefined,
            border: '1px solid rgba(255, 255, 255, 0.2)',
            background: 'linear-gradient(to bottom, #FFFFFF, #FDFDFD)',
          },
        }}
        BackdropProps={{
          sx: {
            backgroundColor:
              dialogConfig?.type === 'quickschedule' || dialogConfig?.type === 'sessiondetails'
                ? 'rgba(0, 0, 0, 0.15)'
                : 'rgba(15, 23, 42, 0.3)',
            backdropFilter:
              dialogConfig?.type === 'quickschedule' || dialogConfig?.type === 'sessiondetails'
                ? 'none'
                : 'blur(8px)',
            pointerEvents:
              dialogConfig?.type === 'quickschedule' || dialogConfig?.type === 'sessiondetails'
                ? 'none'
                : 'auto',
          },
        }}
      >
        {dialogConfig && (
          <>
            <DialogTitle
              sx={{
                display: 'flex',
                alignItems: dialogConfig.type === 'quickschedule' ? 'stretch' : 'center',
                justifyContent: dialogConfig.type === 'quickschedule' ? 'flex-start' : 'space-between',
                flexDirection: dialogConfig.type === 'quickschedule' ? 'column' : 'row',
                pb: dialogConfig.type === 'quickschedule' ? 1.5 : { xs: 2.5, sm: 3 },
                pt: dialogConfig.type === 'quickschedule' ? 1.5 : { xs: 2.5, sm: 3.5 },
                px: { xs: 2.5, sm: 4 },
                backgroundColor: '#ffffff',
                borderBottom: '1px solid #F1F5F9',
                position: 'relative',
                gap: dialogConfig.type === 'quickschedule' ? 1 : 0,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, flex: 1, pr: 4 }}>
                {/* Icon Circle - Enhanced design */}
                {(dialogConfig.subtitle || dialogConfig.type === 'rating' || dialogConfig.type === 'alert' || dialogConfig.type === 'scores' || dialogConfig.type === 'juryassign' || dialogConfig.type === 'addjury' || dialogConfig.type === 'addfunding' || dialogConfig.type === 'funding' || dialogConfig.type === 'fundusage' || dialogConfig.type === 'paymentmethod' || dialogConfig.type === 'fundinghistory' || dialogConfig.type === 'startupapplicationlist' || dialogConfig.type === 'addfundingdocuments' || dialogConfig.type === 'mentordetails' || dialogConfig.type === 'mentorverify' || dialogConfig.type === 'assignmentdetails' || dialogConfig.type === 'assignmentaction' || dialogConfig.type === 'startupdetails') && (
                  <Box
                    sx={{
                      width: { xs: 44, sm: 52 },
                      height: { xs: 44, sm: 52 },
                      borderRadius: '16px',
                      backgroundColor: dialogConfig.type === 'rating' ? '#FFFBEB' : dialogConfig.type === 'alert' ? '#EFF6FF' : dialogConfig.type === 'scores' ? '#F0F9FF' : dialogConfig.type === 'juryassign' ? '#EEF2FF' : dialogConfig.type === 'addjury' ? '#EEF2FF' : dialogConfig.type === 'mentordetails' ? '#F0F9FF' : dialogConfig.type === 'mentorverify' ? '#F0F9FF' : dialogConfig.type === 'mentorrejectionnotes' ? '#FEF2F2' : dialogConfig.type === 'assignmentaction' ? '#F0F9FF' : dialogConfig.type === 'startupdetails' ? '#F0F9FF' : '#F8FAFC',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      border: '1px solid',
                      borderColor: dialogConfig.type === 'rating' ? '#FEF3C7' : dialogConfig.type === 'alert' ? '#DBEAFE' : dialogConfig.type === 'scores' ? '#BAE6FD' : dialogConfig.type === 'juryassign' ? '#C7D2FE' : dialogConfig.type === 'addjury' ? '#C7D2FE' : '#E2E8F0',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
                    }}
                  >
                    {dialogConfig.type === 'rating' ? (
                      <StarIcon sx={{ color: '#D97706', fontSize: { xs: 22, sm: 26 } }} />
                    ) : dialogConfig.type === 'alert' ? (
                      <InfoIcon sx={{ color: '#2563EB', fontSize: { xs: 22, sm: 26 } }} />
                    ) : dialogConfig.type === 'scores' ? (
                      <InfoIcon sx={{ color: '#0284C7', fontSize: { xs: 22, sm: 26 } }} />
                    ) : dialogConfig.type === 'juryassign' || dialogConfig.type === 'addjury' ? (
                      <PersonAddIcon sx={{ color: '#4F46E5', fontSize: { xs: 22, sm: 26 } }} />
                    ) : dialogConfig.type === 'addfunding' || dialogConfig.type === 'funding' || dialogConfig.type === 'fundusage' || dialogConfig.type === 'paymentmethod' || dialogConfig.type === 'fundinghistory' || dialogConfig.type === 'startupapplicationlist' || dialogConfig.type === 'addfundingdocuments' ? (
                      <AccountBalanceIcon sx={{ color: '#2563EB', fontSize: { xs: 22, sm: 26 } }} />
                    ) : (dialogConfig.type === 'mentordetails' || dialogConfig.type === 'mentorverify') ? (
                      <PersonIcon sx={{ color: '#0284C7', fontSize: { xs: 22, sm: 26 } }} />
                    ) : dialogConfig.type === 'startupdetails' ? (
                      <BusinessIcon sx={{ color: '#0284C7', fontSize: { xs: 22, sm: 26 } }} />
                    ) : dialogConfig.type === 'assignmentaction' ? (
                      <AssignmentIndIcon sx={{ color: '#2563EB', fontSize: { xs: 22, sm: 26 } }} />
                    ) : dialogConfig.type === 'quickschedule' ? (
                      // For quickschedule, keep header minimal (no large title text)
                      <></>
                    ) : (
                      <EventIcon sx={{ color: '#2563EB', fontSize: { xs: 22, sm: 26 } }} />
                    )}
                  </Box>
                )}

                {/* Title and Subtitle */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {dialogConfig.type === 'mentorverify' ? (
                    <>
                      <Typography
                        variant="h5"
                        component="h2"
                        sx={{
                          fontWeight: 700,
                          color: '#111827',
                          fontSize: { xs: '1.25rem', sm: '1.5rem' },
                          lineHeight: 1.3,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 1,
                          WebkitBoxOrient: 'vertical',
                          wordBreak: 'break-word',
                          overflowWrap: 'break-word',
                        }}
                      >
                        {truncateTitle(dialogConfig.title, 50)}
                      </Typography>
                      {dialogConfig.subtitle && (
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#6B7280',
                            fontWeight: 500,
                            lineHeight: 1.2,
                            mt: 0.25,
                          }}
                        >
                          {dialogConfig.subtitle}
                        </Typography>
                      )}
                    </>
                  ) : (
                    <>
                      {dialogConfig.subtitle && (
                        <Typography
                          variant="overline"
                          sx={{
                            display: 'block',
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            letterSpacing: '0.1em',
                            color: '#6B7280',
                            textTransform: 'uppercase',
                            mb: 0.5,
                            lineHeight: 1.2,
                          }}
                        >
                          {dialogConfig.subtitle}
                        </Typography>
                      )}
                      {dialogConfig.title && (
                        <Typography
                          variant="h5"
                          component="h2"
                          sx={{
                            fontWeight: 700,
                            color: '#111827',
                            fontSize: { xs: '1.25rem', sm: '1.5rem' },
                            lineHeight: 1.3,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: 'vertical',
                            wordBreak: 'break-word',
                            overflowWrap: 'break-word',
                          }}
                        >
                          {truncateTitle(dialogConfig.title, 50)}
                        </Typography>
                      )}
                    </>
                  )}
                </Box>
              </Box>



              {/* Header Actions for juryassign type */}
              {dialogConfig.type === 'juryassign' && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Button
                    onClick={() => {
                      // Call onAddJury callback if provided
                      if (dialogConfig.onAddJury) {
                        dialogConfig.onAddJury();
                      }
                    }}
                    variant="contained"
                    size="small"
                    // startIcon={<PersonAddIcon />}
                    sx={{
                      backgroundColor: 'var(--an-coral)',
                      color: '#ffffff',
                      textTransform: 'none',
                      borderRadius: 1.5,
                      px: 3,
                      py: 1,
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      boxShadow: 'none',
                      '&:hover': {
                        backgroundColor: 'var(--an-coral-deep)',
                        boxShadow: 'none',
                      },
                    }}
                  >
                    Add Jury Member
                  </Button>
                  <IconButton
                    aria-label="close"
                    onClick={handleCancel}
                    size="small"
                    sx={{
                      color: '#6B7280',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      },
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
              {dialogConfig.type === 'quickschedule' && (
                <Tooltip title="cant add session 15 min after any session completed" arrow>
                  <IconButton
                    size="small"
                    sx={{
                      color: '#6B7280',
                      position: 'absolute',
                      top: 12,
                      right: 48,
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      },
                    }}
                  >
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {/* Close Button - Hidden for alert, juryassign, mentorverify, startupdetails, editexpertise, editindustries, manageachievements, managespeaking, managepublications, and manageavailability type */}
              {dialogConfig.type !== 'alert' && dialogConfig.type !== 'juryassign' && dialogConfig.type !== 'mentorverify' && dialogConfig.type !== 'startupdetails' && dialogConfig.type !== 'editindustries' && dialogConfig.type !== 'editexpertise' && dialogConfig.type !== 'manageachievements' && dialogConfig.type !== 'managespeaking' && dialogConfig.type !== 'managepublications' && dialogConfig.type !== 'manageavailability' && (
                <IconButton
                  aria-label="close"
                  onClick={handleCancel}
                  size="small"
                  sx={{
                    color: '#6B7280',
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
              {/* Close Button for editexpertise, editindustries, manageachievements, managespeaking, managepublications, and manageavailability - shown in header */}
              {(dialogConfig.type === 'editexpertise' || dialogConfig.type === 'editindustries' || dialogConfig.type === 'manageachievements' || dialogConfig.type === 'managespeaking' || dialogConfig.type === 'managepublications' || dialogConfig.type === 'manageavailability') && (
                <IconButton
                  aria-label="close"
                  onClick={handleCancel}
                  size="small"
                  sx={{
                    color: '#6B7280',
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            </DialogTitle>

            <DialogContent
              sx={{
                px: { xs: 2, sm: 3 },
                pb: { xs: 2, sm: 3 },
                pt: { xs: 2, sm: 2 },
                backgroundColor: '#ffffff',
                overflowY: 'auto',
                scrollbarWidth: (dialogConfig?.type === 'manageachievements' || dialogConfig?.type === 'managespeaking' || dialogConfig?.type === 'managepublications' || dialogConfig?.type === 'manageavailability' || dialogConfig?.type === 'fundusage') ? 'thin' : 'none',
                msOverflowStyle: (dialogConfig?.type === 'manageachievements' || dialogConfig?.type === 'managespeaking' || dialogConfig?.type === 'managepublications' || dialogConfig?.type === 'manageavailability' || dialogConfig?.type === 'fundusage') ? 'auto' : 'none',
                '&::-webkit-scrollbar': {
                  display: (dialogConfig?.type === 'manageachievements' || dialogConfig?.type === 'managespeaking' || dialogConfig?.type === 'managepublications' || dialogConfig?.type === 'manageavailability' || dialogConfig?.type === 'fundusage') ? 'block' : 'none',
                  width: '6px',
                  height: '6px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#E5E7EB',
                  borderRadius: '10px',
                },
              }}
            >
              {dialogConfig.message && (
                <Typography
                  variant="body1"
                  sx={{
                    color: '#374151',
                    mb: (dialogConfig.type === 'prompt' || dialogConfig.type === 'rating') ? 2 : 0,
                    lineHeight: 1.6,
                    fontSize: '0.95rem',
                    whiteSpace: 'pre-line',
                  }}
                >
                  {dialogConfig.message}
                </Typography>
              )}
              {dialogConfig.type === 'rating' && (
                <Box sx={{ mt: dialogConfig.message ? 3 : 0, display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#374151',
                      mb: 3,
                      fontWeight: 500,
                    }}
                  >
                    Your Rating <span style={{ color: '#EF4444' }}>*</span>
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Rating
                      value={ratingValue}
                      onChange={(event, newValue) => {
                        const selectedRating = newValue || 0;
                        setRatingValue(selectedRating);
                        // Auto-submit when rating is selected
                        if (selectedRating > 0) {
                          setTimeout(() => {
                            closeDialog({ rating: selectedRating, comment: '' });
                          }, 300); // Small delay for better UX
                        }
                      }}
                      size="large"
                      precision={0.5}
                      icon={<StarIcon sx={{ color: '#FCD34D', fontSize: 40 }} />}
                      emptyIcon={<StarBorderIcon sx={{ color: '#D1D5DB', fontSize: 40 }} />}
                    />
                    {ratingValue > 0 && (
                      <Typography
                        variant="body2"
                        sx={{ color: '#6B7280', fontSize: '0.875rem', ml: 1 }}
                      >
                        {ratingValue % 1 === 0 ? ratingValue : ratingValue.toFixed(1)}{' '}
                        {ratingValue === 1 ? 'star' : 'stars'}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
              {dialogConfig.type === 'prompt' && (
                <TextField
                  margin="normal"
                  fullWidth
                  autoFocus
                  label={dialogConfig.inputLabel}
                  placeholder={dialogConfig.placeholder}
                  value={inputValue}
                  onChange={(event) => setInputValue(event.target.value)}
                  onKeyPress={(event) => {
                    if (event.key === 'Enter') {
                      handleConfirm();
                    }
                  }}
                  sx={{
                    mt: dialogConfig.message ? 2 : 0,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1.5,
                    },
                  }}
                />
              )}
              {dialogConfig.type === 'select' && (
                <Box>
                  <TextField
                    select
                    fullWidth
                    label={dialogConfig.selectLabel}
                    margin="normal"
                    value={inputValue}
                    onChange={(event) => setInputValue(event.target.value)}
                    autoFocus
                    sx={{
                      mt: dialogConfig.message ? 2 : 0,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1.5,
                      },
                    }}
                  >
                    {dialogConfig.options.map((option) => (
                      <MenuItem key={option.value} value={option.value} disabled={option.disabled}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  {(() => {
                    const selectedOption = dialogConfig.options.find(opt => opt.value === inputValue);
                    if (selectedOption && selectedOption.description) {
                      return (
                        <Box
                          className="group"
                          sx={{
                            mt: 2.5,
                            p: { xs: 2, sm: 2.5 },
                            bgcolor: '#FFFFFF',
                            borderRadius: '12px',
                            border: '1px solid #E5E7EB',
                            boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                            display: 'flex',
                            gap: 2,
                            alignItems: 'flex-start',
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                            cursor: 'pointer',
                            '&:hover': {
                              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                              borderColor: '#D1D5DB',
                              transform: 'translateY(-1px)',
                            },
                          }}
                        >
                          <Box
                            sx={{
                              p: 1.25,
                              bgcolor: '#F3F4F6',
                              borderRadius: '10px',
                              color: '#4B5563',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.2s',
                              '.group:hover \u0026': {
                                bgcolor: '#EFF6FF',
                                color: '#3B82F6',
                              }
                            }}
                          >
                            <InfoIcon sx={{ fontSize: 22 }} />
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              variant="caption"
                              sx={{
                                color: '#6B7280',
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '0.1em',
                                display: 'block',
                                mb: 0.75,
                                fontSize: '0.7rem',
                                transition: 'color 0.2s',
                                '.group:hover \u0026': {
                                  color: '#3B82F6',
                                }
                              }}
                            >
                              Status Summary
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: '#374151',
                                lineHeight: 1.55,
                                fontWeight: 500,
                                fontSize: '0.9rem',
                              }}
                            >
                              {selectedOption.description}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    }
                    return null;
                  })()}
                </Box>
              )}
              {dialogConfig.type === 'multiselect' && (
                <Box sx={{ mt: dialogConfig.message ? 2 : 0 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#374151',
                      mb: 1.5,
                      fontWeight: 500,
                    }}
                  >
                    {dialogConfig.selectLabel || 'Select Options'}
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    value=""
                    onChange={(event) => {
                      const selectedValue = event.target.value;
                      if (selectedValue && !selectedValues.includes(selectedValue)) {
                        setSelectedValues([...selectedValues, selectedValue]);
                      }
                    }}
                    SelectProps={{
                      displayEmpty: true,
                    }}
                    sx={{
                      mb: 2,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1.5,
                      },
                    }}
                  >
                    <MenuItem value="" disabled>
                      <em>Select from dropdown...</em>
                    </MenuItem>
                    {dialogConfig.options
                      .filter(option => !selectedValues.includes(option.value))
                      .map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                  </TextField>
                  {selectedValues.length > 0 && (
                    <Box
                      sx={{
                        pt: 2,
                        borderTop: '1px solid #E5E7EB',
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#6B7280',
                          mb: 1.5,
                          fontWeight: 500,
                          fontSize: '0.875rem',
                        }}
                      >
                        Selected ({selectedValues.length}):
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 1,
                        }}
                      >
                        {selectedValues.map((value) => {
                          const option = dialogConfig.options.find(opt => opt.value === value);
                          return (
                            <Chip
                              key={value}
                              label={option?.label || value}
                              onDelete={() => {
                                setSelectedValues(selectedValues.filter(v => v !== value));
                              }}
                              deleteIcon={<CancelIcon sx={{ fontSize: '1rem' }} />}
                              sx={{
                                backgroundColor: '#DBEAFE',
                                color: '#1E40AF',
                                fontWeight: 500,
                                fontSize: '0.875rem',
                                '& .MuiChip-deleteIcon': {
                                  color: '#1E40AF',
                                  '&:hover': {
                                    color: '#1E3A8A',
                                  },
                                },
                              }}
                            />
                          );
                        })}
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
              {dialogConfig.type === 'editexpertise' && (
                <Box sx={{ mt: dialogConfig.message ? 2 : 0 }}>
                  {/* Search Bar */}
                  <TextField
                    fullWidth
                    placeholder="Search expertise areas..."
                    value={expertiseSearch}
                    onChange={(e) => setExpertiseSearch(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: '#9CA3AF', fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      mb: 3,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1.5,
                        backgroundColor: '#F9FAFB',
                        '&:hover': {
                          backgroundColor: '#F3F4F6',
                        },
                        '&.Mui-focused': {
                          backgroundColor: '#FFFFFF',
                        },
                      },
                    }}
                  />

                  {/* Notice */}
                  <Box
                    sx={{
                      mb: 2,
                      p: 1.5,
                      backgroundColor: '#EFF6FF',
                      border: '1px solid #BFDBFE',
                      borderRadius: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <InfoIcon sx={{ color: '#2563EB', fontSize: 20, flexShrink: 0 }} />
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#1E40AF',
                        fontWeight: 500,
                        fontSize: '0.875rem',
                      }}
                    >
                      Minimum 5 expertise areas are required
                    </Typography>
                  </Box>

                  {/* Categories and Tags */}
                  <Box
                    sx={{
                      maxHeight: '400px',
                      overflowY: 'auto',
                      pr: 1,
                      scrollbarWidth: 'none', // Firefox
                      msOverflowStyle: 'none', // IE and Edge
                      '&::-webkit-scrollbar': {
                        display: 'none', // Chrome, Safari, Opera
                      },
                    }}
                  >
                    {expertiseCategories.map((category) => {
                      const filteredTags = category.tags.filter((tag) =>
                        tag.toLowerCase().includes(expertiseSearch.toLowerCase())
                      );
                      if (filteredTags.length === 0) return null;

                      return (
                        <Box key={category.id} sx={{ mb: 3 }}>
                          <Typography
                            variant="overline"
                            sx={{
                              display: 'block',
                              fontSize: '0.7rem',
                              fontWeight: 700,
                              letterSpacing: '0.1em',
                              color: '#6B7280',
                              textTransform: 'uppercase',
                              mb: 2,
                            }}
                          >
                            {category.title}
                          </Typography>
                          <Box
                            sx={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: 1.5,
                            }}
                          >
                            {filteredTags.map((tag) => {
                              const tagId = `${category.id}__${tag}`;
                              const isSelected = selectedExpertiseAreas.includes(tagId);
                              return (
                                <Chip
                                  key={tagId}
                                  label={tag}
                                  onClick={() => {
                                    if (isSelected) {
                                      const newSelection = selectedExpertiseAreas.filter((id) => id !== tagId);
                                      setSelectedExpertiseAreas(newSelection);
                                    } else {
                                      const newSelection = [...selectedExpertiseAreas, tagId];
                                      setSelectedExpertiseAreas(newSelection);
                                    }
                                  }}
                                  sx={{
                                    backgroundColor: '#FFFFFF',
                                    color: isSelected ? '#2563EB' : '#64748B',
                                    border: '1px solid',
                                    borderColor: isSelected ? '#3B82F6' : '#D1D5DB',
                                    fontWeight: 500,
                                    fontSize: '0.75rem',
                                    borderRadius: '8px',
                                    py: '6px',
                                    px: '10px',
                                    height: 'auto',
                                    '& .MuiChip-label': { padding: 0 },
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                      backgroundColor: '#F9FAFB',
                                      borderColor: isSelected ? '#2563EB' : '#9CA3AF',
                                      transform: 'translateY(-1px)',
                                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
                                    },
                                  }}
                                  icon={null}
                                />
                              );
                            })}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}
              {dialogConfig.type === 'editindustries' && (
                <Box sx={{ mt: dialogConfig.message ? 2 : 0 }}>
                  {/* Error Message - Only show if there's an error AND no selections */}
                  {industryError && selectedIndustries.length === 0 && (
                    <Box
                      sx={{
                        mb: 2,
                        p: 2,
                        backgroundColor: '#FEF2F2',
                        border: '1px solid #FECACA',
                        borderRadius: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <ExclamationTriangleIcon sx={{ color: '#DC2626', fontSize: 20 }} />
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#DC2626',
                          fontWeight: 500,
                          flex: 1,
                        }}
                      >
                        {industryError}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => setIndustryError('')}
                        sx={{
                          color: '#DC2626',
                          '&:hover': {
                            backgroundColor: 'rgba(220, 38, 38, 0.1)',
                          },
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}

                  {/* Section 1: Select Industries */}
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      color: '#111827',
                      mb: 1.5,
                      fontSize: '0.875rem',
                    }}
                  >
                    Select industries you have experience in:
                  </Typography>



                  {/* Industry Dropdown */}
                  <FormControl 
                    fullWidth 
                    sx={{ 
                      mb: 1.5,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1.5,
                        backgroundColor: '#F9FAFB',
                        '&:hover': {
                          backgroundColor: '#F3F4F6',
                        },
                        '&.Mui-focused': {
                          backgroundColor: '#FFFFFF',
                        },
                      },
                    }}
                  >
                    <Select
                      displayEmpty
                      value={selectedIndustries[0] || ''}
                      onChange={(e) => {
                        const newId = e.target.value;
                        if (!newId) return;

                        const newSelectedIds = [newId];
                        
                        // Handle details management for single selection
                        const newDetails = {};
                        // Initialize details for the newly selected industry
                        newDetails[newId] = industryDetails[newId] || {
                          years: '',
                          roles: [],
                          domains: []
                        };
                        
                        setIndustryDetails(newDetails);
                        setSelectedIndustries(newSelectedIds);
                        setIndustryError('');
                      }}
                      renderValue={(selectedId) => {
                        if (!selectedId) {
                          return <span style={{ color: '#9CA3AF' }}>Select industry...</span>;
                        }
                        const industry = industryDetailOptions.find(i => i.id === selectedId);
                        return (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            <Chip
                              key={selectedId}
                              label={industry?.label || selectedId}
                              size="small"
                              sx={{
                                backgroundColor: '#ECFDF5',
                                color: '#059669',
                                fontWeight: 500,
                                fontSize: '0.75rem',
                                height: '24px',
                              }}
                            />
                          </Box>
                        );
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            maxHeight: 400,
                            mt: 1,
                            borderRadius: 2,
                            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
                            '& .MuiMenuItem-root': {
                              px: 1.5,
                              py: 0.75,
                              borderRadius: 1,
                              mx: 0.5,
                              my: 0.25,
                              '&:hover': {
                                backgroundColor: '#F3F4F6',
                              },
                              '&.Mui-selected': {
                                backgroundColor: '#ECFDF5',
                                '&:hover': {
                                  backgroundColor: '#D1FAE5',
                                },
                              },
                            },
                          },
                        },
                      }}
                    >
                      {industryDetailOptions.map((industry) => {
                          const isSelected = selectedIndustries.includes(industry.id);
                          return (
                            <MenuItem key={industry.id} value={industry.id}>
                              <Typography
                                sx={{
                                  fontSize: '0.875rem',
                                  fontWeight: isSelected ? 600 : 400,
                                  color: isSelected ? '#059669' : '#111827',
                                }}
                              >
                                {industry.label}
                              </Typography>
                            </MenuItem>
                          );
                        })}
                    </Select>
                  </FormControl>

                  {/* Section 2: Add Details for Selected Industries */}
                  {selectedIndustries.length > 0 && (
                    <>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          color: '#111827',
                          mb: 2,
                          mt: 1,
                          fontSize: '0.875rem',
                        }}
                      >
                        Add details for selected industries:
                      </Typography>

                      <Box
                        sx={{
                          maxHeight: '400px',
                          overflowY: 'auto',
                          pr: 1,
                          scrollbarWidth: 'none', // Firefox
                          msOverflowStyle: 'none', // IE and Edge
                          '&::-webkit-scrollbar': {
                            display: 'none', // Chrome, Safari, Opera
                          },
                        }}
                      >
                        {selectedIndustries.map((industryId) => {
                          const industry = industryDetailOptions.find(i => i.id === industryId);
                          if (!industry) return null;

                          const details = industryDetails[industryId] || { years: '', roles: [], domains: [] };

                          return (
                            <Box
                              key={industryId}
                              sx={{
                                mb: 2.5,
                                p: 2,
                                border: '1px solid #E5E7EB',
                                borderRadius: 2,
                                backgroundColor: '#FFFFFF',
                                transition: 'all 0.2s',
                                '&:hover': {
                                  borderColor: '#3B82F6',
                                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
                                },
                              }}
                            >
                              <Box sx={{ 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                alignItems: 'center',
                                mb: 2,
                                pb: 1.5,
                                borderBottom: '1px solid #F3F4F6'
                              }}>
                                <Typography
                                  variant="subtitle1"
                                  sx={{
                                    fontWeight: 700,
                                    color: '#111827',
                                    fontSize: '0.95rem',
                                  }}
                                >
                                  {industry.label}
                                </Typography>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#6B7280' }}>
                                    Years EXP:
                                  </Typography>
                                  <TextField
                                    size="small"
                                    type="number"
                                    value={details.years || ''}
                                    placeholder="0"
                                    onChange={(e) => {
                                      const inputValue = e.target.value;
                                      let value = '';
                                      let error = '';

                                      if (inputValue === '') {
                                        value = '';
                                      } else {
                                        const numValue = parseInt(inputValue, 10);
                                        if (isNaN(numValue)) {
                                          value = '';
                                          error = 'Invalid';
                                        } else if (numValue < 1) {
                                          value = numValue;
                                          error = 'Min 1';
                                        } else if (numValue > 60) {
                                          value = numValue;
                                          error = 'Max 60';
                                        } else {
                                          value = numValue;
                                        }
                                      }

                                      setIndustryDetails({
                                        ...industryDetails,
                                        [industryId]: { ...details, years: value }
                                      });

                                      if (error) {
                                        setIndustryYearsErrors({ ...industryYearsErrors, [industryId]: error });
                                      } else {
                                        const newErrors = { ...industryYearsErrors };
                                        delete newErrors[industryId];
                                        setIndustryYearsErrors(newErrors);
                                      }
                                    }}
                                    error={!!industryYearsErrors[industryId]}
                                    sx={{
                                      width: '65px',
                                      '& .MuiOutlinedInput-root': {
                                        borderRadius: 1,
                                        fontSize: '0.8125rem',
                                        height: '32px',
                                        backgroundColor: '#F9FAFB',
                                      },
                                    }}
                                  />
                                </Box>
                              </Box>

                              {/* Roles Selection */}
                              <Box sx={{ mb: 2 }}>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontWeight: 700,
                                    color: '#4B5563',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.025em',
                                    display: 'block',
                                    mb: 1,
                                  }}
                                >
                                  Role / Position
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                  {industryRoles.map((role) => {
                                    const isSelected = details.roles?.includes(role) || false;
                                    return (
                                      <Chip
                                        key={role}
                                        label={role}
                                        size="small"
                                        onClick={() => {
                                          const currentRoles = details.roles || [];
                                          const newRoles = isSelected
                                            ? currentRoles.filter(r => r !== role)
                                            : [...currentRoles, role];
                                          setIndustryDetails({
                                            ...industryDetails,
                                            [industryId]: { ...details, roles: newRoles }
                                          });
                                        }}
                                        sx={{
                                          height: '26px',
                                          fontSize: '0.75rem',
                                          backgroundColor: isSelected ? '#EFF6FF' : '#FFFFFF',
                                          color: isSelected ? '#2563EB' : '#4B5563',
                                          border: '1px solid',
                                          borderColor: isSelected ? '#3B82F6' : '#E5E7EB',
                                          fontWeight: isSelected ? 600 : 500,
                                          '&:hover': {
                                            backgroundColor: isSelected ? '#DBEAFE' : '#F9FAFB',
                                          },
                                        }}
                                      />
                                    );
                                  })}
                                </Box>
                              </Box>

                              {/* Domain Expertise Selection */}
                              <Box>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontWeight: 700,
                                    color: '#4B5563',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.025em',
                                    display: 'block',
                                    mb: 1,
                                  }}
                                >
                                  Expertise Areas
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                  {industry.domains.map((domain) => {
                                    const isSelected = details.domains?.includes(domain) || false;
                                    return (
                                      <Chip
                                        key={domain}
                                        label={domain}
                                        size="small"
                                        onClick={() => {
                                          const currentDomains = details.domains || [];
                                          const newDomains = isSelected
                                            ? currentDomains.filter(d => d !== domain)
                                            : [...currentDomains, domain];
                                          setIndustryDetails({
                                            ...industryDetails,
                                            [industryId]: { ...details, domains: newDomains }
                                          });
                                        }}
                                        sx={{
                                          height: '26px',
                                          fontSize: '0.75rem',
                                          backgroundColor: isSelected ? '#F0FDF4' : '#FFFFFF',
                                          color: isSelected ? '#16A34A' : '#4B5563',
                                          border: '1px solid',
                                          borderColor: isSelected ? '#22C55E' : '#E5E7EB',
                                          fontWeight: isSelected ? 600 : 500,
                                          '&:hover': {
                                            backgroundColor: isSelected ? '#DCFCE7' : '#F9FAFB',
                                          },
                                        }}
                                      />
                                    );
                                  })}
                                </Box>
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    </>
                  )}
                </Box>
              )}
              {dialogConfig.type === 'manageachievements' && (
                <Box sx={{ mt: dialogConfig.message ? 1.5 : 0, pt: 2.5 }}>

                  {/* Achievements List */}
                  <Box data-achievements-list>
                    {achievements.length === 0 ? (
                      <Box
                        sx={{
                          textAlign: 'center',
                          py: 6,
                          backgroundColor: '#F9FAFB',
                          borderRadius: 2,
                          border: '2px dashed #D1D5DB',
                        }}
                      >
                        <Typography variant="body2" sx={{ color: '#9CA3AF' }}>
                          No achievements added yet
                        </Typography>
                      </Box>
                    ) : (
                      <Box sx={{ 
                        maxHeight: '480px', 
                        overflowY: 'auto',
                        pt: 2.5,
                        pr: 0.5,
                        '&::-webkit-scrollbar': {
                          width: '4px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                          backgroundColor: '#F1F5F9',
                          borderRadius: '10px',
                        }
                      }}>
                        {achievements.map((achievement, idx) => (
                          <Box
                            key={idx}
                            data-achievement-box
                          >

                            {/* Achievement Title */}
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 700,
                                color: '#64748B',
                                mb: 1.5,
                                pr: 0,
                                fontSize: '0.75rem',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                              }}
                            >
                              {/* {dialogConfig.editingIndex !== null && dialogConfig.editingIndex !== undefined
                                ? 'Edit Achievement'
                                : `Achievement #${idx + 1}`} */}
                            </Typography>

                            {/* Title Input */}
                            <Box sx={{ mb: 1.5 }}>
                              <TextField
                                fullWidth
                                label="Title"
                                placeholder="e.g. Helped startup raise Series A"
                                value={achievement?.title ?? ''}
                                onChange={(e) => {
                                  const newValue = e.target.value || '';
                                  // Use functional update to ensure we have the latest state
                                  setAchievements(prevAchievements => {
                                    return prevAchievements.map((ach, index) =>
                                      index === idx
                                        ? { ...ach, title: newValue }
                                        : ach
                                    );
                                  });

                                  // Clear error immediately when user types - use functional update
                                  const errorKey = `achievements.${idx}.title`;
                                  setAchievementsErrors(prev => {
                                    if (prev[errorKey]) {
                                      const newErrors = { ...prev };
                                      delete newErrors[errorKey];
                                      return newErrors;
                                    }
                                    return prev;
                                  });
                                }}
                                error={!!achievementsErrors[`achievements.${idx}.title`]}
                                helperText={achievementsErrors[`achievements.${idx}.title`]}
                                size="small"
                                required
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 1.5,
                                    backgroundColor: '#FFFFFF',
                                  },
                                }}
                              />
                            </Box>

                            {/* Description Input */}
                            <Box sx={{ mb: 1.5 }}>
                              <TextField
                                fullWidth
                                label="Description"
                                placeholder="Describe the achievement..."
                                value={achievement.description || ''}
                                onChange={(e) => {
                                  const newAchievements = [...achievements];
                                  newAchievements[idx] = { ...newAchievements[idx], description: e.target.value };
                                  setAchievements(newAchievements);
                                }}
                                multiline
                                minRows={1}
                                maxRows={5}
                                size="small"
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 1.5,
                                    backgroundColor: '#FFFFFF',
                                  },
                                  '& .MuiInputBase-input': {
                                    scrollbarWidth: 'none',
                                    msOverflowStyle: 'none',
                                    '&::-webkit-scrollbar': {
                                      display: 'none',
                                    },
                                  },
                                }}
                              />
                            </Box>

                            {/* Date and Category Row */}
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, mb: 1.5 }}>
                              {/* Date Input */}
                              <Box>
                                <TextField
                                  fullWidth
                                  label="Date"
                                  type="date"
                                  value={achievement.date || ''}
                                  onChange={(e) => {
                                    const newAchievements = [...achievements];
                                    newAchievements[idx] = { ...newAchievements[idx], date: e.target.value };
                                    setAchievements(newAchievements);
                                    // Clear error when user starts typing
                                    if (achievementsErrors[`achievements.${idx}.date`]) {
                                      const newErrors = { ...achievementsErrors };
                                      delete newErrors[`achievements.${idx}.date`];
                                      setAchievementsErrors(newErrors);
                                    }
                                  }}
                                  error={!!achievementsErrors[`achievements.${idx}.date`]}
                                  helperText={achievementsErrors[`achievements.${idx}.date`]}
                                  size="small"
                                  InputLabelProps={{
                                    shrink: true,
                                  }}
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: 1.5,
                                      backgroundColor: '#FFFFFF',
                                    },
                                  }}
                                />
                              </Box>

                              {/* Category Input */}
                              <Box>
                                <FormControl fullWidth size="small">
                                  <InputLabel
                                    id={`category-label-${idx}`}
                                    shrink={true}
                                    sx={{
                                      transform: 'translate(14px, -9px) scale(0.75)',
                                      '&.MuiInputLabel-shrink': {
                                        transform: 'translate(14px, -9px) scale(0.75)',
                                      },
                                    }}
                                  >
                                    Category
                                  </InputLabel>
                                  <Select
                                    labelId={`category-label-${idx}`}
                                    label="Category"
                                    value={achievement.category || ''}
                                    onChange={(e) => {
                                      const newAchievements = [...achievements];
                                      newAchievements[idx] = { ...newAchievements[idx], category: e.target.value };
                                      setAchievements(newAchievements);
                                    }}
                                    displayEmpty
                                    notched
                                    sx={{
                                      '& .MuiOutlinedInput-root': {
                                        borderRadius: 1.5,
                                        backgroundColor: '#FFFFFF',
                                      },
                                    }}
                                  >
                                    <MenuItem value="">
                                      Select Category
                                    </MenuItem>
                                    {ACHIEVEMENT_CATEGORIES.map((category) => (
                                      <MenuItem key={category} value={category}>
                                        {category}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Box>
                            </Box>

                            {/* File Upload Section */}
                            <Box sx={{ mt: 2 }}>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: '#6B7280',
                                  mb: 1,
                                  fontSize: '0.75rem',
                                  fontWeight: 500,
                                  display: 'block',
                                }}
                              >
                                Add File / Document
                              </Typography>
                              <Box
                                onDrop={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setAchievementDragOver(prev => ({ ...prev, [idx]: false }));

                                  const files = Array.from(e.dataTransfer.files);
                                  if (files.length > 0) {
                                    const newAchievements = [...achievements];
                                    const currentFiles = newAchievements[idx].files || [];
                                    const updatedFiles = [
                                      ...currentFiles,
                                      ...files.map(file => ({
                                        id: `${Date.now()}-${Math.random()}`,
                                        file: file,
                                        name: file.name,
                                        size: file.size,
                                        type: file.type,
                                        uploadDate: new Date().toISOString()
                                      }))
                                    ];
                                    newAchievements[idx] = { ...newAchievements[idx], files: updatedFiles };
                                    setAchievements(newAchievements);
                                  }
                                }}
                                onDragOver={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setAchievementDragOver(prev => ({ ...prev, [idx]: true }));
                                }}
                                onDragLeave={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  if (!e.currentTarget.contains(e.relatedTarget)) {
                                    setAchievementDragOver(prev => ({ ...prev, [idx]: false }));
                                  }
                                }}
                                onClick={() => {
                                  const input = document.createElement('input');
                                  input.type = 'file';
                                  input.multiple = true;
                                  input.onchange = (e) => {
                                    const files = Array.from(e.target.files);
                                    if (files.length > 0) {
                                      const newAchievements = [...achievements];
                                      const currentFiles = newAchievements[idx].files || [];
                                      const updatedFiles = [
                                        ...currentFiles,
                                        ...files.map(file => ({
                                          id: `${Date.now()}-${Math.random()}`,
                                          file: file,
                                          name: file.name,
                                          size: file.size,
                                          type: file.type,
                                          uploadDate: new Date().toISOString()
                                        }))
                                      ];
                                      newAchievements[idx] = { ...newAchievements[idx], files: updatedFiles };
                                      setAchievements(newAchievements);
                                    }
                                  };
                                  input.click();
                                }}
                                sx={{
                                  border: '2px dashed',
                                  borderColor: achievementDragOver[idx] ? '#2563EB' : '#D1D5DB',
                                  borderRadius: 1.5,
                                  p: 2,
                                  textAlign: 'center',
                                  cursor: 'pointer',
                                  backgroundColor: achievementDragOver[idx] ? '#EFF6FF' : '#F9FAFB',
                                  transition: 'all 0.2s',
                                  '&:hover': {
                                    borderColor: '#2563EB',
                                    backgroundColor: '#EFF6FF',
                                  },
                                }}
                              >
                                    <CloudUploadIcon
                                      sx={{
                                        fontSize: 24,
                                        color: achievementDragOver[idx] ? '#2563EB' : '#9CA3AF',
                                    mb: 1
                                  }}
                                />
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: achievementDragOver[idx] ? '#2563EB' : '#6B7280',
                                    fontSize: '0.875rem',
                                    fontWeight: 500,
                                    mb: 0.5,
                                  }}
                                >
                                  {achievementDragOver[idx] ? 'Drop files here' : 'Click or drag files to upload'}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: '#9CA3AF',
                                    fontSize: '0.75rem',
                                  }}
                                >
                                  PDF, DOC, DOCX, PNG, JPG (Max 10MB per file)
                                </Typography>
                              </Box>

                              {/* Display Uploaded Files */}
                              {achievement.files && achievement.files.length > 0 && (
                                <Box sx={{ mt: 2 }}>
                                  {achievement.files.map((fileItem, fileIdx) => (
                                    <Box
                                      key={fileItem.id || fileIdx}
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        p: 1.5,
                                        mb: 1,
                                        backgroundColor: '#FFFFFF',
                                        border: '1px solid #E5E7EB',
                                        borderRadius: 1,
                                      }}
                                    >
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
                                        <DescriptionIcon sx={{ color: '#6B7280', fontSize: 20 }} />
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                          <Typography
                                            variant="body2"
                                            sx={{
                                              fontSize: '0.875rem',
                                              fontWeight: 500,
                                              color: '#111827',
                                              overflow: 'hidden',
                                              textOverflow: 'ellipsis',
                                              whiteSpace: 'nowrap',
                                            }}
                                          >
                                            {fileItem.name}
                                          </Typography>
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              fontSize: '0.75rem',
                                              color: '#6B7280',
                                            }}
                                          >
                                            {(fileItem.size / 1024 / 1024).toFixed(2)} MB
                                          </Typography>
                                        </Box>
                                      </Box>
                                      <IconButton
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const newAchievements = [...achievements];
                                          const updatedFiles = newAchievements[idx].files.filter((_, fIdx) => fIdx !== fileIdx);
                                          newAchievements[idx] = { ...newAchievements[idx], files: updatedFiles };
                                          setAchievements(newAchievements);
                                        }}
                                        sx={{
                                          color: '#EF4444',
                                          '&:hover': {
                                            backgroundColor: '#FEE2E2',
                                          },
                                        }}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Box>
                                  ))}
                                </Box>
                              )}
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                </Box>
              )}
              {dialogConfig.type === 'managespeaking' && (
                <Box sx={{ mt: dialogConfig.message ? 1.5 : 0, pt: 2.5 }}>


                  {/* Speaking Engagements List */}
                  <Box data-speaking-list>
                    {speakingEngagements.length === 0 ? (
                      <Box
                        sx={{
                          textAlign: 'center',
                          py: 6,
                          backgroundColor: '#F9FAFB',
                          borderRadius: 2,
                          border: '2px dashed #D1D5DB',
                        }}
                      >
                        <Typography variant="body2" sx={{ color: '#9CA3AF' }}>
                          No speaking engagements added yet
                        </Typography>
                      </Box>
                    ) : (
                      <Box sx={{ 
                        maxHeight: '480px', 
                        overflowY: 'auto',
                        pt: 2.5,
                        pr: 0.5,
                        '&::-webkit-scrollbar': {
                          width: '4px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                          backgroundColor: '#F1F5F9',
                          borderRadius: '10px',
                        }
                      }}>
                        {speakingEngagements.map((engagement, idx) => (
                          <Box
                            key={idx}
                            data-speaking-box
                          >

                            {/* Title Input */}
                            <Box sx={{ mb: 1.5 }}>
                              <TextField
                                fullWidth
                                label="Title"
                                placeholder="e.g. Tech Conference 2024 Keynote"
                                value={engagement?.title ?? ''}
                                onChange={(e) => {
                                  const newValue = e.target.value || '';
                                  setSpeakingEngagements(prev => {
                                    return prev.map((eng, index) =>
                                      index === idx ? { ...eng, title: newValue } : eng
                                    );
                                  });
                                  const errorKey = `speakingEngagements.${idx}.title`;
                                  setSpeakingEngagementsErrors(prev => {
                                    if (prev[errorKey]) {
                                      const newErrors = { ...prev };
                                      delete newErrors[errorKey];
                                      return newErrors;
                                    }
                                    return prev;
                                  });
                                }}
                                error={!!speakingEngagementsErrors[`speakingEngagements.${idx}.title`]}
                                helperText={speakingEngagementsErrors[`speakingEngagements.${idx}.title`]}
                                size="small"
                                required
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 1.5,
                                    backgroundColor: '#FFFFFF',
                                  },
                                }}
                              />
                            </Box>

                            {/* Event Input */}
                            <Box sx={{ mb: 1.5 }}>
                              <TextField
                                fullWidth
                                label="Event"
                                placeholder="e.g. Global Tech Summit"
                                value={engagement?.event ?? ''}
                                onChange={(e) => {
                                  const newValue = e.target.value || '';
                                  setSpeakingEngagements(prev => {
                                    return prev.map((eng, index) =>
                                      index === idx ? { ...eng, event: newValue } : eng
                                    );
                                  });
                                }}
                                size="small"
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 1.5,
                                    backgroundColor: '#FFFFFF',
                                  },
                                }}
                              />
                            </Box>

                            {/* Date and Location Row */}
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, mb: 1.5 }}>
                              {/* Date Input */}
                              <Box>
                                <TextField
                                  fullWidth
                                  label="Date"
                                  type="date"
                                  value={engagement?.date || ''}
                                  onChange={(e) => {
                                    const newValue = e.target.value || '';
                                    setSpeakingEngagements(prev => {
                                      return prev.map((eng, index) =>
                                        index === idx ? { ...eng, date: newValue } : eng
                                      );
                                    });
                                    const errorKey = `speakingEngagements.${idx}.date`;
                                    setSpeakingEngagementsErrors(prev => {
                                      if (prev[errorKey]) {
                                        const newErrors = { ...prev };
                                        delete newErrors[errorKey];
                                        return newErrors;
                                      }
                                      return prev;
                                    });
                                  }}
                                  error={!!speakingEngagementsErrors[`speakingEngagements.${idx}.date`]}
                                  helperText={speakingEngagementsErrors[`speakingEngagements.${idx}.date`]}
                                  size="small"
                                  InputLabelProps={{
                                    shrink: true,
                                  }}
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: 1.5,
                                      backgroundColor: '#FFFFFF',
                                    },
                                  }}
                                />
                              </Box>

                              {/* Location Input */}
                              <Box>
                                <TextField
                                  fullWidth
                                  label="Location"
                                  placeholder="e.g. San Francisco, CA"
                                  value={engagement?.location ?? ''}
                                  onChange={(e) => {
                                    const newValue = e.target.value || '';
                                    setSpeakingEngagements(prev => {
                                      return prev.map((eng, index) =>
                                        index === idx ? { ...eng, location: newValue } : eng
                                      );
                                    });
                                  }}
                                  size="small"
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: 1.5,
                                      backgroundColor: '#FFFFFF',
                                    },
                                  }}
                                />
                              </Box>
                            </Box>

                            {/* Description Input */}
                            <Box>
                              <TextField
                                fullWidth
                                label="Description"
                                placeholder="Describe the speaking engagement..."
                                value={engagement?.description ?? ''}
                                onChange={(e) => {
                                  const newValue = e.target.value || '';
                                  setSpeakingEngagements(prev => {
                                    return prev.map((eng, index) =>
                                      index === idx ? { ...eng, description: newValue } : eng
                                    );
                                  });
                                }}
                                multiline
                                minRows={1}
                                maxRows={5}
                                size="small"
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 1.5,
                                    backgroundColor: '#FFFFFF',
                                  },
                                  '& .MuiInputBase-input': {
                                    scrollbarWidth: 'none',
                                    msOverflowStyle: 'none',
                                    '&::-webkit-scrollbar': {
                                      display: 'none',
                                    },
                                  },
                                }}
                              />
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                </Box>
              )}
              {dialogConfig.type === 'managepublications' && (
                <Box sx={{ mt: dialogConfig.message ? 1.5 : 0, pt: 2.5 }}>


                  {/* Publications List */}
                  <Box data-publications-list>
                    {publications.length === 0 ? (
                      <Box
                        sx={{
                          textAlign: 'center',
                          py: 6,
                          backgroundColor: '#F9FAFB',
                          borderRadius: 2,
                          border: '2px dashed #D1D5DB',
                        }}
                      >
                        <Typography variant="body2" sx={{ color: '#9CA3AF' }}>
                          No publications added yet
                        </Typography>
                      </Box>
                    ) : (
                      <Box sx={{ 
                        maxHeight: '480px', 
                        overflowY: 'auto',
                        pt: 2.5,
                        pr: 0.5,
                        '&::-webkit-scrollbar': {
                          width: '4px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                          backgroundColor: '#F1F5F9',
                          borderRadius: '10px',
                        }
                      }}>
                        {publications.map((publication, idx) => (
                          <Box
                            key={idx}
                            data-publication-box
                          >

                            {/* Title Input */}
                            <Box sx={{ mb: 1.5 }}>
                              <TextField
                                fullWidth
                                label="Title"
                                placeholder="e.g. The Future of Technology"
                                value={publication?.title ?? ''}
                                onChange={(e) => {
                                  const newValue = e.target.value || '';
                                  setPublications(prev => {
                                    return prev.map((pub, index) =>
                                      index === idx ? { ...pub, title: newValue } : pub
                                    );
                                  });
                                  const errorKey = `publications.${idx}.title`;
                                  setPublicationsErrors(prev => {
                                    if (prev[errorKey]) {
                                      const newErrors = { ...prev };
                                      delete newErrors[errorKey];
                                      return newErrors;
                                    }
                                    return prev;
                                  });
                                }}
                                error={!!publicationsErrors[`publications.${idx}.title`]}
                                helperText={publicationsErrors[`publications.${idx}.title`]}
                                size="small"
                                required
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 1.5,
                                    backgroundColor: '#FFFFFF',
                                  },
                                }}
                              />
                            </Box>

                            {/* Publisher Input */}
                            <Box sx={{ mb: 1.5 }}>
                              <TextField
                                fullWidth
                                label="Publisher"
                                placeholder="e.g. Tech Journal"
                                value={publication?.publisher ?? ''}
                                onChange={(e) => {
                                  const newValue = e.target.value || '';
                                  setPublications(prev => {
                                    return prev.map((pub, index) =>
                                      index === idx ? { ...pub, publisher: newValue } : pub
                                    );
                                  });
                                }}
                                size="small"
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 1.5,
                                    backgroundColor: '#FFFFFF',
                                  },
                                }}
                              />
                            </Box>

                            {/* Date and URL Row */}
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, mb: 1.5 }}>
                              {/* Date Input */}
                              <Box>
                                <TextField
                                  fullWidth
                                  label="Date"
                                  type="date"
                                  value={publication?.date || ''}
                                  onChange={(e) => {
                                    const newValue = e.target.value || '';
                                    setPublications(prev => {
                                      return prev.map((pub, index) =>
                                        index === idx ? { ...pub, date: newValue } : pub
                                      );
                                    });
                                    const errorKey = `publications.${idx}.date`;
                                    setPublicationsErrors(prev => {
                                      if (prev[errorKey]) {
                                        const newErrors = { ...prev };
                                        delete newErrors[errorKey];
                                        return newErrors;
                                      }
                                      return prev;
                                    });
                                  }}
                                  error={!!publicationsErrors[`publications.${idx}.date`]}
                                  helperText={publicationsErrors[`publications.${idx}.date`]}
                                  size="small"
                                  InputLabelProps={{
                                    shrink: true,
                                  }}
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: 1.5,
                                      backgroundColor: '#FFFFFF',
                                    },
                                  }}
                                />
                              </Box>

                              {/* URL Input */}
                              <Box>
                                <TextField
                                  fullWidth
                                  label="URL"
                                  placeholder="example.com/article"
                                  value={publication?.url ?? ''}
                                  onChange={(e) => {
                                    let newValue = e.target.value || '';
                                    setPublications(prev => {
                                      return prev.map((pub, index) =>
                                        index === idx ? { ...pub, url: newValue } : pub
                                      );
                                    });
                                    const errorKey = `publications.${idx}.url`;
                                    setPublicationsErrors(prev => {
                                      if (prev[errorKey]) {
                                        const newErrors = { ...prev };
                                        delete newErrors[errorKey];
                                        return newErrors;
                                      }
                                      return prev;
                                    });
                                  }}
                                  onBlur={(e) => {
                                    let urlValue = e.target.value?.trim() || '';
                                    if (urlValue && !/^https?:\/\//i.test(urlValue)) {
                                      // Auto-add https:// if not present
                                      urlValue = 'https://' + urlValue;
                                      setPublications(prev => {
                                        return prev.map((pub, index) =>
                                          index === idx ? { ...pub, url: urlValue } : pub
                                        );
                                      });
                                    }
                                  }}
                                  error={!!publicationsErrors[`publications.${idx}.url`]}
                                  helperText={publicationsErrors[`publications.${idx}.url`]}
                                  size="small"
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: 1.5,
                                      backgroundColor: '#FFFFFF',
                                    },
                                  }}
                                />
                              </Box>
                            </Box>

                            {/* Description Input */}
                            <Box>
                              <TextField
                                fullWidth
                                label="Description"
                                placeholder="Describe the publication..."
                                value={publication?.description ?? ''}
                                onChange={(e) => {
                                  const newValue = e.target.value || '';
                                  setPublications(prev => {
                                    return prev.map((pub, index) =>
                                      index === idx ? { ...pub, description: newValue } : pub
                                    );
                                  });
                                }}
                                multiline
                                minRows={1}
                                maxRows={5}
                                size="small"
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 1.5,
                                    backgroundColor: '#FFFFFF',
                                  },
                                  '& .MuiInputBase-input': {
                                    scrollbarWidth: 'none',
                                    msOverflowStyle: 'none',
                                    '&::-webkit-scrollbar': {
                                      display: 'none',
                                    },
                                  },
                                }}
                              />
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                </Box>
              )}
              {dialogConfig.type === 'manageavailability' && (
                <Box sx={{ mt: dialogConfig.message ? 2 : 0, pt: 2.5 }}>
                  {/* Compact Grid Layout */}
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2 }}>
                    {/* Frequency Section */}
                    <Box>
                      <FormControl fullWidth error={!!availabilityErrors.frequency} size="small">
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            color: '#64748B',
                            mb: 0.75,
                            fontSize: '0.75rem',
                            display: 'block',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}
                        >
                          Frequency
                        </Typography>
                        <Select
                          value={availability.frequency || ''}
                          onChange={(e) => {
                            setAvailability(prev => ({ ...prev, frequency: e.target.value }));
                            if (availabilityErrors.frequency) {
                              setAvailabilityErrors(prev => ({ ...prev, frequency: '' }));
                            }
                          }}
                          displayEmpty
                          sx={{
                            bgcolor: '#FFFFFF',
                            borderRadius: 1.5,
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: availabilityErrors.frequency ? '#EF4444' : '#E5E7EB',
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: availabilityErrors.frequency ? '#EF4444' : '#CBD5E1',
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: availabilityErrors.frequency ? '#EF4444' : '#2563EB',
                            },
                          }}
                        >
                          <MenuItem value="">
                            <span style={{ color: '#9CA3AF' }}>Select</span>
                          </MenuItem>
                          <MenuItem value="weekly">Weekly</MenuItem>
                          <MenuItem value="biweekly">Bi-weekly</MenuItem>
                          <MenuItem value="monthly">Monthly</MenuItem>
                          <MenuItem value="ad-hoc">Ad-hoc</MenuItem>
                        </Select>
                        {availabilityErrors.frequency && (
                          <FormHelperText sx={{ mt: 0.5, ml: 0, fontSize: '0.7rem' }}>
                            {availabilityErrors.frequency}
                          </FormHelperText>
                        )}
                      </FormControl>
                    </Box>

                    {/* Commitment Section */}
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 600,
                          color: '#64748B',
                          mb: 0.75,
                          fontSize: '0.75rem',
                          display: 'block',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}
                      >
                        Commitment
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="e.g. 2 hours"
                        value={availability.commitment || ''}
                        onChange={(e) => {
                          setAvailability(prev => ({ ...prev, commitment: e.target.value }));
                          if (availabilityErrors.commitment) {
                            setAvailabilityErrors(prev => ({ ...prev, commitment: '' }));
                          }
                        }}
                        error={!!availabilityErrors.commitment}
                        helperText={availabilityErrors.commitment}
                        size="small"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1.5,
                            backgroundColor: '#FFFFFF',
                            '& fieldset': {
                              borderColor: availabilityErrors.commitment ? '#EF4444' : '#E5E7EB',
                            },
                            '&:hover fieldset': {
                              borderColor: availabilityErrors.commitment ? '#EF4444' : '#CBD5E1',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: availabilityErrors.commitment ? '#EF4444' : '#2563EB',
                            },
                          },
                          '& .MuiFormHelperText-root': {
                            ml: 0,
                            fontSize: '0.7rem'
                          }
                        }}
                      />
                    </Box>
                  </Box>

                  {/* Preferred Time Slots Section */}
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        color: '#64748B',
                        mb: 1,
                        fontSize: '0.75rem',
                        display: 'block',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}
                    >
                      Preferred Time Slots
                    </Typography>
                    {availabilityErrors.timeSlots && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#EF4444',
                          mb: 1,
                          display: 'block',
                          fontSize: '0.7rem',
                        }}
                      >
                        {availabilityErrors.timeSlots}
                      </Typography>
                    )}
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 1 }}>
                      {timeSlotOptions.map((slot) => {
                        const isSelected = (availability.timeSlots || []).includes(slot.id);
                        return (
                          <Chip
                            key={slot.id}
                            label={slot.label}
                            onClick={() => {
                              const currentSlots = new Set(availability.timeSlots || []);
                              if (currentSlots.has(slot.id)) {
                                currentSlots.delete(slot.id);
                              } else {
                                currentSlots.add(slot.id);
                              }
                              setAvailability(prev => ({
                                ...prev,
                                timeSlots: Array.from(currentSlots)
                              }));
                              if (availabilityErrors.timeSlots) {
                                setAvailabilityErrors(prev => ({ ...prev, timeSlots: '' }));
                              }
                            }}
                            sx={{
                              backgroundColor: isSelected ? '#EFF6FF' : '#F8FAFC',
                              color: isSelected ? '#2563EB' : '#64748B',
                              border: '1px solid',
                              borderColor: isSelected ? '#3B82F6' : '#E2E8F0',
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              borderRadius: '10px',
                              py: 1.5,
                              height: 'auto',
                              justifyContent: 'flex-start',
                              '& .MuiChip-label': { px: 1.5, py: 0.25 },
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              '&:hover': {
                                backgroundColor: isSelected ? '#DBEAFE' : '#F1F5F9',
                                borderColor: isSelected ? '#2563EB' : '#CBD5E1',
                                transform: 'translateY(-1px)',
                                boxShadow: '0 2px 4px -1px rgba(0, 0, 0, 0.05)',
                              },
                            }}
                          />
                        );
                      })}
                    </Box>
                  </Box>
                </Box>
              )}
              {dialogConfig.type === 'juryassign' && (
                <Box sx={{ mt: dialogConfig.message ? 2 : 0 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#374151',
                      mb: 2,
                      fontWeight: 500,
                    }}
                  >
                    {/* {dialogConfig.message || 'Select one or more jury members to evaluate this application'} */}
                  </Typography>

                  {/* Dropdown to add jury members */}
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#374151',
                      mb: 1,
                      fontWeight: 500,
                    }}
                  >
                    Jury Members
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    value=""
                    onChange={(event) => {
                      const selectedValue = event.target.value;
                      if (selectedValue && !selectedValues.includes(selectedValue)) {
                        setSelectedValues([...selectedValues, selectedValue]);
                        // Close dropdown after selection
                        setDropdownOpen(false);
                      }
                    }}
                    SelectProps={{
                      displayEmpty: true,
                      open: dropdownOpen,
                      onClose: () => setDropdownOpen(false),
                      onOpen: () => setDropdownOpen(true),
                    }}
                    sx={{
                      mb: 2,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1.5,
                      },
                    }}
                    placeholder="Select jury members"
                  >
                    <MenuItem value="" disabled>
                      <em>Select jury members</em>
                    </MenuItem>
                    {dialogConfig.options
                      .filter(option => !selectedValues.includes(option.value))
                      .map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                  </TextField>

                  {/* Selected Jury Members as Cards */}
                  {selectedValues.length > 0 && (
                    <Box
                      sx={{
                        pt: 2,
                        borderTop: '1px solid #E5E7EB',
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#6B7280',
                          mb: 2,
                          fontWeight: 500,
                          fontSize: '0.875rem',
                        }}
                      >
                        Assigned Jury Members ({selectedValues.length}):
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1.5,
                        }}
                      >
                        {selectedValues.map((value) => {
                          const option = dialogConfig.options.find(opt => opt.value === value);
                          // Check if this member was already assigned (before dialog opened)
                          const wasAlreadyAssigned = dialogConfig.alreadyAssignedIds?.includes(value);

                          return (
                            <Box
                              key={value}
                              sx={{
                                p: 2.5,
                                bgcolor: '#FFFFFF',
                                border: '1px solid',
                                borderColor: '#F1F5F9',
                                borderRadius: '16px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
                                transition: 'all 0.2s',
                                '&:hover': {
                                  borderColor: '#E2E8F0',
                                  boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
                                },
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box
                                  sx={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: '50%',
                                    bgcolor: '#DBEAFE',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                >
                                  <PersonIcon sx={{ color: '#2563EB', fontSize: 20 }} />
                                </Box>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827' }}>
                                  {option?.label || value}
                                </Typography>
                              </Box>
                              {/* Show delete button only for newly selected members (not already assigned) */}
                              {!wasAlreadyAssigned && (
                                <IconButton
                                  onClick={() => {
                                    setSelectedValues(selectedValues.filter(v => v !== value));
                                  }}
                                  size="small"
                                  sx={{
                                    color: '#EF4444',
                                    '&:hover': {
                                      backgroundColor: '#FEE2E2',
                                    },
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  )}

                  {/* Score deadline date - always shown */}
                  <Box sx={{ mt: 3 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#374151',
                        mb: 1,
                        fontWeight: 500,
                      }}
                    >
                      Evaluation Deadline <span style={{ color: '#EF4444' }}>*</span>
                    </Typography>
                    <TextField
                      type="date"
                      size="small"
                      value={deadlineDate || ''}
                      onChange={(e) => {
                        setDeadlineDate(e.target.value);
                        // Clear error when user starts typing
                        if (deadlineDateError) {
                          setDeadlineDateError('');
                        }
                      }}
                      fullWidth
                      required
                      error={!!deadlineDateError}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1.5,
                          backgroundColor: '#FFFFFF',
                        },
                      }}
                      // helperText={deadlineDateError || "Final date for jury members to submit evaluation scores"}
                      inputProps={{
                        min: new Date().toISOString().split('T')[0] // Prevent selecting past dates
                      }}
                    />
                  </Box>
                </Box>
              )}
              {dialogConfig.type === 'scores' && (
                <Box sx={{ mt: dialogConfig.message ? 2 : 0, maxHeight: '65vh', overflowY: 'auto' }}>
                  {dialogConfig.loadingScores ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8 }}>
                      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
                      <Typography variant="body2" sx={{ color: '#6B7280', mt: 2 }}>
                        Loading scores...
                      </Typography>
                    </Box>
                  ) : dialogConfig.scoresData && dialogConfig.scoresData.allScores && dialogConfig.scoresData.allScores.length > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      {/* Average Score - Compact Design */}
                      {dialogConfig.scoresData.averageScore !== null && (
                        <Box
                          sx={{
                            p: 3,
                            background: 'linear-gradient(135deg, #F0F9FF 0%, #E0F2FE 100%)',
                            border: '1px solid',
                            borderColor: '#BAE6FD',
                            borderRadius: '16px',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E40AF', mb: 0.25 }}>
                                Average Score
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#64748B', display: 'block', fontSize: '0.7rem' }}>
                                Based on {dialogConfig.scoresData.allScores.length} {dialogConfig.scoresData.allScores.length === 1 ? 'evaluation' : 'evaluations'}
                              </Typography>
                            </Box>
                            <Box sx={{ textAlign: 'right' }}>
                              <Typography variant="h5" sx={{ fontWeight: 700, color: '#2563EB', lineHeight: 1.2 }}>
                                {dialogConfig.scoresData.averageScore.toFixed(1)}/100
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      )}

                      {/* Individual Scores - Enhanced Header */}
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
                          <Box
                            sx={{
                              width: 4,
                              height: 20,
                              bgcolor: '#2563EB',
                              borderRadius: '2px',
                            }}
                          />
                          <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827' }}>
                            Individual Scores
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#6B7280', ml: 'auto' }}>
                            {dialogConfig.scoresData.allScores.length} {dialogConfig.scoresData.allScores.length === 1 ? 'jury member' : 'jury members'}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                          {dialogConfig.scoresData.allScores.map((scoreItem, index) => {
                            // Rank badge colors matching the image style
                            const getRankBadgeStyle = (rank) => {
                              if (rank === 1) {
                                return { bgcolor: '#FCD34D', color: '#FFFFFF', fontWeight: 600 }; // Yellow for #1
                              } else if (rank === 2) {
                                return { bgcolor: '#F3F4F6', color: '#374151', fontWeight: 600 }; // Grey for #2
                              } else if (rank === 3) {
                                return { bgcolor: '#FB923C', color: '#FFFFFF', fontWeight: 600 }; // Orange for #3
                              } else {
                                return { bgcolor: '#3B82F6', color: '#FFFFFF', fontWeight: 600 }; // Blue for #4+
                              }
                            };

                            const rank = index + 1;
                            const badgeStyle = getRankBadgeStyle(rank);

                            // Calculate progress percentage (score out of 100)
                            const scoreValue = parseFloat(scoreItem.score) || 0;
                            const progressPercentage = scoreValue;

                            // Find highest score for percentage calculation
                            const allScores = dialogConfig.scoresData.allScores.map(s => parseFloat(s.score) || 0);
                            const highestScore = Math.max(...allScores);
                            const percentageOfHighest = highestScore > 0 ? ((scoreValue / highestScore) * 100).toFixed(1) : '0.0';

                            return (
                              <Box
                                key={scoreItem.id || index}
                                sx={{
                                  p: 3.5,
                                  bgcolor: '#FFFFFF',
                                  border: '1px solid',
                                  borderColor: '#F1F5F9',
                                  borderRadius: '24px',
                                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.03), 0 2px 4px -1px rgba(0, 0, 0, 0.02)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 3,
                                  transition: 'all 0.3s ease',
                                  '&:hover': {
                                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.05), 0 4px 6px -2px rgba(0, 0, 0, 0.03)',
                                    borderColor: '#E2E8F0',
                                    transform: 'translateY(-2px)',
                                  },
                                }}
                              >
                                {/* Rank Badge - Enhanced */}
                                <Box
                                  sx={{
                                    minWidth: 48,
                                    height: 48,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '12px',
                                    fontSize: '16px',
                                    fontWeight: 700,
                                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                                    ...badgeStyle,
                                  }}
                                >
                                  #{rank}
                                </Box>

                                {/* Jury Member Info and Progress Bar - Center Section */}
                                <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 1 }}>
                                  <Typography variant="body1" sx={{ fontWeight: 600, color: '#111827', fontSize: '0.9375rem' }}>
                                    {scoreItem.jury_member_name || scoreItem.jury_member_email || 'Jury Member'}
                                  </Typography>

                                  {/* Progress Bar - Enhanced */}
                                  <Box>
                                    <Box
                                      sx={{
                                        width: '100%',
                                        height: 10,
                                        bgcolor: '#F3F4F6',
                                        borderRadius: '5px',
                                        overflow: 'hidden',
                                        position: 'relative',
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          width: `${progressPercentage}%`,
                                          height: '100%',
                                          background: 'linear-gradient(90deg, #10B981 0%, #059669 100%)',
                                          transition: 'width 0.5s ease',
                                          borderRadius: '5px',
                                        }}
                                      />
                                    </Box>
                                  </Box>
                                </Box>

                                {/* Score and Percentage - Right Section - Enhanced */}
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'center', minWidth: '140px' }}>
                                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#10B981', mb: 0.5, fontSize: '1.125rem' }}>
                                    {scoreValue}/100
                                  </Typography>
                                  {highestScore > 0 && scoreValue < highestScore && (
                                    <Typography variant="caption" sx={{ color: '#6B7280', fontSize: '0.75rem' }}>
                                      {percentageOfHighest}% of top score
                                    </Typography>
                                  )}
                                  {scoreValue === highestScore && highestScore > 0 && (
                                    <Typography variant="caption" sx={{ color: '#6B7280', fontSize: '0.75rem' }}>
                                      100.0% of top score
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    </Box>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 10 }}>
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          borderRadius: '50%',
                          bgcolor: '#F3F4F6',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mx: 'auto',
                          mb: 3,
                        }}
                      >
                        <InfoIcon sx={{ fontSize: 40, color: '#9CA3AF' }} />
                      </Box>
                      <Typography variant="h6" sx={{ color: '#374151', fontWeight: 600, mb: 1 }}>
                        No Scores Yet
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#6B7280', maxWidth: 300, mx: 'auto' }}>
                        Jury members haven't submitted their scores for this application yet.
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
              {dialogConfig.infoChips.length > 0 && (
                <Box
                  sx={{
                    mt:
                      dialogConfig.type === 'prompt' || dialogConfig.type === 'rating' || dialogConfig.message
                        ? 3
                        : 0,
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1.5,
                  }}
                >
                  {dialogConfig.infoChips.map((chip, index) => (
                    <Box
                      key={`${chip.label}-${chip.value}-${index}`}
                      sx={{
                        px: 2,
                        py: 1,
                        borderRadius: 999,
                        backgroundColor: '#F5F9FF',
                        border: '1px solid #DBEAFE',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        fontSize: '0.85rem',
                        color: '#1F2937',
                      }}
                    >
                      <Typography
                        component="span"
                        sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#1D4ED8' }}
                      >
                        {chip.label}:
                      </Typography>
                      <Typography component="span" sx={{ fontWeight: 500 }}>
                        {chip.value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
              {dialogConfig.type === 'addjury' && (
                <Box sx={{ mt: dialogConfig.message ? 2 : 0 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#374151',
                      mb: 3,
                      fontWeight: 500,
                    }}
                  >
                    {dialogConfig.message || 'Add a new jury member to the system'}
                  </Typography>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    <TextField
                      label="First Name"
                      required
                      fullWidth
                      value={juryForm.firstName}
                      onChange={(e) => {
                        setJuryForm({ ...juryForm, firstName: e.target.value });
                        if (juryFormErrors.firstName) {
                          setJuryFormErrors({ ...juryFormErrors, firstName: '' });
                        }
                      }}
                      error={!!juryFormErrors.firstName}
                      helperText={juryFormErrors.firstName}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1.5,
                        },
                      }}
                    />

                    <TextField
                      label="Last Name"
                      required
                      fullWidth
                      value={juryForm.lastName}
                      onChange={(e) => {
                        setJuryForm({ ...juryForm, lastName: e.target.value });
                        if (juryFormErrors.lastName) {
                          setJuryFormErrors({ ...juryFormErrors, lastName: '' });
                        }
                      }}
                      error={!!juryFormErrors.lastName}
                      helperText={juryFormErrors.lastName}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1.5,
                        },
                      }}
                    />

                    <TextField
                      label="Email Address"
                      type="email"
                      required
                      fullWidth
                      value={juryForm.email}
                      onChange={(e) => {
                        setJuryForm({ ...juryForm, email: e.target.value });
                        if (juryFormErrors.email) {
                          setJuryFormErrors({ ...juryFormErrors, email: '' });
                        }
                      }}
                      error={!!juryFormErrors.email}
                      helperText={juryFormErrors.email}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1.5,
                        },
                      }}
                    />

                    <Box
                      sx={{
                        p: 2,
                        bgcolor: '#F0F9FF',
                        border: '1px solid #BAE6FD',
                        borderRadius: 1.5,
                        mt: 1,
                      }}
                    >
                      <Typography variant="body2" sx={{ color: '#0369A1', fontSize: '0.875rem' }}>
                        <strong>Note:</strong> A secure password will be auto-generated and sent to the user's email address. The user will have the role "Jury Member" and status "Active".
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}
              {dialogConfig.type === 'mentordetails' && (
                <Box sx={{ mt: 1 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {/* Basic Info */}
                    <Box>
                      <Typography variant="subtitle2" sx={{ color: '#6B7280', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon sx={{ fontSize: '1rem' }} /> Basic Information
                      </Typography>
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5, bgcolor: '#F9FAFB', p: 2.5, borderRadius: 2.5, border: '1px solid #E5E7EB' }}>
                        <Box>
                          <Typography variant="caption" sx={{ color: '#6B7280', display: 'block', mb: 0.5, fontWeight: 500 }}>Full Name</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827' }}>{dialogConfig.mentor.name}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" sx={{ color: '#6B7280', display: 'block', mb: 0.5, fontWeight: 500 }}>Email Address</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827' }}>{dialogConfig.mentor.email}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" sx={{ color: '#6B7280', display: 'block', mb: 0.5, fontWeight: 500 }}>Phone Number</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827' }}>{dialogConfig.mentor.phone || '-'}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" sx={{ color: '#6B7280', display: 'block', mb: 0.5, fontWeight: 500 }}>LinkedIn Profile</Typography>
                          {(dialogConfig.mentor.linkedin_url || dialogConfig.mentor.linkedinUrl || dialogConfig.mentor.linkedin_profile) ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <LinkedInIcon sx={{ fontSize: '1.1rem', color: '#0077B5' }} />
                              <a
                                href={dialogConfig.mentor.linkedin_url || dialogConfig.mentor.linkedinUrl || dialogConfig.mentor.linkedin_profile}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  fontSize: '0.875rem',
                                  color: '#2563EB',
                                  textDecoration: 'none',
                                  fontWeight: 500,
                                  maxWidth: '200px',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  display: 'inline-block',
                                  verticalAlign: 'middle'
                                }}
                              >
                                View Profile
                              </a>
                            </Box>
                          ) : (
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827' }}>-</Typography>
                          )}
                        </Box>
                      </Box>

                    </Box>

                    {/* Industry Experience */}
                    <Box>
                      <Typography variant="subtitle2" sx={{ color: '#6B7280', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <WorkIcon sx={{ fontSize: '1rem' }} /> Industry Experience
                      </Typography>
                      <Box sx={{ p: 2.5, borderRadius: 2.5, border: '1px solid #E5E7EB', bgcolor: '#FFFFFF', minHeight: '80px' }}>
                        {(() => {
                          const formatToTitleCase = (text) => {
                            return text
                              .replace(/_/g, ' ')
                              .split(' ')
                              .map(word => {
                                // Handle acronyms (all caps words like AI, SEO, API)
                                if (word.length <= 3 && word === word.toUpperCase()) {
                                  return word;
                                }
                                // Regular word: capitalize first letter, lowercase rest
                                return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
                              })
                              .join(' ');
                          };

                          const industries = dialogConfig.mentor?.industries || [];
                          const industryDetails = dialogConfig.mentor?.industryDetails || dialogConfig.industryDetails || {};
                          const industryList = Array.isArray(industries) ? industries.filter(Boolean) : [];

                          if (industryList.length === 0) {
                            return (
                              <Typography variant="body2" sx={{ color: '#9CA3AF', fontStyle: 'italic' }}>No industry experience added yet</Typography>
                            );
                          }

                          return (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                              {industryList.map((industryId, index) => {
                                // Handle both ID-based and string-based industry references
                                let industryLabel = '';
                                let detail = {};

                                if (typeof industryId === 'string' || typeof industryId === 'number') {
                                  // Try to find in industryDetailOptions
                                  const industry = industryDetailOptions.find(i =>
                                    i.id === industryId ||
                                    i.id === String(industryId) ||
                                    String(i.id) === String(industryId)
                                  );
                                  if (industry) {
                                    industryLabel = industry.label;
                                    // Try multiple key formats to find the detail
                                    detail = industryDetails[industryId] ||
                                      industryDetails[String(industryId)] ||
                                      industryDetails[Number(industryId)] ||
                                      industryDetails[industry.id] ||
                                      industryDetails[String(industry.id)] ||
                                      {};
                                  } else {
                                    // Fallback: format the ID as text
                                    industryLabel = formatToTitleCase(String(industryId));
                                    // Try multiple key formats
                                    detail = industryDetails[industryId] ||
                                      industryDetails[String(industryId)] ||
                                      industryDetails[Number(industryId)] ||
                                      {};
                                  }
                                } else if (typeof industryId === 'object' && industryId !== null) {
                                  industryLabel = industryId.label || industryId.name || formatToTitleCase(String(industryId));
                                  const id = industryId.id || industryId.name || String(industryId);
                                  // Try multiple key formats
                                  detail = industryDetails[id] ||
                                    industryDetails[String(id)] ||
                                    industryDetails[Number(id)] ||
                                    {};
                                } else {
                                  industryLabel = formatToTitleCase(String(industryId));
                                  // Try multiple key formats
                                  detail = industryDetails[industryId] ||
                                    industryDetails[String(industryId)] ||
                                    industryDetails[Number(industryId)] ||
                                    {};
                                }

                                return (
                                  <Box key={index} sx={{ pb: 2, borderBottom: index < industryList.length - 1 ? '1px solid #E5E7EB' : 'none' }}>
                                    {/* Industry Name as Title */}
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827', mb: 1.5 }}>
                                      {industryLabel}
                                    </Typography>

                                    {/* Content Details */}
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pl: 0 }}>
                                      {/* Role */}
                                      {detail.roles && Array.isArray(detail.roles) && detail.roles.length > 0 && (
                                        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                          <Box component="span" sx={{ fontWeight: 500, color: '#374151' }}>Role:</Box>{' '}
                                          <Box component="span" sx={{ color: '#4B5563' }}>{detail.roles.join(', ')}</Box>
                                        </Typography>
                                      )}

                                      {/* Domain Expertise */}
                                      {detail.domains && Array.isArray(detail.domains) && detail.domains.length > 0 && (
                                        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                          <Box component="span" sx={{ fontWeight: 500, color: '#374151' }}>Domain:</Box>{' '}
                                          <Box component="span" sx={{ color: '#4B5563' }}>{detail.domains.join(', ')}</Box>
                                        </Typography>
                                      )}

                                      {/* Year of Experience */}
                                      {detail.years && (
                                        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                          <Box component="span" sx={{ fontWeight: 500, color: '#374151' }}>Year of experience:</Box>{' '}
                                          <Box component="span" sx={{ color: '#4B5563' }}>
                                            {detail.years} {detail.years === 1 ? 'year' : 'years'}
                                          </Box>
                                        </Typography>
                                      )}
                                    </Box>
                                  </Box>
                                );
                              })}
                            </Box>
                          );
                        })()}
                      </Box>
                    </Box>

                    {/* Expertise Taxonomy */}
                    <Box>
                      <Typography variant="subtitle2" sx={{ color: '#6B7280', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LightbulbIcon sx={{ fontSize: '1rem' }} /> Areas of Expertise
                      </Typography>
                      <Box sx={{ p: 2.5, borderRadius: 2.5, border: '1px solid #E5E7EB', bgcolor: '#FFFFFF', minHeight: '80px' }}>
                        {(() => {
                          const formatToTitleCase = (text) => {
                            return text
                              .replace(/_/g, ' ')
                              .split(' ')
                              .map(word => {
                                // Handle acronyms (all caps words like AI, SEO, API)
                                if (word.length <= 3 && word === word.toUpperCase()) {
                                  return word;
                                }
                                // Handle words with slashes (e.g., "AI / Machine Learning")
                                if (word.includes('/')) {
                                  return word.split('/').map(w => {
                                    const trimmed = w.trim();
                                    if (trimmed.length <= 3 && trimmed === trimmed.toUpperCase()) {
                                      return trimmed;
                                    }
                                    return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
                                  }).join(' / ');
                                }
                                // Regular word: capitalize first letter, lowercase rest
                                return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
                              })
                              .join(' ');
                          };

                          const expertiseLabels = dialogConfig.expertiseLabels || [];
                          const formattedLabels = Array.isArray(expertiseLabels)
                            ? expertiseLabels
                              .filter(Boolean)
                              .map(label => {
                                // Format: extract skill name from "category_subcategory_Skill Name" format
                                if (typeof label === 'string' && label.includes('_')) {
                                  const parts = label.split('_');
                                  if (parts.length >= 3) {
                                    // Get the skill name (everything after category_subcategory_)
                                    const skillName = parts.slice(2).join('_');
                                    return formatToTitleCase(skillName);
                                  }
                                }
                                // If no underscore format, just format to title case
                                if (typeof label === 'string') {
                                  return formatToTitleCase(label);
                                }
                                return typeof label === 'string' ? label : (label?.name || label?.label || String(label));
                              })
                            : [];

                          return formattedLabels.length > 0 ? (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                              {formattedLabels.map((label, index) => (
                                <Box
                                  key={index}
                                  sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    px: 1.5,
                                    py: 0.5,
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    borderRadius: '9999px',
                                    bgcolor: '#DBEAFE',
                                    color: '#1E40AF',
                                    whiteSpace: 'nowrap',
                                    width: 'fit-content'
                                  }}
                                >
                                  {label}
                                </Box>
                              ))}
                            </Box>
                          ) : (
                            <Typography variant="body2" sx={{ color: '#9CA3AF', fontStyle: 'italic' }}>No expertise areas added yet</Typography>
                          );
                        })()}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              )}

              {dialogConfig.type === 'mentorverify' && (
                <Box sx={{ mt: 1 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {/* Verification Checklist */}
                    <Box>
                      <Typography variant="subtitle2" sx={{ color: '#6B7280', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FactCheckIcon sx={{ fontSize: '1rem' }} /> Verification Checklist
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={mentorVerifyForm.linkedin_verified}
                              onChange={(e) => setMentorVerifyForm({ ...mentorVerifyForm, linkedin_verified: e.target.checked })}
                              sx={{ color: '#DADCE0', '&.Mui-checked': { color: '#2563EB' } }}
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <LinkedInIcon sx={{ fontSize: '1.25rem', color: mentorVerifyForm.linkedin_verified ? '#0077B5' : '#6B7280' }} />
                              <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#374151' }}>LinkedIn Profile Verified</Typography>
                            </Box>
                          }
                          sx={{
                            m: 0,
                            p: 2,
                            borderRadius: 2.5,
                            border: '1px solid',
                            borderColor: mentorVerifyForm.linkedin_verified ? '#BFDBFE' : '#E5E7EB',
                            bgcolor: mentorVerifyForm.linkedin_verified ? '#EFF6FF' : 'transparent',
                            '&:hover': { bgcolor: mentorVerifyForm.linkedin_verified ? '#EFF6FF' : '#F9FAFB' },
                            transition: 'all 0.2s',
                            width: '100%'
                          }}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={mentorVerifyForm.experience_verified}
                              onChange={(e) => setMentorVerifyForm({ ...mentorVerifyForm, experience_verified: e.target.checked })}
                              sx={{ color: '#DADCE0', '&.Mui-checked': { color: '#2563EB' } }}
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <WorkIcon sx={{ fontSize: '1.25rem', color: mentorVerifyForm.experience_verified ? '#F59E0B' : '#6B7280' }} />
                              <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#374151' }}>Professional Experience Verified</Typography>
                            </Box>
                          }
                          sx={{
                            m: 0,
                            p: 2,
                            borderRadius: 2.5,
                            border: '1px solid',
                            borderColor: mentorVerifyForm.experience_verified ? '#BFDBFE' : '#E5E7EB',
                            bgcolor: mentorVerifyForm.experience_verified ? '#EFF6FF' : 'transparent',
                            '&:hover': { bgcolor: mentorVerifyForm.experience_verified ? '#EFF6FF' : '#F9FAFB' },
                            transition: 'all 0.2s',
                            width: '100%'
                          }}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={mentorVerifyForm.domain_expertise_assessed}
                              onChange={(e) => setMentorVerifyForm({ ...mentorVerifyForm, domain_expertise_assessed: e.target.checked })}
                              sx={{ color: '#DADCE0', '&.Mui-checked': { color: '#2563EB' } }}
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <LightbulbIcon sx={{ fontSize: '1.25rem', color: mentorVerifyForm.domain_expertise_assessed ? '#F59E0B' : '#6B7280' }} />
                              <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#374151' }}>Domain Expertise Assessed</Typography>
                            </Box>
                          }
                          sx={{
                            m: 0,
                            p: 2,
                            borderRadius: 2.5,
                            border: '1px solid',
                            borderColor: mentorVerifyForm.domain_expertise_assessed ? '#BFDBFE' : '#E5E7EB',
                            bgcolor: mentorVerifyForm.domain_expertise_assessed ? '#EFF6FF' : 'transparent',
                            '&:hover': { bgcolor: mentorVerifyForm.domain_expertise_assessed ? '#EFF6FF' : '#F9FAFB' },
                            transition: 'all 0.2s',
                            width: '100%'
                          }}
                        />
                      </Box>
                    </Box>

                    {/* Rejection History - Show previous rejections if any */}
                    {dialogConfig.mentor?.verification_details?.rejection_history &&
                      Array.isArray(dialogConfig.mentor.verification_details.rejection_history) &&
                      dialogConfig.mentor.verification_details.rejection_history.length > 0 && (
                        <Box>
                          <Typography variant="subtitle2" sx={{ color: '#6B7280', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <InfoIcon sx={{ fontSize: '1rem' }} /> Previous Rejection Notes
                          </Typography>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, maxHeight: '200px', overflowY: 'auto', pr: 1 }}>
                            {dialogConfig.mentor.verification_details.rejection_history.map((rejection, index) => (
                              <Box
                                key={index}
                                sx={{
                                  p: 2,
                                  borderRadius: 2,
                                  bgcolor: '#FEF2F2',
                                  border: '1px solid #FEE2E2',
                                }}
                              >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                  <Typography variant="caption" sx={{ color: '#991B1B', fontWeight: 600 }}>
                                    Rejection #{index + 1}
                                  </Typography>
                                  {rejection.rejected_at && (
                                    <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
                                      {(() => {
                                        const date = new Date(rejection.rejected_at);
                                        const day = String(date.getDate()).padStart(2, '0');
                                        const month = String(date.getMonth() + 1).padStart(2, '0');
                                        const year = date.getFullYear();
                                        let hours = date.getHours();
                                        const minutes = String(date.getMinutes()).padStart(2, '0');
                                        const ampm = hours >= 12 ? 'PM' : 'AM';
                                        hours = hours % 12;
                                        hours = hours ? hours : 12; // the hour '0' should be '12'
                                        const hoursStr = String(hours).padStart(2, '0');
                                        return `${day}/${month}/${year}, ${hoursStr}:${minutes} ${ampm}`;
                                      })()}
                                    </Typography>
                                  )}
                                </Box>
                                <Typography variant="body2" sx={{ color: '#374151', whiteSpace: 'pre-wrap' }}>
                                  {rejection.notes || 'No notes provided'}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      )}

                    {/* Verification Notes */}
                    <Box>
                      <Typography variant="subtitle2" sx={{ color: '#6B7280', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <InfoIcon sx={{ fontSize: '1rem' }} />Notes
                      </Typography>
                      <TextField
                        multiline
                        rows={4}
                        fullWidth
                        placeholder="Add notes about the verification process..."
                        value={mentorVerifyForm.notes}
                        onChange={(e) => setMentorVerifyForm({ ...mentorVerifyForm, notes: e.target.value })}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            bgcolor: '#F9FAFB',
                            borderRadius: 2.5,
                            '& fieldset': { borderColor: '#E5E7EB' },
                            '&:hover fieldset': { borderColor: '#D1D5DB' },
                            '&.Mui-focused fieldset': { borderColor: '#2563EB' },
                          }
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
              )}

              {dialogConfig.type === 'mentorrejectionnotes' && (
                <Box sx={{ mt: 1 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {/* Rejection History */}
                    {dialogConfig.rejectionHistory &&
                      Array.isArray(dialogConfig.rejectionHistory) &&
                      dialogConfig.rejectionHistory.length > 0 ? (
                      <Box>
                        <Typography variant="subtitle2" sx={{ color: '#6B7280', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <InfoIcon sx={{ fontSize: '1rem' }} /> Previous Rejection Notes
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, maxHeight: '400px', overflowY: 'auto', pr: 1 }}>
                          {dialogConfig.rejectionHistory.map((rejection, index) => (
                            <Box
                              key={index}
                              sx={{
                                p: 2,
                                borderRadius: 2,
                                bgcolor: '#FEF2F2',
                                border: '1px solid #FEE2E2',
                              }}
                            >
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                <Typography variant="caption" sx={{ color: '#991B1B', fontWeight: 600 }}>
                                  Rejection #{index + 1}
                                </Typography>
                                {rejection.rejected_at && (
                                  <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
                                    {(() => {
                                      const date = new Date(rejection.rejected_at);
                                      const day = String(date.getDate()).padStart(2, '0');
                                      const month = String(date.getMonth() + 1).padStart(2, '0');
                                      const year = date.getFullYear();
                                      let hours = date.getHours();
                                      const minutes = String(date.getMinutes()).padStart(2, '0');
                                      const ampm = hours >= 12 ? 'PM' : 'AM';
                                      hours = hours % 12;
                                      hours = hours ? hours : 12; // the hour '0' should be '12'
                                      const hoursStr = String(hours).padStart(2, '0');
                                      return `${day}/${month}/${year}, ${hoursStr}:${minutes} ${ampm}`;
                                    })()}
                                  </Typography>
                                )}
                              </Box>
                              <Typography variant="body2" sx={{ color: '#374151', whiteSpace: 'pre-wrap' }}>
                                {rejection.notes || 'No notes provided'}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    ) : (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="body2" sx={{ color: '#9CA3AF' }}>
                          No rejection notes available
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              )}

              {dialogConfig.type === 'startupdetails' && (
                <Box sx={{ mt: 1 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {/* Basic Information */}
                    <Box>
                      <Typography variant="subtitle2" sx={{ color: '#6B7280', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon sx={{ fontSize: '1rem' }} /> Basic Information
                      </Typography>
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5, bgcolor: '#F9FAFB', p: 2.5, borderRadius: 2.5, border: '1px solid #E5E7EB' }}>
                        <Box>
                          <Typography variant="caption" sx={{ color: '#6B7280', display: 'block', mb: 0.5, fontWeight: 500 }}>Company Name</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827' }}>
                            {dialogConfig.startup?.company_name || dialogConfig.startup?.startup_name || '-'}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" sx={{ color: '#6B7280', display: 'block', mb: 0.5, fontWeight: 500 }}>Email Address</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827' }}>
                            {dialogConfig.startup?.startup_email || '-'}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" sx={{ color: '#6B7280', display: 'block', mb: 0.5, fontWeight: 500 }}>Industry</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827' }}>
                            {dialogConfig.startup?.industry || dialogConfig.startup?.startup_sector ?
                              (dialogConfig.startup.industry || dialogConfig.startup.startup_sector)
                                .toLowerCase()
                                .split(/[\s_-]+/)
                                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                                .join(' ')
                              : '-'}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" sx={{ color: '#6B7280', display: 'block', mb: 0.5, fontWeight: 500 }}>Startup Stage</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827' }}>
                            {dialogConfig.startup?.stage || dialogConfig.startup?.startup_stage ?
                              (dialogConfig.startup.stage || dialogConfig.startup.startup_stage)
                                .toLowerCase()
                                .split(/[\s_-]+/)
                                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                                .join(' ')
                              : '-'}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" sx={{ color: '#6B7280', display: 'block', mb: 0.5, fontWeight: 500 }}>Team Size</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827' }}>
                            {dialogConfig.startup?.team_size || dialogConfig.startup?.employee_count || 'N/A'}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" sx={{ color: '#6B7280', display: 'block', mb: 0.5, fontWeight: 500 }}>Assigned Date</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827' }}>
                            {dialogConfig.startup?.assigned_date ?
                              (() => {
                                const date = new Date(dialogConfig.startup.assigned_date);
                                const day = String(date.getDate()).padStart(2, '0');
                                const month = String(date.getMonth() + 1).padStart(2, '0');
                                const year = date.getFullYear();
                                return `${day}/${month}/${year}`;
                              })()
                              : '-'}
                          </Typography>
                        </Box>
                        {(dialogConfig.startup?.startup_email || dialogConfig.startup?.email) && (
                          <Box>
                            <Typography variant="caption" sx={{ color: '#6B7280', display: 'block', mb: 0.5, fontWeight: 500 }}>Email</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#2563EB', wordBreak: 'break-all' }}>
                              <a href={`mailto:${dialogConfig.startup.startup_email || dialogConfig.startup.email}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                                {dialogConfig.startup.startup_email || dialogConfig.startup.email}
                              </a>
                            </Typography>
                          </Box>
                        )}
                        {(dialogConfig.startup?.location?.city || dialogConfig.startup?.location?.state || dialogConfig.startup?.location?.country) && (
                          <Box>
                            <Typography variant="caption" sx={{ color: '#6B7280', display: 'block', mb: 0.5, fontWeight: 500 }}>Location</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827' }}>
                              {[dialogConfig.startup.location.city, dialogConfig.startup.location.state, dialogConfig.startup.location.country]
                                .filter(Boolean)
                                .join(', ') || '-'}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>

                    {/* Description */}
                    {dialogConfig.startup?.description && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ color: '#6B7280', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <DescriptionIcon sx={{ fontSize: '1rem' }} /> Description
                        </Typography>
                        <Box sx={{ p: 2.5, borderRadius: 2.5, border: '1px solid #E5E7EB', bgcolor: '#FFFFFF', minHeight: '80px' }}>
                          <Typography variant="body2" sx={{ color: '#374151', whiteSpace: 'pre-wrap' }}>
                            {dialogConfig.startup.description}
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    {/* Contact Information - Only show if website or phone are available */}
                    {(dialogConfig.startup?.website || dialogConfig.startup?.phone) && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ color: '#6B7280', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <EmailIcon sx={{ fontSize: '1rem' }} /> Contact Information
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5, bgcolor: '#F9FAFB', p: 2.5, borderRadius: 2.5, border: '1px solid #E5E7EB' }}>
                          {dialogConfig.startup?.website && (
                            <Box>
                              <Typography variant="caption" sx={{ color: '#6B7280', display: 'block', mb: 0.5, fontWeight: 500 }}>Website</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#2563EB', wordBreak: 'break-all' }}>
                                <a href={dialogConfig.startup.website} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit' }}>
                                  {dialogConfig.startup.website}
                                </a>
                              </Typography>
                            </Box>
                          )}
                          {dialogConfig.startup?.phone && (
                            <Box>
                              <Typography variant="caption" sx={{ color: '#6B7280', display: 'block', mb: 0.5, fontWeight: 500 }}>Phone</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827' }}>
                                {dialogConfig.startup.phone}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    )}

                    {/* Founding Team */}
                    {dialogConfig.startup?.founders && Array.isArray(dialogConfig.startup.founders) && dialogConfig.startup.founders.length > 0 && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ color: '#6B7280', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <GroupIcon sx={{ fontSize: '1rem' }} /> Founding Team
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5 }}>
                          {dialogConfig.startup.founders.map((founder, index) => (
                            <Box key={index} sx={{ bgcolor: '#F9FAFB', p: 2.5, borderRadius: 2.5, border: '1px solid #E5E7EB' }}>
                              <Typography variant="body2" sx={{ fontWeight: 700, color: '#111827', mb: 0.5, fontSize: '1rem' }}>
                                {founder.name}
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#6B7280', display: 'block', mb: 1.5, fontWeight: 500 }}>
                                {founder.role || 'Founder'}
                              </Typography>
                              {founder.email && (
                                <Typography variant="body2" sx={{ color: '#374151', fontSize: '0.875rem', mb: 0.5 }}>
                                  <EmailIcon sx={{ fontSize: '0.875rem', verticalAlign: 'middle', mr: 0.5 }} />
                                  {founder.email}
                                </Typography>
                              )}
                              {founder.linkedin && (
                                <Typography variant="body2" sx={{ color: '#2563EB', fontSize: '0.875rem' }}>
                                  <a href={founder.linkedin} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit' }}>
                                    LinkedIn Profile
                                  </a>
                                </Typography>
                              )}
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}

                    {/* Business Model & Revenue */}
                    {(dialogConfig.startup?.business_model || dialogConfig.startup?.revenue_model) && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ color: '#6B7280', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <BusinessIcon sx={{ fontSize: '1rem' }} /> Business Model & Revenue
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5 }}>
                          {dialogConfig.startup.business_model && (
                            <Box>
                              <Typography variant="caption" sx={{ color: '#6B7280', display: 'block', mb: 1, fontWeight: 600 }}>Business Model</Typography>
                              <Box sx={{ bgcolor: '#F9FAFB', p: 2, borderRadius: 2, border: '1px solid #E5E7EB' }}>
                                <Typography variant="body2" sx={{ color: '#374151', fontSize: '0.875rem' }}>
                                  {typeof dialogConfig.startup.business_model === 'object' ? dialogConfig.startup.business_model.data : dialogConfig.startup.business_model}
                                </Typography>
                              </Box>
                            </Box>
                          )}
                          {dialogConfig.startup.revenue_model && (
                            <Box>
                              <Typography variant="caption" sx={{ color: '#6B7280', display: 'block', mb: 1, fontWeight: 600 }}>Revenue Model</Typography>
                              <Box sx={{ bgcolor: '#F9FAFB', p: 2, borderRadius: 2, border: '1px solid #E5E7EB' }}>
                                <Typography variant="body2" sx={{ color: '#374151', fontSize: '0.875rem' }}>
                                  {typeof dialogConfig.startup.revenue_model === 'object' ? dialogConfig.startup.revenue_model.data : dialogConfig.startup.revenue_model}
                                </Typography>
                              </Box>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    )}

                    {/* Target Market */}
                    {dialogConfig.startup?.target_market && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ color: '#6B7280', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TrendingUpIcon sx={{ fontSize: '1rem' }} /> Target Market
                        </Typography>
                        <Box sx={{ p: 2.5, borderRadius: 2.5, border: '1px solid #E5E7EB', bgcolor: '#F9FAFB' }}>
                          <Typography variant="body2" sx={{ color: '#374151' }}>
                            {typeof dialogConfig.startup.target_market === 'object' ? dialogConfig.startup.target_market.data : dialogConfig.startup.target_market}
                          </Typography>
                        </Box>
                      </Box>
                    )}



                    {/* Current Challenges */}
                    {dialogConfig.startup?.challenges && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ color: '#6B7280', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LightbulbIcon sx={{ fontSize: '1rem' }} /> Current Challenges
                        </Typography>
                        <Box sx={{ p: 2.5, borderRadius: 2.5, border: '1px solid #E5E7EB', bgcolor: '#F9FAFB' }}>
                          {Array.isArray(dialogConfig.startup.challenges) ? (
                            <Box component="ul" sx={{ m: 0, pl: 2.5, listStyle: 'none' }}>
                              {dialogConfig.startup.challenges.map((challenge, idx) => (
                                <Box component="li" key={idx} sx={{ display: 'flex', alignItems: 'flex-start', mb: 1, '&:last-child': { mb: 0 } }}>
                                  <Box component="span" sx={{ color: '#F59E0B', mr: 1, fontSize: '1.25rem', lineHeight: 1.4, flexShrink: 0 }}>•</Box>
                                  <Typography variant="body2" sx={{ color: '#374151', flex: 1 }}>
                                    {typeof challenge === 'string' ? challenge : challenge.description}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          ) : (
                            <Typography variant="body2" sx={{ color: '#374151' }}>
                              {dialogConfig.startup.challenges}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Box>
              )}

              {dialogConfig.type === 'addfunding' && (
                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  {/* Fund offering dropdown — pick which catalog fund this funding draws from */}
                  {!(dialogConfig?.defaultFundingId && dialogConfig?.existingInstallments) && (
                    <TextField
                      select
                      label="Fund"
                      required
                      fullWidth
                      value={fundingForm.fundOfferingId || ''}
                      onChange={(e) => {
                        const next = e.target.value;
                        const offering = fundOfferingsList.find((o) => String(o.id) === String(next));
                        setFundingForm((prev) => ({
                          ...prev,
                          fundOfferingId: next,
                          // Pre-fill amount with the offering's max_amount only when the
                          // user hasn't typed one yet — never overwrite their input.
                          amount: prev.amount || (offering?.max_amount ? String(offering.max_amount) : prev.amount),
                        }));
                        if (fundingFormErrors.fundOfferingId) {
                          setFundingFormErrors({ ...fundingFormErrors, fundOfferingId: '' });
                        }
                      }}
                      error={!!fundingFormErrors.fundOfferingId}
                      helperText={fundingFormErrors.fundOfferingId || (loadingFundOfferings ? 'Loading funds…' : 'Select the fund this funding draws from')}
                      disabled={loadingFundOfferings}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                    >
                      <MenuItem value="">
                        <em>Select Fund</em>
                      </MenuItem>
                      {fundOfferingsList.map((o) => (
                        <MenuItem key={o.id} value={o.id}>
                          {o.name}
                          {o.scheme_code ? ` (${o.scheme_code})` : ''}
                          {o.org_name ? ` — ${o.org_name}` : ''}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}

                  {/* Selected fund balance card. The catalog row stores the
                      sanctioned / disbursed / available totals; show them so
                      the user knows how much is left before entering an amount. */}
                  {fundingForm.fundOfferingId && (() => {
                    const offering = fundOfferingsList.find((o) => String(o.id) === String(fundingForm.fundOfferingId));
                    if (!offering) return null;
                    const currency = offering.currency || 'INR';
                    const fmt = (n) => {
                      const v = Number(n);
                      if (!Number.isFinite(v)) return '—';
                      try {
                        return new Intl.NumberFormat(currency === 'INR' ? 'en-IN' : 'en-US', {
                          style: 'currency',
                          currency,
                          maximumFractionDigits: 0,
                        }).format(v);
                      } catch (_) {
                        return `${currency} ${v.toLocaleString()}`;
                      }
                    };
                    const sanctioned = Number(offering.amount_sanctioned) || 0;
                    const disbursed = Number(offering.amount_disbursed) || 0;
                    const available = Number(offering.amount_available);
                    const availableKnown = Number.isFinite(available);
                    // Only surface the Available Balance card when the fund has a
                    // positive available balance. When it's 0 / untracked we hide
                    // it — the admin adds funding (e.g. as milestones) without the
                    // balance restriction. Once an available balance exists it
                    // shows here again.
                    if (!availableKnown || available <= 0) return null;
                    const enteredAmount = parseFloat(fundingForm.amount);
                    const overspend = availableKnown && Number.isFinite(enteredAmount) && enteredAmount > available;
                    return (
                      <Box
                        sx={{
                          mt: -1.5,
                          p: 1.5,
                          borderRadius: 1.5,
                          border: '1px solid',
                          borderColor: overspend ? '#FCA5A5' : '#DBEAFE',
                          backgroundColor: overspend ? '#FEF2F2' : '#F0F7FF',
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 2,
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Box>
                          <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, letterSpacing: 0.4 }}>
                            AVAILABLE BALANCE
                          </Typography>
                          <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, color: overspend ? '#B91C1C' : '#0F172A', lineHeight: 1.2 }}>
                            {availableKnown ? fmt(available) : 'Not tracked'}
                          </Typography>
                          {overspend && (
                            <Typography variant="caption" sx={{ color: '#B91C1C', display: 'block', mt: 0.25 }}>
                              Requested amount exceeds available balance.
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                          <Box>
                            <Typography variant="caption" sx={{ color: '#64748B' }}>Sanctioned</Typography>
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#0F172A' }}>{fmt(sanctioned)}</Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" sx={{ color: '#64748B' }}>Disbursed</Typography>
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#0F172A' }}>{fmt(disbursed)}</Typography>
                          </Box>
                        </Box>
                      </Box>
                    );
                  })()}

                  {/* Startup Dropdown (only if startups list is provided) */}
                  {startupsList.length > 0 && (
                    <TextField
                      select
                      label="Startup"
                      required
                      fullWidth
                      value={fundingForm.startupId}
                      onChange={(e) => {
                        const selectedStartupId = e.target.value;
                        setFundingForm({
                          ...fundingForm,
                          startupId: selectedStartupId,
                          applicationId: '' // Reset application when startup changes
                        });
                        if (fundingFormErrors.startupId) {
                          setFundingFormErrors({ ...fundingFormErrors, startupId: '' });
                        }
                        // Fetch applications for selected startup
                        fetchApplicationsForStartup(selectedStartupId);
                      }}
                      error={!!fundingFormErrors.startupId}
                      helperText={fundingFormErrors.startupId || ''}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1.5,
                        },
                      }}
                    >
                      <MenuItem value="">
                        <em>Select Startup</em>
                      </MenuItem>
                      {startupsList.map((startup) => (
                        <MenuItem key={startup.id} value={startup.id}>
                          {startup.name}{startup.email ? ` (${startup.email})` : ''}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}

                  {/* Application Dropdown (only if startups list is provided and startup is selected) */}
                  {startupsList.length > 0 && fundingForm.startupId && (
                    <TextField
                      select
                      label="Application"
                      required
                      fullWidth
                      value={fundingForm.applicationId}
                      onChange={(e) => {
                        setFundingForm({ ...fundingForm, applicationId: e.target.value });
                        if (fundingFormErrors.applicationId) {
                          setFundingFormErrors({ ...fundingFormErrors, applicationId: '' });
                        }
                      }}
                      error={!!fundingFormErrors.applicationId}
                      helperText={fundingFormErrors.applicationId || ''}
                      disabled={loadingApplications}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1.5,
                        },
                      }}
                    >
                      <MenuItem value="">
                        <em>Select Application</em>
                      </MenuItem>
                      {loadingApplications ? (
                        <MenuItem disabled>Loading applications...</MenuItem>
                      ) : (
                        applicationsList.map((app) => (
                          <MenuItem key={app.id} value={app.id}>
                            {app.program_name || `Application #${app.id}`}
                          </MenuItem>
                        ))
                      )}
                    </TextField>
                  )}

                  {/* Payment Type Selection - shown only when dialog explicitly asks for it (e.g. from Funding History) and not editing existing installments */}
                  {dialogConfig?.showPaymentMethod && !dialogConfig?.defaultFundingId && !dialogConfig?.existingInstallments && (
                    <Box sx={{ mb: 2 }}>
                      <ToggleButtonGroup
                        value={fundingForm.paymentType}
                        exclusive
                        onChange={(e, newValue) => {
                          if (newValue !== null) {
                            // When switching to installments, ensure at least one amount+date row exists
                            const nextCustomInstallments =
                              newValue === 'installment'
                                ? (fundingForm.customInstallments && fundingForm.customInstallments.length > 0
                                  ? fundingForm.customInstallments
                                  : [{ amount: '', date: '', status: 'unpaid' }])
                                : [];
                            setFundingForm({
                              ...fundingForm,
                              paymentType: newValue,
                              // When installment is selected, always use custom installments
                              installmentType: newValue === 'installment' ? 'custom' : fundingForm.installmentType,
                              numberOfInstallments: '', // not used anymore
                              amount: fundingForm.amount, // keep current amount (may be auto-filled)
                              currencyId: newValue === 'full' ? (fundingForm.currencyId || defaultCurrencyId) : fundingForm.currencyId,
                              customInstallments: nextCustomInstallments,
                              documents: fundingForm.documents
                            });
                            setFundingFormErrors({});
                          }
                        }}
                        sx={{
                          width: '100%',
                          backgroundColor: '#F1F5F9',
                          p: 0.5,
                          borderRadius: '14px',
                          '& .MuiToggleButton-root': {
                            flex: 1,
                            border: 'none',
                            borderRadius: '10px !important',
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '0.875rem',
                            color: '#64748B',
                            py: 1.5,
                            '&.Mui-selected': {
                              backgroundColor: '#FFFFFF',
                              color: '#2563EB',
                              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)',
                              '&:hover': {
                                backgroundColor: '#FFFFFF',
                              },
                            },
                          },
                        }}
                      >
                        <ToggleButton value="full" aria-label="Full Payment">
                          Full Payment
                        </ToggleButton>
                        <ToggleButton value="installment" aria-label="Milestone">
                          Milestone
                        </ToggleButton>
                      </ToggleButtonGroup>
                      {fundingFormErrors.paymentType && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                          {fundingFormErrors.paymentType}
                        </Typography>
                      )}
                    </Box>
                  )}

                  {/* Show fields only after payment type is selected */}
                  {fundingForm.paymentType && (
                    <>
                      {/* For Full Payment: Show Amount and Currency */}
                      {fundingForm.paymentType === 'full' && (
                        <>
                          <Box sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            gap: { xs: 2, sm: 2 },
                            alignItems: 'flex-start',
                            mb: { xs: 2, sm: 2.5 }
                          }}>
                            <TextField
                              label="Amount"
                              type="number"
                              required
                              fullWidth
                              placeholder="Enter amount"
                              value={fundingForm.amount}
                              onChange={(e) => {
                                // When opened from Funding History (showPaymentMethod), full payment amount should not be editable
                                if (dialogConfig?.showPaymentMethod) return;
                                setFundingForm({ ...fundingForm, amount: e.target.value });
                                if (fundingFormErrors.amount) {
                                  setFundingFormErrors({ ...fundingFormErrors, amount: '' });
                                }
                              }}
                              error={!!fundingFormErrors.amount || (() => {
                                const o = fundOfferingsList.find((x) => String(x.id) === String(fundingForm.fundOfferingId));
                                const avail = Number(o?.amount_available);
                                const v = parseFloat(fundingForm.amount);
                                return Number.isFinite(avail) && avail > 0 && Number.isFinite(v) && v > avail;
                              })()}
                              helperText={fundingFormErrors.amount || (() => {
                                const o = fundOfferingsList.find((x) => String(x.id) === String(fundingForm.fundOfferingId));
                                const avail = Number(o?.amount_available);
                                const v = parseFloat(fundingForm.amount);
                                if (Number.isFinite(avail) && avail > 0 && Number.isFinite(v) && v > avail) {
                                  return 'Exceeds available balance for this fund';
                                }
                                return '';
                              })()}
                              inputProps={(() => {
                                const o = fundOfferingsList.find((x) => String(x.id) === String(fundingForm.fundOfferingId));
                                const avail = Number(o?.amount_available);
                                const base = {
                                  min: 0,
                                  step: 0.01,
                                  readOnly: !!dialogConfig?.showPaymentMethod,
                                };
                                return Number.isFinite(avail) && avail > 0 ? { ...base, max: avail } : base;
                              })()}
                              disabled={!!dialogConfig?.showPaymentMethod}
                              sx={{
                                flex: { xs: 'none', sm: 1 },
                                width: { xs: '100%', sm: 'auto' },
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: '12px',
                                },
                                '& .MuiInputLabel-root': {
                                  fontSize: { xs: '0.875rem', sm: '0.875rem' },
                                },
                              }}
                            />
                            <TextField
                              select
                              label="Currency"
                              value={fundingForm.currencyId || defaultCurrencyId}
                              onChange={(e) => {
                                // Currency should also be fixed when opened from Funding History
                                if (dialogConfig?.showPaymentMethod) return;
                                setFundingForm({ ...fundingForm, currencyId: e.target.value });
                              }}
                              disabled={!!dialogConfig?.showPaymentMethod}
                              sx={{
                                width: { xs: '100%', sm: 'auto' },
                                minWidth: { xs: '100%', sm: 150 },
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: '12px',
                                },
                                '& .MuiInputLabel-root': {
                                  fontSize: { xs: '0.875rem', sm: '0.875rem' },
                                },
                              }}
                            >
                              {currenciesList.map((currency) => (
                                <MenuItem key={currency.id} value={currency.id}>
                                  {currency.symbol} {currency.code}
                                </MenuItem>
                              ))}
                            </TextField>
                          </Box>

                          {/* Document Upload Section (for Full Payment) */}
                          <Box sx={{ mb: 2.5 }}>
                            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'text.primary', fontSize: '0.875rem' }}>
                              Documents <span style={{ color: 'red' }}>*</span>
                            </Typography>
                            <input
                              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png"
                              style={{ display: 'none' }}
                              id="funding-documents-upload-full"
                              type="file"
                              multiple
                              onChange={(e) => {
                                const files = Array.from(e.target.files);
                                const allowedExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'jpg', 'jpeg', 'png'];
                                const maxSize = 10 * 1024 * 1024; // 10MB
                                
                                const validFiles = [];
                                const errors = [];

                                files.forEach(file => {
                                  const ext = file.name.split('.').pop().toLowerCase();
                                  if (!allowedExtensions.includes(ext)) {
                                    errors.push(`${file.name} (Invalid format)`);
                                  } else if (file.size > maxSize) {
                                    errors.push(`${file.name} (Exceeds 10MB)`);
                                  } else {
                                    validFiles.push(file);
                                  }
                                });

                                if (errors.length > 0) {
                                  toast.error(errors.join(', '));
                                }

                                if (validFiles.length > 0) {
                                  setFundingForm({ ...fundingForm, documents: [...fundingForm.documents, ...validFiles] });
                                  if (fundingFormErrors.documents) {
                                    setFundingFormErrors({ ...fundingFormErrors, documents: '' });
                                  }
                                }
                                e.target.value = '';
                              }}
                            />
                            <Box
                              onDragOver={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                e.currentTarget.style.backgroundColor = fundingFormErrors.documents ? '#FEE2E2' : '#F1F5F9';
                                e.currentTarget.style.borderColor = '#2563EB';
                              }}
                              onDragLeave={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                e.currentTarget.style.backgroundColor = fundingFormErrors.documents ? '#FEF2F2' : '#F8FAFC';
                                e.currentTarget.style.borderColor = fundingFormErrors.documents ? '#DC2626' : '#E2E8F0';
                              }}
                              onDrop={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                e.currentTarget.style.backgroundColor = '#F8FAFC';
                                e.currentTarget.style.borderColor = fundingFormErrors.documents ? '#DC2626' : '#E2E8F0';
                                
                                const files = Array.from(e.dataTransfer.files);
                                const allowedExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'jpg', 'jpeg', 'png'];
                                const maxSize = 10 * 1024 * 1024;
                                
                                const validFiles = [];
                                const errors = [];

                                files.forEach(file => {
                                  const ext = file.name.split('.').pop().toLowerCase();
                                  if (!allowedExtensions.includes(ext)) {
                                    errors.push(`${file.name} (Invalid format)`);
                                  } else if (file.size > maxSize) {
                                    errors.push(`${file.name} (Exceeds 10MB)`);
                                  } else {
                                    validFiles.push(file);
                                  }
                                });

                                if (errors.length > 0) {
                                  toast.error(errors.join(', '));
                                }

                                if (validFiles.length > 0) {
                                  setFundingForm({ ...fundingForm, documents: [...fundingForm.documents, ...validFiles] });
                                  if (fundingFormErrors.documents) {
                                    setFundingFormErrors({ ...fundingFormErrors, documents: '' });
                                  }
                                }
                              }}
                              onClick={() => {
                                document.getElementById('funding-documents-upload-full').click();
                              }}
                              sx={{
                                border: '2px dashed',
                                borderColor: fundingFormErrors.documents ? '#DC2626' : '#E2E8F0',
                                borderRadius: 2,
                                p: 2,
                                textAlign: 'center',
                                backgroundColor: fundingFormErrors.documents ? '#FEF2F2' : '#F8FAFC',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  backgroundColor: fundingFormErrors.documents ? '#FEE2E2' : '#F1F5F9',
                                  borderColor: '#2563EB',
                                },
                              }}
                            >
                              <CloudUploadIcon sx={{ fontSize: 32, color: '#6B7280', mb: 0.5 }} />
                              <Typography variant="body2" sx={{ color: '#6B7280', fontWeight: 500, mb: 0.5 }}>
                                Drop a file here to upload, or
                              </Typography>
                              <Typography
                                variant="body2"
                                component="span"
                                sx={{
                                  color: '#2563EB',
                                  textDecoration: 'underline',
                                  cursor: 'pointer',
                                  fontWeight: 600
                                }}
                              >
                                click here to browse
                              </Typography>
                              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#94A3B8' }}>
                                Allowed: PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, JPG, PNG &bull; Max 10MB
                              </Typography>
                            </Box>
                            {fundingFormErrors.documents && (
                              <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                {fundingFormErrors.documents}
                              </Typography>
                            )}
                            {fundingForm.documents && fundingForm.documents.length > 0 && (
                              <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {fundingForm.documents.map((file, index) => {
                                  const ext = file.name.split('.').pop().toLowerCase();
                                  const extColorMap = {
                                    pdf: '#EF4444', doc: '#2563EB', docx: '#2563EB',
                                    xls: '#16A34A', xlsx: '#16A34A',
                                    ppt: '#EA580C', pptx: '#EA580C',
                                    jpg: '#7C3AED', jpeg: '#7C3AED', png: '#7C3AED',
                                  };
                                  const dotColor = extColorMap[ext] || '#6B7280';
                                  const sizeKB = (file.size / 1024).toFixed(1);
                                  const sizeDisplay = file.size >= 1024 * 1024
                                    ? `${(file.size / (1024 * 1024)).toFixed(1)} MB`
                                    : `${sizeKB} KB`;
                                  return (
                                    <Box
                                      key={index}
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.5,
                                        p: 1.25,
                                        bgcolor: '#F8FAFC',
                                        borderRadius: 1.5,
                                        border: '1px solid #E2E8F0',
                                        transition: 'background-color 0.15s',
                                        '&:hover': { bgcolor: '#F1F5F9' },
                                      }}
                                    >
                                      <Box sx={{
                                        width: 36, height: 36, borderRadius: 1.5,
                                        bgcolor: dotColor + '18',
                                        border: `1px solid ${dotColor}40`,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        flexShrink: 0,
                                      }}>
                                        {ext === 'pdf' ? <PdfIcon sx={{ color: dotColor, fontSize: 18 }} /> :
                                         (ext === 'doc' || ext === 'docx') ? <WordIcon sx={{ color: dotColor, fontSize: 18 }} /> :
                                         (ext === 'xls' || ext === 'xlsx') ? <ExcelIcon sx={{ color: dotColor, fontSize: 18 }} /> :
                                         (ext === 'ppt' || ext === 'pptx') ? <PptIcon sx={{ color: dotColor, fontSize: 18 }} /> :
                                         (ext === 'jpg' || ext === 'jpeg' || ext === 'png') ? <ImageIcon sx={{ color: dotColor, fontSize: 18 }} /> :
                                         <FileIcon sx={{ color: dotColor, fontSize: 18 }} />}
                                      </Box>
                                      <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="body2" sx={{ color: '#1E293B', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                          {file.name}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: '#94A3B8' }}>
                                          {sizeDisplay}
                                        </Typography>
                                      </Box>
                                      <IconButton
                                        size="small"
                                        onClick={() => {
                                          const updatedDocuments = fundingForm.documents.filter((_, i) => i !== index);
                                          setFundingForm({ ...fundingForm, documents: updatedDocuments });
                                        }}
                                        sx={{ color: '#DC2626', flexShrink: 0, '&:hover': { backgroundColor: '#FEE2E2' } }}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Box>
                                  );
                                })}
                              </Box>
                            )}
                          </Box>
                        </>
                      )}

                      {/* For Custom Instalments: Show Total Amount and Installment Entries */}
                      {fundingForm.paymentType === 'installment' && fundingForm.installmentType === 'custom' && (
                        <>
                          {/* Total Amount and Currency in one row - always read-only (comes from original funding) */}
                          <Box sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            gap: { xs: 2, sm: 2 },
                            alignItems: 'flex-start',
                            mb: { xs: 2, sm: 2 }
                          }}>
                            <TextField
                              label="Total Amount"
                              type="number"
                              required
                              placeholder="Total amount"
                              value={fundingForm.amount}
                              // Total amount is fixed based on original funding and cannot be edited here
                              onChange={() => { }}
                              error={!!fundingFormErrors.amount}
                              helperText={fundingFormErrors.amount || ''}
                              inputProps={{
                                min: 0,
                                step: 0.01,
                                readOnly: true,
                              }}
                              disabled
                              sx={{
                                flex: 1,
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 1.5,
                                },
                              }}
                            />

                            <TextField
                              select
                              label="Currency"
                              value={fundingForm.currencyId || defaultCurrencyId}
                              // Currency is fixed from original funding; not editable here
                              onChange={() => { }}
                              disabled
                              sx={{
                                minWidth: 150,
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 1.5,
                                },
                              }}
                            >
                              {currenciesList.map((currency) => (
                                <MenuItem key={currency.id} value={currency.id}>
                                  {currency.symbol} {currency.code}
                                </MenuItem>
                              ))}
                            </TextField>
                          </Box>

                          {/* Custom Installments List */}
                          <Box sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                Milestones
                              </Typography>
                              <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                {(() => {
                                  const totalAmount = parseFloat(fundingForm.amount) || 0;
                                  const installmentSum = fundingForm.customInstallments.reduce((sum, inst) => {
                                    const amount = parseFloat(inst.amount) || 0;
                                    return sum + amount;
                                  }, 0);
                                  const remaining = totalAmount - installmentSum;
                                  return remaining > 0
                                    ? `Remaining: ${remaining.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                    : remaining < 0
                                      ? `Excess: ${Math.abs(remaining).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                      : `Remaining: ${remaining.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                                })()}
                              </Typography>
                            </Box>
                            {fundingForm.customInstallments.map((installment, index) => {
                              const isPaid = (installment.status || '').toLowerCase() === 'paid';
                              const isDisabled = isPaid; // Disable if paid

                              return (
                                <Box key={index} sx={{ mb: 3, p: { xs: 1.5, sm: 2 }, bgcolor: '#F9FAFB', borderRadius: 1.5, border: '1px solid', borderColor: '#E5E7EB' }}>
                                  <Typography variant="body2" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
                                    Milestone {installment.installment_number || (index + 1)}
                                  </Typography>

                                  {/* Amount and Date Row */}
                                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1.5, sm: 2 }, mb: 2 }}>
                                    <TextField
                                      label={`Milestone ${installment.installment_number || (index + 1)} Amount`}
                                      type="number"
                                      required
                                      placeholder="Enter amount"
                                      value={installment.amount}
                                      onChange={(e) => {
                                        if (isDisabled) return; // Don't allow changes if paid
                                        const updatedInstallments = [...fundingForm.customInstallments];
                                        if (!updatedInstallments[index]) {
                                          updatedInstallments[index] = { amount: '', date: '', status: 'unpaid', notes: '' };
                                        }
                                        updatedInstallments[index] = { ...updatedInstallments[index], amount: e.target.value };
                                        setFundingForm({ ...fundingForm, customInstallments: updatedInstallments });
                                        // Clear errors
                                        if (fundingFormErrors[`installment_${index}_amount`]) {
                                          const newErrors = { ...fundingFormErrors };
                                          delete newErrors[`installment_${index}_amount`];
                                          setFundingFormErrors(newErrors);
                                        }
                                      }}
                                      error={!!fundingFormErrors[`installment_${index}_amount`]}
                                      helperText={fundingFormErrors[`installment_${index}_amount`] || ''}
                                      inputProps={{
                                        min: 0,
                                        step: 0.01
                                      }}
                                      disabled={isDisabled}
                                      sx={{
                                        flex: 1,
                                        '& .MuiOutlinedInput-root': {
                                          borderRadius: 1.5,
                                        },
                                      }}
                                    />
                                    <TextField
                                      label={`Milestone ${installment.installment_number || (index + 1)} Date`}
                                      type="date"
                                      required
                                      value={installment.date}
                                      onChange={(e) => {
                                        if (isDisabled) return; // Don't allow changes if paid
                                        const updatedInstallments = [...fundingForm.customInstallments];
                                        if (!updatedInstallments[index]) {
                                          updatedInstallments[index] = { amount: '', date: '', status: 'unpaid', notes: '' };
                                        }
                                        updatedInstallments[index] = { ...updatedInstallments[index], date: e.target.value };
                                        setFundingForm({ ...fundingForm, customInstallments: updatedInstallments });
                                        // Clear errors
                                        if (fundingFormErrors[`installment_${index}_date`]) {
                                          const newErrors = { ...fundingFormErrors };
                                          delete newErrors[`installment_${index}_date`];
                                          setFundingFormErrors(newErrors);
                                        }
                                      }}
                                      error={!!fundingFormErrors[`installment_${index}_date`]}
                                      helperText={fundingFormErrors[`installment_${index}_date`] || ''}
                                      InputLabelProps={{
                                        shrink: true,
                                      }}
                                      disabled={isDisabled}
                                      sx={{
                                        flex: 1,
                                        '& .MuiOutlinedInput-root': {
                                          borderRadius: 1.5,
                                        },
                                      }}
                                    />
                                    {/* Add Milestone Button - Show on first row */}
                                    {index === 0 && (
                                      <IconButton
                                        onClick={() => {
                                          setFundingForm({
                                            ...fundingForm,
                                            customInstallments: [...fundingForm.customInstallments, { amount: '', date: '', status: 'unpaid', notes: '' }]
                                          });
                                        }}
                                        sx={{
                                          alignSelf: { xs: 'flex-end', sm: 'center' },
                                          border: '1px solid',
                                          borderColor: 'divider',
                                          '&:hover': {
                                            backgroundColor: 'action.hover',
                                          },
                                        }}
                                        aria-label="Add Milestone"
                                      >
                                        <AddIcon />
                                      </IconButton>
                                    )}
                                    {/* Delete Button - allow deleting any unpaid row except the first, when more than one milestone exists */}
                                    {!isPaid && index > 0 && fundingForm.customInstallments.length > 1 && (
                                      <IconButton
                                        onClick={() => {
                                          const updatedInstallments = fundingForm.customInstallments.filter((_, i) => i !== index);
                                          setFundingForm({ ...fundingForm, customInstallments: updatedInstallments });
                                        }}
                                        sx={{
                                          alignSelf: { xs: 'flex-end', sm: 'center' },
                                          border: '1px solid',
                                          borderColor: 'divider',
                                          '&:hover': {
                                            backgroundColor: 'action.hover',
                                          },
                                        }}
                                        aria-label="Delete Milestone"
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                    )}
                                  </Box>

                                  {/* Notes Field for this Milestone */}
                                  <TextField
                                    label={`Notes for Milestone ${installment.installment_number || (index + 1)}`}
                                    fullWidth
                                    multiline
                                    rows={3}
                                    value={installment.notes || ''}
                                    onChange={(e) => {
                                      if (isDisabled) return; // Don't allow changes if paid
                                      const value = e.target.value;
                                      const updatedInstallments = [...fundingForm.customInstallments];
                                      if (!updatedInstallments[index]) {
                                        updatedInstallments[index] = { amount: '', date: '', status: 'unpaid', notes: '' };
                                      }
                                      updatedInstallments[index] = {
                                        ...updatedInstallments[index],
                                        notes: value
                                      };
                                      setFundingForm({ ...fundingForm, customInstallments: updatedInstallments });
                                    }}
                                    disabled={isDisabled}
                                    placeholder={`Add notes for Milestone ${installment.installment_number || (index + 1)}...`}
                                    sx={{
                                      '& .MuiOutlinedInput-root': {
                                        borderRadius: 1.5,
                                      },
                                    }}
                                  />
                                </Box>
                              );
                            })}
                          </Box>

                          {/* Document Upload Section - Hide when editing installments */}
                          {!(dialogConfig?.defaultFundingId && dialogConfig?.existingInstallments) && (
                           <Box sx={{ mb: 2.5 }}>
                            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'text.primary', fontSize: '0.875rem' }}>
                              Documents <span style={{ color: 'red' }}>*</span>
                            </Typography>
                            <input
                              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png"
                              style={{ display: 'none' }}
                              id="funding-documents-upload-custom"
                              type="file"
                              multiple
                              onChange={(e) => {
                                const files = Array.from(e.target.files);
                                const allowedExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'jpg', 'jpeg', 'png'];
                                const maxSize = 10 * 1024 * 1024;
                                
                                const validFiles = [];
                                const errors = [];

                                files.forEach(file => {
                                  const ext = file.name.split('.').pop().toLowerCase();
                                  if (!allowedExtensions.includes(ext)) {
                                    errors.push(`${file.name} (Invalid format)`);
                                  } else if (file.size > maxSize) {
                                    errors.push(`${file.name} (Exceeds 10MB)`);
                                  } else {
                                    validFiles.push(file);
                                  }
                                });

                                if (errors.length > 0) {
                                  toast.error(errors.join(', '));
                                }

                                if (validFiles.length > 0) {
                                  setFundingForm({ ...fundingForm, documents: [...fundingForm.documents, ...validFiles] });
                                  if (fundingFormErrors.documents) {
                                    setFundingFormErrors({ ...fundingFormErrors, documents: '' });
                                  }
                                }
                                e.target.value = '';
                              }}
                            />
                            <Box
                              onDragOver={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                e.currentTarget.style.backgroundColor = fundingFormErrors.documents ? '#FEE2E2' : '#F1F5F9';
                                e.currentTarget.style.borderColor = '#2563EB';
                              }}
                              onDragLeave={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                e.currentTarget.style.backgroundColor = fundingFormErrors.documents ? '#FEF2F2' : '#F8FAFC';
                                e.currentTarget.style.borderColor = fundingFormErrors.documents ? '#DC2626' : '#E2E8F0';
                              }}
                              onDrop={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                e.currentTarget.style.backgroundColor = '#F8FAFC';
                                e.currentTarget.style.borderColor = fundingFormErrors.documents ? '#DC2626' : '#E2E8F0';
                                
                                const files = Array.from(e.dataTransfer.files);
                                const allowedExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'jpg', 'jpeg', 'png'];
                                const maxSize = 10 * 1024 * 1024;
                                
                                const validFiles = [];
                                const errors = [];

                                files.forEach(file => {
                                  const ext = file.name.split('.').pop().toLowerCase();
                                  if (!allowedExtensions.includes(ext)) {
                                    errors.push(`${file.name} (Invalid format)`);
                                  } else if (file.size > maxSize) {
                                    errors.push(`${file.name} (Exceeds 10MB)`);
                                  } else {
                                    validFiles.push(file);
                                  }
                                });

                                if (errors.length > 0) {
                                  toast.error(errors.join(', '));
                                }

                                if (validFiles.length > 0) {
                                  setFundingForm({ ...fundingForm, documents: [...fundingForm.documents, ...validFiles] });
                                  if (fundingFormErrors.documents) {
                                    setFundingFormErrors({ ...fundingFormErrors, documents: '' });
                                  }
                                }
                              }}
                              onClick={() => {
                                document.getElementById('funding-documents-upload-custom').click();
                              }}
                              sx={{
                                border: '2px dashed',
                                borderColor: fundingFormErrors.documents ? '#DC2626' : '#E2E8F0',
                                borderRadius: 2,
                                p: 2,
                                textAlign: 'center',
                                backgroundColor: fundingFormErrors.documents ? '#FEF2F2' : '#F8FAFC',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  backgroundColor: fundingFormErrors.documents ? '#FEE2E2' : '#F1F5F9',
                                  borderColor: '#2563EB',
                                },
                              }}
                            >
                              <CloudUploadIcon sx={{ fontSize: 32, color: '#6B7280', mb: 0.5 }} />
                              <Typography variant="body2" sx={{ color: '#6B7280', fontWeight: 500, mb: 0.5 }}>
                                Drop a file here to upload, or
                              </Typography>
                              <Typography
                                variant="body2"
                                component="span"
                                sx={{
                                  color: '#2563EB',
                                  textDecoration: 'underline',
                                  cursor: 'pointer',
                                  fontWeight: 600
                                }}
                              >
                                click here to browse
                              </Typography>
                              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#94A3B8' }}>
                                Allowed: PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, JPG, PNG &bull; Max 10MB
                              </Typography>
                            </Box>
                            {fundingFormErrors.documents && (
                              <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                {fundingFormErrors.documents}
                              </Typography>
                            )}
                            {fundingForm.documents && fundingForm.documents.length > 0 && (
                              <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {fundingForm.documents.map((file, index) => {
                                  const ext = file.name.split('.').pop().toLowerCase();
                                  const extColorMap = {
                                    pdf: '#EF4444', doc: '#2563EB', docx: '#2563EB',
                                    xls: '#16A34A', xlsx: '#16A34A',
                                    ppt: '#EA580C', pptx: '#EA580C',
                                    jpg: '#7C3AED', jpeg: '#7C3AED', png: '#7C3AED',
                                  };
                                  const dotColor = extColorMap[ext] || '#6B7280';
                                  const sizeKB = (file.size / 1024).toFixed(1);
                                  const sizeDisplay = file.size >= 1024 * 1024
                                    ? `${(file.size / (1024 * 1024)).toFixed(1)} MB`
                                    : `${sizeKB} KB`;
                                  return (
                                    <Box
                                      key={index}
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.5,
                                        p: 1.25,
                                        bgcolor: '#F8FAFC',
                                        borderRadius: 1.5,
                                        border: '1px solid #E2E8F0',
                                        transition: 'background-color 0.15s',
                                        '&:hover': { bgcolor: '#F1F5F9' },
                                      }}
                                    >
                                      <Box sx={{
                                        width: 36, height: 36, borderRadius: 1.5,
                                        bgcolor: dotColor + '18',
                                        border: `1px solid ${dotColor}40`,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        flexShrink: 0,
                                      }}>
                                        {ext === 'pdf' ? <PdfIcon sx={{ color: dotColor, fontSize: 18 }} /> :
                                         (ext === 'doc' || ext === 'docx') ? <WordIcon sx={{ color: dotColor, fontSize: 18 }} /> :
                                         (ext === 'xls' || ext === 'xlsx') ? <ExcelIcon sx={{ color: dotColor, fontSize: 18 }} /> :
                                         (ext === 'ppt' || ext === 'pptx') ? <PptIcon sx={{ color: dotColor, fontSize: 18 }} /> :
                                         (ext === 'jpg' || ext === 'jpeg' || ext === 'png') ? <ImageIcon sx={{ color: dotColor, fontSize: 18 }} /> :
                                         <FileIcon sx={{ color: dotColor, fontSize: 18 }} />}
                                      </Box>
                                      <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="body2" sx={{ color: '#1E293B', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                          {file.name}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: '#94A3B8' }}>
                                          {sizeDisplay}
                                        </Typography>
                                      </Box>
                                      <IconButton
                                        size="small"
                                        onClick={() => {
                                          const updatedDocuments = fundingForm.documents.filter((_, i) => i !== index);
                                          setFundingForm({ ...fundingForm, documents: updatedDocuments });
                                        }}
                                        sx={{ color: '#DC2626', flexShrink: 0, '&:hover': { backgroundColor: '#FEE2E2' } }}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Box>
                                  );
                                })}
                              </Box>
                            )}
                          </Box>
                          )}
                        </>
                      )}

                      {/* Payment Frequency (only for Fix Instalments) */}
                      {fundingForm.paymentType === 'installment' && fundingForm.installmentType === 'fix' && (
                        <>
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 500, color: 'text.primary', fontSize: '0.875rem' }}>
                              Payment Frequency <span style={{ color: 'red' }}>*</span>
                            </Typography>
                            <ToggleButtonGroup
                              value={fundingForm.paymentFrequency}
                              exclusive
                              onChange={(e, newValue) => {
                                if (newValue !== null) {
                                  setFundingForm({
                                    ...fundingForm,
                                    paymentFrequency: newValue
                                  });
                                }
                              }}
                              fullWidth
                              sx={{
                                '& .MuiToggleButton-root': {
                                  py: 1.5,
                                  px: 2,
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  textTransform: 'uppercase',
                                  fontSize: '0.875rem',
                                  fontWeight: 500,
                                  '&.Mui-selected': {
                                    backgroundColor: 'primary.main',
                                    color: 'white',
                                    '&:hover': {
                                      backgroundColor: 'primary.dark',
                                    },
                                  },
                                  '&:hover': {
                                    backgroundColor: 'action.hover',
                                  },
                                },
                              }}
                            >
                              <ToggleButton value="monthly" aria-label="Monthly">
                                Monthly
                              </ToggleButton>
                              <ToggleButton value="yearly" aria-label="Yearly">
                                Yearly
                              </ToggleButton>
                            </ToggleButtonGroup>
                          </Box>

                          {/* Document Upload Section */}
                          <Box sx={{ mb: 2.5 }}>
                            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'text.primary', fontSize: '0.875rem' }}>
                              Documents <span style={{ color: 'red' }}>*</span>
                            </Typography>
                            <input
                              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png"
                              style={{ display: 'none' }}
                              id="funding-documents-upload-fix"
                              type="file"
                              multiple
                              onChange={(e) => {
                                const files = Array.from(e.target.files);
                                const allowedExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'jpg', 'jpeg', 'png'];
                                const maxSize = 10 * 1024 * 1024;
                                
                                const validFiles = [];
                                const errors = [];

                                files.forEach(file => {
                                  const actualExt = file.name.split('.').pop().toLowerCase();
                                  if (!allowedExtensions.includes(actualExt)) {
                                    errors.push(`${file.name} (Invalid format)`);
                                  } else if (file.size > maxSize) {
                                    errors.push(`${file.name} (Exceeds 10MB)`);
                                  } else {
                                    validFiles.push(file);
                                  }
                                });

                                if (errors.length > 0) {
                                  toast.error(errors.join(', '));
                                }

                                if (validFiles.length > 0) {
                                  setFundingForm({ ...fundingForm, documents: [...fundingForm.documents, ...validFiles] });
                                  if (fundingFormErrors.documents) {
                                    setFundingFormErrors({ ...fundingFormErrors, documents: '' });
                                  }
                                }
                                e.target.value = '';
                              }}
                            />
                            <Box
                              onDragOver={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                e.currentTarget.style.backgroundColor = fundingFormErrors.documents ? '#FEE2E2' : '#F1F5F9';
                                e.currentTarget.style.borderColor = '#2563EB';
                              }}
                              onDragLeave={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                e.currentTarget.style.backgroundColor = fundingFormErrors.documents ? '#FEF2F2' : '#F8FAFC';
                                e.currentTarget.style.borderColor = fundingFormErrors.documents ? '#DC2626' : '#E2E8F0';
                              }}
                              onDrop={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                e.currentTarget.style.backgroundColor = '#F8FAFC';
                                e.currentTarget.style.borderColor = fundingFormErrors.documents ? '#DC2626' : '#E2E8F0';
                                
                                const files = Array.from(e.dataTransfer.files);
                                const allowedExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'jpg', 'jpeg', 'png'];
                                const maxSize = 10 * 1024 * 1024;
                                
                                const validFiles = [];
                                const errors = [];

                                files.forEach(file => {
                                  const ext = file.name.split('.').pop().toLowerCase();
                                  if (!allowedExtensions.includes(ext)) {
                                    errors.push(`${file.name} (Invalid format)`);
                                  } else if (file.size > maxSize) {
                                    errors.push(`${file.name} (Exceeds 10MB)`);
                                  } else {
                                    validFiles.push(file);
                                  }
                                });

                                if (errors.length > 0) {
                                  toast.error(errors.join(', '));
                                }

                                if (validFiles.length > 0) {
                                  setFundingForm({ ...fundingForm, documents: [...fundingForm.documents, ...validFiles] });
                                  if (fundingFormErrors.documents) {
                                    setFundingFormErrors({ ...fundingFormErrors, documents: '' });
                                  }
                                }
                              }}
                              onClick={() => {
                                document.getElementById('funding-documents-upload-fix').click();
                              }}
                              sx={{
                                border: '2px dashed',
                                borderColor: fundingFormErrors.documents ? '#DC2626' : '#E2E8F0',
                                borderRadius: 2,
                                p: 2,
                                textAlign: 'center',
                                backgroundColor: fundingFormErrors.documents ? '#FEF2F2' : '#F8FAFC',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  backgroundColor: fundingFormErrors.documents ? '#FEE2E2' : '#F1F5F9',
                                  borderColor: '#2563EB',
                                },
                              }}
                            >
                              <CloudUploadIcon sx={{ fontSize: 32, color: '#6B7280', mb: 0.5 }} />
                              <Typography variant="body2" sx={{ color: '#6B7280', fontWeight: 500, mb: 0.5 }}>
                                Drop a file here to upload, or
                              </Typography>
                              <Typography
                                variant="body2"
                                component="span"
                                sx={{
                                  color: '#2563EB',
                                  textDecoration: 'underline',
                                  cursor: 'pointer',
                                  fontWeight: 600
                                }}
                              >
                                click here to browse
                              </Typography>
                              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#94A3B8' }}>
                                Allowed: PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, JPG, PNG &bull; Max 10MB
                              </Typography>
                            </Box>
                            {fundingFormErrors.documents && (
                              <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                {fundingFormErrors.documents}
                              </Typography>
                            )}
                            {fundingForm.documents && fundingForm.documents.length > 0 && (
                              <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {fundingForm.documents.map((file, index) => {
                                  const ext = file.name.split('.').pop().toLowerCase();
                                  const extColorMap = {
                                    pdf: '#EF4444', doc: '#2563EB', docx: '#2563EB',
                                    xls: '#16A34A', xlsx: '#16A34A',
                                    ppt: '#EA580C', pptx: '#EA580C',
                                    jpg: '#7C3AED', jpeg: '#7C3AED', png: '#7C3AED',
                                  };
                                  const dotColor = extColorMap[ext] || '#6B7280';
                                  const sizeKB = (file.size / 1024).toFixed(1);
                                  const sizeDisplay = file.size >= 1024 * 1024
                                    ? `${(file.size / (1024 * 1024)).toFixed(1)} MB`
                                    : `${sizeKB} KB`;
                                  return (
                                    <Box
                                      key={index}
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.5,
                                        p: 1.25,
                                        bgcolor: '#F8FAFC',
                                        borderRadius: 1.5,
                                        border: '1px solid #E2E8F0',
                                        transition: 'background-color 0.15s',
                                        '&:hover': { bgcolor: '#F1F5F9' },
                                      }}
                                    >
                                      <Box sx={{
                                        width: 36, height: 36, borderRadius: 1.5,
                                        bgcolor: dotColor + '18',
                                        border: `1px solid ${dotColor}40`,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        flexShrink: 0,
                                      }}>
                                        {ext === 'pdf' ? <PdfIcon sx={{ color: dotColor, fontSize: 18 }} /> :
                                         (ext === 'doc' || ext === 'docx') ? <WordIcon sx={{ color: dotColor, fontSize: 18 }} /> :
                                         (ext === 'xls' || ext === 'xlsx') ? <ExcelIcon sx={{ color: dotColor, fontSize: 18 }} /> :
                                         (ext === 'ppt' || ext === 'pptx') ? <PptIcon sx={{ color: dotColor, fontSize: 18 }} /> :
                                         (ext === 'jpg' || ext === 'jpeg' || ext === 'png') ? <ImageIcon sx={{ color: dotColor, fontSize: 18 }} /> :
                                         <FileIcon sx={{ color: dotColor, fontSize: 18 }} />}
                                      </Box>
                                      <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="body2" sx={{ color: '#1E293B', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                          {file.name}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: '#94A3B8' }}>
                                          {sizeDisplay}
                                        </Typography>
                                      </Box>
                                      <IconButton
                                        size="small"
                                        onClick={() => {
                                          const updatedDocuments = fundingForm.documents.filter((_, i) => i !== index);
                                          setFundingForm({ ...fundingForm, documents: updatedDocuments });
                                        }}
                                        sx={{ color: '#DC2626', flexShrink: 0, '&:hover': { backgroundColor: '#FEE2E2' } }}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Box>
                                  );
                                })}
                              </Box>
                            )}
                          </Box>

                          {/* Real-time Milestone Preview (only relevant for old fixed installments - disabled now) */}
                          {false && fundingForm.amount && fundingForm.numberOfInstallments && parseFloat(fundingForm.amount) > 0 && parseInt(fundingForm.numberOfInstallments) > 0 && (
                            <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                              <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 600, color: 'text.primary' }}>
                                Milestone Preview
                              </Typography>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {(() => {
                                  const totalAmount = parseFloat(fundingForm.amount) || 0;
                                  const numInstallments = parseInt(fundingForm.numberOfInstallments) || 0;
                                  const installmentAmount = totalAmount / numInstallments;
                                  const today = new Date();
                                  const installments = [];

                                  for (let i = 0; i < numInstallments; i++) {
                                    const installmentDate = new Date(today);
                                    if (fundingForm.paymentFrequency === 'monthly') {
                                      installmentDate.setMonth(today.getMonth() + i);
                                    } else {
                                      installmentDate.setFullYear(today.getFullYear() + i);
                                    }

                                    const formattedDate = installmentDate.toLocaleDateString('en-GB', {
                                      day: '2-digit',
                                      month: 'short',
                                      year: 'numeric'
                                    });

                                    // Round the last milestone to handle rounding differences
                                    const amount = i === numInstallments - 1
                                      ? (totalAmount - (installmentAmount * (numInstallments - 1))).toFixed(2)
                                      : installmentAmount.toFixed(2);

                                    installments.push({
                                      number: i + 1,
                                      amount: parseFloat(amount),
                                      date: formattedDate
                                    });
                                  }

                                  return installments.map((inst) => (
                                    <Box key={inst.number} sx={{ display: 'flex', gap: 2, alignItems: 'center', py: 0.5 }}>
                                      <Typography variant="body2" sx={{ minWidth: 100, fontWeight: 500 }}>
                                        Milestone {inst.number}:
                                      </Typography>
                                      <Typography variant="body2" sx={{ flex: 1, color: 'text.secondary' }}>
                                        Amount: {fundingForm.currencyId ? (
                                          currenciesList.find(c => c.id === parseInt(fundingForm.currencyId))?.symbol || '₹'
                                        ) : '₹'}{inst.amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                      </Typography>
                                      <Typography variant="body2" sx={{ flex: 1, color: 'text.secondary' }}>
                                        Date: {inst.date}
                                      </Typography>
                                    </Box>
                                  ));
                                })()}
                              </Box>
                            </Box>
                          )}
                        </>
                      )}
                    </>
                  )}
                </Box>
              )}
              {dialogConfig.type === 'selectstartupapplication' && (
                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  {/* Startup Dropdown */}
                  <TextField
                    select
                    label="Startup"
                    required
                    fullWidth
                    value={startupApplicationForm.startupId}
                    onChange={(e) => {
                      const selectedStartupId = e.target.value;
                      setStartupApplicationForm({
                        startupId: selectedStartupId,
                        applicationId: '' // Reset application when startup changes
                      });
                      if (startupApplicationErrors.startupId) {
                        setStartupApplicationErrors({ ...startupApplicationErrors, startupId: '' });
                      }
                      // Fetch applications for selected startup
                      fetchApplicationsForStartup(selectedStartupId);
                    }}
                    error={!!startupApplicationErrors.startupId}
                    helperText={startupApplicationErrors.startupId || ''}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1.5,
                      },
                    }}
                  >
                    <MenuItem value="">
                      <em>Select Startup</em>
                    </MenuItem>
                    {startupsList.map((startup) => (
                      <MenuItem key={startup.id} value={startup.id}>
                        {startup.name}{startup.email ? ` (${startup.email})` : ''}
                      </MenuItem>
                    ))}
                  </TextField>

                  {/* Application Dropdown (only if startup is selected) */}
                  {startupApplicationForm.startupId && (
                    <TextField
                      select
                      label="Application"
                      required
                      fullWidth
                      value={startupApplicationForm.applicationId}
                      onChange={(e) => {
                        setStartupApplicationForm({ ...startupApplicationForm, applicationId: e.target.value });
                        if (startupApplicationErrors.applicationId) {
                          setStartupApplicationErrors({ ...startupApplicationErrors, applicationId: '' });
                        }
                      }}
                      error={!!startupApplicationErrors.applicationId}
                      helperText={startupApplicationErrors.applicationId || ''}
                      disabled={loadingApplications}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1.5,
                        },
                      }}
                    >
                      <MenuItem value="">
                        <em>Select Application</em>
                      </MenuItem>
                      {loadingApplications ? (
                        <MenuItem disabled>Loading applications...</MenuItem>
                      ) : (
                        applicationsList.map((app) => (
                          <MenuItem key={app.id} value={app.id}>
                            {app.program_name || `Application #${app.id}`}
                          </MenuItem>
                        ))
                      )}
                    </TextField>
                  )}
                </Box>
              )}
              {dialogConfig.type === 'funding' && (
                <Box sx={{ mt: dialogConfig.message ? 2 : 0 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#374151',
                      mb: 3,
                      fontWeight: 500,
                    }}
                  >
                    {dialogConfig.message || 'Funding information for this startup'}
                  </Typography>

                  {/* Currency-wise Total Funding Display */}
                  {dialogConfig.currencyWiseTotals && dialogConfig.currencyWiseTotals.length > 0 ? (
                    dialogConfig.currencyWiseTotals.map((currencyTotal, index) => (
                      <Box
                        key={currencyTotal.currency_code || index}
                        sx={{
                          p: 3,
                          bgcolor: '#F0FDF4',
                          border: '1px solid #86EFAC',
                          borderRadius: 2,
                          mb: index < dialogConfig.currencyWiseTotals.length - 1 ? 2 : 3,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#166534',
                            mb: 1,
                            fontWeight: 600,
                            fontSize: '0.875rem',
                          }}
                        >
                          Total Funding ({currencyTotal.currency_code || 'INR'})
                        </Typography>
                        <Typography
                          variant="h4"
                          sx={{
                            color: '#15803D',
                            fontWeight: 700,
                          }}
                        >
                          {currencyTotal.currency_symbol || '₹'}{parseFloat(currencyTotal.total || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </Typography>
                      </Box>
                    ))
                  ) : (
                    <Box
                      sx={{
                        p: 3,
                        bgcolor: '#F0FDF4',
                        border: '1px solid #86EFAC',
                        borderRadius: 2,
                        mb: 3,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#166534',
                          mb: 1,
                          fontWeight: 600,
                          fontSize: '0.875rem',
                        }}
                      >
                        Total Funding
                      </Typography>
                      <Typography
                        variant="h4"
                        sx={{
                          color: '#15803D',
                          fontWeight: 700,
                        }}
                      >
                        {dialogConfig.totalFunding !== null && dialogConfig.totalFunding !== undefined
                          ? `₹${parseFloat(dialogConfig.totalFunding).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                          : '₹0.00'}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
              {dialogConfig.type === 'fundusage' && (
                <Box sx={{ mt: { xs: 1, sm: 2 }, display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 2.5 } }}>
                  <TextField
                    label="Title"
                    required
                    fullWidth
                    value={fundUsageForm.title}
                    onChange={(e) => {
                      setFundUsageForm({ ...fundUsageForm, title: e.target.value });
                      if (fundUsageFormErrors.title) {
                        setFundUsageFormErrors({ ...fundUsageFormErrors, title: '' });
                      }
                    }}
                    error={!!fundUsageFormErrors.title}
                    helperText={fundUsageFormErrors.title}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1.5,
                      },
                    }}
                  />

                  <TextField
                    label="Amount"
                    type="number"
                    required
                    fullWidth
                    placeholder="Enter amount used"
                    value={fundUsageForm.amount}
                    onChange={(e) => {
                      setFundUsageForm({ ...fundUsageForm, amount: e.target.value });
                      if (fundUsageFormErrors.amount) {
                        setFundUsageFormErrors({ ...fundUsageFormErrors, amount: '' });
                      }
                    }}
                    error={!!fundUsageFormErrors.amount}
                    helperText={fundUsageFormErrors.amount}
                    inputProps={{
                      min: 0,
                      step: 0.01
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1.5,
                      },
                    }}
                  />

                  <TextField
                    label="Description"
                    multiline
                    rows={4}
                    required
                    fullWidth
                    placeholder="Describe how the funds are being used"
                    value={fundUsageForm.description}
                    onChange={(e) => {
                      setFundUsageForm({ ...fundUsageForm, description: e.target.value });
                      if (fundUsageFormErrors.description) {
                        setFundUsageFormErrors({ ...fundUsageFormErrors, description: '' });
                      }
                    }}
                    error={!!fundUsageFormErrors.description}
                    helperText={fundUsageFormErrors.description}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1.5,
                      },
                    }}
                  />

                  <Box>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'text.primary', fontSize: '0.875rem' }}>
                      Documents <span style={{ color: 'red' }}>*</span>
                    </Typography>
                    <input
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png"
                      style={{ display: 'none' }}
                      id="fund-usage-documents-upload"
                      type="file"
                      multiple
                      onChange={(e) => {
                        const files = Array.from(e.target.files);
                        const allowedExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'jpg', 'jpeg', 'png'];
                        const maxSize = 10 * 1024 * 1024; // 10 MB
                        const validFiles = [];
                        const invalidFiles = [];
                        const invalidTypeFiles = [];
                        files.forEach(file => {
                          const ext = file.name.split('.').pop().toLowerCase();
                          if (!allowedExtensions.includes(ext)) {
                            invalidTypeFiles.push(file.name);
                          } else if (file.size > maxSize) {
                            invalidFiles.push(file.name);
                          } else {
                            validFiles.push(file);
                          }
                        });
                        if (invalidTypeFiles.length > 0) {
                          toast.error(`Unsupported file type(s): ${invalidTypeFiles.join(', ')}. Allowed: PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, JPG, PNG`);
                        }
                        if (invalidFiles.length > 0) {
                          toast.error(`File size limit is 10 MB. The following files exceed the limit: ${invalidFiles.join(', ')}`);
                        }
                        if (validFiles.length > 0) {
                          setFundUsageForm({
                            ...fundUsageForm,
                            documents: [...fundUsageForm.documents, ...validFiles]
                          });
                          if (fundUsageFormErrors.documents) {
                            setFundUsageFormErrors({ ...fundUsageFormErrors, documents: '' });
                          }
                        }
                        // Reset input to allow selecting same files again
                        e.target.value = '';
                      }}
                    />
                    <Box
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        e.currentTarget.style.backgroundColor = fundUsageFormErrors.documents ? '#FCA5A5' : '#D1D5DB';
                      }}
                      onDragLeave={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        e.currentTarget.style.backgroundColor = fundUsageFormErrors.documents ? '#FEF2F2' : '#F3F4F6';
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        e.currentTarget.style.backgroundColor = '#F3F4F6';
                        const files = Array.from(e.dataTransfer.files);
                        const allowedExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'jpg', 'jpeg', 'png'];
                        const maxSize = 10 * 1024 * 1024; // 10 MB
                        const validFiles = [];
                        const invalidFiles = [];
                        const invalidTypeFiles = [];
                        files.forEach(file => {
                          const ext = file.name.split('.').pop().toLowerCase();
                          if (!allowedExtensions.includes(ext)) {
                            invalidTypeFiles.push(file.name);
                          } else if (file.size > maxSize) {
                            invalidFiles.push(file.name);
                          } else {
                            validFiles.push(file);
                          }
                        });
                        if (invalidTypeFiles.length > 0) {
                          toast.error(`Unsupported file type(s): ${invalidTypeFiles.join(', ')}. Allowed: PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, JPG, PNG`);
                        }
                        if (invalidFiles.length > 0) {
                          toast.error(`File size limit is 10 MB. The following files exceed the limit: ${invalidFiles.join(', ')}`);
                        }
                        if (validFiles.length > 0) {
                          setFundUsageForm({
                            ...fundUsageForm,
                            documents: [...fundUsageForm.documents, ...validFiles]
                          });
                          if (fundUsageFormErrors.documents) {
                            setFundUsageFormErrors({ ...fundUsageFormErrors, documents: '' });
                          }
                        }
                      }}
                      onClick={() => {
                        document.getElementById('fund-usage-documents-upload').click();
                      }}
                      sx={{
                        border: `2px dashed ${fundUsageFormErrors.documents ? '#DC2626' : '#9CA3AF'}`,
                        borderRadius: 2,
                        p: 1.5,
                        textAlign: 'center',
                        backgroundColor: fundUsageFormErrors.documents ? '#FEF2F2' : '#F3F4F6',
                        cursor: 'pointer',
                        transition: 'background-color 0.2s, border-color 0.2s',
                        '&:hover': {
                          backgroundColor: fundUsageFormErrors.documents ? '#FEE2E2' : '#E5E7EB',
                        },
                      }}
                    >
                      <CloudUploadIcon sx={{ fontSize: 32, color: '#6B7280', mb: 0.5 }} />
                      <Typography variant="body2" sx={{ color: '#6B7280', fontWeight: 500, mb: 0.5 }}>
                        Drop a file here to upload, or
                      </Typography>
                      <Typography
                        variant="body2"
                        component="span"
                        sx={{
                          color: '#6B7280',
                          textDecoration: 'underline',
                          cursor: 'pointer',
                        }}
                      >
                        click here to browse
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#9CA3AF' }}>
                        Allowed: PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, JPG, PNG &bull; Max 10MB
                      </Typography>
                    </Box>
                    {fundUsageFormErrors.documents && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                        {fundUsageFormErrors.documents}
                      </Typography>
                    )}
                    {fundUsageForm.documents && fundUsageForm.documents.length > 0 && (
                      <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {fundUsageForm.documents.map((file, index) => {
                          const ext = file.name.split('.').pop().toLowerCase();
                          const extColorMap = {
                            pdf: '#EF4444', doc: '#2563EB', docx: '#2563EB',
                            xls: '#16A34A', xlsx: '#16A34A',
                            ppt: '#EA580C', pptx: '#EA580C',
                            jpg: '#7C3AED', jpeg: '#7C3AED', png: '#7C3AED',
                          };
                          const dotColor = extColorMap[ext] || '#6B7280';
                          const sizeKB = (file.size / 1024).toFixed(1);
                          const sizeDisplay = file.size >= 1024 * 1024
                            ? `${(file.size / (1024 * 1024)).toFixed(1)} MB`
                            : `${sizeKB} KB`;
                          return (
                            <Box
                              key={index}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                p: 1.25,
                                bgcolor: '#F8FAFC',
                                borderRadius: 1.5,
                                border: '1px solid #E2E8F0',
                                transition: 'background-color 0.15s',
                                '&:hover': { bgcolor: '#F1F5F9' },
                              }}
                            >
                              {/* File type icon */}
                              <Box sx={{
                                width: 36, height: 36, borderRadius: 1.5,
                                bgcolor: dotColor + '18',
                                border: `1px solid ${dotColor}40`,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                flexShrink: 0,
                              }}>
                                {ext === 'pdf' ? <PdfIcon sx={{ color: dotColor, fontSize: 18 }} /> :
                                 (ext === 'doc' || ext === 'docx') ? <WordIcon sx={{ color: dotColor, fontSize: 18 }} /> :
                                 (ext === 'xls' || ext === 'xlsx') ? <ExcelIcon sx={{ color: dotColor, fontSize: 18 }} /> :
                                 (ext === 'ppt' || ext === 'pptx') ? <PptIcon sx={{ color: dotColor, fontSize: 18 }} /> :
                                 (ext === 'jpg' || ext === 'jpeg' || ext === 'png') ? <ImageIcon sx={{ color: dotColor, fontSize: 18 }} /> :
                                 <FileIcon sx={{ color: dotColor, fontSize: 18 }} />}
                              </Box>
                              {/* Name + size */}
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="body2" sx={{ color: '#1E293B', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {file.name}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#94A3B8' }}>
                                  {sizeDisplay}
                                </Typography>
                              </Box>
                              {/* Delete */}
                              <IconButton
                                size="small"
                                onClick={() => {
                                  const updatedDocuments = fundUsageForm.documents.filter((_, i) => i !== index);
                                  setFundUsageForm({ ...fundUsageForm, documents: updatedDocuments });
                                  if (fundUsageFormErrors.documents) {
                                    setFundUsageFormErrors({ ...fundUsageFormErrors, documents: '' });
                                  }
                                }}
                                sx={{ color: '#DC2626', flexShrink: 0, '&:hover': { backgroundColor: '#FEE2E2' } }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                  </Box>
                </Box>
              )}
              {dialogConfig.type === 'paymentmethod' && (
                <Box sx={{ mt: { xs: 1, sm: 2 }, display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 2.5 } }}>
                  {/* Payment Type Selection */}
                  <Box sx={{ mb: 2 }}>
                    <ToggleButtonGroup
                      value={paymentMethodForm.paymentType}
                      exclusive
                      onChange={(e, newValue) => {
                        if (newValue !== null) {
                          setPaymentMethodForm(prev => {
                            // When switching to installments, ensure at least one amount+date row exists
                            const nextCustomInstallments =
                              newValue === 'installment'
                                ? (prev.customInstallments && prev.customInstallments.length > 0
                                  ? prev.customInstallments
                                  : [{ amount: dialogConfig.defaultAmount || '', date: '', status: 'unpaid' }])
                                : [];
                            return {
                              ...prev,
                              paymentType: newValue,
                              customInstallments: nextCustomInstallments
                            };
                          });
                          setPaymentMethodFormErrors({});
                        }
                      }}
                      fullWidth
                      orientation="horizontal"
                      sx={{
                        '& .MuiToggleButton-root': {
                          py: { xs: 1.25, sm: 1.5 },
                          px: { xs: 2, sm: 3 },
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                          border: '1px solid',
                          borderColor: 'divider',
                          '&.Mui-selected': {
                            backgroundColor: 'primary.main',
                            color: 'white',
                            '&:hover': {
                              backgroundColor: 'primary.dark',
                            },
                          },
                          '&:hover': {
                            backgroundColor: 'action.hover',
                          },
                        },
                      }}
                    >
                      <ToggleButton value="full" aria-label="Full Payment">
                        Full Payment
                      </ToggleButton>
                      <ToggleButton value="installment" aria-label="Milestone">
                        Milestone
                      </ToggleButton>
                    </ToggleButtonGroup>
                    {paymentMethodFormErrors.paymentType && !(paymentMethodForm.paymentType === 'installment' || (paymentMethodForm.customInstallments && paymentMethodForm.customInstallments.length > 0 && paymentMethodForm.customInstallments.some(inst => inst.amount && parseFloat(inst.amount) > 0 && inst.date))) && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                        {paymentMethodFormErrors.paymentType}
                      </Typography>
                    )}
                  </Box>

                  {/* Show fields only after payment type is selected */}
                  {paymentMethodForm.paymentType && (
                    <>
                      {/* For Full Payment: Show info message */}
                      {paymentMethodForm.paymentType === 'full' && (
                        <>
                          <Box sx={{
                            p: 2,
                            bgcolor: '#E3F2FD',
                            borderRadius: 1.5,
                            border: '1px solid #BBDEFB'
                          }}>
                            <Typography variant="body2" sx={{ color: '#0D47A1', fontSize: '0.875rem' }}>
                              {(() => {
                                const symbol = currenciesList.find(c => c.id === dialogConfig.defaultCurrencyId)?.symbol || '₹';
                                const amount = dialogConfig.defaultAmount || '0';
                                return `This is full payment of ${symbol}${amount}.`;
                              })()}
                            </Typography>
                          </Box>

                          {/* Notes Textarea */}
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'text.primary' }}>
                              Notes <span style={{ color: 'red' }}>*</span>
                            </Typography>
                            <TextField
                              fullWidth
                              multiline
                              rows={4}
                              required
                              placeholder="Add any notes about the reimbursement..."
                              value={paymentMethodForm.notes}
                              onChange={(e) => {
                                setPaymentMethodForm({
                                  ...paymentMethodForm,
                                  notes: e.target.value
                                });
                                if (paymentMethodFormErrors.notes) {
                                  const newErrors = { ...paymentMethodFormErrors };
                                  delete newErrors.notes;
                                  setPaymentMethodFormErrors(newErrors);
                                }
                              }}
                              error={!!paymentMethodFormErrors.notes}
                              helperText={paymentMethodFormErrors.notes}
                              variant="outlined"
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 1.5,
                                },
                              }}
                            />
                          </Box>
                        </>
                      )}

                      {/* For Installment (Milestone): Show custom milestones */}
                      {paymentMethodForm.paymentType === 'installment' && (
                        <Box>
                          <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 500, color: 'text.primary' }}>
                            Milestones <span style={{ color: 'red' }}>*</span>
                          </Typography>

                          {/* Total Amount Display (read-only) */}
                          {(() => {
                            const totalAmount = parseFloat(dialogConfig.defaultAmount || 0);
                            const installmentsSum = paymentMethodForm.customInstallments.reduce((sum, inst) => {
                              return sum + (parseFloat(inst.amount) || 0);
                            }, 0);
                            const remaining = totalAmount - installmentsSum;
                            const currencySymbol = currenciesList.find(c => c.id === dialogConfig.defaultCurrencyId)?.symbol || '₹';

                            return (
                              <Box sx={{
                                p: 2,
                                mb: 2,
                                bgcolor: '#F9FAFB',
                                borderRadius: 1.5,
                                border: '1px solid #E5E7EB'
                              }}>
                                <Typography variant="body2" sx={{ color: '#374151', fontSize: '0.875rem', mb: 1 }}>
                                  <strong>Total Amount:</strong> {currencySymbol}{totalAmount.toFixed(2)}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontSize: '0.875rem',
                                    color: remaining === 0 ? '#10B981' : '#EF4444',
                                    fontWeight: 500
                                  }}
                                >
                                  <strong>Remaining:</strong> {currencySymbol}{remaining.toFixed(2)}
                                </Typography>
                              </Box>
                            );
                          })()}

                          {/* Custom Milestones */}
                          {paymentMethodForm.customInstallments.map((installment, index) => (
                            <Box
                              key={index}
                              sx={{
                                mb: 3,
                                p: { xs: 1.5, sm: 2 },
                                bgcolor: '#F9FAFB',
                                borderRadius: 1.5,
                                border: '1px solid',
                                borderColor: '#E5E7EB',
                              }}
                            >
                              <Typography variant="body2" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
                                Milestone {index + 1}
                              </Typography>

                              {/* Amount and Date Row */}
                              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1.5, sm: 2 }, mb: 2 }}>
                                <TextField
                                  label={`Milestone ${index + 1} Amount`}
                                  type="number"
                                  required
                                  value={installment.amount}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    setPaymentMethodForm(prev => {
                                      const updatedInstallments = [...(prev.customInstallments || [])];
                                      if (!updatedInstallments[index]) {
                                        updatedInstallments[index] = { amount: '', date: '', status: 'unpaid', notes: '' };
                                      }
                                      updatedInstallments[index] = {
                                        ...updatedInstallments[index],
                                        amount: value
                                      };
                                      return { ...prev, customInstallments: updatedInstallments };
                                    });
                                    if (paymentMethodFormErrors[`installment_${index}_amount`]) {
                                      const newErrors = { ...paymentMethodFormErrors };
                                      delete newErrors[`installment_${index}_amount`];
                                      setPaymentMethodFormErrors(newErrors);
                                    }
                                  }}
                                  error={!!paymentMethodFormErrors[`installment_${index}_amount`]}
                                  helperText={paymentMethodFormErrors[`installment_${index}_amount`]}
                                  inputProps={{
                                    min: 0,
                                    step: 0.01,
                                  }}
                                  sx={{
                                    flex: 1,
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: 1.5,
                                    },
                                  }}
                                />
                                <TextField
                                  label="Date"
                                  type="date"
                                  required
                                  value={installment.date}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    setPaymentMethodForm(prev => {
                                      const updatedInstallments = [...(prev.customInstallments || [])];
                                      if (!updatedInstallments[index]) {
                                        updatedInstallments[index] = { amount: '', date: '', status: 'unpaid', notes: '' };
                                      }
                                      updatedInstallments[index] = {
                                        ...updatedInstallments[index],
                                        date: value
                                      };
                                      return { ...prev, customInstallments: updatedInstallments };
                                    });
                                    if (paymentMethodFormErrors[`installment_${index}_date`]) {
                                      const newErrors = { ...paymentMethodFormErrors };
                                      delete newErrors[`installment_${index}_date`];
                                      setPaymentMethodFormErrors(newErrors);
                                    }
                                  }}
                                  error={!!paymentMethodFormErrors[`installment_${index}_date`]}
                                  helperText={paymentMethodFormErrors[`installment_${index}_date`]}
                                  InputLabelProps={{
                                    shrink: true,
                                  }}
                                  sx={{
                                    flex: 1,
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: 1.5,
                                    },
                                  }}
                                />
                                {/* Add (+) button - show only for first installment */}
                                {index === 0 && (
                                  <IconButton
                                    onClick={() => {
                                      setPaymentMethodForm(prev => ({
                                        ...prev,
                                        customInstallments: [
                                          ...(prev.customInstallments || []),
                                          { amount: '', date: '', status: 'unpaid', notes: '' }
                                        ]
                                      }));
                                    }}
                                    sx={{
                                      color: '#2563EB',
                                      alignSelf: { xs: 'flex-end', sm: 'center' },
                                      bgcolor: '#EFF6FF',
                                      '&:hover': {
                                        bgcolor: '#DBEAFE',
                                        color: '#1D4ED8',
                                      },
                                    }}
                                  >
                                    <AddIcon />
                                  </IconButton>
                                )}
                                {/* Delete button - not for first installment */}
                                {index > 0 && (
                                  <IconButton
                                    onClick={() => {
                                      const updatedInstallments = paymentMethodForm.customInstallments.filter((_, i) => i !== index);
                                      setPaymentMethodForm({ ...paymentMethodForm, customInstallments: updatedInstallments });
                                    }}
                                    sx={{
                                      color: '#EF4444',
                                      alignSelf: { xs: 'flex-end', sm: 'center' },
                                      bgcolor: '#FEE2E2',
                                      '&:hover': {
                                        bgcolor: '#FECACA',
                                      },
                                    }}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                )}
                              </Box>

                              {/* Notes Field for this Milestone */}
                              <TextField
                                label={`Notes for Milestone ${index + 1}`}
                                fullWidth
                                multiline
                                rows={3}
                                required
                                value={installment.notes || ''}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  setPaymentMethodForm(prev => {
                                    const updatedInstallments = [...(prev.customInstallments || [])];
                                    if (!updatedInstallments[index]) {
                                      updatedInstallments[index] = { amount: '', date: '', status: 'unpaid', notes: '' };
                                    }
                                    updatedInstallments[index] = {
                                      ...updatedInstallments[index],
                                      notes: value
                                    };
                                    return { ...prev, customInstallments: updatedInstallments };
                                  });
                                  // Clear error when user types
                                  if (paymentMethodFormErrors[`installment_${index}_notes`]) {
                                    const newErrors = { ...paymentMethodFormErrors };
                                    delete newErrors[`installment_${index}_notes`];
                                    setPaymentMethodFormErrors(newErrors);
                                  }
                                }}
                                error={!!paymentMethodFormErrors[`installment_${index}_notes`]}
                                helperText={paymentMethodFormErrors[`installment_${index}_notes`]}
                                placeholder={`Add notes for Milestone ${index + 1}...`}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 1.5,
                                  },
                                }}
                              />
                            </Box>
                          ))}

                          {paymentMethodFormErrors.customInstallments && (
                            <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                              {paymentMethodFormErrors.customInstallments}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              )}
              {dialogConfig.type === 'addfundingdocuments' && (
                <Box sx={{ mt: { xs: 1, sm: 2 } }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    {/* Document Upload */}
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          mb: 1,
                          fontWeight: 500,
                          color: 'text.primary',
                          fontSize: '0.875rem',
                        }}
                      >
                        Upload Documents <span style={{ color: 'red' }}>*</span>
                      </Typography>
                      <input
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png"
                        style={{ display: 'none' }}
                        id="funding-documents-upload"
                        type="file"
                        multiple
                        onChange={(e) => {
                          const files = Array.from(e.target.files);
                          const allowedExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'jpg', 'jpeg', 'png'];
                          const maxSize = 10 * 1024 * 1024;
                          
                          const validFiles = [];
                          const errors = [];

                          files.forEach(file => {
                            const ext = file.name.split('.').pop().toLowerCase();
                            if (!allowedExtensions.includes(ext)) {
                              errors.push(`${file.name} (Invalid format)`);
                            } else if (file.size > maxSize) {
                              errors.push(`${file.name} (Exceeds 10MB)`);
                            } else {
                              validFiles.push(file);
                            }
                          });

                          if (errors.length > 0) {
                            toast.error(errors.join(', '));
                          }

                          if (validFiles.length > 0) {
                            setFundingDocumentsForm({ ...fundingDocumentsForm, documents: [...fundingDocumentsForm.documents, ...validFiles] });
                            if (fundingDocumentsFormErrors.documents) {
                              setFundingDocumentsFormErrors({ ...fundingDocumentsFormErrors, documents: '' });
                            }
                          }
                          e.target.value = '';
                        }}
                      />
                      <Box
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          e.currentTarget.style.backgroundColor = fundingDocumentsFormErrors.documents ? '#FEE2E2' : '#F1F5F9';
                          e.currentTarget.style.borderColor = '#2563EB';
                        }}
                        onDragLeave={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          e.currentTarget.style.backgroundColor = fundingDocumentsFormErrors.documents ? '#FEF2F2' : '#F8FAFC';
                          e.currentTarget.style.borderColor = fundingDocumentsFormErrors.documents ? '#DC2626' : '#E2E8F0';
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          e.currentTarget.style.backgroundColor = '#F8FAFC';
                          e.currentTarget.style.borderColor = fundingDocumentsFormErrors.documents ? '#DC2626' : '#E2E8F0';
                          
                          const files = Array.from(e.dataTransfer.files);
                          const allowedExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'jpg', 'jpeg', 'png'];
                          const maxSize = 10 * 1024 * 1024;
                          
                          const validFiles = [];
                          const errors = [];

                          files.forEach(file => {
                            const ext = file.name.split('.').pop().toLowerCase();
                            if (!allowedExtensions.includes(ext)) {
                              errors.push(`${file.name} (Invalid format)`);
                            } else if (file.size > maxSize) {
                              errors.push(`${file.name} (Exceeds 10MB)`);
                            } else {
                              validFiles.push(file);
                            }
                          });

                          if (errors.length > 0) {
                            toast.error(errors.join(', '));
                          }

                          if (validFiles.length > 0) {
                            setFundingDocumentsForm({ ...fundingDocumentsForm, documents: [...fundingDocumentsForm.documents, ...validFiles] });
                            if (fundingDocumentsFormErrors.documents) {
                              setFundingDocumentsFormErrors({ ...fundingDocumentsFormErrors, documents: '' });
                            }
                          }
                        }}
                        onClick={() => {
                          document.getElementById('funding-documents-upload').click();
                        }}
                        sx={{
                          border: '2px dashed',
                          borderColor: fundingDocumentsFormErrors.documents ? '#DC2626' : '#E2E8F0',
                          borderRadius: 2,
                          p: 2,
                          textAlign: 'center',
                          backgroundColor: fundingDocumentsFormErrors.documents ? '#FEF2F2' : '#F8FAFC',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            backgroundColor: fundingDocumentsFormErrors.documents ? '#FEE2E2' : '#F1F5F9',
                            borderColor: '#2563EB',
                          },
                        }}
                      >
                        <CloudUploadIcon sx={{ fontSize: 32, color: '#6B7280', mb: 0.5 }} />
                        <Typography variant="body2" sx={{ color: '#6B7280', fontWeight: 500, mb: 0.5 }}>
                          Drop a file here to upload, or
                        </Typography>
                        <Typography
                          variant="body2"
                          component="span"
                          sx={{
                            color: '#2563EB',
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            fontWeight: 600
                          }}
                        >
                          click here to browse
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#94A3B8' }}>
                          Allowed: PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, JPG, PNG &bull; Max 10MB
                        </Typography>
                      </Box>
                      {fundingDocumentsFormErrors.documents && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                          {fundingDocumentsFormErrors.documents}
                        </Typography>
                      )}
                      {fundingDocumentsForm.documents && fundingDocumentsForm.documents.length > 0 && (
                        <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {fundingDocumentsForm.documents.map((file, index) => {
                            const ext = file.name.split('.').pop().toLowerCase();
                            const extColorMap = {
                              pdf: '#EF4444', doc: '#2563EB', docx: '#2563EB',
                              xls: '#16A34A', xlsx: '#16A34A',
                              ppt: '#EA580C', pptx: '#EA580C',
                              jpg: '#7C3AED', jpeg: '#7C3AED', png: '#7C3AED',
                            };
                            const dotColor = extColorMap[ext] || '#6B7280';
                            const sizeKB = (file.size / 1024).toFixed(1);
                            const sizeDisplay = file.size >= 1024 * 1024
                              ? `${(file.size / (1024 * 1024)).toFixed(1)} MB`
                              : `${sizeKB} KB`;
                            return (
                              <Box
                                key={index}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1.5,
                                  p: 1.25,
                                  bgcolor: '#F8FAFC',
                                  borderRadius: 1.5,
                                  border: '1px solid #E2E8F0',
                                  transition: 'background-color 0.15s',
                                  '&:hover': { bgcolor: '#F1F5F9' },
                                }}
                              >
                                <Box sx={{
                                  width: 36, height: 36, borderRadius: 1.5,
                                  bgcolor: dotColor + '18',
                                  border: `1px solid ${dotColor}40`,
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  flexShrink: 0,
                                }}>
                                  {ext === 'pdf' ? <PdfIcon sx={{ color: dotColor, fontSize: 18 }} /> :
                                   (ext === 'doc' || ext === 'docx') ? <WordIcon sx={{ color: dotColor, fontSize: 18 }} /> :
                                   (ext === 'xls' || ext === 'xlsx') ? <ExcelIcon sx={{ color: dotColor, fontSize: 18 }} /> :
                                   (ext === 'ppt' || ext === 'pptx') ? <PptIcon sx={{ color: dotColor, fontSize: 18 }} /> :
                                   (ext === 'jpg' || ext === 'jpeg' || ext === 'png') ? <ImageIcon sx={{ color: dotColor, fontSize: 18 }} /> :
                                   <FileIcon sx={{ color: dotColor, fontSize: 18 }} />}
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography variant="body2" sx={{ color: '#1E293B', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {file.name}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: '#94A3B8' }}>
                                    {sizeDisplay}
                                  </Typography>
                                </Box>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    const updatedDocuments = fundingDocumentsForm.documents.filter((_, i) => i !== index);
                                    setFundingDocumentsForm({ ...fundingDocumentsForm, documents: updatedDocuments });
                                  }}
                                  sx={{ color: '#DC2626', flexShrink: 0, '&:hover': { backgroundColor: '#FEE2E2' } }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            );
                          })}
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>
              )}
              {dialogConfig.type === 'fundinghistory' && (
                <Box sx={{ mt: { xs: 1, sm: 2 } }}>
                  {/* Fund Usage Entries */}
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          color: '#374151',
                          fontWeight: 600,
                          fontSize: '0.875rem',
                        }}
                      >
                        Fund Usage Entries {dialogConfig.fundUsageEntries && dialogConfig.fundUsageEntries.length > 0 ? `(${dialogConfig.fundUsageEntries.length})` : ''}
                      </Typography>
                      {dialogConfig.fundingId && (() => {
                        // Check if user is startup founder - only show button for startup founders
                        const userRole = (localStorage.getItem('roleName') || localStorage.getItem('role') || '').toLowerCase();
                        const isStartupFounder = userRole === 'startup_founder' || userRole === 'startup founder';

                        if (!isStartupFounder) {
                          return null; // Don't show button for admins
                        }

                        return (
                          <IconButton
                            onClick={async () => {
                              try {
                                // Store current dialog config for reopening
                                const currentConfig = dialogConfig;

                                // Close current dialog first
                                closeDialog(null);

                                // Wait a bit for dialog to close
                                await new Promise(resolve => setTimeout(resolve, 100));

                                // Open Add Fund Usage dialog using external handler
                                if (externalDialogHandler) {
                                  await externalDialogHandler({
                                    type: 'fundusage',
                                    title: 'Add Fund Usage',
                                    fundingId: currentConfig.fundingId,
                                    onConfirm: async (result) => {
                                      // Refresh funding history if callback provided
                                      if (currentConfig.onRefresh) {
                                        currentConfig.onRefresh();
                                      }
                                      // Reopen funding history dialog with updated data
                                      if (currentConfig.fundingData) {
                                        // Fetch updated fund usage entries
                                        try {
                                          const token = localStorage.getItem('token');
                                          if (token) {
                                            const usageResponse = await fetch(`${API_BASE_URL}/funding/usage/${currentConfig.fundingId}`, {
                                              headers: {
                                                'Authorization': `Bearer ${token}`
                                              }
                                            });
                                            if (usageResponse.ok) {
                                              const usageData = await usageResponse.json();
                                              if (usageData.success && usageData.data) {
                                                // Reopen funding history with updated entries
                                                await externalDialogHandler({
                                                  type: 'fundinghistory',
                                                  title: 'Funding History',
                                                  fundingId: currentConfig.fundingId,
                                                  fundingData: currentConfig.fundingData,
                                                  fundUsageEntries: usageData.data,
                                                  confirmText: 'Close',
                                                  showCancel: false,
                                                  onRefresh: currentConfig.onRefresh
                                                });
                                              }
                                            }
                                          }
                                        } catch (error) {
                                          console.error('Error refreshing funding history:', error);
                                        }
                                      }
                                    }
                                  });
                                }
                              } catch (error) {
                                console.error('Error opening add fund usage dialog:', error);
                              }
                            }}
                            size="small"
                            sx={{
                              backgroundColor: 'var(--an-coral)',
                              color: '#ffffff',
                              width: 32,
                              height: 32,
                              '&:hover': {
                                backgroundColor: 'var(--an-coral-deep)',
                              },
                            }}
                            title="Add Expense"
                          >
                            <AddIcon sx={{ fontSize: 20 }} />
                          </IconButton>
                        );
                      })()}
                    </Box>
                    {dialogConfig.fundUsageEntries && dialogConfig.fundUsageEntries.length > 0 ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {dialogConfig.fundUsageEntries.map((entry, index) => (
                          <Accordion
                            key={entry.id || index}
                            sx={{
                              border: '1px solid #E5E7EB',
                              borderRadius: 2,
                              '&:before': {
                                display: 'none',
                              },
                              boxShadow: 'none',
                              '&.Mui-expanded': {
                                margin: 0,
                              },
                            }}
                          >
                            <AccordionSummary
                              expandIcon={<ExpandMoreIcon sx={{ color: '#6B7280' }} />}
                              sx={{
                                px: 2.5,
                                py: 1.5,
                                '&.Mui-expanded': {
                                  minHeight: 48,
                                },
                                '& .MuiAccordionSummary-content': {
                                  margin: '12px 0',
                                  '&.Mui-expanded': {
                                    margin: '12px 0',
                                  },
                                },
                              }}
                            >
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', pr: 2 }}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: '#111827',
                                    fontWeight: 600,
                                    fontSize: '0.875rem',
                                  }}
                                >
                                  {entry.title || 'N/A'}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: '#059669',
                                    fontWeight: 600,
                                    fontSize: '0.875rem',
                                  }}
                                >
                                  ₹{parseFloat(entry.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </Typography>
                              </Box>
                            </AccordionSummary>
                            <AccordionDetails
                              sx={{
                                px: 2.5,
                                pb: 2.5,
                                pt: 0,
                              }}
                            >
                              {entry.description && (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: '#6B7280',
                                    fontSize: '0.8125rem',
                                    mb: 1.5,
                                    lineHeight: 1.5,
                                  }}
                                >
                                  {entry.description}
                                </Typography>
                              )}
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: '#9CA3AF',
                                    fontSize: '0.75rem',
                                  }}
                                >
                                  {entry.created_at ? new Date(entry.created_at).toLocaleDateString('en-IN') : 'N/A'}
                                </Typography>
                                {entry.file && (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: '#2563EB',
                                      fontSize: '0.75rem',
                                      textDecoration: 'underline',
                                      cursor: 'pointer',
                                    }}
                                    onClick={async () => {
                                      if (entry.id) {
                                        try {
                                          const token = localStorage.getItem('token');
                                          const url = `${API_BASE_URL}/funding/usage/file/${entry.id}`;

                                          const response = await fetch(url, {
                                            headers: {
                                              'Authorization': `Bearer ${token}`
                                            }
                                          });

                                          if (!response.ok) {
                                            throw new Error('Failed to load file');
                                          }

                                          const blob = await response.blob();

                                          // Create blob URL with proper type for viewing
                                          const blobUrl = window.URL.createObjectURL(new Blob([blob], { type: blob.type || 'application/pdf' }));

                                          // Open in new tab
                                          const newWindow = window.open(blobUrl, '_blank');

                                          // Check if popup was blocked
                                          if (!newWindow) {
                                            toast.error('Please allow popups to view files');
                                            window.URL.revokeObjectURL(blobUrl);
                                            return;
                                          }

                                          // Revoke URL after document loads (keep it longer for viewing)
                                          setTimeout(() => window.URL.revokeObjectURL(blobUrl), 10000);
                                        } catch (error) {
                                          console.error('Error opening file:', error);
                                          toast.error('Failed to open file');
                                        }
                                      }
                                    }}
                                  >
                                    View Document
                                  </Typography>
                                )}
                              </Box>
                            </AccordionDetails>
                          </Accordion>
                        ))}
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          p: 3,
                          textAlign: 'center',
                          bgcolor: '#F9FAFB',
                          border: '1px solid #E5E7EB',
                          borderRadius: 2,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#9CA3AF',
                            fontSize: '0.875rem',
                          }}
                        >
                          No fund usage entries found.
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              )}
              {dialogConfig.type === 'startupapplicationlist' && (
                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  {startupApplicationListData.length === 0 ? (
                    <Box
                      sx={{
                        p: 3,
                        textAlign: 'center',
                        bgcolor: '#F9FAFB',
                        border: '1px solid #E5E7EB',
                        borderRadius: 2,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#9CA3AF',
                          fontSize: '0.875rem',
                        }}
                      >
                        No startups available.
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      {/* Startup Dropdown */}
                      <TextField
                        select
                        label="Startup"
                        required
                        fullWidth
                        value={fundingForm.startupId || ''}
                        onChange={(e) => {
                          const selectedStartupId = e.target.value;
                          setFundingForm({
                            startupId: selectedStartupId,
                            applicationId: '', // Reset application when startup changes
                            amount: fundingForm.amount
                          });
                          // Fetch applications for selected startup
                          if (selectedStartupId) {
                            fetchApplicationsForStartupInList(selectedStartupId);
                          } else {
                            // Clear applications when no startup selected
                            setStartupApplicationListData(prev =>
                              prev.map(startup => ({ ...startup, applications: [] }))
                            );
                          }
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1.5,
                          },
                          '& .MuiInputLabel-asterisk': {
                            color: '#EF4444',
                          },
                        }}
                      >
                        <MenuItem value="">
                          <em>Select Startup</em>
                        </MenuItem>
                        {startupApplicationListData.map((startup) => (
                          <MenuItem key={startup.id} value={startup.id}>
                            {startup.name || `Startup #${startup.id}`}{startup.email ? ` (${startup.email})` : ''}
                          </MenuItem>
                        ))}
                      </TextField>

                      {/* Application Dropdown (only if startup is selected) */}
                      {fundingForm.startupId && (
                        <TextField
                          select
                          label="Application"
                          required
                          fullWidth
                          value={fundingForm.applicationId || ''}
                          onChange={(e) => {
                            setFundingForm({ ...fundingForm, applicationId: e.target.value });
                          }}
                          disabled={loadingStartupApplications[fundingForm.startupId]}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 1.5,
                            },
                            '& .MuiInputLabel-asterisk': {
                              color: '#EF4444',
                            },
                          }}
                        >
                          <MenuItem value="">
                            <em>Select Application</em>
                          </MenuItem>
                          {loadingStartupApplications[fundingForm.startupId] ? (
                            <MenuItem disabled>Loading applications...</MenuItem>
                          ) : (
                            (() => {
                              const selectedStartup = startupApplicationListData.find(s => s.id === parseInt(fundingForm.startupId));
                              const applications = selectedStartup?.applications || [];

                              if (applications.length === 0) {
                                return (
                                  <MenuItem disabled>
                                    No eligible for fund applications found
                                  </MenuItem>
                                );
                              }

                              return applications.map((app) => (
                                <MenuItem key={app.id} value={app.id}>
                                  {app.program_name || `Application #${app.id}`}
                                  {app.center_name ? ` - ${app.center_name}` : ''}
                                </MenuItem>
                              ));
                            })()
                          )}
                        </TextField>
                      )}
                    </>
                  )}
                </Box>
              )}
              {dialogConfig.type === 'sessiondetails' && (
                <Box sx={{ mt: 2.5 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {/* Startup Name */}
                    {dialogConfig.startupName && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon sx={{ fontSize: '1.1rem', color: '#6B7280' }} />
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#374151',
                            fontSize: '0.85rem',
                            fontWeight: 500,
                          }}
                        >
                          {dialogConfig.startupName}
                        </Typography>
                      </Box>
                    )}

                    {/* Time Range */}
                    {dialogConfig.dateRangeText && (
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <AccessTimeIcon sx={{ fontSize: '1rem', color: '#6B7280', mt: 0.25 }} />
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#374151',
                            fontSize: '0.8125rem',
                          }}
                        >
                          {dialogConfig.dateRangeText}
                        </Typography>
                      </Box>
                    )}

                    {/* Location */}
                    {dialogConfig.location && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LocationOnIcon sx={{ fontSize: '1rem', color: '#6B7280' }} />
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#4B5563',
                            fontSize: '0.8125rem',
                          }}
                        >
                          {dialogConfig.location}
                        </Typography>
                      </Box>
                    )}

                    {/* Description */}
                    {dialogConfig.description && (
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <NotesIcon sx={{ fontSize: '1rem', color: '#6B7280', mt: 0.25 }} />
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#4B5563',
                            fontSize: '0.8125rem',
                          }}
                        >
                          {dialogConfig.description}
                        </Typography>
                      </Box>
                    )}

                    {/* Notes File - Display if notes contains a PDF file */}
                    {dialogConfig.notes && (
                      dialogConfig.notes.startsWith('data:application/pdf;base64,') ? (
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                          <AttachFileIcon sx={{ fontSize: '1rem', color: '#6B7280', mt: 0.25 }} />
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                color: '#4B5563',
                                fontSize: '0.8125rem',
                                fontWeight: 500,
                              }}
                            >
                              Notes File:
                            </Typography>
                            <Button
                              onClick={() => {
                                try {
                                  // Convert base64 data URL to Blob
                                  const base64Data = dialogConfig.notes.split(',')[1];
                                  const byteCharacters = atob(base64Data);
                                  const byteNumbers = new Array(byteCharacters.length);
                                  for (let i = 0; i < byteCharacters.length; i++) {
                                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                                  }
                                  const byteArray = new Uint8Array(byteNumbers);
                                  const blob = new Blob([byteArray], { type: 'application/pdf' });
                                  
                                  // Create blob URL and open in new window
                                  const blobUrl = URL.createObjectURL(blob);
                                  const newWindow = window.open(blobUrl, '_blank');
                                  
                                  // Clean up blob URL after window opens
                                  if (newWindow) {
                                    newWindow.addEventListener('load', () => {
                                      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
                                    });
                                  } else {
                                    // Fallback: if popup blocked, create download link
                                    const link = document.createElement('a');
                                    link.href = blobUrl;
                                    link.target = '_blank';
                                    link.click();
                                    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
                                  }
                                } catch (error) {
                                  console.error('Error opening PDF:', error);
                                  toast.error('Failed to open PDF file');
                                }
                              }}
                              variant="text"
                              size="small"
                              startIcon={<DescriptionIcon sx={{ fontSize: '0.9rem' }} />}
                              sx={{
                                textTransform: 'none',
                                fontSize: '0.8125rem',
                                color: '#2563EB',
                                padding: 0,
                                justifyContent: 'flex-start',
                                '&:hover': {
                                  backgroundColor: 'transparent',
                                  textDecoration: 'underline',
                                },
                              }}
                            >
                              View file
                            </Button>
                          </Box>
                        </Box>
                      ) : dialogConfig.notes.startsWith('Attachment:') ? (
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                          <AttachFileIcon sx={{ fontSize: '1rem', color: '#6B7280', mt: 0.25 }} />
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                color: '#4B5563',
                                fontSize: '0.8125rem',
                                fontWeight: 500,
                              }}
                            >
                              Notes File:
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: '#4B5563',
                                fontSize: '0.8125rem',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                              }}
                            >
                              <DescriptionIcon sx={{ fontSize: '0.9rem', color: '#2563EB' }} />
                              {dialogConfig.notes.replace('Attachment: ', '')}
                            </Typography>
                          </Box>
                        </Box>
                      ) : null
                    )}
                  </Box>
                </Box>
              )}
              {dialogConfig.type === 'quickschedule' && (
                <Box sx={{ mt: 0 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
                    {/* Title (UI only) */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 0.5 }}>
                      <DocumentTextIcon sx={{ fontSize: '1.1rem', color: '#6B7280', mt: 0.5 }} />
                      <TextField
                        fullWidth
                        required
                        placeholder="Add a title"
                        value={quickScheduleForm.title}
                        onChange={(e) => {
                          const newValue = e.target.value;
                          if (newValue.length <= MAX_SESSION_TITLE_LENGTH) {
                            setQuickScheduleForm({ ...quickScheduleForm, title: newValue });
                          }
                          if (quickScheduleErrors.title) {
                            setQuickScheduleErrors({ ...quickScheduleErrors, title: '' });
                          }
                        }}
                        error={!!quickScheduleErrors.title}
                        helperText={
                          quickScheduleErrors.title ||
                          `${(quickScheduleForm.title || '').length}/${MAX_SESSION_TITLE_LENGTH} characters`
                        }
                        variant="standard"
                        InputProps={{
                          disableUnderline: false,
                        }}
                        sx={{
                          '& .MuiInputBase-input': {
                            fontSize: '0.95rem',
                            fontWeight: 500,
                            paddingTop: '4px',
                            paddingBottom: '4px',
                          },
                        }}
                      />
                    </Box>

                    {/* Description (Positioned conditionally based on route) */}
                    {window.location.pathname.includes('/sessions/schedule') && (
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <NotesIcon sx={{ fontSize: '1.1rem', color: '#64748B', mt: 0.5 }} />
                        <TextField
                          fullWidth
                          multiline
                          minRows={1}
                          required
                          variant="standard"
                          value={quickScheduleForm.description}
                          onChange={(e) => {
                            setQuickScheduleForm({
                              ...quickScheduleForm,
                              description: e.target.value,
                            });
                            if (quickScheduleErrors.description) {
                              setQuickScheduleErrors({ ...quickScheduleErrors, description: '' });
                            }
                          }}
                          placeholder="Enter session description..."
                          error={!!quickScheduleErrors.description}
                          helperText={quickScheduleErrors.description || ''}
                          sx={{
                            '& .MuiInputBase-root': {
                              backgroundColor: 'transparent',
                            },
                            '& .MuiInputBase-input, & .MuiInputBase-inputMultiline, & textarea': {
                              paddingTop: '0px',
                              paddingBottom: '0px',
                              minHeight: '30px',
                              fontSize: '0.85rem',
                            },
                          }}
                        />
                      </Box>
                    )}

                    {/* Target Startup - show whenever startupOptions is explicitly provided (even if empty) */}
                    {(dialogConfig.startupOptions && dialogConfig.startupOptions.length > 0) &&
                      dialogConfig.enableStartupSelection !== false && (
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                          <PersonIcon sx={{ fontSize: '1.1rem', color: '#6B7280', mt: 0.5 }} />
                          <TextField
                            select
                            fullWidth
                            required
                            value={quickScheduleForm.targetStartup}
                            onChange={(e) => {
                              setQuickScheduleForm({ ...quickScheduleForm, targetStartup: e.target.value });
                              if (quickScheduleErrors.targetStartup) {
                                setQuickScheduleErrors({ ...quickScheduleErrors, targetStartup: '' });
                              }
                            }}
                            placeholder="Select the Startups"
                            error={!!quickScheduleErrors.targetStartup}
                            helperText={quickScheduleErrors.targetStartup || ''}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 1,
                                backgroundColor: '#FFFFFF',
                              },
                              '& .MuiSelect-select, & .MuiOutlinedInput-input': {
                                paddingTop: '4px',
                                paddingBottom: '4px',
                                minHeight: '30px',
                                fontSize: '0.85rem',
                                lineHeight: '30px',
                                display: 'flex',
                                alignItems: 'center',
                              },
                            }}
                            SelectProps={{
                              displayEmpty: true,
                              renderValue: (selected) => {
                                const baseStyle = { color: '#9CA3AF', fontSize: '0.85rem', width: '100%', display: 'flex', alignItems: 'center' };
                                if (selected == null || selected === '') {
                                  return <span style={baseStyle}>Select the Startups</span>;
                                }
                                const option = (dialogConfig.startupOptions || []).find(opt => String(opt.value) === String(selected));
                                return option ? <span style={{ fontSize: '0.85rem' }}>{option.label}</span> : selected;
                              }
                            }}
                          >
                            {(dialogConfig.startupOptions || []).map((option) => (
                              <MenuItem key={option.value} value={String(option.value)}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Box>
                      )}

                    {/* Description (Original Position fallback) */}
                    {!window.location.pathname.includes('/sessions/schedule') && (
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <NotesIcon sx={{ fontSize: '1.1rem', color: '#64748B', mt: 0.5 }} />
                        <TextField
                          fullWidth
                          multiline
                          minRows={1}
                          required
                          variant="standard"
                          value={quickScheduleForm.description}
                          onChange={(e) => {
                            setQuickScheduleForm({
                              ...quickScheduleForm,
                              description: e.target.value,
                            });
                            if (quickScheduleErrors.description) {
                              setQuickScheduleErrors({ ...quickScheduleErrors, description: '' });
                            }
                          }}
                          placeholder="Enter session description..."
                          error={!!quickScheduleErrors.description}
                          helperText={quickScheduleErrors.description || ''}
                          sx={{
                            '& .MuiInputBase-root': {
                              backgroundColor: 'transparent',
                            },
                            '& .MuiInputBase-input, & .MuiInputBase-inputMultiline, & textarea': {
                              paddingTop: '0px',
                              paddingBottom: '0px',
                              minHeight: '30px',
                              fontSize: '0.85rem',
                            },
                          }}
                        />
                      </Box>
                    )}

                    {/* Start and End Date/Time Rows */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                      <Box sx={{ pt: 1 }}>
                        <AccessTimeIcon sx={{ fontSize: '1.1rem', color: '#6B7280' }} />
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, flex: 1 }}>
                        {/* Mentor Availability Indicator */}
                        {quickScheduleForm.mentorAvailability && (
                          <Box sx={{ mb: 1, p: 1, bgcolor: '#F0F9FF', borderRadius: 1, border: '1px solid #BAE6FD' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#0369A1' }}>
                                Mentor's Availability:
                              </Typography>
                              {quickScheduleForm.mentorAvailability.frequency && (
                                <Typography sx={{ fontSize: '0.65rem', color: '#0284C7', bgcolor: '#E0F2FE', px: 0.8, py: 0.2, borderRadius: 1 }}>
                                  {quickScheduleForm.mentorAvailability.frequency}
                                </Typography>
                              )}
                            </Box>
                            
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                              {(() => {
                                let slots = quickScheduleForm.mentorAvailability.timeSlots || quickScheduleForm.mentorAvailability.preferredTimeSlots || quickScheduleForm.mentorAvailability.preferred_time_slots || [];
                                if (typeof slots === 'string') {
                                  try { slots = JSON.parse(slots); } catch (e) { slots = [slots]; }
                                }
                                if (!Array.isArray(slots)) slots = [];
                                
                                if (slots.length === 0) return <Typography sx={{ fontSize: '0.75rem', color: '#64748B' }}>No specific preferences set</Typography>;
                                
                                const labelMap = {
                                  'wd_morning': 'Weekdays (Morning)',
                                  'wd_afternoon': 'Weekdays (Afternoon)',
                                  'wd_evening': 'Weekdays (Evening)',
                                  'we_morning': 'Weekends (Morning)',
                                  'we_afternoon': 'Weekends (Afternoon)',
                                  'we_evening': 'Weekends (Evening)',
                                  'flexible': 'Flexible'
                                };
                                
                                return slots.map((s, idx) => {
                                  const label = labelMap[s] || labelMap[s.toLowerCase()] || s;
                                  return (
                                    <Box key={idx} sx={{ px: 0.8, py: 0.2, bgcolor: '#FFFFFF', border: '1px solid #E0F2FE', borderRadius: 1, height: 'auto' }}>
                                      <Typography sx={{ fontSize: '0.65rem', color: '#0369A1', fontWeight: 500 }}>{label}</Typography>
                                    </Box>
                                  );
                                });
                              })()}
                            </Box>
                            {quickScheduleForm.mentorAvailability.commitment && (
                              <Typography sx={{ fontSize: '0.65rem', color: '#64748B', mt: 0.5, fontStyle: 'italic' }}>
                                Commitment: {quickScheduleForm.mentorAvailability.commitment}
                              </Typography>
                            )}
                          </Box>
                        )}

                        <Box sx={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr', gap: 0.5 }}>
                          {/* Single Date Selection */}
                          <TextField
                            type="date"
                            fullWidth
                            value={quickScheduleForm.startDate}
                            onChange={(e) => {
                              const newDate = e.target.value;
                              setQuickScheduleForm({
                                ...quickScheduleForm,
                                startDate: newDate,
                                endDate: newDate,
                                startTime: '', // Reset times when date changes
                                endTime: ''
                              });
                            }}
                            variant="outlined"
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 1,
                              },
                              '& .MuiInputBase-input': {
                                paddingTop: '8px',
                                paddingBottom: '8px',
                                fontSize: '0.85rem',
                              },
                            }}
                          />

                          {/* Start Time Select */}
                          <TextField
                            select
                            fullWidth
                            required
                            disabled={!quickScheduleForm.startDate}
                            value={quickScheduleForm.startTime}
                            onChange={(e) => {
                              const newStartTime = e.target.value;
                              setQuickScheduleForm({ ...quickScheduleForm, startTime: newStartTime });
                              if (quickScheduleErrors.startTime) {
                                setQuickScheduleErrors({ ...quickScheduleErrors, startTime: '' });
                              }
                              // Validate end time is after start time
                              if (newStartTime && quickScheduleForm.endTime && quickScheduleForm.endTime <= newStartTime) {
                                setQuickScheduleErrors({ ...quickScheduleErrors, endTime: 'End must be after start' });
                              } else if (quickScheduleErrors.endTime && newStartTime && quickScheduleForm.endTime && newStartTime < quickScheduleForm.endTime) {
                                setQuickScheduleErrors({ ...quickScheduleErrors, endTime: '' });
                              }
                            }}
                            error={!!quickScheduleErrors.startTime}
                            helperText={quickScheduleErrors.startTime || ''}
                            variant="outlined"
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 1,
                              },
                              '& .MuiInputBase-input': {
                                paddingTop: '8px',
                                paddingBottom: '8px',
                                fontSize: '0.85rem',
                              },
                            }}
                            SelectProps={{
                              displayEmpty: true,
                              renderValue: (selected) => {
                                if (!selected) {
                                  return <span style={{ color: '#9CA3AF' }}>Start Time</span>;
                                }
                                // Convert 24h to 12h for display in the box
                                const [h, m] = selected.split(':');
                                const hour = parseInt(h, 10);
                                const min = m;
                                if (hour === 0) return `12:${min} AM`;
                                if (hour < 12) return `${hour}:${min} AM`;
                                if (hour === 12) return `12:${min} PM`;
                                return `${hour - 12}:${min} PM`;
                              },
                              MenuProps: { PaperProps: { style: { maxHeight: 200 } } }
                            }}
                          >
                            {(() => {
                              const isTimeAvailable = (dateStr, timeStr) => {
                                const availability = quickScheduleForm.mentorAvailability;
                                if (!availability) return true;
                                let slots = availability.timeSlots || availability.preferredTimeSlots || [];
                                if (typeof slots === 'string') { try { slots = JSON.parse(slots); } catch (e) { slots = [slots]; } }
                                if (!Array.isArray(slots)) slots = [slots];

                                const isFlexible = slots.some(s => typeof s === 'string' && (s.toLowerCase().includes('flexible') || s.toLowerCase() === 'flexible'));
                                if (slots.length === 0 || isFlexible) return true;
                                if (!dateStr || !timeStr) return true;

                                const parts = dateStr.split('-').map(Number);
                                const d = new Date(parts[0], parts[1]-1, parts[2]);
                                const day = d.getDay();
                                const isWeekend = day === 0 || day === 6;
                                const prefix = isWeekend ? 'we_' : 'wd_';

                                const [h, m] = timeStr.split(':').map(Number);
                                const timeVal = h + m / 60;
                                
                                const potentialSegments = [];
                                if (timeVal >= 8 && timeVal <= 12) potentialSegments.push('morning');
                                if (timeVal >= 12 && timeVal <= 17) potentialSegments.push('afternoon');
                                if (timeVal >= 17 && timeVal <= 20) potentialSegments.push('evening');

                                const labelMap = {
                                  'wd_morning': 'Weekdays (Morning)',
                                  'wd_afternoon': 'Weekdays (Afternoon)',
                                  'wd_evening': 'Weekdays (Evening)',
                                  'we_morning': 'Weekends (Morning)',
                                  'we_afternoon': 'Weekends (Afternoon)',
                                  'we_evening': 'Weekends (Evening)'
                                };

                                return potentialSegments.some(segment => {
                                  const slotId = `${prefix}${segment}`;
                                  const targetLabel = labelMap[slotId]?.toLowerCase();
                                  const targetId = slotId.toLowerCase();
                                  
                                  return slots.some(s => {
                                    if (typeof s !== 'string') return false;
                                    const lower = s.toLowerCase();
                                    return lower === targetId || lower === targetLabel;
                                  });
                                });
                              };

                              const opts = [<MenuItem key="placeholder" value="" disabled sx={{ fontSize: '0.85rem', color: '#9CA3AF' }}>Start Time</MenuItem>];
                              const startHour = 8;
                              const endHour = 20;
                              
                              for (let h = startHour; h <= endHour; h++) {
                                for (let m = 0; m < 60; m += 15) {
                                  if (h === endHour && m > 0) break;
                                  const hourStr = h.toString().padStart(2, '0');
                                  const minStr = m.toString().padStart(2, '0');
                                  const val = `${hourStr}:${minStr}`;
                                  
                                  let label = '';
                                  if (h === 0) label = `12:${minStr} AM`;
                                  else if (h < 12) label = `${h}:${minStr} AM`;
                                  else if (h === 12) label = `12:${minStr} PM`;
                                  else label = `${h - 12}:${minStr} PM`;
                                  
                                  const available = isTimeAvailable(quickScheduleForm.startDate, val);
                                  const disabled = !available;
                                  
                                  opts.push(
                                    <MenuItem key={val} value={val} disabled={disabled} sx={{ fontSize: '0.85rem' }}>
                                      {label}
                                    </MenuItem>
                                  );
                                }
                              }
                              return opts;
                            })()}
                          </TextField>

                          {/* End Time Select */}
                          <TextField
                            select
                            fullWidth
                            required
                            disabled={!quickScheduleForm.startDate}
                            value={quickScheduleForm.endTime}
                            onChange={(e) => {
                              const newEndTime = e.target.value;
                              setQuickScheduleForm({ ...quickScheduleForm, endTime: newEndTime });
                              // Validate end time is after start time
                              if (quickScheduleForm.startTime && newEndTime && newEndTime <= quickScheduleForm.startTime) {
                                setQuickScheduleErrors({ ...quickScheduleErrors, endTime: 'End must be after start' });
                              } else if (quickScheduleErrors.endTime) {
                                setQuickScheduleErrors({ ...quickScheduleErrors, endTime: '' });
                              }
                            }}
                            error={!!quickScheduleErrors.endTime}
                            helperText={quickScheduleErrors.endTime || ''}
                            variant="outlined"
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 1,
                              },
                              '& .MuiInputBase-input': {
                                paddingTop: '8px',
                                paddingBottom: '8px',
                                fontSize: '0.85rem',
                              },
                            }}
                            SelectProps={{
                              displayEmpty: true,
                              renderValue: (selected) => {
                                if (!selected) {
                                  return <span style={{ color: '#9CA3AF' }}>End Time</span>;
                                }
                                // Convert 24h to 12h for display in the box
                                const [h, m] = selected.split(':');
                                const hour = parseInt(h, 10);
                                const min = m;
                                if (hour === 0) return `12:${min} AM`;
                                if (hour < 12) return `${hour}:${min} AM`;
                                if (hour === 12) return `12:${min} PM`;
                                return `${hour - 12}:${min} PM`;
                              },
                              MenuProps: { PaperProps: { style: { maxHeight: 200 } } }
                            }}
                          >
                            {(() => {
                              const isTimeAvailable = (dateStr, timeStr) => {
                                const availability = quickScheduleForm.mentorAvailability;
                                if (!availability) return true;
                                let slots = availability.timeSlots || availability.preferredTimeSlots || [];
                                if (typeof slots === 'string') { try { slots = JSON.parse(slots); } catch (e) { slots = [slots]; } }
                                if (!Array.isArray(slots)) slots = [slots];

                                const isFlexible = slots.some(s => typeof s === 'string' && (s.toLowerCase().includes('flexible') || s.toLowerCase() === 'flexible'));
                                if (slots.length === 0 || isFlexible) return true;
                                if (!dateStr || !timeStr) return true;

                                const parts = dateStr.split('-').map(Number);
                                const d = new Date(parts[0], parts[1]-1, parts[2]);
                                const day = d.getDay();
                                const isWeekend = day === 0 || day === 6;
                                const prefix = isWeekend ? 'we_' : 'wd_';

                                const [h, m] = timeStr.split(':').map(Number);
                                const timeVal = h + m / 60;
                                
                                const potentialSegments = [];
                                if (timeVal >= 8 && timeVal <= 12) potentialSegments.push('morning');
                                if (timeVal >= 12 && timeVal <= 17) potentialSegments.push('afternoon');
                                if (timeVal >= 17 && timeVal <= 20) potentialSegments.push('evening');

                                const labelMap = {
                                  'wd_morning': 'Weekdays (Morning)',
                                  'wd_afternoon': 'Weekdays (Afternoon)',
                                  'wd_evening': 'Weekdays (Evening)',
                                  'we_morning': 'Weekends (Morning)',
                                  'we_afternoon': 'Weekends (Afternoon)',
                                  'we_evening': 'Weekends (Evening)'
                                };

                                return potentialSegments.some(segment => {
                                  const slotId = `${prefix}${segment}`;
                                  const targetLabel = labelMap[slotId]?.toLowerCase();
                                  const targetId = slotId.toLowerCase();
                                  
                                  return slots.some(s => {
                                    if (typeof s !== 'string') return false;
                                    const lower = s.toLowerCase();
                                    return lower === targetId || lower === targetLabel;
                                  });
                                });
                              };

                              const opts = [<MenuItem key="placeholder" value="" disabled sx={{ fontSize: '0.85rem', color: '#9CA3AF' }}>End Time</MenuItem>];
                              const startHour = 8;
                              const endHour = 20;
                              
                              for (let h = startHour; h <= endHour; h++) {
                                for (let m = 0; m < 60; m += 15) {
                                  if (h === endHour && m > 0) break;
                                  const hourStr = h.toString().padStart(2, '0');
                                  const minStr = m.toString().padStart(2, '0');
                                  const val = `${hourStr}:${minStr}`;
                                  
                                  let label = '';
                                  if (h === 0) label = `12:${minStr} AM`;
                                  else if (h < 12) label = `${h}:${minStr} AM`;
                                  else if (h === 12) label = `12:${minStr} PM`;
                                  else label = `${h - 12}:${minStr} PM`;
                                  
                                  const available = isTimeAvailable(quickScheduleForm.startDate, val);
                                  const disabled = !available;
                                  
                                  opts.push(
                                    <MenuItem key={val} value={val} disabled={disabled} sx={{ fontSize: '0.85rem' }}>
                                      {label}
                                    </MenuItem>
                                  );
                                }
                              }
                              return opts;
                            })()}
                          </TextField>
                        </Box>
                      </Box>
                    </Box>

                    {/* Location */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <LocationOnIcon sx={{ fontSize: '1.1rem', color: '#6B7280', mt: 0.5 }} />
                      <TextField
                        fullWidth
                        multiline
                        minRows={1}
                        value={quickScheduleForm.location}
                        onChange={(e) =>
                          setQuickScheduleForm({
                            ...quickScheduleForm,
                            location: e.target.value,
                          })
                        }
                        placeholder="Enter location (e.g., Conference Room A, Office address)"
                        variant="standard"
                        sx={{
                          '& .MuiInputBase-root': {
                            backgroundColor: 'transparent',
                          },
                          '& .MuiInputBase-input': {
                            paddingTop: '0px',
                            paddingBottom: '0px',
                            minHeight: '30px',
                            fontSize: '0.85rem',
                          },
                        }}
                      />
                    </Box>



                    {/* Meeting link */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <LinkIcon sx={{ fontSize: '1.1rem', color: '#6B7280', mt: 0.5 }} />
                      <TextField
                        fullWidth
                        type="url"
                        value={quickScheduleForm.link}
                        onChange={(e) => {
                          setQuickScheduleForm({
                            ...quickScheduleForm,
                            link: e.target.value,
                          });
                        }}
                        onBlur={(e) => {
                          let linkValue = e.target.value?.trim() || '';
                          if (linkValue && !/^https?:\/\//i.test(linkValue)) {
                            // Auto-add https:// if not present
                            linkValue = 'https://' + linkValue;
                            setQuickScheduleForm({
                              ...quickScheduleForm,
                              link: linkValue,
                            });
                          }
                        }}
                        placeholder="meet.google.com/..."
                        error={!!(quickScheduleForm.link && quickScheduleForm.link.trim() && !/^https?:\/\/.+\..+/i.test(quickScheduleForm.link.trim()))}
                        helperText={quickScheduleForm.link && quickScheduleForm.link.trim() && !/^https?:\/\/.+\..+/i.test(quickScheduleForm.link.trim()) ? 'Please enter a valid URL (e.g., meet.google.com/abc-xyz)' : ''}
                        variant="standard"
                        sx={{
                          '& .MuiInputBase-root': {
                            backgroundColor: 'transparent',
                          },
                          '& .MuiInputBase-input': {
                            paddingTop: '0px',
                            paddingBottom: '0px',
                            minHeight: '30px',
                            fontSize: '0.85rem',
                          },
                        }}
                      />
                    </Box>

                    {/* Notes - File Upload (PDF only, max 10MB) */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <AttachFileIcon sx={{ fontSize: '1.1rem', color: '#6B7280', mt: 1 }} />
                      <Box sx={{ flex: 1 }}>
                        <Box
                          sx={{
                            border: '1px dashed #E2E8F0',
                            borderRadius: 2,
                            p: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            backgroundColor: '#F8FAFC',
                            transition: 'all 0.2s',
                            '&:hover': {
                              borderColor: '#2563EB',
                              backgroundColor: '#F1F5F9',
                            }
                          }}
                        >
                          <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', gap: quickScheduleForm.attachmentFileName ? 1.5 : 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', justifyContent: 'space-between' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                                <Button
                                  component="label"
                                  variant="contained"
                                  size="small"
                                  sx={{
                                    textTransform: 'none',
                                    fontSize: '0.75rem',
                                    paddingX: 1.5,
                                    py: 0.6,
                                    borderRadius: 1.5,
                                    backgroundColor: 'var(--an-coral)',
                                    boxShadow: 'none',
                                    '&:hover': {
                                      backgroundColor: 'var(--an-coral-deep)',
                                      boxShadow: 'none',
                                    },
                                    flexShrink: 0
                                  }}
                                >
                                  {quickScheduleForm.attachmentFileName ? 'Change' : 'Choose File'}
                                  <input
                                    type="file"
                                    hidden
                                    accept=".pdf,application/pdf"
                                    onChange={(e) => {
                                      const file = e.target.files?.[0];
                                      if (file) {
                                        if (file.type !== 'application/pdf') {
                                          setQuickScheduleErrors({ ...quickScheduleErrors, attachmentFile: 'Only PDF files are allowed' });
                                          e.target.value = '';
                                          return;
                                        }
                                        const maxSizeInBytes = 10 * 1024 * 1024;
                                        if (file.size > maxSizeInBytes) {
                                          setQuickScheduleErrors({ ...quickScheduleErrors, attachmentFile: 'File size must not exceed 10MB' });
                                          e.target.value = '';
                                          return;
                                        }
                                        setQuickScheduleErrors({ ...quickScheduleErrors, attachmentFile: '' });
                                        setQuickScheduleForm({ ...quickScheduleForm, attachmentFileName: file.name, attachmentFile: file });
                                      }
                                    }}
                                  />
                                </Button>

                                {!quickScheduleForm.attachmentFileName && (
                                  <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#94A3B8', flex: 1 }}>
                                    No file selected
                                  </Typography>
                                )}
                              </Box>
                              
                              <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#94A3B8', fontStyle: 'italic', flexShrink: 0 }}>
                                PDF, Max 10MB
                              </Typography>
                            </Box>

                            {quickScheduleForm.attachmentFileName && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', bgcolor: '#EFF6FF', px: 1.5, py: 1, borderRadius: 1.5, border: '1px solid #DBEAFE' }}>
                                <DescriptionIcon sx={{ fontSize: '1.1rem', color: '#2563EB', flexShrink: 0 }} />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontSize: '0.8rem',
                                      color: '#1E40AF',
                                      fontWeight: 600,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap'
                                    }}
                                  >
                                    {quickScheduleForm.attachmentFileName}
                                  </Typography>
                                </Box>
                                <IconButton 
                                  size="small" 
                                  onClick={() => setQuickScheduleForm({ ...quickScheduleForm, attachmentFileName: '', attachmentFile: null })}
                                  sx={{ p: 0.5, color: '#93C5FD', '&:hover': { color: '#ef4444', bgcolor: '#fee2e2' } }}
                                >
                                  <CloseIcon sx={{ fontSize: '1rem' }} />
                                </IconButton>
                              </Box>
                            )}
                          </Box>
                        {quickScheduleErrors.attachmentFile && (
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: '0.7rem',
                              color: '#DC2626',
                              fontWeight: 500,
                              mt: 0.5,
                              display: 'block'
                            }}
                          >
                            {quickScheduleErrors.attachmentFile}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>

                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        width: '100%',
                        mt: 1
                      }}
                    >
                      <Button
                        onClick={handleConfirm}
                        variant="contained"
                        size="small"
                        startIcon={<SaveIcon fontSize="small" />}
                        sx={{
                          backgroundColor: 'var(--an-coral)',
                          color: '#ffffff',
                          textTransform: 'none',
                          borderRadius: 1,
                          px: 2,
                          py: 0.5,
                          fontSize: '0.8125rem',
                          fontWeight: 600,
                          boxShadow: 'none',
                          '&:hover': {
                            backgroundColor: 'var(--an-coral-deep)',
                            boxShadow: 'none',
                          },
                        }}
                      >
                        Schedule
                      </Button>
                    </Box>

                    {/* Buffer Info - Always Applied (UI hidden as per latest requirements)
                    <Box sx={{ p: 2, bgcolor: '#EFF6FF', borderRadius: 1.5, border: '1px solid #BFDBFE' }}>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem', color: '#1D4ED8', fontWeight: 500 }}>
                        ℹ️ A 15-minute buffer is automatically added after each session
                      </Typography>
                    </Box>
                    */}
                  </Box>
                </Box>
              )}
              {dialogConfig.type === 'addexpense' && (
                <Box sx={{ mt: 0 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    {/* Expense Type */}
                    <Box>
                      <Typography
                        variant="overline"
                        sx={{
                          color: '#64748B',
                          mb: 1.5,
                          fontWeight: 700,
                          fontSize: '0.75rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.1em',
                          display: 'block',
                        }}
                      >
                        EXPENSE TYPE
                        <span style={{ color: '#d32f2f', marginLeft: '4px' }}>*</span>
                      </Typography>
                      <TextField
                        select
                        fullWidth
                        required
                        value={expenseForm.expense_type}
                        onChange={(e) => setExpenseForm({ ...expenseForm, expense_type: e.target.value })}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1.5,
                            backgroundColor: '#FFFFFF',
                          },
                        }}
                      >
                        <MenuItem value="travel">Travel</MenuItem>
                        <MenuItem value="venue">Venue</MenuItem>
                        <MenuItem value="equipment">Equipment</MenuItem>
                        <MenuItem value="other">Other</MenuItem>
                      </TextField>
                    </Box>

                    {/* Currency */}
                    <Box>
                      <Typography
                        variant="overline"
                        sx={{
                          color: '#64748B',
                          mb: 1.5,
                          fontWeight: 700,
                          fontSize: '0.75rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.1em',
                          display: 'block',
                        }}
                      >
                        CURRENCY
                      </Typography>
                      <TextField
                        select
                        fullWidth
                        value={expenseForm.currency}
                        onChange={(e) => setExpenseForm({ ...expenseForm, currency: e.target.value })}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1.5,
                            backgroundColor: '#FFFFFF',
                          },
                        }}
                      >
                        {Object.entries(EXPENSE_CURRENCIES).map(([code, { name, symbol }]) => (
                          <MenuItem key={code} value={code}>
                            {code} - {name} ({symbol})
                          </MenuItem>
                        ))}
                      </TextField>
                    </Box>

                    {/* Amount */}
                    <Box>
                      <Typography
                        variant="overline"
                        sx={{
                          color: '#64748B',
                          mb: 1.5,
                          fontWeight: 700,
                          fontSize: '0.75rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.1em',
                          display: 'block',
                        }}
                      >
                        AMOUNT
                        <span style={{ color: '#d32f2f', marginLeft: '4px' }}>*</span>
                      </Typography>
                      <TextField
                        fullWidth
                        required
                        type="number"
                        value={expenseForm.amount}
                        onChange={(e) => {
                          const value = e.target.value;
                          // Prevent negative numbers
                          if (value === '' || (parseFloat(value) >= 0 && !isNaN(parseFloat(value)))) {
                            setExpenseForm({ ...expenseForm, amount: value });
                          }
                        }}
                        placeholder="0.00"
                        inputProps={{
                          min: 0,
                          step: 0.01,
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Typography sx={{ color: '#6B7280', fontWeight: 500 }}>
                                {EXPENSE_CURRENCIES[expenseForm.currency]?.symbol || expenseForm.currency}
                              </Typography>
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1.5,
                            backgroundColor: '#FFFFFF',
                          },
                        }}
                      />
                    </Box>

                    {/* Description */}
                    <Box>
                      <Typography
                        variant="overline"
                        sx={{
                          color: '#64748B',
                          mb: 1.5,
                          fontWeight: 700,
                          fontSize: '0.75rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.1em',
                          display: 'block',
                        }}
                      >
                        DESCRIPTION
                        <span style={{ color: '#d32f2f', marginLeft: '4px' }}>*</span>
                      </Typography>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        value={expenseForm.description}
                        onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })}
                        placeholder="e.g., Taxi to innovation hub"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1.5,
                            backgroundColor: '#FFFFFF',
                          },
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
              )}
              {dialogConfig.type === 'assignmentaction' && (
                <Box sx={{ mt: 1 }}>
                  {/* Assignment Information Header Box */}
                  {dialogConfig.assignment && (
                    <Box
                      sx={{
                        mb: 3.5,
                        p: 2.5,
                        bgcolor: '#FFFFFF',
                        borderRadius: 2.5,
                        border: '1px solid #E2E8F0',
                        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                      }}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Typography variant="body2" sx={{ color: '#64748B', fontWeight: 500 }}>
                            Assignment:
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#0F172A', fontWeight: 700 }}>
                            {dialogConfig.assignment.startup_name}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Typography variant="body2" sx={{ color: '#64748B', fontWeight: 500 }}>
                            Current Mentor:
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#0F172A', fontWeight: 700 }}>
                            {dialogConfig.assignment.mentor_first_name} {dialogConfig.assignment.mentor_last_name}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3.5 }}>
                    {/* Action Selection */}
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#334155' }}>
                          Action <span style={{ color: '#EF4444' }}>*</span>
                        </Typography>
                      </Box>
                      <FormControl fullWidth error={!!assignmentActionErrors.selectedAction}>
                        <Select
                          value={assignmentActionForm.selectedAction}
                          displayEmpty
                          onChange={(e) => {
                            const action = e.target.value;
                            setAssignmentActionForm(prev => ({
                              ...prev,
                              selectedAction: action,
                              reassignMentorId: action === 'reassign' ? prev.reassignMentorId : '',
                              escalationReason: action === 'escalate' ? prev.escalationReason : '',
                            }));
                            if (assignmentActionErrors.selectedAction) {
                              setAssignmentActionErrors(prev => ({ ...prev, selectedAction: '' }));
                            }
                          }}
                          sx={{
                            bgcolor: '#F8FAFC',
                            borderRadius: 2.5,
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E2E8F0' },
                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#CBD5E1' },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#2563EB' },
                          }}
                        >
                          <MenuItem key="placeholder" value="" disabled>
                            <span style={{ color: '#94A3B8' }}>Select an action</span>
                          </MenuItem>
                          <MenuItem key="reassign" value="reassign">Reassign Mentor</MenuItem>
                          <MenuItem key="escalate" value="escalate">Escalate Assignment</MenuItem>
                        </Select>
                        {assignmentActionErrors.selectedAction && (
                          <FormHelperText sx={{ mt: 1, ml: 1 }}>{assignmentActionErrors.selectedAction}</FormHelperText>
                        )}
                      </FormControl>
                    </Box>

                    {/* Reassign Mentor Selection */}
                    {assignmentActionForm.selectedAction === 'reassign' && (
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                          <AssignmentIndIcon sx={{ fontSize: '1.1rem', color: '#6366F1' }} />
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#334155' }}>
                            Select New Mentor <span style={{ color: '#EF4444' }}>*</span>
                          </Typography>
                        </Box>
                        <FormControl fullWidth error={!!assignmentActionErrors.reassignMentorId} disabled={loadingMentors}>
                          <Select
                            value={assignmentActionForm.reassignMentorId}
                            displayEmpty
                            onChange={(e) => {
                              setAssignmentActionForm(prev => ({ ...prev, reassignMentorId: e.target.value }));
                              if (assignmentActionErrors.reassignMentorId) {
                                setAssignmentActionErrors(prev => ({ ...prev, reassignMentorId: '' }));
                              }
                            }}
                            sx={{
                              bgcolor: '#F8FAFC',
                              borderRadius: 2.5,
                              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E2E8F0' },
                              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#CBD5E1' },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#2563EB' },
                            }}
                          >
                            <MenuItem key="placeholder-mentor" value="" disabled>
                              <span style={{ color: '#94A3B8' }}>{loadingMentors ? 'Loading mentors...' : 'Select a mentor'}</span>
                            </MenuItem>
                            {availableMentors
                              .filter(mentor => {
                                const mentorId = mentor.mentor_id || mentor.id;
                                const currentMentorId = dialogConfig.assignment?.mentor_id;
                                return mentorId && currentMentorId && mentorId !== currentMentorId;
                              })
                              .map((mentor) => {
                                const mentorId = mentor.mentor_id || mentor.id;
                                const mentorName = mentor.name || mentor.email || `Mentor #${mentorId}`;
                                const mentorEmail = mentor.email ? ` (${mentor.email})` : '';
                                return (
                                  <MenuItem key={mentorId} value={String(mentorId)}>
                                    {mentorName}{mentorEmail}
                                  </MenuItem>
                                );
                              })}
                          </Select>
                          {assignmentActionErrors.reassignMentorId && (
                            <FormHelperText sx={{ mt: 1, ml: 1 }}>{assignmentActionErrors.reassignMentorId}</FormHelperText>
                          )}
                        </FormControl>
                      </Box>
                    )}

                    {/* Escalation Reason */}
                    {assignmentActionForm.selectedAction === 'escalate' && (
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                          <ExclamationTriangleIcon sx={{ fontSize: '1.1rem', color: '#EF4444' }} />
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#334155' }}>
                            Escalation Reason <span style={{ color: '#EF4444' }}>*</span>
                          </Typography>
                        </Box>
                        <TextField
                          fullWidth
                          multiline
                          rows={4}
                          value={assignmentActionForm.escalationReason}
                          onChange={(e) => {
                            setAssignmentActionForm({ ...assignmentActionForm, escalationReason: e.target.value });
                            if (assignmentActionErrors.escalationReason) {
                              setAssignmentActionErrors({ ...assignmentActionErrors, escalationReason: '' });
                            }
                          }}
                          error={!!assignmentActionErrors.escalationReason}
                          helperText={assignmentActionErrors.escalationReason}
                          placeholder="Describe the issue requiring escalation..."
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              bgcolor: '#FFF5F5',
                              borderRadius: 2.5,
                              '& fieldset': { borderColor: '#FEE2E2' },
                              '&:hover fieldset': { borderColor: '#FECACA' },
                              '&.Mui-focused fieldset': { borderColor: '#EF4444' },
                            }
                          }}
                        />
                      </Box>
                    )}
                  </Box>
                </Box>
              )}
              {dialogConfig.type === 'confirmassignment' && (
                <Box sx={{ mt: 1 }}>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3.5 }}>
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <FactCheckIcon sx={{ fontSize: '1.1rem', color: '#2563EB' }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#334155' }}>
                          Assignment Goals <span style={{ color: '#EF4444' }}>*</span>
                        </Typography>
                      </Box>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        placeholder="What are the key objectives for this mentorship? (e.g., Scaling sales, Product-Market Fit...)"
                        value={assignmentForm.goals}
                        onChange={(e) => {
                          setAssignmentForm({ ...assignmentForm, goals: e.target.value });
                          if (assignmentFormErrors.goals) {
                            setAssignmentFormErrors({ ...assignmentFormErrors, goals: '' });
                          }
                        }}
                        error={!!assignmentFormErrors.goals}
                        helperText={assignmentFormErrors.goals}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            bgcolor: '#F8FAFC',
                            borderRadius: 2.5,
                            '& fieldset': { borderColor: '#E2E8F0' },
                            '&:hover fieldset': { borderColor: '#CBD5E1' },
                            '&.Mui-focused fieldset': { borderColor: '#2563EB' },
                          }
                        }}
                      />
                    </Box>

                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <LightbulbIcon sx={{ fontSize: '1.1rem', color: '#F59E0B' }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#334155' }}>
                          Assignment Reason / Notes
                        </Typography>
                      </Box>
                      <TextField
                        fullWidth
                        multiline
                        rows={2}
                        placeholder="Why is this a good match?"
                        value={assignmentForm.reason}
                        onChange={(e) => setAssignmentForm({ ...assignmentForm, reason: e.target.value })}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            bgcolor: '#F8FAFC',
                            borderRadius: 2.5,
                            '& fieldset': { borderColor: '#E2E8F0' },
                            '&:hover fieldset': { borderColor: '#CBD5E1' },
                            '&.Mui-focused fieldset': { borderColor: '#2563EB' },
                          }
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
              )}
              {dialogConfig.type === 'assignmentdetails' && (
                <Box sx={{ mt: 1 }}>
                  {dialogConfig.assignment && (
                    <Box sx={{ p: 1 }}>
                      {/* Action Inputs for Completion */}
                      <Box sx={{ mb: 0, p: 3, bgcolor: '#FFFFFF', borderRadius: 2, border: '1px solid #E5E7EB' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
                          <ChartBarIcon sx={{ fontSize: '1.25rem', color: '#2563EB' }} />
                          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#111827' }}>
                            Completion Feedback
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                          <Box>
                            <Typography variant="body2" sx={{ color: '#374151', mb: 1, fontWeight: 600 }}>
                              Completion Rating (1-5) <span style={{ color: '#EF4444' }}>*</span>
                            </Typography>
                            <TextField
                              type="number"
                              inputProps={{ min: 1, max: 5 }}
                              value={assignmentDetailsState.completionRating}
                              onChange={(e) => setAssignmentDetailsState({ ...assignmentDetailsState, completionRating: Number(e.target.value) })}
                              sx={{
                                width: 100,
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 1.5,
                                  backgroundColor: '#FFFFFF',
                                },
                              }}
                            />
                          </Box>
                          <Box>
                            <Typography variant="body2" sx={{ color: '#374151', mb: 1, fontWeight: 600 }}>
                              Feedback Comment (original)
                            </Typography>
                            <TextField
                              fullWidth
                              multiline
                              rows={3}
                              value={assignmentDetailsState.completionComment}
                              onChange={(e) => setAssignmentDetailsState({ ...assignmentDetailsState, completionComment: e.target.value })}
                              placeholder="Any final notes or feedback"
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 1.5,
                                  backgroundColor: '#FFFFFF',
                                },
                              }}
                            />
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
              {dialogConfig.type === 'declineassignment' && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" sx={{ color: '#6B7280', mb: 3 }}>
                    {dialogConfig.message || 'Please provide a reason for declining this assignment. This will be shared with the administrator.'}
                  </Typography>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#374151',
                        fontWeight: 600,
                        fontSize: '0.875rem',
                      }}
                    >
                      Reason for declining <span style={{ color: '#EF4444' }}>*</span>
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      value={declineForm.reason}
                      onChange={(e) => {
                        setDeclineForm({ reason: e.target.value });
                        if (declineFormErrors.reason) {
                          setDeclineFormErrors({ ...declineFormErrors, reason: '' });
                        }
                      }}
                      error={!!declineFormErrors.reason}
                      helperText={declineFormErrors.reason}
                      placeholder="Reason for declining..."
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          backgroundColor: '#F9FAFB',
                          '& fieldset': {
                            borderColor: '#E5E7EB',
                          },
                          '&:hover fieldset': {
                            borderColor: '#D1D5DB',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#2563EB',
                          },
                        },
                      }}
                    />
                  </Box>
                </Box>
              )}
            </DialogContent>


            {dialogConfig.type !== 'rating' && dialogConfig.type !== 'scores' && dialogConfig.type !== 'funding' && dialogConfig.type !== 'fundinghistory' && dialogConfig.type !== 'mentordetails' && dialogConfig.type !== 'mentorverify' && dialogConfig.type !== 'mentorrejectionnotes' && dialogConfig.type !== 'assignmentdetails' && dialogConfig.type !== 'startupdetails' && dialogConfig.type !== 'editexpertise' && dialogConfig.type !== 'editindustries' && dialogConfig.type !== 'manageachievements' && dialogConfig.type !== 'managespeaking' && dialogConfig.type !== 'managepublications' && dialogConfig.type !== 'manageavailability' && dialogConfig.type !== 'quickschedule' && dialogConfig.type !== 'sessiondetails' && (
              <DialogActions
                sx={{
                  px: { xs: 2, sm: 3 },
                  py: { xs: 2, sm: 2.5 },
                  gap: { xs: 1.5, sm: 1.5 },
                  backgroundColor: '#ffffff',
                  borderTop: '1px solid #F3F4F6',
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'flex-end',
                  alignItems: 'stretch',
                  mt: 'auto',
                }}
              >
                {dialogConfig.type !== 'alert' && dialogConfig.showCancel !== false && dialogConfig.type !== 'addfunding' && dialogConfig.type !== 'fundusage' && dialogConfig.type !== 'paymentmethod' && dialogConfig.type !== 'fundinghistory' && dialogConfig.type !== 'assignmentaction' && dialogConfig.type !== 'addfundingdocuments' && (
                  <Button
                    onClick={handleCancel}
                    variant="text"
                    fullWidth
                    sx={{
                      minWidth: { xs: '100%', sm: 100 },
                      textTransform: 'none',
                      borderRadius: '12px',
                      px: 4,
                      py: 1.5,
                      backgroundColor: '#F1F5F9',
                      color: '#475569',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      border: 'none',
                      textAlign: 'center',
                      '&:hover': {
                        backgroundColor: '#E2E8F0',
                      },
                    }}
                  >
                    {dialogConfig.cancelText}
                  </Button>
                )}
                {(() => {
                  const isJuryAssignDisabled = dialogConfig.type === 'juryassign' && (selectedValues.length === 0 || !deadlineDate || !deadlineDate.trim());
                  const button = (
                    <Button
                      onClick={dialogConfig.type === 'startupapplicationlist' ? () => {
                        // Validate that both startup and application are selected
                        if (!fundingForm.startupId) {
                          toast.error('Please select a startup');
                          return;
                        }
                        if (!fundingForm.applicationId) {
                          toast.error('Please select an application');
                          return;
                        }

                        // Call onConfirm callback if provided
                        if (dialogConfig.onConfirm) {
                          dialogConfig.onConfirm({
                            applicationId: fundingForm.applicationId,
                            startupId: fundingForm.startupId
                          });
                        }

                        // Close dialog with result
                        closeDialog({
                          applicationId: fundingForm.applicationId,
                          startupId: fundingForm.startupId
                        });
                      } : handleConfirm}
                      variant="contained"
                      fullWidth={dialogConfig.showCancel !== false && dialogConfig.type !== 'addfunding' && dialogConfig.type !== 'fundusage' && dialogConfig.type !== 'paymentmethod' && dialogConfig.type !== 'fundinghistory' && dialogConfig.type !== 'assignmentaction' && dialogConfig.type !== 'startupapplicationlist'}
                      disabled={
                        (dialogConfig.type === 'rating' && ratingValue === 0) ||
                        (dialogConfig.type === 'multiselect' && selectedValues.length === 0) ||
                        (dialogConfig.type === 'juryassign' && (selectedValues.length === 0 || !deadlineDate || !deadlineDate.trim())) ||
                        (dialogConfig.type === 'addjury' && submittingJury) ||
                        (dialogConfig.type === 'addfunding' && (submittingFunding || (() => {
                          // For custom installments, check if remaining is 0
                          if (fundingForm.paymentType === 'installment' && fundingForm.installmentType === 'custom') {
                            const totalAmount = parseFloat(fundingForm.amount) || 0;
                            const installmentSum = fundingForm.customInstallments.reduce((sum, inst) => {
                              const amount = parseFloat(inst.amount) || 0;
                              return sum + amount;
                            }, 0);
                            const remaining = totalAmount - installmentSum;
                            if (Math.abs(remaining) > 0.01) return true; // remaining must be 0
                          }
                          // Hard block when the requested amount exceeds the fund's
                          // available balance — but ONLY when the fund tracks a
                          // positive available balance. 0 / untracked means no
                          // balance restriction (admin adds funding as milestones).
                          if (fundingForm.fundOfferingId) {
                            const offering = fundOfferingsList.find((o) => String(o.id) === String(fundingForm.fundOfferingId));
                            const available = Number(offering?.amount_available);
                            const value = parseFloat(fundingForm.amount);
                            if (Number.isFinite(available) && available > 0 && Number.isFinite(value) && value > available) {
                              return true;
                            }
                          }
                          return false;
                        })())) ||
                        (dialogConfig.type === 'fundusage' && submittingFundUsage) ||
                        (dialogConfig.type === 'paymentmethod' && (submittingPaymentMethod || (() => {
                          // If payment type is not selected, disable button
                          if (!paymentMethodForm.paymentType) {
                            return true;
                          }
                          // If installment type, check if remaining is 0.00
                          if (paymentMethodForm.paymentType === 'installment') {
                            const totalAmount = parseFloat(dialogConfig.defaultAmount || 0);
                            const installmentsSum = paymentMethodForm.customInstallments.reduce((sum, inst) => {
                              return sum + (parseFloat(inst.amount) || 0);
                            }, 0);
                            const remaining = totalAmount - installmentsSum;
                            // Disable if remaining is not 0.00 (with small tolerance for floating point)
                            if (Math.abs(remaining) > 0.01) {
                              return true;
                            }
                            // Also check if all installments have valid amount and date
                            const hasInvalidInstallments = paymentMethodForm.customInstallments.some(inst => {
                              return !inst.amount || parseFloat(inst.amount) <= 0 || !inst.date;
                            });
                            if (hasInvalidInstallments) {
                              return true;
                            }
                          }
                          return false;
                        })())) ||
                        (dialogConfig.type === 'addfundingdocuments' && (submittingFundingDocuments || !fundingDocumentsForm.documents || fundingDocumentsForm.documents.length === 0)) ||
                        (dialogConfig.type === 'assignmentaction' && (!assignmentActionForm.selectedAction ||
                          (assignmentActionForm.selectedAction === 'reassign' && !assignmentActionForm.reassignMentorId) ||
                          (assignmentActionForm.selectedAction === 'escalate' && !assignmentActionForm.escalationReason.trim()))) ||
                        (dialogConfig.type === 'startupapplicationlist' && (!fundingForm.startupId || !fundingForm.applicationId))
                      }
                      sx={{
                        minWidth: { xs: '100%', sm: 120 },
                        textTransform: 'none',
                        borderRadius: '12px',
                        px: 4,
                        py: 1.5,
                        fontWeight: 700,
                        fontSize: '0.875rem',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                        '&.Mui-disabled': {
                          backgroundColor: '#F1F5F9',
                          color: '#94A3B8',
                        },
                        backgroundColor: dialogConfig.destructive || dialogConfig.type === 'declineassignment' || (dialogConfig.type === 'assignmentaction' && assignmentActionForm.selectedAction === 'escalate') ? '#C4453C' : 'var(--an-coral)',
                        color: 'var(--an-on-coral, #1A0E0B)',
                        '&:hover': {
                          backgroundColor: dialogConfig.destructive || dialogConfig.type === 'declineassignment' || (dialogConfig.type === 'assignmentaction' && assignmentActionForm.selectedAction === 'escalate') ? '#A23830' : 'var(--an-coral-deep)',
                          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                          transform: 'translateY(-1px)',
                        },
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      }}
                    >
                      {dialogConfig.type === 'addjury' && submittingJury ? 'Creating...' :
                        dialogConfig.type === 'addfunding' && submittingFunding ? 'Adding...' :
                          dialogConfig.type === 'fundusage' && submittingFundUsage ? 'Adding...' :
                            dialogConfig.type === 'paymentmethod' && submittingPaymentMethod ? 'Updating...' :
                              dialogConfig.type === 'addfundingdocuments' && submittingFundingDocuments ? 'Adding...' :
                                dialogConfig.type === 'assignmentaction' ?
                                  (assignmentActionForm.selectedAction === 'escalate' ? 'Submit Escalation' :
                                    assignmentActionForm.selectedAction === 'reassign' ? 'Confirm Reassign' :
                                      'Confirm') :
                                  dialogConfig.type === 'declineassignment' ? 'Decline Assignment' :
                                    (dialogConfig.confirmText || 'Confirm')}
                    </Button>
                  );

                  // Wrap with tooltip if juryassign and disabled
                  if (isJuryAssignDisabled) {
                    return (
                      <Tooltip title="Select jury member(s) and deadline to proceed" arrow>
                        <span>{button}</span>
                      </Tooltip>
                    );
                  }

                  return button;
                })()}
              </DialogActions>
            )}
            {/* Actions for sessiondetails */}
            {dialogConfig?.type === 'sessiondetails' && (
              <DialogActions
                sx={{
                  px: { xs: 2, sm: 3 },
                  py: { xs: 1.25, sm: 1.75 },
                  gap: 1.5,
                  backgroundColor: '#ffffff',
                  display: 'flex',
                  justifyContent: 'flex-end',
                }}
              >
                <Button
                  onClick={async () => {
                    if (dialogConfig.onEdit) {
                      await dialogConfig.onEdit();
                    }
                    closeDialog(null);
                  }}
                  variant="outlined"
                  size="small"
                  startIcon={<EditIcon fontSize="small" />}
                  sx={{
                    textTransform: 'none',
                    borderRadius: 1,
                    fontSize: '0.8125rem',
                    fontWeight: 500,
                  }}
                >
                  Edit
                </Button>
                <Button
                  onClick={async () => {
                    if (dialogConfig.onDelete) {
                      await dialogConfig.onDelete();
                    }
                    closeDialog(null);
                  }}
                  variant="outlined"
                  size="small"
                  startIcon={<DeleteIcon fontSize="small" />}
                  sx={{
                    textTransform: 'none',
                    borderRadius: 1,
                    fontSize: '0.8125rem',
                    fontWeight: 500,
                    borderColor: '#EF4444',
                    color: '#B91C1C',
                    '&:hover': {
                      borderColor: '#B91C1C',
                      backgroundColor: '#FEF2F2',
                    },
                  }}
                >
                  Delete
                </Button>
              </DialogActions>
            )}
            {/* Actions for assignmentdetails */}
            {dialogConfig?.type === 'assignmentdetails' && (
              <DialogActions
                sx={{
                  px: { xs: 2, sm: 3 },
                  py: { xs: 2, sm: 2.5 },
                  gap: 1.5,
                  backgroundColor: '#F9FAFB',
                  borderTop: '1px solid #E5E7EB',
                  display: 'flex',
                  justifyContent: 'flex-end',
                }}
              >
                <Button
                  onClick={async () => {
                    if (assignmentDetailsState.completionRating < 1 || assignmentDetailsState.completionRating > 5) {
                      toast.error('Please provide a rating between 1 and 5');
                      return;
                    }
                    setAssignmentDetailsState({ ...assignmentDetailsState, submitting: true });
                    try {
                      const payload = {
                        status: 'COMPLETED',
                        feedback_rating: assignmentDetailsState.completionRating,
                        feedback_comment: assignmentDetailsState.completionComment || ''
                      };
                      if (dialogConfig.onComplete) {
                        await dialogConfig.onComplete(payload);
                      }
                      closeDialog(payload);
                    } catch (error) {
                      setAssignmentDetailsState({ ...assignmentDetailsState, submitting: false });
                    }
                  }}
                  disabled={assignmentDetailsState.submitting || assignmentDetailsState.completionRating < 1 || assignmentDetailsState.completionRating > 5}
                  sx={{
                    textTransform: 'none',
                    borderRadius: 1.5,
                    px: { xs: 3, sm: 4 },
                    py: 1.25,
                    bgcolor: '#2563EB',
                    color: '#ffffff',
                    fontWeight: 600,
                    '&:hover': { bgcolor: '#1D4ED8' },
                    '&.Mui-disabled': {
                      bgcolor: '#D1D5DB',
                      color: '#9CA3AF'
                    },
                    boxShadow: 'none',
                  }}
                >
                  {assignmentDetailsState.submitting ? 'Submitting...' : 'Submit Completion'}
                </Button>
                <Button
                  onClick={handleCancel}
                  disabled={assignmentDetailsState.submitting}
                  sx={{
                    textTransform: 'none',
                    borderRadius: 1.5,
                    px: { xs: 3, sm: 4 },
                    py: 1.25,
                    border: '1px solid #D1D5DB',
                    bgcolor: '#FFFFFF',
                    color: '#374151',
                    fontWeight: 500,
                    '&:hover': { bgcolor: '#F9FAFB' },
                  }}
                >
                  Cancel
                </Button>
              </DialogActions>
            )}
            {/* Actions for mentorverify */}
            {dialogConfig?.type === 'mentorverify' && (
              <DialogActions
                sx={{
                  px: { xs: 2, sm: 3 },
                  py: { xs: 2, sm: 2.5 },
                  gap: 2,
                  backgroundColor: '#ffffff',
                  display: 'flex',
                  justifyContent: 'flex-end',
                  borderTop: '1px solid #F3F4F6',
                  mt: 'auto'
                }}
              >
                <Button
                  onClick={() => {
                    if (!mentorVerifyForm.notes?.trim()) {
                      toast.error('Verification notes are required for rejection');
                      return;
                    }
                    closeDialog({ status: 'rejected', ...mentorVerifyForm });
                  }}
                  sx={{
                    textTransform: 'none',
                    borderRadius: 2,
                    px: 4,
                    py: 1.25,
                    color: '#EF4444',
                    bgcolor: '#FEF2F2',
                    '&:hover': { bgcolor: '#FEE2E2' },
                    fontWeight: 600,
                    minWidth: 120
                  }}
                >
                  Reject
                </Button>
                <Button
                  onClick={() => closeDialog({ status: 'approved', ...mentorVerifyForm })}
                  disabled={!mentorVerifyForm.linkedin_verified || !mentorVerifyForm.experience_verified || !mentorVerifyForm.domain_expertise_assessed}
                  variant="contained"
                  sx={{
                    textTransform: 'none',
                    borderRadius: 2,
                    px: 4,
                    py: 1.25,
                    bgcolor: '#2563EB',
                    '&:hover': { bgcolor: '#1D4ED8' },
                    fontWeight: 600,
                    minWidth: 120,
                    boxShadow: 'none',
                    '&.Mui-disabled': {
                      bgcolor: '#E5E7EB',
                      color: '#9CA3AF'
                    }
                  }}
                >
                  Verify
                </Button>
              </DialogActions>
            )}
            {/* Actions for editexpertise */}
            {dialogConfig?.type === 'editexpertise' && (
              <DialogActions
                sx={{
                  px: { xs: 2, sm: 3 },
                  py: { xs: 2, sm: 2.5 },
                  gap: 1.5,
                  backgroundColor: '#ffffff',
                  borderTop: '1px solid #E5E7EB',
                  display: 'flex',
                  justifyContent: 'flex-end',
                }}
              >
                <Button
                  onClick={handleCancel}
                  variant="text"
                  sx={{
                    textTransform: 'none',
                    borderRadius: '12px',
                    px: 4,
                    py: 1.25,
                    backgroundColor: '#F1F5F9',
                    color: '#475569',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    '&:hover': {
                      backgroundColor: '#E2E8F0',
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    handleConfirm();
                  }}
                  variant="contained"
                  startIcon={<CheckIcon />}
                  disabled={selectedExpertiseAreas.length < 5}
                  sx={{
                    textTransform: 'none',
                    borderRadius: '12px',
                    px: 4,
                    py: 1.25,
                    backgroundColor: 'var(--an-coral)',
                    color: '#ffffff',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.2)',
                    '&:hover': {
                      backgroundColor: 'var(--an-coral-deep)',
                      boxShadow: '0 10px 15px -3px rgba(37, 99, 235, 0.3)',
                    },
                    '&.Mui-disabled': {
                      backgroundColor: '#F1F5F9',
                      color: '#94A3B8',
                    },
                  }}
                >
                  Save
                </Button>
              </DialogActions>
            )}
            {/* Actions for manageachievements, managespeaking, managepublications, and manageavailability */}
            {(dialogConfig?.type === 'manageachievements' || dialogConfig?.type === 'managespeaking' || dialogConfig?.type === 'managepublications' || dialogConfig?.type === 'manageavailability') && (
              <DialogActions
                sx={{
                  px: { xs: 2, sm: 3 },
                  py: { xs: 2, sm: 2.5 },
                  gap: 1.5,
                  backgroundColor: '#ffffff',
                  borderTop: '1px solid #E5E7EB',
                  display: 'flex',
                  justifyContent: 'flex-end',
                }}
              >
                <Button
                  onClick={handleCancel}
                  variant="text"
                  sx={{
                    textTransform: 'none',
                    borderRadius: '12px',
                    px: 4,
                    py: 1.25,
                    backgroundColor: '#F1F5F9',
                    color: '#475569',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    '&:hover': {
                      backgroundColor: '#E2E8F0',
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirm}
                  variant="contained"
                  startIcon={<CheckIcon />}
                  sx={{
                    textTransform: 'none',
                    borderRadius: '12px',
                    px: 4,
                    py: 1.25,
                    backgroundColor: 'var(--an-coral)',
                    color: '#ffffff',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.2)',
                    '&:hover': {
                      backgroundColor: 'var(--an-coral-deep)',
                      boxShadow: '0 10px 15px -3px rgba(37, 99, 235, 0.3)',
                    },
                  }}
                >
                  Save
                </Button>
              </DialogActions>
            )}
            {/* Actions for editindustries */}
            {dialogConfig?.type === 'editindustries' && (
              <DialogActions
                sx={{
                  px: { xs: 2, sm: 3 },
                  py: { xs: 2, sm: 2.5 },
                  gap: 1.5,
                  backgroundColor: '#ffffff',
                  borderTop: '1px solid #E5E7EB',
                  display: 'flex',
                  justifyContent: 'flex-end',
                }}
              >
                <Button
                  onClick={handleCancel}
                  variant="text"
                  sx={{
                    textTransform: 'none',
                    borderRadius: '12px',
                    px: 4,
                    py: 1.25,
                    backgroundColor: '#F1F5F9',
                    color: '#475569',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    '&:hover': {
                      backgroundColor: '#E2E8F0',
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    // Ensure error is cleared before attempting to save
                    if (selectedIndustries.length > 0) {
                      setIndustryError('');
                    }
                    handleConfirm();
                  }}
                  variant="contained"
                  startIcon={<CheckIcon />}
                  disabled={selectedIndustries.length === 0}
                  sx={{
                    textTransform: 'none',
                    borderRadius: '12px',
                    px: 4,
                    py: 1.25,
                    backgroundColor: 'var(--an-coral)',
                    color: '#ffffff',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.2)',
                    '&:hover': {
                      backgroundColor: 'var(--an-coral-deep)',
                      boxShadow: '0 10px 15px -3px rgba(37, 99, 235, 0.3)',
                    },
                    '&.Mui-disabled': {
                      backgroundColor: '#F1F5F9',
                      color: '#94A3B8',
                    },
                  }}
                >
                  Save
                </Button>
              </DialogActions>
            )}
          </>
        )
        }
      </Dialog >

      {/* Confirmation Dialog for Add Fund */}
      < Dialog
        open={showFundingConfirmation}
        onClose={() => setShowFundingConfirmation(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          },
        }}
      >
        <DialogTitle sx={{ pb: 1, fontWeight: 600, fontSize: '1.125rem' }}>
          {dialogConfig?.defaultFundingId && dialogConfig?.existingInstallments ? 'EDIT' : 'Confirm Add Fund'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mt: 1 }}>
            {dialogConfig?.defaultFundingId && dialogConfig?.existingInstallments
              ? 'Are you sure you want to edit these installments?'
              : 'Are you sure you want to add this fund?'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
          <Button
            onClick={() => setShowFundingConfirmation(false)}
            variant="outlined"
            sx={{
              textTransform: 'none',
              borderRadius: 1.5,
              px: 3,
              py: 1,
              borderColor: '#D1D5DB',
              color: '#374151',
              '&:hover': {
                borderColor: '#9CA3AF',
                backgroundColor: '#F9FAFB',
              },
            }}
          >
            No
          </Button>
          <Button
            onClick={async () => {
              setShowFundingConfirmation(false);
              setSubmittingFunding(true);
              setFundingFormErrors({});

              try {
                const token = localStorage.getItem('token');
                if (!token) {
                  toast.error('Session expired. Please log in again.');
                  setSubmittingFunding(false);
                  return;
                }

                // Determine startup_id - priority: form > defaultStartupId > fetch from application
                let startupId = null;
                if (fundingForm.startupId && fundingForm.startupId.toString().trim()) {
                  startupId = parseInt(fundingForm.startupId);
                } else if (dialogConfig.defaultStartupId) {
                  startupId = parseInt(dialogConfig.defaultStartupId);
                } else if (dialogConfig.defaultApplicationId) {
                  // If startupId not provided but applicationId is, fetch startup_id from application
                  try {
                    // Try both endpoints
                    let appResponse = await fetch(`${API_BASE_URL}/applications/${dialogConfig.defaultApplicationId}`, {
                      headers: {
                        'Authorization': `Bearer ${token}`
                      }
                    });

                    // If that fails, try the CFA endpoint
                    if (!appResponse.ok) {
                      appResponse = await fetch(`${API_BASE_URL}/cfa/applications/${dialogConfig.defaultApplicationId}`, {
                        headers: {
                          'Authorization': `Bearer ${token}`
                        }
                      });
                    }

                    if (appResponse.ok) {
                      const appData = await appResponse.json();
                      if (appData.success && appData.data) {
                        startupId = appData.data.startup_id;
                      } else if (appData.data && appData.data.startup_id) {
                        startupId = appData.data.startup_id;
                      } else if (appData.startup_id) {
                        // Handle case where response is direct object
                        startupId = appData.startup_id;
                      }
                    }
                  } catch (err) {
                    console.error('Error fetching application:', err);
                  }
                }

                // Validate that we have startup_id
                if (!startupId) {
                  toast.error('Startup ID is required. Please select a startup or application.');
                  setSubmittingFunding(false);
                  return;
                }

                // Validate amount (for full payment and fix installments)
                if (fundingForm.paymentType !== 'installment' || fundingForm.installmentType !== 'custom') {
                  if (!fundingForm.amount || !fundingForm.amount.toString().trim()) {
                    toast.error('Amount is required.');
                    setSubmittingFunding(false);
                    return;
                  }
                }

                // For custom installments, validate installments array and remaining amount
                if (fundingForm.paymentType === 'installment' && fundingForm.installmentType === 'custom') {
                  if (!fundingForm.amount || !fundingForm.amount.toString().trim()) {
                    toast.error('Total amount is required.');
                    setSubmittingFunding(false);
                    return;
                  }

                  if (!fundingForm.customInstallments || fundingForm.customInstallments.length === 0) {
                    toast.error('At least one installment is required.');
                    setSubmittingFunding(false);
                    return;
                  }

                  // Validate each installment
                  let totalInstallmentAmount = 0;
                  for (let i = 0; i < fundingForm.customInstallments.length; i++) {
                    const inst = fundingForm.customInstallments[i];
                    if (!inst.amount || !inst.amount.toString().trim()) {
                      toast.error(`Installment ${i + 1}: Amount is required.`);
                      setSubmittingFunding(false);
                      return;
                    }
                    const amountValue = parseFloat(inst.amount);
                    if (isNaN(amountValue) || amountValue <= 0) {
                      toast.error(`Installment ${i + 1}: Amount must be a positive number.`);
                      setSubmittingFunding(false);
                      return;
                    }
                    totalInstallmentAmount += amountValue;

                    if (!inst.date || !inst.date.toString().trim()) {
                      toast.error(`Installment ${i + 1}: Date is required.`);
                      setSubmittingFunding(false);
                      return;
                    }
                  }

                  // Validate that remaining amount is 0
                  const totalAmount = parseFloat(fundingForm.amount);
                  const remaining = totalAmount - totalInstallmentAmount;
                  if (Math.abs(remaining) > 0.01) {
                    toast.error(`Installment total (${totalInstallmentAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}) must equal total amount (${totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}). Remaining: ${remaining.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
                    setSubmittingFunding(false);
                    return;
                  }
                }

                const applicationId = fundingForm.applicationId && fundingForm.applicationId.toString().trim()
                  ? parseInt(fundingForm.applicationId)
                  : (dialogConfig.defaultApplicationId ? parseInt(dialogConfig.defaultApplicationId) : null);

                // If navigateToApplication is true, skip API call and navigate
                if (dialogConfig.navigateToApplication && applicationId) {
                  const result = {
                    navigateToApplication: true,
                    applicationId: applicationId,
                    startupId: startupId
                  };

                  // Call onConfirm callback if provided (for navigation)
                  if (dialogConfig.onConfirm) {
                    try {
                      await dialogConfig.onConfirm(result);
                    } catch (error) {
                      console.error('Error in onConfirm callback:', error);
                    }
                  }

                  closeDialog(result);
                  setSubmittingFunding(false);
                  return;
                }

                // Prepare FormData for file uploads
                const formData = new FormData();
                formData.append('startup_id', startupId);
                if (applicationId) {
                  formData.append('application_id', applicationId);
                }
                formData.append('currency_id', parseInt(fundingForm.currencyId) || defaultCurrencyId);
                // Also send the code so the backend can upsert the currency and resolve the real DB id.
                const _currencyEntry = currenciesList.find(c => c.id === (parseInt(fundingForm.currencyId) || defaultCurrencyId));
                if (_currencyEntry) {
                  formData.append('currency_code', _currencyEntry.code);
                  formData.append('currency_name', _currencyEntry.name);
                  formData.append('currency_symbol', _currencyEntry.symbol);
                }
                if (fundingForm.fundOfferingId) {
                  formData.append('fund_offering_id', parseInt(fundingForm.fundOfferingId));
                }
                // Default funding status should be 'unpaid' when created
                formData.append('status', 'unpaid');
                // Only send payment_type if Payment Method is being selected (showPaymentMethod is true)
                // For first entry (Add Fund), payment_type should be NULL
                if (dialogConfig?.showPaymentMethod) {
                  formData.append('payment_type', fundingForm.paymentType || 'full');
                  if (fundingForm.installmentType) {
                    formData.append('installment_type', fundingForm.installmentType);
                  }
                }
                // num_installments and payment_frequency are only relevant for old 'fix' installments

                // Check if editing existing installments
                const isEditingInstallments = dialogConfig?.defaultFundingId && dialogConfig?.existingInstallments;

                if (isEditingInstallments) {
                  // For editing installments, use PUT request to update existing and add new installments
                  const installmentsToUpdate = fundingForm.customInstallments
                    .map(inst => ({
                      id: inst.id || null, // Include ID if exists (for update), null for new installments
                      amount: parseFloat(inst.amount),
                      date: inst.date,
                      notes: inst.notes ? inst.notes.trim() : '' // Include notes for each milestone
                    }));

                  // Prepare request body with installments and notes
                  const requestBody = {
                    installments: installmentsToUpdate
                  };

                  // Add notes if provided
                  if (fundingForm.notes !== undefined && fundingForm.notes !== null) {
                    requestBody.notes = fundingForm.notes.trim();
                  }

                  const response = await fetch(`${API_BASE_URL}/funding/${dialogConfig.defaultFundingId}/installments`, {
                    method: 'PUT',
                    headers: {
                      'Authorization': `Bearer ${token}`,
                      'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(requestBody)
                  });

                  if (response.ok) {
                    const data = await response.json();
                    if (data.success) {
                      toast.success('Installments updated and added successfully');
                      const result = {
                        fundingId: dialogConfig.defaultFundingId,
                        installments: data.data
                      };

                      // Call onConfirm callback if provided (for refreshing data)
                      if (dialogConfig.onConfirm) {
                        try {
                          await dialogConfig.onConfirm(result);
                        } catch (error) {
                          console.error('Error in onConfirm callback:', error);
                        }
                      }

                      closeDialog(result);
                      setSubmittingFunding(false);
                      return;
                    } else {
                      toast.error(data.message || 'Failed to update installments');
                      setSubmittingFunding(false);
                      return;
                    }
                  } else {
                    const errorData = await response.json();
                    toast.error(errorData.message || 'Failed to update installments');
                    setSubmittingFunding(false);
                    return;
                  }
                }

                // For new funding, continue with POST request
                // Add amount - for custom installments, use total amount; for others, use the amount field
                if (fundingForm.paymentType === 'installment' && fundingForm.installmentType === 'custom') {
                  // For custom installments, amount is the total amount
                  formData.append('amount', parseFloat(fundingForm.amount) || 0);
                  // Add custom installments array as JSON string
                  formData.append('custom_installments', JSON.stringify(fundingForm.customInstallments.map(inst => ({
                    amount: parseFloat(inst.amount),
                    date: inst.date
                  }))));
                } else {
                  // For full payment or fix installments, use the amount field
                  formData.append('amount', parseFloat(fundingForm.amount));
                }


                // Add documents
                if (fundingForm.documents && fundingForm.documents.length > 0) {
                  fundingForm.documents.forEach((file, index) => {
                    formData.append(`documents`, file);
                  });
                }

                const response = await fetch(`${API_BASE_URL}/funding`, {
                  method: 'POST',
                  headers: {
                    'Authorization': `Bearer ${token}`
                    // Don't set Content-Type, let browser set it with boundary for FormData
                  },
                  body: formData
                });

                if (response.ok) {
                  const data = await response.json();
                  if (data.success) {
                    toast.success('Funding added successfully');
                    const result = {
                      startupId: startupId,
                      applicationId: applicationId,
                      amount: parseFloat(fundingForm.amount)
                    };

                    // Call onConfirm callback if provided (for refreshing data)
                    if (dialogConfig.onConfirm) {
                      try {
                        await dialogConfig.onConfirm(result);
                      } catch (error) {
                        console.error('Error in onConfirm callback:', error);
                      }
                    }

                    closeDialog(result);
                  } else {
                    toast.error(data.message || 'Failed to add funding');
                    setSubmittingFunding(false);
                  }
                } else {
                  const errorData = await response.json();
                  toast.error(errorData.message || 'Failed to add funding');
                  setSubmittingFunding(false);
                }
              } catch (err) {
                console.error('Error adding funding:', err);
                toast.error('Network error. Please try again.');
                setSubmittingFunding(false);
              }
            }}
            variant="contained"
            sx={{
              textTransform: 'none',
              borderRadius: 1.5,
              px: 3,
              py: 1,
              backgroundColor: 'var(--an-coral)',
              color: '#ffffff',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: 'var(--an-coral-deep)',
              },
            }}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog >

      {/* Confirmation Dialog for Full Payment */}
      < Dialog
        open={showPaymentMethodConfirmation}
        onClose={() => {
          setShowPaymentMethodConfirmation(false);
          setPendingPaymentMethodData(null);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          },
        }}
      >
        <DialogTitle sx={{ pb: 1, fontWeight: 600, fontSize: '1.125rem' }}>
          Confirm Payment
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mt: 1 }}>
            Please confirm whether you want to mark this payment released. Once confirmed, the amount cannot be reverted.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
          <Button
            onClick={() => {
              setShowPaymentMethodConfirmation(false);
              setPendingPaymentMethodData(null);
            }}
            variant="outlined"
            sx={{
              textTransform: 'none',
              borderRadius: 1.5,
              px: 3,
              py: 1,
              borderColor: '#D1D5DB',
              color: '#374151',
              '&:hover': {
                borderColor: '#9CA3AF',
                backgroundColor: '#F9FAFB',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={async () => {
              if (!pendingPaymentMethodData) {
                setShowPaymentMethodConfirmation(false);
                return;
              }

              setShowPaymentMethodConfirmation(false);
              setSubmittingPaymentMethod(true);

              try {
                const token = localStorage.getItem('token');
                if (!token) {
                  toast.error('Session expired. Please log in again.');
                  setSubmittingPaymentMethod(false);
                  setPendingPaymentMethodData(null);
                  return;
                }

                const response = await fetch(`${API_BASE_URL}/funding/${pendingPaymentMethodData.fundingId}`, {
                  method: 'PUT',
                  headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                  },
                  body: JSON.stringify(pendingPaymentMethodData.updateData)
                });

                if (response.ok) {
                  const data = await response.json();
                  if (data.success) {
                    toast.success('Payment method updated to paid successfully');
                    const result = {
                      fundingId: pendingPaymentMethodData.fundingId
                    };

                    // Call onConfirm callback if provided (for refreshing data)
                    if (dialogConfig?.onConfirm) {
                      try {
                        await dialogConfig.onConfirm(result);
                      } catch (error) {
                        console.error('Error in onConfirm callback:', error);
                      }
                    }

                    closeDialog(result);
                    setSubmittingPaymentMethod(false);
                    setPendingPaymentMethodData(null);
                    return;
                  } else {
                    toast.error(data.message || 'Failed to update payment method');
                    setSubmittingPaymentMethod(false);
                    setPendingPaymentMethodData(null);
                    return;
                  }
                } else {
                  const errorData = await response.json().catch(() => ({}));
                  toast.error(errorData.message || 'Failed to update payment method');
                  setSubmittingPaymentMethod(false);
                  setPendingPaymentMethodData(null);
                  return;
                }
              } catch (error) {
                console.error('Error updating payment method:', error);
                toast.error('Network error. Please try again.');
                setSubmittingPaymentMethod(false);
                setPendingPaymentMethodData(null);
              }
            }}
            variant="contained"
            sx={{
              textTransform: 'none',
              borderRadius: 1.5,
              px: 3,
              py: 1,
              backgroundColor: 'var(--an-coral)',
              color: '#ffffff',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: 'var(--an-coral-deep)',
              },
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog >

      {/* Verification Modal */}
      {dialogConfig?.type === 'verification' && (
        <Dialog
          open={true}
          onClose={handleCancel}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            },
          }}
        >
          <DialogTitle
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              pb: 2.5,
              pt: 3.5,
              px: 4,
              backgroundColor: '#ffffff',
              borderBottom: '1px solid #F1F5F9',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, flex: 1, pr: 4 }}>
              {/* Icon Circle */}
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: '16px',
                  backgroundColor: '#EFF6FF',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  border: '1px solid #DBEAFE',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
                }}
              >
                <VerifiedUserIcon sx={{ color: '#2563EB', fontSize: 26 }} />
              </Box>

              {/* Title and Subtitle */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {dialogConfig.subtitle && (
                  <Typography
                    variant="overline"
                    sx={{
                      display: 'block',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      letterSpacing: '0.1em',
                      color: '#6B7280',
                      textTransform: 'uppercase',
                      mb: 0.5,
                      lineHeight: 1.2,
                    }}
                  >
                    {dialogConfig.subtitle}
                  </Typography>
                )}
                <Typography
                  variant="h5"
                  component="h2"
                  sx={{
                    fontWeight: 700,
                    color: '#111827',
                    fontSize: '1.5rem',
                    lineHeight: 1.3,
                  }}
                >
                  {dialogConfig.title}
                </Typography>
              </Box>
            </Box>

            <IconButton
              aria-label="close"
              onClick={handleCancel}
              size="small"
              sx={{
                color: '#6B7280',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ px: 3, pt: 2, pb: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Verification Status */}
              <FormControl fullWidth required error={Boolean(verificationErrors.status)}>
                <InputLabel id="verification-status-label">Verification Status</InputLabel>
                <Select
                  labelId="verification-status-label"
                  id="verification-status"
                  value={verificationForm.status || ''}
                  onChange={(e) => {
                    const selectedValue = String(e.target.value || '').trim();
                    if (selectedValue) {
                      setVerificationForm(prev => ({ ...prev, status: selectedValue }));
                      setVerificationErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors.status;
                        return newErrors;
                      });
                    }
                  }}
                  label="Verification Status"
                >
                  {dialogConfig.statuses && Object.keys(dialogConfig.statuses).length > 0 ? (
                    Object.entries(dialogConfig.statuses).map(([key, label]) => (
                      <MenuItem key={key} value={key}>{label}</MenuItem>
                    ))
                  ) : (
                    <>
                      <MenuItem value="approved">Approved</MenuItem>
                      <MenuItem value="rejected">Rejected</MenuItem>
                      <MenuItem value="needs_clarification">Needs Clarification</MenuItem>
                    </>
                  )}
                </Select>
                {verificationErrors.status && (
                  <FormHelperText sx={{ color: '#DC2626', ml: 0 }}>
                    {verificationErrors.status}
                  </FormHelperText>
                )}
              </FormControl>

              {/* Comments */}
              <Box>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Comments"
                  placeholder="Add verification comments..."
                  value={verificationForm.comments}
                  onChange={(e) => setVerificationForm(prev => ({ ...prev, comments: e.target.value }))}
                  inputProps={{ maxLength: 500 }}
                  error={Boolean(verificationErrors.comments)}
                  helperText={verificationErrors.comments || `${verificationForm.comments.length}/500 characters`}
                />
              </Box>
            </Box>
          </DialogContent>

          <DialogActions
            sx={{
              px: { xs: 2, sm: 3 },
              py: { xs: 2, sm: 2.5 },
              gap: { xs: 1.5, sm: 1.5 },
              backgroundColor: '#ffffff',
              borderTop: 'none',
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'flex-end',
              alignItems: 'stretch',
            }}
          >
            <Button
              onClick={handleCancel}
              variant="text"
              fullWidth
              sx={{
                minWidth: { xs: '100%', sm: 100 },
                textTransform: 'none',
                borderRadius: '12px',
                px: 4,
                py: 1.5,
                backgroundColor: '#F1F5F9',
                color: '#475569',
                fontWeight: 600,
                fontSize: '0.875rem',
                border: 'none',
                textAlign: 'center',
                '&:hover': {
                  backgroundColor: '#E2E8F0',
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              variant="contained"
              fullWidth
              sx={{
                minWidth: { xs: '100%', sm: 120 },
                textTransform: 'none',
                borderRadius: '12px',
                px: 4,
                py: 1.5,
                fontWeight: 700,
                fontSize: '0.875rem',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                '&.Mui-disabled': {
                  backgroundColor: '#F1F5F9',
                  color: '#94A3B8',
                },
                backgroundColor: 'var(--an-coral)',
                '&:hover': {
                  backgroundColor: 'var(--an-coral-deep)',
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                  transform: 'translateY(-1px)',
                },
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              {dialogConfig.confirmText}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Bulk Verification Modal */}
      {dialogConfig?.type === 'bulkverification' && (
        <Dialog
          open={true}
          onClose={handleCancel}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            },
          }}
        >
          <DialogTitle
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              pb: 2.5,
              pt: 3.5,
              px: 4,
              backgroundColor: '#ffffff',
              borderBottom: '1px solid #F1F5F9',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, flex: 1, pr: 4 }}>
              {/* Icon Circle */}
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: '16px',
                  backgroundColor: '#EFF6FF',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  border: '1px solid #DBEAFE',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
                }}
              >
                <VerifiedUserIcon sx={{ color: '#2563EB', fontSize: 26 }} />
              </Box>

              {/* Title and Subtitle */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {dialogConfig.subtitle && (
                  <Typography
                    variant="overline"
                    sx={{
                      display: 'block',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      letterSpacing: '0.1em',
                      color: '#6B7280',
                      textTransform: 'uppercase',
                      mb: 0.5,
                      lineHeight: 1.2,
                    }}
                  >
                    {dialogConfig.subtitle}
                  </Typography>
                )}
                <Typography
                  variant="h5"
                  component="h2"
                  sx={{
                    fontWeight: 700,
                    color: '#111827',
                    fontSize: '1.5rem',
                    lineHeight: 1.3,
                  }}
                >
                  {dialogConfig.title}
                </Typography>
              </Box>
            </Box>

            <IconButton
              aria-label="close"
              onClick={handleCancel}
              size="small"
              sx={{
                color: '#6B7280',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ px: 3, pt: 2, pb: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Action Type Selection */}
              <FormControl fullWidth>
                <InputLabel id="bulk-verification-action-label">Bulk Action</InputLabel>
                <Select
                  labelId="bulk-verification-action-label"
                  id="bulk-verification-action"
                  value={bulkVerificationForm.action}
                  onChange={(e) => {
                    setBulkVerificationForm(prev => ({ ...prev, action: e.target.value }));
                    // Clear status error when switching actions
                    if (e.target.value === 'export_selected') {
                      setBulkVerificationErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors.status;
                        return newErrors;
                      });
                    }
                  }}
                  label="Bulk Action"
                >
                  <MenuItem value="update_status">Update verification status</MenuItem>
                  <MenuItem value="export_selected">Export selected documents</MenuItem>
                </Select>
                <FormHelperText>
                  {bulkVerificationForm.action === 'update_status'
                    ? 'Set the same verification status for all selected documents'
                    : 'Generate a report for only the selected documents'}
                </FormHelperText>
              </FormControl>

              {/* Verification Status - Only shown when action is update_status */}
              {bulkVerificationForm.action === 'update_status' && (
                <>
                  <FormControl fullWidth required error={Boolean(bulkVerificationErrors.status)}>
                    <InputLabel id="bulk-verification-status-label">Verification Status</InputLabel>
                    <Select
                      labelId="bulk-verification-status-label"
                      id="bulk-verification-status"
                      value={bulkVerificationForm.status}
                      onChange={(e) => {
                        setBulkVerificationForm(prev => ({ ...prev, status: e.target.value }));
                        // Clear error immediately when value is selected
                        setBulkVerificationErrors(prev => {
                          const newErrors = { ...prev };
                          delete newErrors.status;
                          return newErrors;
                        });
                      }}
                      label="Verification Status"
                    >
                      {dialogConfig.statuses && Object.keys(dialogConfig.statuses).length > 0 ? (
                        Object.entries(dialogConfig.statuses).map(([key, label]) => (
                          <MenuItem key={key} value={key}>{label}</MenuItem>
                        ))
                      ) : (
                        <>
                          <MenuItem value="approved">Approved</MenuItem>
                          <MenuItem value="rejected">Rejected</MenuItem>
                          <MenuItem value="needs_clarification">Needs Clarification</MenuItem>
                        </>
                      )}
                    </Select>
                    {bulkVerificationErrors.status && (
                      <FormHelperText sx={{ color: '#DC2626', ml: 0 }}>
                        {bulkVerificationErrors.status}
                      </FormHelperText>
                    )}
                  </FormControl>

                  {/* Comments */}
                  <Box>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label="Comments"
                      placeholder="Add comments for all selected documents..."
                      value={bulkVerificationForm.comments}
                      onChange={(e) => setBulkVerificationForm(prev => ({ ...prev, comments: e.target.value }))}
                      inputProps={{ maxLength: 500 }}
                      error={Boolean(bulkVerificationErrors.comments)}
                      helperText={bulkVerificationErrors.comments || `${bulkVerificationForm.comments.length}/500 characters`}
                    />
                  </Box>
                </>
              )}

              {/* Export Format - Only shown when action is export_selected */}
              {bulkVerificationForm.action === 'export_selected' && (
                <FormControl fullWidth>
                  <InputLabel id="bulk-export-format-label">Export Format</InputLabel>
                  <Select
                    labelId="bulk-export-format-label"
                    id="bulk-export-format"
                    value={bulkVerificationForm.exportFormat}
                    onChange={(e) => setBulkVerificationForm(prev => ({ ...prev, exportFormat: e.target.value }))}
                    label="Export Format"
                  >
                    <MenuItem value="csv">CSV</MenuItem>
                    <MenuItem value="excel">Excel (.xls)</MenuItem>
                    <MenuItem value="pdf">PDF</MenuItem>
                  </Select>
                </FormControl>
              )}
            </Box>
          </DialogContent>

          <DialogActions
            sx={{
              px: { xs: 2, sm: 3 },
              py: { xs: 2, sm: 2.5 },
              gap: { xs: 1.5, sm: 1.5 },
              backgroundColor: '#ffffff',
              borderTop: 'none',
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'flex-end',
              alignItems: 'stretch',
            }}
          >
            <Button
              onClick={handleCancel}
              variant="text"
              fullWidth
              sx={{
                minWidth: { xs: '100%', sm: 100 },
                textTransform: 'none',
                borderRadius: '12px',
                px: 4,
                py: 1.5,
                backgroundColor: '#F1F5F9',
                color: '#475569',
                fontWeight: 600,
                fontSize: '0.875rem',
                border: 'none',
                textAlign: 'center',
                '&:hover': {
                  backgroundColor: '#E2E8F0',
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              variant="contained"
              fullWidth
              sx={{
                minWidth: { xs: '100%', sm: 120 },
                textTransform: 'none',
                borderRadius: '12px',
                px: 4,
                py: 1.5,
                fontWeight: 700,
                fontSize: '0.875rem',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                '&.Mui-disabled': {
                  backgroundColor: '#F1F5F9',
                  color: '#94A3B8',
                },
                backgroundColor: 'var(--an-coral)',
                '&:hover': {
                  backgroundColor: 'var(--an-coral-deep)',
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                  transform: 'translateY(-1px)',
                },
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              {dialogConfig.confirmText}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Document History Modal */}
      {dialogConfig?.type === 'documenthistory' && (
        <Dialog
          open={true}
          onClose={handleCancel}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              maxHeight: '90vh',
            },
          }}
        >
          <DialogTitle
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              pb: 2.5,
              pt: 3.5,
              px: 4,
              backgroundColor: '#ffffff',
              borderBottom: '1px solid #F1F5F9',
              position: 'sticky',
              top: 0,
              zIndex: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, flex: 1, pr: 4 }}>
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: '16px',
                  backgroundColor: '#EFF6FF',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  border: '1px solid #DBEAFE',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
                }}
              >
                <DescriptionIcon sx={{ color: '#2563EB', fontSize: 26 }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="h5"
                  component="h2"
                  sx={{
                    fontWeight: 700,
                    color: '#111827',
                    fontSize: '1.5rem',
                    lineHeight: 1.3,
                  }}
                >
                  {dialogConfig.title || 'Document History'}
                </Typography>
                {dialogConfig.subtitle && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#6B7280',
                      mt: 0.5,
                      fontSize: '0.875rem',
                    }}
                  >
                    {dialogConfig.subtitle}
                  </Typography>
                )}
              </Box>
            </Box>
            <IconButton
              aria-label="close"
              onClick={handleCancel}
              size="small"
              sx={{
                color: '#6B7280',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>

          <DialogContent
            sx={{
              px: 4,
              py: 3,
              maxHeight: 'calc(90vh - 140px)',
              overflow: 'auto',
              // Hide scrollbar
              '&::-webkit-scrollbar': {
                display: 'none',
              },
              scrollbarWidth: 'none', // Firefox
              msOverflowStyle: 'none', // IE and Edge
              mt: 2
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Document Information - Hide for startup founders */}
              {!dialogConfig.hideDocumentInfo && dialogConfig.history?.document && (() => {
                // Format status to Title Case (e.g., "rejected" -> "Rejected", "needs_clarification" -> "Needs Clarification")
                const formatStatusTitle = (status) => {
                  if (!status) return 'Unknown';
                  return status
                    .toLowerCase()
                    .split('_')
                    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ');
                };

                const status = dialogConfig.history.document.verification_status || 'Unknown';
                const formattedStatus = formatStatusTitle(status);

                return (
                  <Box
                    sx={{
                      backgroundColor: '#FFFFFF',
                      borderRadius: 3,
                      p: 4,
                      border: '1px solid #E5E7EB',
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: '#111827',
                        mb: 3,
                        fontSize: '1.125rem',
                        borderBottom: '2px solid #F3F4F6',
                        pb: 1.5,
                      }}
                    >
                      Document Information
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: '#6B7280',
                            mb: 1,
                            fontSize: '0.75rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                          }}
                        >
                          Startup
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            color: '#111827',
                            fontWeight: 500,
                            fontSize: '1rem',
                          }}
                        >
                          {dialogConfig.history.document.startup_name || 'Unknown'}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: '#6B7280',
                            mb: 1,
                            fontSize: '0.75rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                          }}
                        >
                          Current Status
                        </Typography>
                        <Typography
                          variant="h6"
                          component="div"
                          sx={{
                            color: '#111827',
                            fontWeight: 500,
                            fontSize: '1rem',
                            textTransform: 'capitalize',
                          }}
                        >
                          {formattedStatus}
                        </Typography>
                      </Box>
                      <Box sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: '#6B7280',
                            mb: 1,
                            fontSize: '0.75rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                          }}
                        >
                          Verified By
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            color: '#111827',
                            fontWeight: 500,
                            fontSize: '1rem',
                          }}
                        >
                          {dialogConfig.history.document.verified_by_name || 'Not verified'}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                );
              })()}

              {/* Verification History */}
              <Box
                sx={{
                  backgroundColor: '#FFFFFF',
                  borderRadius: 3,
                  p: 4,
                  border: '1px solid #E5E7EB',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: '#111827',
                    mb: 3,
                    fontSize: '1.125rem',
                    borderBottom: '2px solid #F3F4F6',
                    pb: 1.5,
                  }}
                >
                  Verification History
                </Typography>
                <Box
                  sx={{
                    maxHeight: '320px',
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    // Hide scrollbar
                    '&::-webkit-scrollbar': {
                      display: 'none',
                    },
                    scrollbarWidth: 'none', // Firefox
                    msOverflowStyle: 'none', // IE and Edge
                  }}
                >
                  {dialogConfig.history?.verification_history?.length > 0 ? (
                    dialogConfig.history.verification_history.map((entry, index) => (
                      <Box
                        key={index}
                        sx={{
                          borderLeft: '4px solid #DBEAFE',
                          pl: 3,
                          pb: 2,
                          backgroundColor: '#ffffff',
                          borderRadius: '0 8px 8px 0',
                          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                          <Chip
                            label={entry.action?.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) || 'Action'}
                            sx={{
                              backgroundColor: '#DBEAFE',
                              color: '#1E40AF',
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              height: '24px',
                            }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#6B7280',
                              fontSize: '0.75rem',
                            }}
                          >
                            {entry.timestamp ? new Date(entry.timestamp).toLocaleDateString() : 'N/A'}
                          </Typography>
                        </Box>
                        {entry.comments && (
                          <Typography
                            variant="body2"
                            sx={{
                              color: '#374151',
                              mb: 1.5,
                              backgroundColor: '#F9FAFB',
                              p: 1.5,
                              borderRadius: 1,
                              wordWrap: 'break-word',
                              overflowWrap: 'break-word',
                              whiteSpace: 'pre-wrap',
                            }}
                          >
                            {entry.comments}
                          </Typography>
                        )}
                        {entry.rejection_reason && (
                          <Box
                            sx={{
                              backgroundColor: '#FEF2F2',
                              p: 1.5,
                              borderRadius: 1,
                              mb: 1,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                color: '#991B1B',
                                fontWeight: 600,
                                mb: 0.5,
                              }}
                            >
                              Rejection Reason:
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: '#991B1B',
                                wordWrap: 'break-word',
                                overflowWrap: 'break-word',
                                whiteSpace: 'pre-wrap',
                              }}
                            >
                              {entry.rejection_reason}
                            </Typography>
                          </Box>
                        )}
                        {entry.verification_notes && (
                          <Box
                            sx={{
                              backgroundColor: '#EFF6FF',
                              p: 1.5,
                              borderRadius: 1,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                color: '#1E40AF',
                                fontWeight: 600,
                                mb: 0.5,
                              }}
                            >
                              Notes:
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: '#1E40AF',
                                wordWrap: 'break-word',
                                overflowWrap: 'break-word',
                                whiteSpace: 'pre-wrap',
                              }}
                            >
                              {entry.verification_notes}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    ))
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body2" sx={{ color: '#6B7280' }}>
                        No verification history available
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          </DialogContent>
        </Dialog>
      )}

      {/* Bulk Review Modal */}
      {dialogConfig?.type === 'bulkreview' && (
        <Dialog
          open={true}
          onClose={handleCancel}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            },
          }}
        >
          <DialogTitle
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              pb: 2.5,
              pt: 3.5,
              px: 4,
              backgroundColor: '#ffffff',
              borderBottom: '1px solid #F1F5F9',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, flex: 1, pr: 4 }}>
              {/* Icon Circle */}
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: '16px',
                  backgroundColor: '#EEF2FF',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  border: '1px solid #C7D2FE',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
                }}
              >
                <FactCheckIcon sx={{ color: '#4F46E5', fontSize: 26 }} />
              </Box>

              {/* Title */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="h5"
                  component="h2"
                  sx={{
                    fontWeight: 700,
                    color: '#111827',
                    fontSize: '1.5rem',
                    lineHeight: 1.3,
                  }}
                >
                  {dialogConfig.title || `Bulk Review (${dialogConfig.selectedStartups?.length || 0} ${(dialogConfig.selectedStartups?.length || 0) === 1 ? 'startup' : 'startups'})`}
                </Typography>
              </Box>
            </Box>

            <IconButton
              aria-label="close"
              onClick={handleCancel}
              size="small"
              sx={{
                color: '#6B7280',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ px: 4, pt: 2, pb: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {/* Selected Startups */}
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    color: '#374151',
                    mb: 2,
                    fontSize: '0.875rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <PersonIcon sx={{ fontSize: 18, color: '#2563EB' }} />
                  Selected Startups:
                </Typography>
                <Box
                  sx={{
                    backgroundColor: '#DBEAFE',
                    borderRadius: 2,
                    p: 2.5,
                    border: '1px solid #BFDBFE',
                    maxHeight: '120px',
                    overflowY: 'auto',
                    '&::-webkit-scrollbar': {
                      width: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                      backgroundColor: '#F1F5F9',
                      borderRadius: '3px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: '#CBD5E1',
                      borderRadius: '3px',
                      '&:hover': {
                        backgroundColor: '#94A3B8',
                      },
                    },
                  }}
                >
                  {dialogConfig.selectedStartups && dialogConfig.selectedStartups.length > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {dialogConfig.selectedStartups.map((startup, index) => (
                        <Typography
                          key={index}
                          variant="body2"
                          sx={{
                            color: '#1E40AF',
                            fontWeight: 500,
                            fontSize: '0.875rem',
                          }}
                        >
                          {startup.name} ({startup.email})
                        </Typography>
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" sx={{ color: '#6B7280' }}>
                      No startups selected
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Bulk Review Assessment */}
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    color: '#374151',
                    mb: 2,
                    fontSize: '0.875rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <FilterListIcon sx={{ fontSize: 18, color: '#2563EB' }} />
                  Bulk Review Assessment
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {/* Review Decision */}
                  <FormControl fullWidth required error={Boolean(bulkReviewErrors.status)}>
                    <InputLabel id="bulk-review-status-label" sx={{ backgroundColor: '#fff', px: 1, ml: -1 }}>Review Decision</InputLabel>
                    <Select
                      labelId="bulk-review-status-label"
                      id="bulk-review-status"
                      value={bulkReviewForm.status}
                      displayEmpty
                      renderValue={(selected) => {
                        if (!selected) {
                          return <span style={{ color: '#9CA3AF' }}>Select status</span>;
                        }
                        const statusLabels = {
                          'approved': 'Approved',
                          'needs_revision': 'Needs Revision',
                          'rejected': 'Rejected'
                        };
                        return statusLabels[selected] || selected;
                      }}
                      onChange={(e) => {
                        setBulkReviewForm(prev => ({ ...prev, status: e.target.value }));
                        if (bulkReviewErrors.status) {
                          setBulkReviewErrors(prev => {
                            const newErrors = { ...prev };
                            delete newErrors.status;
                            return newErrors;
                          });
                        }
                      }}
                      label="Review Decision"
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderRadius: '12px',
                        },
                      }}
                    >
                      <MenuItem value="approved">Approved</MenuItem>
                      <MenuItem value="rejected">Rejected</MenuItem>
                      <MenuItem value="needs_revision">Needs Revision</MenuItem>
                    </Select>
                    {bulkReviewErrors.status && (
                      <FormHelperText sx={{ color: '#DC2626', ml: 0 }}>
                        {bulkReviewErrors.status}
                      </FormHelperText>
                    )}
                  </FormControl>

                  {/* Comments */}
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Comments"
                    placeholder="Provide comments for all selected startups..."
                    value={bulkReviewForm.comments}
                    onChange={(e) => setBulkReviewForm(prev => ({ ...prev, comments: e.target.value }))}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                      },
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </DialogContent>

          <DialogActions
            sx={{
              px: { xs: 2, sm: 3 },
              py: { xs: 2, sm: 2.5 },
              gap: { xs: 1.5, sm: 1.5 },
              backgroundColor: '#F9FAFB',
              borderTop: '1px solid #E5E7EB',
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'flex-end',
              alignItems: 'stretch',
            }}
          >
            <Button
              onClick={handleCancel}
              variant="text"
              fullWidth
              sx={{
                minWidth: { xs: '100%', sm: 100 },
                textTransform: 'none',
                borderRadius: '12px',
                px: 4,
                py: 1.5,
                backgroundColor: '#F1F5F9',
                color: '#475569',
                fontWeight: 600,
                fontSize: '0.875rem',
                border: 'none',
                textAlign: 'center',
                '&:hover': {
                  backgroundColor: '#E2E8F0',
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              variant="contained"
              disabled={submittingBulkReview || !bulkReviewForm.status}
              fullWidth
              sx={{
                minWidth: { xs: '100%', sm: 150 },
                textTransform: 'none',
                borderRadius: '12px',
                px: 4,
                py: 1.5,
                fontWeight: 700,
                fontSize: '0.875rem',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                '&.Mui-disabled': {
                  backgroundColor: '#F1F5F9',
                  color: '#94A3B8',
                },
                backgroundColor: 'var(--an-coral)',
                '&:hover': {
                  backgroundColor: 'var(--an-coral-deep)',
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                  transform: 'translateY(-1px)',
                },
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              {submittingBulkReview ? 'Processing...' : dialogConfig.confirmText || `Review ${dialogConfig.selectedStartups?.length || 0} ${(dialogConfig.selectedStartups?.length || 0) === 1 ? 'Startup' : 'Startups'}`}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Profile Review Modal */}
      {dialogConfig?.type === 'profilereview' && dialogConfig.startup && (
        <Dialog
          open={true}
          onClose={handleCancel}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: { xs: 0, sm: 3 },
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              maxHeight: { xs: '100vh', sm: '90vh' },
              height: { xs: '100vh', sm: 'auto' },
              margin: { xs: 0, sm: 'auto' },
            },
          }}
        >
          <DialogTitle
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              pb: { xs: 2, sm: 2.5 },
              pt: { xs: 2, sm: 3.5 },
              px: { xs: 2, sm: 4 },
              backgroundColor: '#ffffff',
              borderBottom: '1px solid #E5E7EB',
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0, pr: 2 }}>
              {dialogConfig.readOnly ? (
                // Simplified header for mentors
                <>
                  <Typography
                    variant="h5"
                    component="h2"
                    sx={{
                      fontWeight: 700,
                      color: '#111827',
                      fontSize: { xs: '1rem', sm: '1.25rem' },
                      lineHeight: 1.3,
                      mb: 0.5,
                    }}
                  >
                    {dialogConfig.startup.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#6B7280',
                      fontSize: { xs: '0.8rem', sm: '0.875rem' },
                      wordBreak: 'break-all'
                    }}
                  >
                    {dialogConfig.startup.email}
                  </Typography>
                </>
              ) : (
                // Full header for admins
                <>
                  <Typography
                    variant="h5"
                    component="h2"
                    sx={{
                      fontWeight: 700,
                      color: '#111827',
                      fontSize: { xs: '1rem', sm: '1.25rem' },
                      lineHeight: 1.3,
                      mb: 1,
                    }}
                  >
                    Review Profile: {dialogConfig.startup.name}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 0.5, sm: 1 }, alignItems: { xs: 'flex-start', sm: 'center' }, fontSize: { xs: '0.75rem', sm: '0.875rem' }, color: '#6B7280' }}>
                    <Box>
                      <Typography component="span" sx={{ fontWeight: 500 }}>Status:</Typography>{' '}
                      <Typography component="span" sx={{ color: '#374151' }}>
                        {(() => {
                          const status = dialogConfig.startup.status || '';
                          const normalized = status.toUpperCase().replace(/\s+/g, '_');
                          if (normalized === 'APPROVED') return 'Approved';
                          if (normalized === 'REJECTED') return 'Rejected';
                          if (normalized === 'UNDER_REVIEW' || normalized === 'UNDER REVIEW') return 'Under Review';
                          if (normalized === 'NEEDS_REVISION' || normalized === 'NEEDS REVISION') return 'Needs Revision';
                          if (normalized === 'RESUBMITTED' || normalized === 'RE-SUBMITTED') return 'Resubmitted';
                          return status || 'Draft';
                        })()}
                      </Typography>
                    </Box>
                    <Box sx={{ display: { xs: 'none', sm: 'block' }, color: '#D1D5DB' }}>|</Box>
                    <Box>
                      <Typography component="span" sx={{ fontWeight: 500 }}>Email:</Typography>{' '}
                      <Typography component="span" sx={{ color: '#374151', wordBreak: 'break-all' }}>
                        {dialogConfig.startup.email}
                      </Typography>
                    </Box>
                  </Box>
                </>
              )}
            </Box>
            <IconButton
              aria-label="close"
              onClick={handleCancel}
              size="small"
              sx={{
                color: '#6B7280',
                flexShrink: 0,
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>

          <DialogContent
            sx={{
              px: { xs: 1.5, sm: 3 },
              py: { xs: 1.5, sm: 3 },
              flex: 1,
              overflowY: 'auto',
              '&::-webkit-scrollbar': {
                display: 'none',
              },
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
            }}
          >
            {profileReviewLoading ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 6 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    border: '3px solid #E5E7EB',
                    borderTopColor: '#2563EB',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    mb: 2,
                    '@keyframes spin': {
                      '0%': { transform: 'rotate(0deg)' },
                      '100%': { transform: 'rotate(360deg)' },
                    },
                  }}
                />
                <Typography variant="body2" sx={{ color: '#6B7280' }}>
                  Loading profile data...
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 3 } }}>
                {/* Submitted Profile Sections */}
                {profileReviewData && (() => {
                  const filterOfficialProfileSections = (profileData) => {
                    if (!profileData || !profileData.profile) return {};
                    const officialSections = Object.values(PROFILE_SECTIONS);
                    const filteredProfile = {};
                    Object.keys(profileData.profile).forEach(sectionName => {
                      if (officialSections.includes(sectionName)) {
                        filteredProfile[sectionName] = profileData.profile[sectionName];
                      }
                    });
                    return filteredProfile;
                  };

                  const formatLabel = (key) => {
                    if (!key) return '';
                    // Custom label mappings
                    const normalizedKey = key.toString().toLowerCase().replace(/\s+/g, '_');
                    if (normalizedKey === 'employee_count' || normalizedKey === 'team_size') {
                      return 'Team Size';
                    }
                    // Default: convert underscores to spaces and capitalize each word
                    return key.toString().replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
                  };

                  const isCurrencyKey = (key) => {
                    if (!key) return false;
                    const k = key.toLowerCase();
                    return ['revenue', 'cost', 'costs', 'capex', 'opex', 'budget', 'amount', 'valuation', 'price', 'expenses', 'investment'].some((p) => k.includes(p));
                  };

                  const isPercentageKey = (key) => {
                    if (!key) return false;
                    const k = key.toLowerCase();
                    return ['percentage', 'percent', 'growth_rate', 'margin'].some((p) => k.includes(p));
                  };

                  const formatPrimitive = (value, key) => {
                    if (value === null || value === undefined || value === '') return '—';
                    if (typeof value === 'number') {
                      if (isCurrencyKey(key)) {
                        try {
                          return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(value);
                        } catch (_) {
                          return new Intl.NumberFormat().format(value);
                        }
                      }
                      if (isPercentageKey(key)) {
                        return `${value}%`;
                      }
                      return new Intl.NumberFormat().format(value);
                    }
                    if (typeof value === 'string') {
                      // Apply title case for sector and stage fields
                      const normalizedKey = key.toLowerCase().replace(/\s+/g, '_');
                      if (normalizedKey === 'sector' || normalizedKey === 'stage') {
                        // Convert to title case: capitalize first letter of each word
                        return value.toLowerCase().split(/[\s_-]+/).map(word =>
                          word.charAt(0).toUpperCase() + word.slice(1)
                        ).join(' ');
                      }

                      if (value.length > 120) {
                        return value.split(/\n+/).map((para, idx) => (
                          <Typography key={idx} sx={{ mb: 1, whiteSpace: 'pre-wrap', lineHeight: 1.6, color: '#374151' }}>
                            {para}
                          </Typography>
                        ));
                      }
                      return value;
                    }
                    return String(value);
                  };

                  // Fields to display for mentors (read-only mode)
                  const ALLOWED_FIELDS_FOR_MENTORS = [
                    'business_model',
                    'employee_count',
                    'team_size',  // Alternative field name for employee count
                    'go_to_market_strategy',
                    'revenue_model',
                    'value_proposition',
                    'overview',
                    'sector',
                    'stage',
                    'competitive_advantage',
                    'profitability_timeline'
                  ];

                  const renderDataValue = (value, key, isTopLevel = false) => {
                    if (Array.isArray(value)) {
                      if (value.length === 0) return '—';
                      return (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {value.map((item, idx) => (
                            <Box
                              key={idx}
                              sx={{
                                backgroundColor: '#F9FAFB',
                                borderRadius: 1.5,
                                p: 2,
                                border: '1px solid #E5E7EB',
                              }}
                            >
                              {typeof item === 'object' && item !== null ? (
                                renderDataValue(item, key, false)
                              ) : (
                                <Typography variant="body2" sx={{ color: '#374151' }}>
                                  {formatPrimitive(item, key)}
                                </Typography>
                              )}
                            </Box>
                          ))}
                        </Box>
                      );
                    }
                    if (value && typeof value === 'object') {
                      const entries = Object.entries(value).filter(([_, v]) => v !== undefined);

                      // Filter entries if in read-only mode (mentor view) and at top level
                      const filteredEntries = dialogConfig.readOnly && isTopLevel
                        ? entries.filter(([k, v]) => {
                          const normalizedKey = k.toLowerCase().replace(/\s+/g, '_');
                          // Check if field is in allowed list AND has valid data
                          const hasData = v !== null && v !== undefined && v !== '' && v !== '—';
                          return ALLOWED_FIELDS_FOR_MENTORS.includes(normalizedKey) && hasData;
                        })
                        : entries.filter(([_, v]) => {
                          // For admin view, also filter out empty values
                          return v !== null && v !== undefined && v !== '' && v !== '—';
                        });

                      if (filteredEntries.length === 0) return null; // Don't show anything if no data
                      return (
                        <Box>
                          {filteredEntries.map(([k, v]) => (
                            <Box key={k} sx={{ mb: 1.5, pb: 1.5, borderBottom: '1px solid #F3F4F6' }}>
                              <Typography variant="caption" sx={{ fontWeight: 600, color: '#6B7280', display: 'block', mb: 0.5 }}>
                                {formatLabel(k)}
                              </Typography>
                              <Typography variant="body2" sx={{ color: '#111827' }}>
                                {renderDataValue(v, k, false)}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      );
                    }
                    return formatPrimitive(value, key);
                  };

                  const filteredProfile = filterOfficialProfileSections(profileReviewData);
                  const normalizedStatus = dialogConfig.startup.status ? dialogConfig.startup.status.toUpperCase().replace(/\s+/g, '_') : '';
                  const isReadOnly = normalizedStatus === 'APPROVED' || normalizedStatus === 'REJECTED';

                  return (
                    <>
                      {Object.keys(filteredProfile).length > 0 ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 1 }}>
                          {Object.entries(filteredProfile).map(([sectionName, sectionData]) => (
                            <Box key={sectionName}>
                              {dialogConfig.readOnly ? (
                                // Mentor view: No section header, no box wrapper - just show fields
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                  <Box sx={{ fontSize: '0.875rem', color: '#111827' }}>
                                    {renderDataValue(sectionData.data, sectionName, true)}
                                  </Box>
                                </Box>
                              ) : (
                                // Admin view: Show section with header
                                <Box
                                  sx={{
                                    backgroundColor: '#FFFFFF',
                                    borderRadius: 2,
                                    p: 2,
                                    border: '1px solid #E5E7EB',
                                    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                                  }}
                                >
                                  {/* Section header with conditional completion and toggle */}
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: profileReviewExpandedSections[sectionName] ? 2 : 0 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#374151', textTransform: 'capitalize' }}>
                                      {sectionName.replace(/_/g, ' ')}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Typography variant="caption" sx={{ color: '#6B7280' }}>
                                        {sectionData.completion_percentage || 0}% Complete
                                      </Typography>
                                      <Button
                                        size="small"
                                        onClick={() => {
                                          setProfileReviewExpandedSections(prev => ({
                                            ...prev,
                                            [sectionName]: !prev[sectionName]
                                          }));
                                        }}
                                        sx={{
                                          textTransform: 'none',
                                          fontSize: '0.75rem',
                                          color: '#2563EB',
                                          minWidth: 'auto',
                                          px: 1,
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                        }}
                                      >
                                        <ExpandMoreIcon
                                          sx={{
                                            fontSize: 18,
                                            transform: profileReviewExpandedSections[sectionName]
                                              ? 'rotate(180deg)'
                                              : 'rotate(0deg)',
                                            transition: 'transform 0.2s ease-in-out',
                                          }}
                                        />
                                      </Button>
                                    </Box>
                                  </Box>

                                  {/* Section content - conditional for admins */}
                                  {profileReviewExpandedSections[sectionName] && (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                      {/* Section Data Display */}
                                      <Box sx={{ backgroundColor: '#F9FAFB', borderRadius: 1.5, p: 2 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#374151', mb: 1 }}>
                                          Submitted Data:
                                        </Typography>
                                        <Box sx={{ fontSize: '0.875rem', color: '#111827' }}>
                                          {renderDataValue(sectionData.data, sectionName, true)}
                                        </Box>
                                      </Box>

                                      {/* Show existing feedback if read-only (for approved/rejected profiles) */}
                                      {isReadOnly && profileReviewForm.feedback[sectionName] && (
                                        <Box sx={{ backgroundColor: '#DBEAFE', borderRadius: 1.5, p: 2, border: '1px solid #BFDBFE' }}>
                                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#374151', mb: 0.5 }}>
                                            Feedback:
                                          </Typography>
                                          <Typography variant="body2" sx={{ color: '#374151', whiteSpace: 'pre-wrap' }}>
                                            {profileReviewForm.feedback[sectionName]}
                                          </Typography>
                                        </Box>
                                      )}

                                      {/* Per-Section Feedback (only for admins in editable mode) */}
                                      {!isReadOnly && (
                                        <TextField
                                          fullWidth
                                          multiline
                                          rows={3}
                                          label={`Feedback for ${sectionName.replace(/_/g, ' ')}`}
                                          value={profileReviewForm.feedback[sectionName] || ''}
                                          onChange={(e) => setProfileReviewForm(prev => ({
                                            ...prev,
                                            feedback: {
                                              ...prev.feedback,
                                              [sectionName]: e.target.value
                                            }
                                          }))}
                                          placeholder={`Provide specific feedback for ${sectionName.replace(/_/g, ' ')}...`}
                                          sx={{
                                            '& .MuiOutlinedInput-root': {
                                              borderRadius: 1.5,
                                            },
                                          }}
                                        />
                                      )}
                                    </Box>
                                  )}
                                </Box>
                              )}
                            </Box>
                          ))}
                        </Box>
                      ) : (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                          <DocumentTextIcon sx={{ fontSize: 48, color: '#D1D5DB', mb: 2 }} />
                          <Typography variant="body1" sx={{ fontWeight: 500, color: '#374151', mb: 0.5 }}>
                            No Profile Data Available
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#6B7280' }}>
                            This startup hasn't submitted any profile sections yet.
                          </Typography>
                        </Box>
                      )}
                    </>
                  );
                })()}

                {/* Review Form - Dashboard Theme (only show for admins, not mentors) */}
                {!dialogConfig.readOnly && (() => {
                  const normalizedStatus = dialogConfig.startup.status ? dialogConfig.startup.status.toUpperCase().replace(/\s+/g, '_') : '';
                  const isReadOnly = normalizedStatus === 'APPROVED' || normalizedStatus === 'REJECTED';

                  if (isReadOnly) return null;

                  return (
                    <Box
                      sx={{
                        backgroundColor: '#FFFFFF',
                        borderRadius: 2,
                        p: 3,
                        border: '1px solid #E5E7EB',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                        <ChatBubbleLeftRightIcon sx={{ fontSize: 20, color: '#2563EB' }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#111827' }}>
                          Review Assessment
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {/* Status Selection */}
                        <FormControl fullWidth required error={Boolean(profileReviewErrors.status)}>
                          <InputLabel id="profile-review-status-label" sx={{ backgroundColor: '#fff', px: 1, ml: -1 }}>Review Decision</InputLabel>
                          <Select
                            labelId="profile-review-status-label"
                            id="profile-review-status"
                            value={profileReviewForm.status}
                            displayEmpty
                            renderValue={(selected) => {
                              if (!selected) {
                                return <span style={{ color: '#9CA3AF' }}>Select status</span>;
                              }
                              const statusLabels = {
                                'approved': 'Approved',
                                'needs_revision': 'Needs Revision',
                                'rejected': 'Rejected'
                              };
                              return statusLabels[selected] || selected;
                            }}
                            onChange={(e) => {
                              setProfileReviewForm(prev => ({ ...prev, status: e.target.value }));
                              if (profileReviewErrors.status) {
                                setProfileReviewErrors(prev => {
                                  const newErrors = { ...prev };
                                  delete newErrors.status;
                                  return newErrors;
                                });
                              }
                            }}
                            label="Review Decision"
                            sx={{
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderRadius: '12px',
                              },
                            }}
                          >
                            <MenuItem value="approved">Approved</MenuItem>
                            <MenuItem value="rejected">Rejected</MenuItem>
                            <MenuItem value="needs_revision">Needs Revision</MenuItem>
                          </Select>
                          {profileReviewErrors.status && (
                            <FormHelperText sx={{ color: '#DC2626', ml: 0 }}>
                              {profileReviewErrors.status}
                            </FormHelperText>
                          )}
                        </FormControl>

                        {/* General Comments */}
                        <TextField
                          fullWidth
                          multiline
                          rows={4}
                          label="General Comments"
                          placeholder="Provide overall feedback and comments..."
                          value={profileReviewForm.comments}
                          onChange={(e) => setProfileReviewForm(prev => ({ ...prev, comments: e.target.value }))}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '12px',
                            },
                          }}
                        />
                      </Box>
                    </Box>
                  );
                })()}
              </Box>
            )}
          </DialogContent>

          {/* Actions - Hide when readOnly is true */}
          {
            (() => {
              const normalizedStatus = dialogConfig.startup.status ? dialogConfig.startup.status.toUpperCase().replace(/\s+/g, '_') : '';
              const isReadOnly = normalizedStatus === 'APPROVED' || normalizedStatus === 'REJECTED' || dialogConfig.readOnly;

              if (isReadOnly) return null;

              return (
                <DialogActions
                  sx={{
                    px: { xs: 2, sm: 3 },
                    py: { xs: 2, sm: 2.5 },
                    gap: { xs: 1.5, sm: 1.5 },
                    backgroundColor: '#F9FAFB',
                    borderTop: '1px solid #E5E7EB',
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'flex-end',
                    alignItems: 'stretch',
                  }}
                >
                  <Button
                    onClick={handleCancel}
                    variant="text"
                    fullWidth
                    sx={{
                      minWidth: { xs: '100%', sm: 100 },
                      textTransform: 'none',
                      borderRadius: '12px',
                      px: 4,
                      py: 1.5,
                      backgroundColor: '#F1F5F9',
                      color: '#475569',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      border: 'none',
                      textAlign: 'center',
                      '&:hover': {
                        backgroundColor: '#E2E8F0',
                      },
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleConfirm}
                    variant="contained"
                    disabled={submittingProfileReview || !profileReviewForm.status}
                    fullWidth
                    sx={{
                      minWidth: { xs: '100%', sm: 150 },
                      textTransform: 'none',
                      borderRadius: '12px',
                      px: 4,
                      py: 1.5,
                      fontWeight: 700,
                      fontSize: '0.875rem',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                      '&.Mui-disabled': {
                        backgroundColor: '#F1F5F9',
                        color: '#94A3B8',
                      },
                      backgroundColor: 'var(--an-coral)',
                      '&:hover': {
                        backgroundColor: 'var(--an-coral-deep)',
                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                        transform: 'translateY(-1px)',
                      },
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                  >
                    {submittingProfileReview ? 'Submitting...' : 'Submit Review'}
                  </Button>
                </DialogActions>
              );
            })()
          }
        </Dialog >
      )}

      {/* Review History Modal */}
      {
        dialogConfig?.type === 'reviewhistory' && dialogConfig.startup && (
          <Dialog
            open={true}
            onClose={handleCancel}
            maxWidth="md"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: { xs: 0, sm: 3 },
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                maxHeight: { xs: '100vh', sm: '90vh' },
                height: { xs: '100vh', sm: 'auto' },
                margin: { xs: 0, sm: 'auto' },
              },
            }}
          >
            <DialogTitle
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                pb: { xs: 2, sm: 2.5 },
                pt: { xs: 2, sm: 3.5 },
                px: { xs: 2, sm: 4 },
                backgroundColor: '#ffffff',
                borderBottom: '1px solid #F1F5F9',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, flex: 1, pr: 4 }}>
                {/* Icon Circle */}
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: '16px',
                    backgroundColor: '#EEF2FF',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    border: '1px solidrgb(9, 107, 245)',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
                  }}
                >
                  <HistoryIcon sx={{ color: '#4F46E5', fontSize: 26 }} />
                </Box>

                {/* Title */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="h5"
                    component="h2"
                    sx={{
                      fontWeight: 700,
                      color: '#111827',
                      fontSize: '1.5rem',
                      lineHeight: 1.3,
                      mb: 0.5,
                    }}
                  >
                    Review History: {dialogConfig.startup.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#6B7280',
                      fontSize: '0.875rem',
                    }}
                  >
                    Email: <Box component="span" sx={{ fontWeight: 500, color: '#374151' }}>{dialogConfig.startup.email}</Box>
                  </Typography>
                </Box>
              </Box>

              <IconButton
                aria-label="close"
                onClick={handleCancel}
                size="small"
                sx={{
                  color: '#6B7280',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </DialogTitle>

            <DialogContent
              sx={{
                px: { xs: 2, sm: 4 },
                pt: 2,
                pb: 2,
                flex: 1,
                overflowY: 'auto',
                '&::-webkit-scrollbar': {
                  display: 'none',
                },
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
              }}
            >
              {reviewHistoryLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      border: '3px solid #E5E7EB',
                      borderTopColor: '#2563EB',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                      '@keyframes spin': {
                        '0%': { transform: 'rotate(0deg)' },
                        '100%': { transform: 'rotate(360deg)' },
                      },
                    }}
                  />
                </Box>
              ) : reviewHistoryData.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {reviewHistoryData.map((review, index) => {
                    // Utility function to normalize status
                    const normalizeStatus = (status) => {
                      if (!status) return status;
                      const upperStatus = status.toUpperCase().replace(/\s+/g, '_');
                      if (upperStatus === 'NEEDS_REVISION' || upperStatus === 'NEEDS REVISION') return 'NEEDS_REVISION';
                      if (upperStatus === 'UNDER_REVIEW' || upperStatus === 'UNDER REVIEW') return 'UNDER_REVIEW';
                      if (upperStatus === 'APPROVED') return 'APPROVED';
                      if (upperStatus === 'REJECTED') return 'REJECTED';
                      if (upperStatus === 'RESUBMITTED' || upperStatus === 'RE-SUBMITTED') return 'RESUBMITTED';
                      return status;
                    };

                    // Utility function to get status label
                    const getStatusLabel = (status) => {
                      if (!status) return 'Draft';
                      const normalized = normalizeStatus(status);
                      switch (normalized) {
                        case 'UNDER_REVIEW':
                          return 'Under Review';
                        case 'NEEDS_REVISION':
                          return 'Needs Revision';
                        case 'APPROVED':
                          return 'Approved';
                        case 'REJECTED':
                          return 'Rejected';
                        case 'RESUBMITTED':
                          return 'Resubmitted';
                        case 'DRAFT':
                          return 'Draft';
                        default:
                          return status || 'Draft';
                      }
                    };

                    // Get status color
                    const normalizedStatus = normalizeStatus(review.status);
                    const getStatusColor = () => {
                      switch (normalizedStatus) {
                        case 'APPROVED':
                          return { bg: '#D1FAE5', text: '#065F46' };
                        case 'REJECTED':
                          return { bg: '#FEE2E2', text: '#991B1B' };
                        case 'NEEDS_REVISION':
                          return { bg: '#FED7AA', text: '#92400E' };
                        case 'RESUBMITTED':
                          return { bg: '#DBEAFE', text: '#1E40AF' };
                        default:
                          return { bg: '#FED7AA', text: '#92400E' };
                      }
                    };

                    const statusColor = getStatusColor();

                    return (
                      <Box
                        key={index}
                        sx={{
                          backgroundColor: '#F9FAFB',
                          p: 3,
                          borderRadius: 2,
                          border: '1px solid #E5E7EB',
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                            <Chip
                              label={getStatusLabel(review.status)}
                              sx={{
                                backgroundColor: statusColor.bg,
                                color: statusColor.text,
                                fontWeight: 600,
                                fontSize: '0.875rem',
                                height: 28,
                              }}
                            />
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <CalendarIcon sx={{ fontSize: 16, color: '#9CA3AF' }} />
                              <Typography
                                variant="body2"
                                sx={{
                                  color: '#6B7280',
                                  fontSize: '0.875rem',
                                }}
                              >
                                {review.review_date ? formatUTCDate(review.review_date, 'date') : 'No date'}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>

                        {review.feedback && Object.keys(review.feedback).length > 0 && (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {/* General Comments */}
                            {review.feedback.general_comments && (
                              <Box>
                                <Typography
                                  variant="subtitle2"
                                  sx={{
                                    fontWeight: 600,
                                    color: '#374151',
                                    mb: 1,
                                    fontSize: '0.875rem',
                                  }}
                                >
                                  General Comments:
                                </Typography>
                                <Box
                                  sx={{
                                    backgroundColor: '#FFFFFF',
                                    p: 2,
                                    borderRadius: 1,
                                    border: '1px solid #E5E7EB',
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: '#374151',
                                      whiteSpace: 'pre-wrap',
                                      fontSize: '0.875rem',
                                      lineHeight: 1.6,
                                    }}
                                  >
                                    {review.feedback.general_comments}
                                  </Typography>
                                </Box>
                              </Box>
                            )}

                            {/* Section Feedback */}
                            {review.feedback.section_feedback &&
                              Object.entries(review.feedback.section_feedback).filter(([section, feedback]) => feedback && feedback.trim() !== '').length > 0 && (
                                <Box>
                                  <Typography
                                    variant="subtitle2"
                                    sx={{
                                      fontWeight: 600,
                                      color: '#374151',
                                      mb: 1.5,
                                      fontSize: '0.875rem',
                                    }}
                                  >
                                    Section Feedback:
                                  </Typography>
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                    {Object.entries(review.feedback.section_feedback)
                                      .filter(([section, feedback]) => feedback && feedback.trim() !== '')
                                      .map(([section, feedback]) => (
                                        <Box
                                          key={section}
                                          sx={{
                                            backgroundColor: '#FFFFFF',
                                            p: 2,
                                            borderRadius: 1,
                                            border: '1px solid #E5E7EB',
                                          }}
                                        >
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              fontWeight: 600,
                                              color: '#6B7280',
                                              textTransform: 'capitalize',
                                              fontSize: '0.75rem',
                                              display: 'block',
                                              mb: 0.5,
                                            }}
                                          >
                                            {section.replace(/_/g, ' ')}:
                                          </Typography>
                                          <Typography
                                            variant="body2"
                                            sx={{
                                              color: '#374151',
                                              whiteSpace: 'pre-wrap',
                                              fontSize: '0.875rem',
                                              lineHeight: 1.6,
                                            }}
                                          >
                                            {feedback}
                                          </Typography>
                                        </Box>
                                      ))}
                                  </Box>
                                </Box>
                              )}
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      backgroundColor: '#F3F4F6',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 2,
                    }}
                  >
                    <CalendarIcon sx={{ fontSize: 32, color: '#D1D5DB' }} />
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 500,
                      color: '#374151',
                      mb: 1,
                      fontSize: '1.125rem',
                    }}
                  >
                    No Review History
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#6B7280',
                      fontSize: '0.875rem',
                    }}
                  >
                    This startup hasn't been reviewed yet
                  </Typography>
                </Box>
              )}
            </DialogContent>
          </Dialog>
        )
      }

      {/* Context Review Dialog */}
      {dialogConfig?.type === 'contextreview' && (
        <Dialog
          open={true}
          onClose={handleCancel}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: { xs: 0, sm: 3 },
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              maxHeight: { xs: '100vh', sm: '90vh' },
              height: { xs: '100vh', sm: 'auto' },
              margin: { xs: 0, sm: 'auto' },
            },
          }}
        >
          <DialogTitle
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              pb: { xs: 2, sm: 2.5 },
              pt: { xs: 2, sm: 3.5 },
              px: { xs: 2, sm: 4 },
              backgroundColor: '#ffffff',
              borderBottom: '1px solid #E5E7EB',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  backgroundColor: 'var(--an-coral)',
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <CheckIcon sx={{ color: '#ffffff', fontSize: 20 }} />
              </Box>
              <Typography
                variant="h5"
                component="h2"
                sx={{
                  fontWeight: 700,
                  color: '#111827',
                  fontSize: { xs: '1rem', sm: '1.25rem' },
                  lineHeight: 1.3,
                }}
              >
                Review Assessment
              </Typography>
            </Box>
            <IconButton
              aria-label="close"
              onClick={handleCancel}
              size="small"
              sx={{
                color: '#6B7280',
                flexShrink: 0,
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>

          <DialogContent
            sx={{
              px: { xs: 2, sm: 3 },
              py: { xs: 2, sm: 3 },
              flex: 1,
              overflowY: 'auto',
              '&::-webkit-scrollbar': {
                display: 'none',
              },
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 3 } }}>
              {/* Review Decision */}
              <FormControl fullWidth error={!!contextReviewErrors.status} sx={{ mt: '24px !important' }}>
                <InputLabel id="review-decision-label" sx={{ backgroundColor: '#fff', px: 1, ml: -1 }}>Review Decision</InputLabel>
                <Select
                  labelId="review-decision-label"
                  value={contextReviewForm.status || ''}
                  onChange={(e) => {
                    const newStatus = e.target.value;
                    // Update ref first so validation always sees latest value
                    contextReviewStatusRef.current = newStatus;
                    // Preserve section comments when status changes - don't lose them!
                    setContextReviewForm(prev => ({
                      ...prev,
                      status: newStatus,
                      // Keep existing sectionComments and overallComment
                      sectionComments: prev.sectionComments || {},
                      overallComment: prev.overallComment || ''
                    }));
                    // Clear status error when a valid status is selected
                    const trimmedStatus = newStatus ? String(newStatus).trim() : '';
                    if (trimmedStatus && ['APPROVED', 'REJECTED', 'NEEDS_REVISION'].includes(trimmedStatus.toUpperCase())) {
                      setContextReviewErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors.status;
                        return newErrors;
                      });
                    }
                  }}
                  label="Review Decision"
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected || selected === '') {
                      return <span style={{ color: '#9CA3AF' }}>Select status</span>;
                    }
                    const statusLabels = {
                      'APPROVED': 'Approved',
                      'NEEDS_REVISION': 'Needs Revision',
                      'REJECTED': 'Rejected'
                    };
                    return statusLabels[selected] || selected;
                  }}
                  sx={{
                    borderRadius: '12px',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderRadius: '12px',
                    },
                  }}
                >
                  <MenuItem value="APPROVED">Approved</MenuItem>
                  <MenuItem value="NEEDS_REVISION">Needs Revision</MenuItem>
                  <MenuItem value="REJECTED">Rejected</MenuItem>
                </Select>
                {contextReviewErrors.status && (
                  <FormHelperText>{contextReviewErrors.status}</FormHelperText>
                )}
              </FormControl>

              {/* Selected Sections for Revision - Only show when status is NEEDS_REVISION */}
              {contextReviewForm.status === 'NEEDS_REVISION' && contextReviewSelectedSections.length > 0 && (
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      color: '#374151',
                      mb: 2,
                      fontSize: '0.875rem',
                    }}
                  >
                    Sections Marked for Revision ({contextReviewSelectedSections.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {contextReviewSelectedSections.map(sectionId => {
                      const section = contextReviewSections.find(s => s.id === sectionId);
                      const hasComment = contextReviewForm.sectionComments[sectionId] && contextReviewForm.sectionComments[sectionId].trim().length > 0;
                      const isMissingComment = submitAttemptedContextReview && !hasComment;
                      return (
                        <Box
                          key={sectionId}
                          sx={{
                            border: hasComment ? '1px solid #86EFAC' : '1px solid #FED7AA',
                            borderRadius: 2,
                            p: 2,
                            backgroundColor: hasComment ? '#F0FDF4' : '#FFF7ED',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                            {section?.icon && React.createElement(section.icon, {
                              className: 'section-icon',
                              style: {
                                width: '16px',
                                height: '16px',
                                color: hasComment ? '#16A34A' : '#EA580C',
                                flexShrink: 0
                              }
                            })}
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                color: hasComment ? '#166534' : '#9A3412',
                                fontSize: '0.875rem',
                              }}
                            >
                              {section?.label || sectionId}
                            </Typography>
                          </Box>
                          <TextField
                            fullWidth
                            multiline
                            rows={3}
                            label="Section Comments"
                            value={contextReviewForm.sectionComments[sectionId] || ''}
                            onChange={(e) => {
                              setContextReviewForm(prev => ({
                                ...prev,
                                sectionComments: {
                                  ...prev.sectionComments,
                                  [sectionId]: e.target.value
                                }
                              }));
                              if (submitAttemptedContextReview && e.target.value.trim()) {
                                setSubmitAttemptedContextReview(false);
                              }
                            }}
                            placeholder="Provide specific feedback for this section (required)..."
                            error={isMissingComment}
                            helperText={isMissingComment ? 'Please provide feedback for this section' : ''}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                              },
                            }}
                            required
                          />
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}

              {/* General Comments */}
              <TextField
                fullWidth
                multiline
                rows={4}
                label="General Comments"
                value={contextReviewForm.overallComment}
                onChange={(e) => setContextReviewForm(prev => ({ ...prev, overallComment: e.target.value }))}
                placeholder={
                  contextReviewForm.status === 'REJECTED'
                    ? 'Please provide a reason for rejection...'
                    : 'Optional overall comments or notes...'
                }
                error={!!contextReviewErrors.overallComment}
                helperText={contextReviewErrors.overallComment}
                required={contextReviewForm.status === 'REJECTED'}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                  },
                }}
              />

              {/* Information Note */}
              {contextReviewForm.status === 'NEEDS_REVISION' && contextReviewSelectedSections.length === 0 && (
                <Box
                  sx={{
                    backgroundColor: '#EFF6FF',
                    border: '1px solid #BFDBFE',
                    borderRadius: 2,
                    p: 2,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <FlagIcon sx={{ fontSize: 20, color: '#2563EB', flexShrink: 0, mt: 0.5 }} /> {/* Material-UI icon - sx prop is correct */}
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: '#1E40AF',
                          mb: 0.5,
                          fontSize: '0.875rem',
                        }}
                      >
                        Note
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#1E3A8A',
                          fontSize: '0.875rem',
                        }}
                      >
                        To mark this profile as "Needs Revision", please mark at least one section for revision using the "Mark for Revision" button on section headers, then provide comments for those sections.
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          </DialogContent>

          <DialogActions
            sx={{
              px: { xs: 2, sm: 3 },
              py: { xs: 2, sm: 2.5 },
              gap: { xs: 1.5, sm: 1.5 },
              backgroundColor: '#F9FAFB',
              borderTop: '1px solid #E5E7EB',
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'flex-end',
              alignItems: 'stretch',
            }}
          >
            <Button
              onClick={handleCancel}
              variant="text"
              fullWidth
              sx={{
                minWidth: { xs: '100%', sm: 100 },
                textTransform: 'none',
                borderRadius: '12px',
                px: 4,
                py: 1.5,
                backgroundColor: '#F1F5F9',
                color: '#475569',
                fontWeight: 600,
                fontSize: '0.875rem',
                border: 'none',
                textAlign: 'center',
                '&:hover': {
                  backgroundColor: '#E2E8F0',
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              variant="contained"
              disabled={
                submittingContextReview ||
                !contextReviewForm.status ||
                !contextReviewForm.status.trim() ||
                !['APPROVED', 'REJECTED', 'NEEDS_REVISION'].includes(contextReviewForm.status.trim().toUpperCase()) ||
                (contextReviewForm.status.trim().toUpperCase() === 'NEEDS_REVISION' && contextReviewSelectedSections.length === 0)
              }
              fullWidth
              sx={{
                minWidth: { xs: '100%', sm: 150 },
                textTransform: 'none',
                borderRadius: '12px',
                px: 4,
                py: 1.5,
                fontWeight: 700,
                fontSize: '0.875rem',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                '&.Mui-disabled': {
                  backgroundColor: '#F1F5F9',
                  color: '#94A3B8',
                },
                backgroundColor: 'var(--an-coral)',
                '&:hover': {
                  backgroundColor: 'var(--an-coral-deep)',
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                  transform: 'translateY(-1px)',
                },
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              {submittingContextReview ? 'Submitting...' : 'Submit Review'}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </DialogContext.Provider >
  );
};

export const useDialog = () => {
  const context = useContext(DialogContext);
  if (!context) {
    throw new Error('useDialog must be used within a DialogProvider');
  }
  return context;
};

